import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Edit2, Trash2, Car, User, Phone, MapPin, Calendar, X, Check, Filter, Save, Package, Upload, Download, Users, Box, Briefcase, FileText, DollarSign, Clock, AlertCircle, TrendingUp, Bell, PhoneCall, PhoneMissed, PhoneOff, MessageSquare, Target, Zap, ChevronDown, TrendingDown, Wallet, Info, BarChart2, CreditCard, Receipt, ArrowUpCircle, ArrowDownCircle, RefreshCw, Send, Flag, CheckCircle, ArrowRight, Tag, Wrench, MessageCircle, Lock, Key, LogOut, Settings, Camera, Ban } from 'lucide-react';
import UserManagement from './UserManagement';
import LiveMap from './LiveMap';
import PipelineModule from './PipelineModule';
import { resolveUserRole, buildModuleAccess, APP_MODULES, REPORT_SECTIONS } from './permissions';

// ===============================================
// API CONFIGURATION
// ===============================================
// Auto-switch: on localhost (npm start) → local XAMPP API; anywhere else → live.
// So the deployed build always uses live and local dev always uses local — no manual edits.
const IS_LOCAL = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const API_BASE_URL = IS_LOCAL ? 'http://localhost/vehicle-api' : 'https://crm.securewheels.pk/api';
// ===============================================
// INVOICE DUE DATE CONFIGURATION
// ===============================================
// Change this value to set how many days until invoice is due
// Examples: 15 days, 21 days, 30 days, 45 days, 60 days
const INVOICE_DUE_DAYS = 30;  // ← CHANGE THIS NUMBER TO ADJUST DUE DATE PERIOD

// ===============================================
// DATA TRANSFORM
// ===============================================
// Maps a raw API customer (snake_case, nested vehicles + invoices) to the
// frontend shape. Shared by the full load (loadCustomers) and the lazy
// single-customer detail fetch so both stay perfectly in sync.
const transformCustomer = (c) => ({
  id: c.id.toString(),
  name: c.name,
  type: c.type,
  gender: c.gender,
  phone: c.phone || '',
  cell: c.cell || '',
  whatsapp: c.whatsapp || '',
  email: c.email || '',
  cnic: c.cnic || '',
  cpContact: c.cp_contact || '',
  address: c.address || '',
  city: c.city || '',
  state: c.state || '',
  country: c.country || 'Pakistan',
  contactPerson: c.contact_person || '',
  pocEmail: c.poc_email || '',
  followUp: c.follow_up_by || '',
  salesperson: c.salesperson || '',
  invoiceCount: parseInt(c.invoice_count) || 0,
  vehicles: (c.vehicles || []).map(v => ({
    id: parseInt(v.id),
    vehicleNo: v.vehicle_no,
    make: v.make || '',
    model: v.model || '',
    fuelAverage: v.fuel_average || '',
    engineNo: v.engine_no || '',
    chassisNo: v.chassis_no || '',
    activeDate: v.active_date || '',
    serviceUpto: v.service_upto || '',
    status: v.status,
    invoiceStatus: v.invoice_status,
    items: parseInt(v.items) || 0,
    imei: v.imei || '',
    deviceName: v.device_name || '',
    packagePrice: v.package_price ?? '',   // '' = not entered, 0 = free
    amc: v.amc ?? '',
    commission: v.commission ?? '',          // Rs, never shown on the invoice
    invoiceFullSetup: Boolean(v.invoice_full_setup), // true = bill full setup, ignore commission
    invoicingPeriod: v.invoicing_period || 'Monthly',
    division: v.division || '',
    division_id: v.division_id != null ? Number(v.division_id) : null,
    salesperson: v.salesperson || '',
    setupInvoiceCreated: Boolean(v.setup_invoice_created),
    invoices: (v.invoices || []).map(inv => ({
      id: parseInt(inv.id),
      invoice_number: inv.invoice_number,
      customer_id: parseInt(inv.customer_id),
      vehicle_id: parseInt(inv.vehicle_id),
      customer_name: inv.customer_name,
      vehicle_no: inv.vehicle_no,
      type: inv.type,
      amount: parseFloat(inv.amount),
      paid_amount: parseFloat(inv.paid_amount),
      balance: parseFloat(inv.balance),
      status: inv.status,
      due_date: inv.due_date,
      issue_date: inv.issue_date,
      created_date: inv.created_date,
      period_from: inv.period_from,
      period_to: inv.period_to,
      payments: inv.payments || []
    }))
  }))
});

// Display-only customer code: prefix by type (Corporate=CO, else IN) + 5-digit
// zero-padded id, e.g. IN-00045 / CO-00045. Never used for lookups — the raw
// numeric id stays the source of truth.
const formatCustomerCode = (id, type) => {
  const prefix = String(type || '').toLowerCase() === 'corporate' ? 'CO' : 'IN';
  return `${prefix}-${String(parseInt(id, 10) || 0).padStart(5, '0')}`;
};

// Download an array of rows as a CSV (opens cleanly in Excel). UTF-8 BOM + quoting.
const downloadCSV = (filename, headers, rows) => {
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const csv = [headers.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 500);
};

const api = {
  async call(endpoint, method = 'GET', data = null) {
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (data && method !== 'GET') {
      options.body = JSON.stringify(data);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, options);
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'API request failed');
      return result;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  },
  customers: {
    getAll: () => api.call('customers.php'),
    // Slim, server-paginated + searchable list for the sidebar (scalable).
    list: (page = 1, limit = 25, search = '') =>
      api.call(`customers.php?mode=list&page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
    // Full nested detail for a single customer (lazy-loaded on select).
    detail: (id) => api.call(`customers.php?mode=detail&id=${encodeURIComponent(id)}`),
    create: (data) => api.call('customers.php', 'POST', data),
    update: (data) => api.call('customers.php', 'PUT', data),
    delete: (id) => api.call('customers.php', 'DELETE', { id }),
  },
  vehicles: {
    create: (data) => api.call('vehicles.php', 'POST', data),
    update: (data) => api.call('vehicles.php', 'PUT', data),
    delete: (id) => api.call('vehicles.php', 'DELETE', { id }),
    getNumberChanges: (vehicleId) => api.call(`vehicles.php?action=number_changes&vehicle_id=${vehicleId}`),
    // Pull engine + chassis from the GPSWOX devices table (match on vehicle no + imei).
    getGpswoxDevice: (vehicleNo, imei) =>
      api.call(`vehicles.php?action=gpswox_device&vehicle_no=${encodeURIComponent(vehicleNo)}&imei=${encodeURIComponent(imei)}`),
    getGpswoxByVehicle: (vehicleNo) =>
      api.call(`vehicles.php?action=gpswox_by_vehicle&vehicle_no=${encodeURIComponent(vehicleNo)}`),
  },
  invoices: {
    getAll: () => api.call('invoices.php'),
    create: (data) => api.call('invoices.php', 'POST', data),
    generateInvoiceNumber: () => api.call('invoices.php', 'PUT', { action: 'generate_invoice_number' }),
    cancel: (id, reason) => api.call('invoices.php', 'PUT', { action: 'cancel', id, reason: reason || '' }),
  },
  quotations: {
    getAll: () => api.call('quotations.php'),
    getOne: (id) => api.call(`quotations.php?id=${id}`),
    generateNumber: () => api.call('quotations.php?action=generate_number'),
    create: (data) => api.call('quotations.php', 'POST', data),
    update: (data) => api.call('quotations.php', 'PUT', data),
    delete: (id) => api.call('quotations.php', 'DELETE', { id }),
  },
  payments: {
    create: (data) => api.call('payments.php', 'POST', data),
  },
  notifications: {
    officeList: () => api.call('notifications.php?scope=office'),
    officeUnread: () => api.call('notifications.php?scope=office&action=unread_count'),
    officeMarkRead: (id) => api.call('notifications.php?action=mark_read', 'POST', id ? { id } : { scope: 'office' }),
  },
  inventory: {
    getAll: () => api.call('inventory.php'),
    create: (data) => api.call('inventory.php', 'POST', data),
    update: (data) => api.call('inventory.php', 'PUT', data),
    delete: (id) => api.call('inventory.php', 'DELETE', { id }),
  },
  imeiLogs: {
    getByImei: (imei) => api.call(`imei_logs.php?imei=${encodeURIComponent(imei)}`),
  },
  reports: {
    stockIssuance: (params) => api.call(`stock_issuance_report.php?${new URLSearchParams(params).toString()}`),
    imeiRevenue: (imei) => api.call(`imei_revenue_report.php?imei=${encodeURIComponent(imei)}`),
    vehicleRevenue: (vehicleNo) => api.call(`vehicle_revenue_report.php?vehicle_no=${encodeURIComponent(vehicleNo)}`),
  },
  vendors: {
    getAll: () => api.call('vendors.php'),
    create: (data) => api.call('vendors.php', 'POST', data),
    update: (data) => api.call('vendors.php', 'PUT', data),
    delete: (id) => api.call('vendors.php', 'DELETE', { id }),
  },
  devices: {
    getAll: () => api.call('devices.php'),
    create: (data) => api.call('devices.php', 'POST', data),
    update: (data) => api.call('devices.php', 'PUT', data),
    delete: (id) => api.call('devices.php', 'DELETE', { id }),
  },
  vehicleModels: {
    getAll: () => api.call('vehicle_models.php'),
    create: (data) => api.call('vehicle_models.php', 'POST', data),
    update: (data) => api.call('vehicle_models.php', 'PUT', data),
    delete: (id) => api.call('vehicle_models.php', 'DELETE', { id }),
  },
  cities: {
    getAll: () => api.call('cities.php'),
    create: (data) => api.call('cities.php', 'POST', data),
    update: (data) => api.call('cities.php', 'PUT', data),
    delete: (id) => api.call('cities.php', 'DELETE', { id }),
  },
  employees: {
    getAll: () => api.call('employees.php'),
    create: (data) => api.call('employees.php', 'POST', data),
    update: (data) => api.call('employees.php', 'PUT', data),
    delete: (id) => api.call('employees.php', 'DELETE', { id, action: 'delete' }),
    deactivate: (id) => api.call('employees.php', 'DELETE', { id, action: 'deactivate' }),
  },
  divisions: {
    getAll: (customerId) => api.call(`divisions.php${customerId ? '?customer_id=' + customerId : ''}`),
    create: (data) => api.call('divisions.php', 'POST', data),
    update: (data) => api.call('divisions.php', 'PUT', data),
    delete: (id) => api.call('divisions.php', 'DELETE', { id }),
  },
  followups: {
    getAll:          ()     => api.call('followups.php?mode=customer'),
    saveCallLog:     (data) => api.call('followups.php?mode=customer', 'POST', data),
    deleteLog:       (id)   => api.call('followups.php', 'DELETE', { logId: id }),
  },
  // Vehicle-wise invoice follow-ups (Invoices → Follow-Up)
  vehicleFollowups: {
    getAll: ()     => api.call('vehicle_followups.php'),
    save:   (data) => api.call('vehicle_followups.php', 'POST', data),
  },
  cashflow: {
    getSummary:  (month, from, to) => api.call(`cashflow.php?type=summary${month ? '&month='+month : ''}${from&&to ? '&from='+from+'&to='+to : ''}`),
    getOutflows: (month, from, to) => api.call(`cashflow.php?type=outflows${month ? '&month='+month : ''}${from&&to ? '&from='+from+'&to='+to : ''}`),
    getInflows:  (month, from, to) => api.call(`cashflow.php?type=inflows${month ? '&month='+month : ''}${from&&to ? '&from='+from+'&to='+to : ''}`),
    addEntry:    (data)  => api.call('cashflow.php', 'POST',   data),
    updateEntry: (data)  => api.call('cashflow.php', 'PUT',    data),
    deleteEntry: (data)  => api.call('cashflow.php', 'DELETE', data),
    getCategories: ()    => api.call('cashflow.php?type=categories'),
    addCategory:    (d)  => api.call('cashflow.php', 'POST',   { entryType: 'category', ...d }),
    updateCategory: (d)  => api.call('cashflow.php', 'PUT',    { entryType: 'category', ...d }),
    deleteCategory: (id) => api.call('cashflow.php', 'DELETE', { entryType: 'category', id }),
    getAdvances:    ()   => api.call('cashflow.php?type=advances'),
    issueAdvance:   (d)  => api.call('cashflow.php', 'POST',   { entryType: 'advance_issue',  ...d }),
    returnAdvance:  (d)  => api.call('cashflow.php', 'POST',   { entryType: 'advance_return', ...d }),
    adjustAdvance:  (d)  => api.call('cashflow.php', 'POST',   { entryType: 'advance_adjust', ...d }),
    deleteAdvance:  (id) => api.call('cashflow.php', 'DELETE', { entryType: 'advance', id }),
  },
  complaints: {
    getAll:          (qs = '') => api.call(`complaints.php${qs}`),
    getOne:          (id)      => api.call(`complaints.php?id=${id}`),
    getStats:        ()        => api.call('complaints.php?action=stats'),
    getInstVehicles: (id)      => api.call(`complaints.php?action=inst_vehicles&id=${id}`),
    create:          (d)       => api.call('complaints.php', 'POST', d),
    bulkDelete:      (d)       => api.call('complaints.php?action=bulk_delete', 'POST', d),
    update:          (d)       => api.call('complaints.php', 'PUT', d),
    setStatus:       (d)       => api.call('complaints.php?action=status', 'PUT', d),
    setTaskStatus:   (d)       => api.call('complaints.php?action=task_status', 'PUT', d),
    setCustomer:     (d)       => api.call('complaints.php?action=set_customer', 'PUT', d),
    convert:         (d)       => api.call('complaints.php?action=convert', 'PUT', d),
    assign:          (d)       => api.call('complaints.php?action=assign', 'PUT', d),
    complete:        (d)       => api.call('complaints.php?action=complete', 'PUT', d),
    saveInstVehicle: (d)       => api.call('complaints.php?action=inst_vehicle', 'POST', d),
    addReply:        (d)       => api.call('complaints.php?action=reply', 'POST', d),
    getReplies:      (id)      => api.call(`complaints.php?action=replies&id=${id}`),
    delete:          (id)      => api.call('complaints.php', 'DELETE', { id }),
  },
  reconciliation: {
    getNRVehicles: () => api.call('nr_vehicles.php'),
    getFullReport: () => api.call('reconciliation.php'),
  },
};


// Office notification bell — reads the shared office feed (employee_id=0) that the
// backend fills on technician actions (job submitted, technician remark). Self-contained.
function NotificationBell({ onOpenTicket }) {
  const [open, setOpen]     = React.useState(false);
  const [items, setItems]   = React.useState([]);
  const [unread, setUnread] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const ref = React.useRef(null);

  const loadUnread = React.useCallback(async () => {
    try { const r = await api.notifications.officeUnread(); if (r?.success) setUnread(r.data?.unread || 0); } catch {}
  }, []);
  const loadList = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.notifications.officeList();
      if (r?.success) { const d = r.data || []; setItems(d); setUnread(d.filter(n => !n.is_read).length); }
    } catch {} finally { setLoading(false); }
  }, []);

  React.useEffect(() => { loadUnread(); const t = setInterval(loadUnread, 30000); return () => clearInterval(t); }, [loadUnread]);
  React.useEffect(() => { if (open) loadList(); }, [open, loadList]);
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const markAll = async () => {
    try { await api.notifications.officeMarkRead(); } catch {}
    setItems(prev => prev.map(n => ({ ...n, is_read: 1 }))); setUnread(0);
  };
  const clickRow = async (n) => {
    if (!n.is_read) {
      try { await api.notifications.officeMarkRead(n.id); } catch {}
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    if (onOpenTicket) onOpenTicket(n);
  };

  const timeAgo = (raw) => {
    if (!raw) return '';
    const then = new Date(raw.replace(' ', 'T')).getTime();
    if (isNaN(then)) return raw;
    const diff = Date.now() - then;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return new Date(then).toLocaleDateString();
  };
  const rowIcon = (type) => type === 'tech_submitted'
    ? <CheckCircle size={16} className="text-green-600" />
    : type === 'tech_remark' ? <MessageCircle size={16} className="text-red-500" />
    : <Bell size={16} className="text-slate-400" />;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} title="Notifications"
              className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="font-bold text-slate-800 text-sm">Notifications</span>
            {unread > 0 && <button onClick={markAll} className="text-xs font-semibold text-red-600 hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {loading ? <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>
             : items.length === 0 ? <div className="p-6 text-center text-slate-400 text-sm">No notifications</div>
             : items.map(n => (
               <button key={n.id} onClick={() => clickRow(n)}
                       className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${!n.is_read ? 'bg-red-50/40' : ''}`}>
                 <div className="mt-0.5 shrink-0">{rowIcon(n.type)}</div>
                 <div className="min-w-0 flex-1">
                   <p className="text-sm font-semibold text-slate-800 truncate">{n.title}</p>
                   {n.body && <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>}
                   <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                 </div>
                 {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />}
               </button>
             ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Searchable employee picker — type to filter by name / code / designation.
// `activeOnly` hides inactive employees (used for issuance; return shows all).
// Shows the employee code next to the name.
function EmployeeSearchSelect({ employees, value, onChange, activeOnly = false, accent = 'blue', placeholder = 'Search employee by name or code…' }) {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const selected = (employees || []).find(e => String(e.id) === String(value));
  const query = q.trim().toLowerCase();
  const list = (employees || [])
    .filter(e => !activeOnly || (e.status || 'active') !== 'inactive')
    .filter(e => !query || [e.name, e.employee_code, e.designation, e.department].some(f => (f || '').toLowerCase().includes(query)))
    .slice(0, 40);
  const ring = accent === 'green' ? 'focus:border-green-500 focus:ring-green-200' : 'focus:border-blue-500 focus:ring-blue-200';

  if (selected) {
    return (
      <div className="flex items-center gap-2 w-full px-3 py-2 rounded-xl border-2 border-slate-300 bg-white">
        <User size={16} className="text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{selected.name}</p>
          <p className="text-xs text-slate-500 truncate">{selected.employee_code || 'No code'}{selected.designation ? ` · ${selected.designation}` : ''}</p>
        </div>
        <button type="button" onClick={() => { onChange(''); setQ(''); }} className="p-1 text-slate-400 hover:text-red-500 shrink-0" title="Change">
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className={`w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-slate-300 outline-none focus:ring-2 transition-all ${ring}`}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {list.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400">No {activeOnly ? 'active ' : ''}employees found</div>
          ) : list.map(emp => (
            <button
              key={emp.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(String(emp.id)); setQ(''); setOpen(false); }}
              className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-slate-50 last:border-0"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">{emp.name}</span>
                {emp.employee_code && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{emp.employee_code}</span>}
                {(emp.status || 'active') === 'inactive' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">inactive</span>}
              </div>
              {emp.designation && <div className="text-xs text-slate-500">{emp.designation}{emp.department ? ` · ${emp.department}` : ''}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CustomerVehicleManagement({ currentUser: currentUserProp, onLogout, onChangePassword } = {}) {
  const [activeModule, setActiveModule] = useState('customers'); // 'customers', 'inventory', 'vendors', 'devices', 'employees', 'invoices', 'cashflow', 'complaints', 'reconciliation', 'user_management'

  // ── Role-based access ─────────────────────────────────────────────────────
  // roleAccess === null → not resolved yet (treat as "show all" to avoid a flash
  // of hidden tabs). Once roles.php loads we compute the real module whitelist.
  const [roleAccess, setRoleAccess] = useState(null);

  const authUser = React.useMemo(() => {
    if (currentUserProp) return currentUserProp;
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return u && u.user ? u.user : u; // unwrap { user: {...} } if present
    } catch { return null; }
  }, [currentUserProp]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/roles.php`);
        const json = await res.json();
        const roles = Array.isArray(json) ? json : (json.data || []);
        const role = resolveUserRole(authUser, roles);
        if (!cancelled) setRoleAccess(buildModuleAccess(role));
      } catch (e) {
        console.warn('[ACCESS] roles.php load failed — opening all modules:', e);
        if (!cancelled) setRoleAccess(buildModuleAccess(null)); // fail-open
      }
    })();
    return () => { cancelled = true; };
  }, [authUser]);

  // Before roleAccess resolves, show everything; afterwards apply the whitelist.
  const canSee = React.useCallback(
    (moduleKey) => !roleAccess || roleAccess.can(moduleKey),
    [roleAccess]
  );
  // Action-level gate: can this role create/edit/delete in a module?
  const canDo = React.useCallback(
    (moduleKey, action) => !roleAccess || roleAccess.canAction(moduleKey, action),
    [roleAccess]
  );
  // Micro-level: can this role open a specific report section / report head?
  const canReport = React.useCallback(
    (sectionKey) => !roleAccess || roleAccess.canReport(sectionKey),
    [roleAccess]
  );
  const canReportHead = React.useCallback(
    (head) => REPORT_SECTIONS.some((s) => s.head === head && canReport(s.key)),
    [canReport]
  );
  const canSeeAnyManagement = APP_MODULES.some(
    (m) => m.group === 'management' && canSee(m.key)
  );

  // If the current tab isn't permitted for this role, jump to the first one that is.
  useEffect(() => {
    if (!roleAccess) return;
    if (!roleAccess.can(activeModule)) {
      const first = APP_MODULES.find((m) => roleAccess.can(m.key));
      if (first) setActiveModule(first.key);
    }
  }, [roleAccess, activeModule]);

  // ── Reconciliation State ──────────────────────────────────────────────────
  const [reconData, setReconData] = useState(null);
  const [reconApproving, setReconApproving] = useState(null); // crm_id being approved (Low Pricing tab)
  // Expired Vehicles → "Extend" (any user, max 30 days, reason required, logged server-side)
  const [reconExtend, setReconExtend] = useState(null);   // expired row being extended
  // Invoice details popup → GPS Service box (expiry per vehicle of the invoice + Extend / Block)
  const [invGps, setInvGps]       = useState(null);   // [{vehicle_id, vehicle_no, has_device, expiry_pk, expired}] | null
  const [gpsDialog, setGpsDialog] = useState(null);   // { mode: 'extend'|'block', vehicles: [...] }
  const [extUntil, setExtUntil]       = useState('');
  const [extReason, setExtReason]     = useState('');
  const [extSaving, setExtSaving]     = useState(false);
  // Vehicles whose below-standard pricing is not approved yet — setup/renewal invoices are blocked.
  const [pricingPendingIds, setPricingPendingIds] = useState(() => new Set());
  const loadPricingPending = React.useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/reconciliation.php?action=pricing_pending`);
      const json = await res.json();
      if (json.success) setPricingPendingIds(new Set((json.vehicle_ids || []).map(Number)));
    } catch (e) { console.warn('pricing_pending load failed', e); }
  }, []);
  useEffect(() => { loadPricingPending(); }, [loadPricingPending]);
  const isPricingPending = (vehicleId) => pricingPendingIds.has(Number(vehicleId));

  // WhatsApp invoice messages on/off (Invoices tab switch). OFF = new invoices aren't queued.
  const [waSettings, setWaSettings] = useState(null);
  const [waSaving, setWaSaving] = useState(false);
  useEffect(() => {
    fetch(`${API_BASE_URL}/whatsapp_settings.php`).then(r => r.json())
      .then(j => { if (j.success) setWaSettings(j); })
      .catch(e => console.warn('whatsapp_settings load failed', e));
  }, []);
  const toggleWhatsApp = async (kind) => {
    const label = kind === 'setup' ? 'Setup' : 'AMC (renewal)';
    const next = !waSettings?.[kind]?.enabled;
    if (!window.confirm(next
      ? `Turn ON WhatsApp for ${label} invoices?\nCustomers will get a WhatsApp for every NEW ${label} invoice and for each payment received on it (9 AM – 10 PM).`
      : `Turn OFF WhatsApp for ${label} invoices?\nNo WhatsApp will be sent for new ${label} invoices or their payments.`)) return;
    setWaSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/whatsapp_settings.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, enabled: next, user_id: authUser?.id }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.message || 'Failed');
      setWaSettings(j);
    } catch (e) {
      alert('Could not change the WhatsApp setting: ' + e.message);
    } finally {
      setWaSaving(false);
    }
  };
  const PRICING_PENDING_MSG = 'Pricing is below standard and not approved yet. A Master/Admin must approve it in Reconciliation → Low Pricing before invoices can be created.';
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState(null);
  const [reconTab, setReconTab] = useState('gps_not_in_crm');
  const [reconSearch, setReconSearch] = useState('');

  // ── Complaints State ──────────────────────────────────────────────────────
  const [complaints, setComplaints]         = useState([]);
  const [complaintStats, setComplaintStats] = useState(null);
  const [complaintView, setComplaintView]   = useState('list'); // 'list' | 'detail' | 'new' | 'new_installation'
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintLoading, setComplaintLoading]   = useState(false);
  const [complaintSearch, setComplaintSearch]     = useState('');
  const [complaintStatusFilter, setComplaintStatusFilter] = useState('open');
  const [complaintPriorityFilter, setComplaintPriorityFilter] = useState('');
  const [complaintAssignedFilter, setComplaintAssignedFilter] = useState('');
  const [complaintDateFrom, setComplaintDateFrom] = useState('');
  const [complaintDateTo, setComplaintDateTo]     = useState('');
  const [complaintReplyText, setComplaintReplyText] = useState('');
  const [sendingComplaintReply, setSendingComplaintReply] = useState(false);
  const [complaintPrefill, setComplaintPrefill] = useState(null);

  // ── Cashflow State ────────────────────────────────────────────────────────
  const [cashflowTab,     setCashflowTab]     = useState('overview'); // overview | outflows | inflows
  const [cashflowMonth,   setCashflowMonth]   = useState(new Date().toISOString().slice(0,7));
  const [cashflowDateMode, setCashflowDateMode] = useState('month'); // 'month' | 'all' | 'custom'
  const [cashflowDateFrom, setCashflowDateFrom] = useState('');
  const [cashflowDateTo,   setCashflowDateTo]   = useState('');
  const [cashflowSummary, setCashflowSummary] = useState(null);
  const [cfOutflows,      setCfOutflows]      = useState([]);
  const [cfInflows,       setCfInflows]       = useState({ invoice_payments: [], invoice_setup_payments: [], invoice_amc_payments: [], invoice_service_payments: [], other_inflows: [] });
  const [showAddCfEntry,  setShowAddCfEntry]  = useState(false);
  const [cfAddType,       setCfAddType]       = useState('outflow'); // 'outflow' | 'inflow'
  const [editingCfEntry,  setEditingCfEntry]  = useState(null);
  const [cfLoading,       setCfLoading]       = useState(false);
  const [cfCategories,    setCfCategories]    = useState([]); // DB-driven outflow categories
  // Employee advances / petty cash ledger
  const [cfAdvances,      setCfAdvances]      = useState({ balances: [], transactions: [] });
  const [advModal,        setAdvModal]        = useState(null); // null | 'issue' | 'return' | 'adjust' | 'ledger'
  const ADV_EMPTY = { employeeId: '', amount: '', entryDate: new Date().toISOString().split('T')[0], referenceNo: '', notes: '' };
  const [advForm,         setAdvForm]         = useState(ADV_EMPTY);
  const [advAdjustLines,  setAdvAdjustLines]  = useState([]);
  const [advLedgerEmp,    setAdvLedgerEmp]    = useState(null); // {employee_id, employee_name}
  const [showCfCatModal,  setShowCfCatModal]  = useState(false);
  const [cfOutSearch,     setCfOutSearch]     = useState('');
  const [cfOutTypeFilter, setCfOutTypeFilter] = useState('all');  // all | recurring | capital
  const [cfOutCatFilter,  setCfOutCatFilter]  = useState('all');  // all | <slug>
  const [cfCatNewRec,     setCfCatNewRec]     = useState('');
  const [cfCatNewCap,     setCfCatNewCap]     = useState('');
  const [cfCatNewIn,      setCfCatNewIn]      = useState('');
  const [cfCatEdit,       setCfCatEdit]       = useState(null);   // { id, label }
  const [cfCatModalFlow,  setCfCatModalFlow]  = useState('outflow'); // 'outflow' | 'inflow'
  const CF_CAT_COLORS = ['bg-blue-100 text-blue-700','bg-orange-100 text-orange-700','bg-emerald-100 text-emerald-700','bg-indigo-100 text-indigo-700','bg-amber-100 text-amber-700','bg-cyan-100 text-cyan-700','bg-rose-100 text-rose-700','bg-teal-100 text-teal-700','bg-sky-100 text-sky-700','bg-lime-100 text-lime-700','bg-fuchsia-100 text-fuchsia-700','bg-slate-100 text-slate-600'];

  // Fallback list (used only before the DB categories load / seed on first call).
  const CF_OUTFLOW_CATS_FALLBACK = [
    { value: 'salaries_wages',             label: 'Salaries and Wages',                     color: 'bg-red-100 text-red-700',      costType: 'recurring' },
    { value: 'tracker_consumption',        label: 'Tracker Consumption',                    color: 'bg-blue-100 text-blue-700',    costType: 'recurring' },
    { value: 'rent',                       label: 'Rent',                                   color: 'bg-orange-100 text-orange-700', costType: 'recurring' },
    { value: 'telephone_mobile',           label: 'Telephone, Mobile',                      color: 'bg-sky-100 text-sky-700',      costType: 'recurring' },
    { value: 'utility_bills',              label: 'Utility Bills',                          color: 'bg-rose-100 text-rose-700',    costType: 'recurring' },
    { value: 'insurance',                  label: 'Insurance',                              color: 'bg-yellow-100 text-yellow-700', costType: 'recurring' },
    { value: 'vehicle_running',            label: "Vehicles' Running & Maintenance",        color: 'bg-amber-100 text-amber-700',  costType: 'recurring' },
    { value: 'computerization_internet',   label: 'Computerization and Internet',           color: 'bg-indigo-100 text-indigo-700', costType: 'recurring' },
    { value: 'postage_telegram',           label: 'Postage & Telegram',                     color: 'bg-teal-100 text-teal-700',    costType: 'recurring' },
    { value: 'tracker_installation',       label: 'Tracker Installation & Freelancing',     color: 'bg-purple-100 text-purple-700', costType: 'capital' },
    { value: 'tracker_accessories',        label: 'Tracker Accessories / Headphones',       color: 'bg-cyan-100 text-cyan-700',    costType: 'capital' },
    { value: 'other',                      label: 'Other',                                  color: 'bg-slate-100 text-slate-600',  costType: 'capital' },
  ];
  const CF_INFLOW_CATS_FALLBACK = [
    { value: 'tracker_setup',     label: 'Tracker Setup Charges', color: 'bg-green-100 text-green-700'   },
    { value: 'amc',               label: 'AMC',                   color: 'bg-emerald-100 text-emerald-700' },
    { value: 'other',             label: 'Other',                 color: 'bg-teal-100 text-teal-700'     },
  ];
  // Live lists from DB, split by flow (fall back until loaded). Item: {id?, value, label, color, costType?}
  const cfOutRows = cfCategories.filter(c => (c.flow || 'outflow') === 'outflow');
  const cfInRows  = cfCategories.filter(c => c.flow === 'inflow');
  const CF_OUTFLOW_CATS = cfOutRows.length
    ? cfOutRows.map(c => ({ id: c.id, value: c.slug, label: c.label, color: c.color, costType: c.cost_type }))
    : CF_OUTFLOW_CATS_FALLBACK;
  const CF_INFLOW_CATS = cfInRows.length
    ? cfInRows.map(c => ({ id: c.id, value: c.slug, label: c.label, color: c.color }))
    : CF_INFLOW_CATS_FALLBACK;
  const cfRecurringCats = CF_OUTFLOW_CATS.filter(c => c.costType === 'recurring');
  const cfCapitalCats   = CF_OUTFLOW_CATS.filter(c => c.costType === 'capital');
  const CF_CAT_MAP = {};
  [...CF_OUTFLOW_CATS, ...CF_INFLOW_CATS].forEach(c => { CF_CAT_MAP[c.value] = c; });

  const CF_EMPTY_OUT = { entryDate: new Date().toISOString().split('T')[0], category: 'tracker_consumption', costType: 'recurring', frequency: 'monthly', description: '', amount: '', vendorId: '', employeeId: '', referenceNo: '', notes: '' };
  const CF_EMPTY_IN  = { entryDate: new Date().toISOString().split('T')[0], category: 'tracker_setup', description: '', amount: '', customerId: '', referenceNo: '', notes: '' };
  const [cfOutflowForm, setCfOutflowForm] = useState(CF_EMPTY_OUT);
  const [cfInflowForm,  setCfInflowForm]  = useState(CF_EMPTY_IN);
  const [loading, setLoading] = useState(false);
  
  // Customer Management State
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  // In-form customer picker for the Add Vehicle modal (used when adding a vehicle
  // from Reconciliation "GPS not in CRM", where there is no selected customer).
  const [vehicleCustomerSearch, setVehicleCustomerSearch] = useState('');
  const [showVehicleCustomerPicker, setShowVehicleCustomerPicker] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [showEditVehicle, setShowEditVehicle] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // --- Scalable customer sidebar (server-side paginated slim list) ---
  const CUSTOMER_PAGE_SIZE = 25;
  const [customerList, setCustomerList] = useState([]);   // slim rows for the sidebar
  const [listPage, setListPage]         = useState(1);
  const [listTotal, setListTotal]       = useState(0);
  const [listLoading, setListLoading]   = useState(false); // full (re)load of the list
  const [listLoadingMore, setListLoadingMore] = useState(false); // "load more" append
  const [detailLoading, setDetailLoading] = useState(false); // lazy detail fetch
  const searchDebounceRef = useRef(null);
  const gpswoxFetchRef = useRef(null); // debounce for engine/chassis auto-fill
  // Tracks which datasets have already been loaded, so each module's data is
  // fetched only once, lazily, the first time its tab is opened.
  const loadedRef = useRef({});
  // Client-side "Load more" windowing for the big tables (Invoices, Inventory).
  // All rows stay in memory (dashboards/totals still sum everything), but only a
  // window is rendered so the DOM stays light as these tables grow.
  const TABLE_PAGE_SIZE = 50;
  const [invoiceVisibleCount, setInvoiceVisibleCount] = useState(TABLE_PAGE_SIZE);
  const [inventoryVisibleCount, setInventoryVisibleCount] = useState(TABLE_PAGE_SIZE);
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [vehicleNumberChanges, setVehicleNumberChanges] = useState({});
  const [vehicleTransfers, setVehicleTransfers] = useState({}); // vehicleId → { rt:[], ot:[] }
  const [showManagementMenu, setShowManagementMenu] = useState(false);
  const managementMenuRef = useRef(null);
  // Customer profile active tab (Plan A: Overview / Vehicles / Invoices)
  const [custTab, setCustTab] = useState('overview');
  const [custTypeFilter, setCustTypeFilter] = useState('all'); // rail quick filter: all | corporate | individual

  // IMEI Inventory State
  const [showAddInventory, setShowAddInventory] = useState(false);
  const [imeiLogDrawer, setImeiLogDrawer]       = useState(null);
  const [imeiLogs, setImeiLogs]                 = useState([]);
  const [imeiLogsLoading, setImeiLogsLoading]   = useState(false);
  const [imeiRevenue, setImeiRevenue]           = useState(null);
  const [imeiRevenueLoading, setImeiRevenueLoading] = useState(false);
  const [showEditInventory, setShowEditInventory] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryStatusFilter, setInventoryStatusFilter] = useState('all'); // 'all', 'Available', 'Installed', 'Store', 'Issued'
  const [expandedInvId, setExpandedInvId] = useState(null); // inventory row expand (shows ICCID/SIM details)
  const [editingInventory, setEditingInventory] = useState(null);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadResults, setBulkUploadResults] = useState(null); // { success, failed: [{row, imei, reason}] }
  
  // Issuance and Return State
  const [showIssuance, setShowIssuance] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [selectedDevicesForIssuance, setSelectedDevicesForIssuance] = useState([]);
  const [issuanceTechnician, setIssuanceTechnician] = useState('');
  const [issuanceDate, setIssuanceDate] = useState('');
  const [issuanceNotes, setIssuanceNotes] = useState('');
  const [issuanceDeviceSearch, setIssuanceDeviceSearch] = useState('');
  const [returnTechnician, setReturnTechnician] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [returnDeviceSearch, setReturnDeviceSearch] = useState('');
  const [selectedDevicesForReturn, setSelectedDevicesForReturn] = useState([]);
  
  // Removal tracking state
  const [showRemovalPopup, setShowRemovalPopup] = useState(false);
  const [removalEmployee, setRemovalEmployee] = useState('');
  const [removalTechAmount, setRemovalTechAmount] = useState('0');
  const [removalType, setRemovalType] = useState('removed'); // 'removed' (recovered → issue to tech) | 'lost'
  const [installTechId, setInstallTechId] = useState('');
  const [installTechAmount, setInstallTechAmount] = useState('0');
  const [pendingInactiveVehicle, setPendingInactiveVehicle] = useState(null);
  
  // Vendor Management State
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showEditVendor, setShowEditVendor] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);

  // Device Management State
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showEditDevice, setShowEditDevice] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [editingDevice, setEditingDevice] = useState(null);

  // Employee Management State
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Reports Module State
  const [reportsHead, setReportsHead] = useState('inventory'); // 'inventory' | 'invoices' | 'sales'
  const [inventoryReportType, setInventoryReportType] = useState('tech_stock'); // which inventory report is open
  const [invoiceReportType, setInvoiceReportType] = useState('imei_profit'); // which invoice report is open

  // Sales Person Performance Report State
  const [salesReportType, setSalesReportType] = useState('performance'); // which sales report is open

  // Keep the Reports tab pointed at a report this role is actually allowed to open.
  useEffect(() => {
    if (!roleAccess || activeModule !== 'reports') return;
    const allowed = REPORT_SECTIONS.filter((s) => canReport(s.key));
    if (allowed.length === 0) return; // nothing allowed (Reports itself would be hidden)
    const setSubType = (head, type) => {
      if (head === 'inventory') setInventoryReportType(type);
      else if (head === 'invoices') setInvoiceReportType(type);
      else setSalesReportType(type);
    };
    const curType = reportsHead === 'inventory' ? inventoryReportType
      : reportsHead === 'invoices' ? invoiceReportType : salesReportType;
    const inHead = allowed.filter((s) => s.head === reportsHead);
    if (inHead.length) {
      // Stay on this head; just fix the sub-report if the current one is blocked.
      if (!inHead.some((s) => s.type === curType)) setSubType(reportsHead, inHead[0].type);
    } else {
      // Current head has no allowed report → jump to the first allowed one.
      setReportsHead(allowed[0].head);
      setSubType(allowed[0].head, allowed[0].type);
    }
  }, [roleAccess, activeModule, reportsHead, inventoryReportType, invoiceReportType, salesReportType, canReport]);
  const [salesReportFrom, setSalesReportFrom] = useState('');
  const [salesReportTo, setSalesReportTo] = useState('');
  const [salesReportSortBy, setSalesReportSortBy] = useState('revenue'); // 'revenue' | 'customers' | 'vehicles' | 'outstanding'
  const [upcomingRenewalWindow, setUpcomingRenewalWindow] = useState(30); // 30 | 60 | 90 days
  const [upcomingRenewalSalesperson, setUpcomingRenewalSalesperson] = useState('all');
  const [reportTechSearch, setReportTechSearch] = useState('');
  const [reportDeviceTypeFilter, setReportDeviceTypeFilter] = useState('all');
  const [reportShowZeroStock, setReportShowZeroStock] = useState(false);
  const [expandedReportTech, setExpandedReportTech] = useState(null);

  // Stock Issuance Report State (query-based, no auto-load)
  const [reportIssuanceEmployeeFilter, setReportIssuanceEmployeeFilter] = useState('all');
  const [reportIssuanceIncludeAvailable, setReportIssuanceIncludeAvailable] = useState(true);
  const [reportIssuanceIncludeInstalled, setReportIssuanceIncludeInstalled] = useState(true);
  const [reportIssuanceDateFrom, setReportIssuanceDateFrom] = useState('');
  const [reportIssuanceDateTo, setReportIssuanceDateTo] = useState('');
  const [reportIssuanceResults, setReportIssuanceResults] = useState(null); // null = not yet run
  const [reportIssuanceLoading, setReportIssuanceLoading] = useState(false);
  const [reportIssuanceError, setReportIssuanceError] = useState('');

  // IMEI Profit Report State
  const [imeiProfitSearch, setImeiProfitSearch] = useState('');
  const [imeiProfitResult, setImeiProfitResult] = useState(null); // null = not searched yet
  const [imeiProfitLoading, setImeiProfitLoading] = useState(false);
  const [imeiProfitError, setImeiProfitError] = useState('');
  const [showImeiSuggestions, setShowImeiSuggestions] = useState(false);

  // Vehicle Profit Report State
  const [vehicleProfitSearch, setVehicleProfitSearch] = useState('');
  const [vehicleProfitResult, setVehicleProfitResult] = useState(null); // null = not searched yet
  const [vehicleProfitLoading, setVehicleProfitLoading] = useState(false);
  const [vehicleProfitError, setVehicleProfitError] = useState('');
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false);

  // Invoice Management State
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [invoiceFollowUpPersonFilter, setInvoiceFollowUpPersonFilter] = useState('all');
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountInvoice, setDiscountInvoice] = useState(null);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountNote, setDiscountNote] = useState('');
  const [invoiceTab, setInvoiceTab] = useState('dashboard'); // 'summary' or 'dashboard'(Detail) or 'due' or 'setup-due'
  const [agingFilter, setAgingFilter] = useState('all'); // Aging tab bucket filter: all | Current | 1–30 | 31–60 | 60+
  const [showBulkInvoiceCreate, setShowBulkInvoiceCreate] = useState(false);
  const [selectedCustomerForBulk, setSelectedCustomerForBulk] = useState(null);
  const [selectedVehiclesForInvoice, setSelectedVehiclesForInvoice] = useState([]);
  const [bulkInvoiceType, setBulkInvoiceType] = useState('renewal'); // 'renewal' or 'setup'

  // Customer Ledger State
  const [ledgerCustomerSearch, setLedgerCustomerSearch] = useState('');
  const [selectedLedgerCustomer, setSelectedLedgerCustomer] = useState(null);

  // Division Management State
  const [customerDivisions, setCustomerDivisions] = useState([]);
  const [showManageDivisions, setShowManageDivisions] = useState(false);
  const [newDivisionName, setNewDivisionName] = useState('');
  const [editingDivision, setEditingDivision] = useState(null);

  // Follow-Up System State
  // followupData shape: { [invoiceId]: { followupDate, logs: [{date, outcome, note}] } }
  const [followupTab, setFollowupTab] = useState('today');
  const [followupPersonFilter, setFollowupPersonFilter] = useState('all');
  const [followupSearch, setFollowupSearch] = useState('');
  const [followupData, setFollowupData] = useState({});
  // Vehicle-wise follow-ups: { 'customerId:vehicleId': { followupDate, logs } } + GPS status per vehicle
  const [vfData, setVfData] = useState({});
  const [fuGps, setFuGps]   = useState({});
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [expandedFollowup, setExpandedFollowup] = useState(null);
  const [invoicePopup, setInvoicePopup] = useState(null); // { invoiceNumber, createdDate, vehicles: [...inv] }



  
  // Vendors Data
  const [vendors, setVendors] = useState([]);

  // Devices Data
  const [devices, setDevices] = useState([]);

  // Employees Data
  const [employees, setEmployees] = useState([]);

  const [customers, setCustomers] = useState([]);

  // Invoices Data
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);

  const [inventory, setInventory] = useState([]);
  
  

  const [newCustomer, setNewCustomer] = useState({
    name: '', type: 'Individual', gender: 'Male', phone: '', cell: '', whatsapp: '', email: '', cnic: '', address: '', city: '', state: '', country: 'Pakistan', contactPerson: '', pocEmail: '', followUp: '', salesperson: '', invoiceCount: 0
  });

  const [editCustomer, setEditCustomer] = useState({
    name: '', type: 'Individual', gender: 'Male', phone: '', cell: '', whatsapp: '', email: '', cnic: '', address: '', city: '', state: '', country: 'Pakistan', contactPerson: '', pocEmail: '', followUp: '', salesperson: ''
  });

  const [newVehicle, setNewVehicle] = useState({
    vehicleNo: '', make: '', model: '', engineNo: '', chassisNo: '', activeDate: new Date().toISOString().split('T')[0], status: 'active', imei: '', deviceName: '', packagePrice: '', amc: '', commission: '', invoiceFullSetup: false, invoicingPeriod: 'Yearly', division: '', division_id: null, setupInvoiceCreated: false
  });

  const [editVehicle, setEditVehicle] = useState({
    vehicleNo: '', make: '', model: '', engineNo: '', chassisNo: '', activeDate: '', status: 'active', imei: '', deviceName: '', packagePrice: '', amc: '', commission: '', invoiceFullSetup: false, invoicingPeriod: 'Yearly', division: '', division_id: null, setupInvoiceCreated: false
  });

  // Vehicle Make/Model master list (drives cascading dropdown + fuel average)
  const [vehicleModels, setVehicleModels] = useState([]);

  // Cities master list (city → province/country auto-populate in customer form)
  const [cities, setCities] = useState([]);

  const [newInventoryItem, setNewInventoryItem] = useState({
    imei: '', deviceType: '', iccid: '', simNumber: '', vendor: '', amount: '', healthStatus: 'active'
  });

  const [editInventoryItem, setEditInventoryItem] = useState({
    imei: '', deviceType: '', iccid: '', simNumber: '', vendor: '', amount: '', healthStatus: 'active'
  });
  const [newVendor, setNewVendor] = useState({
    name: '', contact: '', email: '', address: '', city: ''
  });

  const [editVendorData, setEditVendorData] = useState({
    name: '', contact: '', email: '', address: '', city: ''
  });

  const [newDevice, setNewDevice] = useState({
    name: '', category: '', description: ''
  });

  const [editDeviceData, setEditDeviceData] = useState({
    name: '', category: '', description: ''
  });

  const [newEmployee, setNewEmployee] = useState({
    name: '', designation: '', department: '', phone: '', email: '', role: 'Sales', joiningDate: '', cnic: '', city: '', emp_type: 'permanent'
  });

  const [editEmployeeData, setEditEmployeeData] = useState({
    name: '', designation: '', department: '', phone: '', email: '', role: 'Sales', joiningDate: '', cnic: '', city: '', emp_type: 'permanent'
  });
  

  
  // ===============================================
  // HELPER FUNCTIONS
  // ===============================================
  
  // Helper function to format date as "31-Jan-26"
  const formatDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = months[d.getMonth()];
    const year = String(d.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };
  
  // ===============================================
  // LOAD DATA FROM DATABASE ON COMPONENT MOUNT
  // ===============================================
  
  useEffect(() => {
    // On startup load only the complaints badge (kept for the nav counter + live
    // polling). Everything else is lazy-loaded per tab. The customer slim list
    // loads via the debounced search effect below, so the app opens instantly.
    loadComplaints();
    loadedRef.current.complaints = true;

    // Poll complaints every 30 seconds for live updates
    const pollInterval = setInterval(() => {
      loadComplaints();
    }, 30000);

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy per-tab loading: fetch a module's data the first time its tab is opened.
  useEffect(() => {
    ensureModuleData(activeModule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModule]);

  // Reset the table render-windows to the first page whenever their filters change.
  useEffect(() => {
    setInventoryVisibleCount(TABLE_PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventorySearch, inventoryStatusFilter]);
  useEffect(() => {
    setInvoiceVisibleCount(TABLE_PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSearch, invoiceStatusFilter, invoiceFollowUpPersonFilter, invoiceTab, ledgerCustomerSearch]);

  // In the Add Vehicle form, auto-select the technician the chosen IMEI is issued
  // to (it can only be installed by that person), so the locked dropdown reflects it.
  useEffect(() => {
    if (!showAddVehicle || !newVehicle.imei) return;
    const issuedItem = inventory.find(i => i.imei === newVehicle.imei);
    if (issuedItem?.issuedTo) {
      setInstallTechId(prev => (String(prev) === String(issuedItem.issuedTo) ? prev : String(issuedItem.issuedTo)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddVehicle, newVehicle.imei, inventory]);

  // Engine No + Chassis No are now typed in the CRM (source of truth) and pushed
  // TO GPSWOX on save (see syncToGpswox in vehicles.php). The old gpswox→CRM
  // auto-fill pull has been removed so the CRM never gets overwritten from GPSWOX.

  // Debounced, server-side customer search. Runs on mount (initial page load of
  // the slim list) and every time the search box changes — waiting 300ms so we
  // don't fire a request on every keystroke. Always resets to page 1.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      loadCustomerList(1, searchTerm.trim(), false);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Keep the open customer's sidebar row in sync with its loaded detail. Any
  // mutation (vehicle add/edit/delete, customer edit) updates selectedCustomer,
  // so this mirrors the fresh name / type / contact / active-vehicle count back
  // into the slim list without an extra network round-trip. Skipped while the
  // detail is still loading (interim object has no vehicles yet).
  useEffect(() => {
    if (detailLoading || !selectedCustomer || !Array.isArray(selectedCustomer.vehicles)) return;
    setCustomerList(prev => prev.map(row =>
      String(row.id) === String(selectedCustomer.id)
        ? {
            ...row,
            name: selectedCustomer.name ?? row.name,
            type: selectedCustomer.type ?? row.type,
            phone: selectedCustomer.phone ?? row.phone,
            cell: selectedCustomer.cell ?? row.cell,
            activeVehicles: selectedCustomer.vehicles.filter(v => v.status === 'active').length,
          }
        : row
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, detailLoading]);

  // Close the Management dropdown when clicking anywhere outside it
  useEffect(() => {
    if (!showManagementMenu) return;
    const handleClickOutside = (e) => {
      if (managementMenuRef.current && !managementMenuRef.current.contains(e.target)) {
        setShowManagementMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showManagementMenu]);



  // Run a loader only the first time it's needed (lazy). Keyed so each dataset is
  // fetched once; on failure the key is cleared so it can retry on the next visit.
  const runOnce = (key, loader) => {
    if (loadedRef.current[key]) return Promise.resolve();
    loadedRef.current[key] = true;
    return Promise.resolve()
      .then(loader)
      .catch(err => {
        loadedRef.current[key] = false;
        console.error(`Lazy load "${key}" failed:`, err);
      });
  };

  // The operational core shared by the aggregate-heavy tabs (invoices, cashflow,
  // reports, services, reconciliation) — they cross-reference customers + invoices
  // + inventory, so we load the whole core once when any of them is first opened.
  const loadCoreData = () => {
    runOnce('customers',  loadCustomers);
    runOnce('invoices',   loadInvoices);
    runOnce('inventory',  loadInventory);
    runOnce('followups',  loadFollowups);
    runOnce('vfollowups', loadVehicleFollowups);
    runOnce('vendors',    loadVendors);
    runOnce('employees',  loadEmployees);
    runOnce('devices',    loadDevices);
    runOnce('vehicleModels', loadVehicleModels);
    runOnce('cities',     loadCities);
  };

  // Lazily load exactly what a tab needs, the first time it's opened. Startup only
  // loads the slim customer list (+ complaints badge), so the app opens instantly.
  const ensureModuleData = (module) => {
    switch (module) {
      case 'customers':
        // Divisions load per selected customer (see the selectedCustomer effect),
        // so we don't preload all divisions here.
        // Make/Model master feeds the cascading dropdown in the add/edit form.
        runOnce('vehicleModels', loadVehicleModels);
        // Cities master feeds the city → province/country auto-populate.
        runOnce('cities', loadCities);
        // Employees feed the Salesperson / Follow-up Person dropdowns in the customer form.
        runOnce('employees', loadEmployees);
        break;
      case 'inventory':
        runOnce('inventory', loadInventory);
        runOnce('vendors',   loadVendors);
        runOnce('devices',   loadDevices);
        runOnce('employees', loadEmployees); // Issue/Return modals need the technician dropdown
        break;
      case 'vendors':    runOnce('vendors',    loadVendors);    break;
      case 'devices':    runOnce('devices',    loadDevices);    break;
      case 'vehicle_models': runOnce('vehicleModels', loadVehicleModels); break;
      case 'cities':     runOnce('cities',     loadCities);     break;
      case 'employees':  runOnce('employees',  loadEmployees);  break;
      case 'complaints':
        runOnce('complaints', loadComplaints);
        runOnce('employees', loadEmployees); // technician/staff dropdown in ticket forms (redo/removal/transfer/installation)
        break;
      case 'quotations':
        runOnce('quotations', loadQuotations);
        runOnce('customers', loadCustomers); // for the customer picker
        break;
      case 'nr_vehicles':
      case 'user_management':
        // These are self-contained sub-components that load their own data.
        break;
      case 'cashflow':
        loadCoreData();                          // vendors / employees / customers for pickers
        runOnce('cashflow',     () => loadCashflow());
        runOnce('cfCategories', loadCfCategories);
        break;
      case 'redo':
        loadCoreData();
        runOnce('vehicleModels', loadVehicleModels); // R&T form's make/model search
        break;
      case 'invoices':
      case 'reports':
      case 'reconciliation':
      default:
        loadCoreData();
        break;
    }
  };

  // Full nested load — kept for reports/dashboards/ledger aggregates and as the
  // refresh source of truth after mutations. Runs in the background, not on the
  // Customers view's critical path.
  const loadCustomers = async () => {
    try {
      const result = await api.customers.getAll();
      const transformedCustomers = result.data.map(transformCustomer);
      setCustomers(transformedCustomers);
      return transformedCustomers; // Return the fresh data
    } catch (error) {
      console.error('Error loading customers:', error);
      return [];
    }
  };

  // Slim, server-paginated + searchable list that powers the sidebar. Only the
  // current page of lightweight rows is fetched/rendered, so the list stays fast
  // no matter how many customers exist. `append` = true keeps existing rows
  // (Load more); otherwise it replaces them.
  const loadCustomerList = async (page = 1, search = '', append = false) => {
    if (append) setListLoadingMore(true); else setListLoading(true);
    try {
      const result = await api.customers.list(page, CUSTOMER_PAGE_SIZE, search);
      const rows = (result.data || []).map(c => ({
        id: c.id.toString(),
        name: c.name,
        type: c.type,
        phone: c.phone || '',
        cell: c.cell || '',
        // Prefer the server-computed count; fall back to counting nested vehicles
        // so this still works if the backend `mode=list` isn't deployed yet.
        activeVehicles: c.active_vehicles != null
          ? (parseInt(c.active_vehicles) || 0)
          : (Array.isArray(c.vehicles) ? c.vehicles.filter(v => v.status === 'active').length : 0),
      }));
      setCustomerList(prev => (append ? [...prev, ...rows] : rows));
      setListPage(page);
      setListTotal(result.total != null ? (parseInt(result.total) || 0) : (append ? listTotal : rows.length));
      return rows;
    } catch (error) {
      console.error('Error loading customer list:', error);
      if (!append) setCustomerList([]);
      return [];
    } finally {
      if (append) setListLoadingMore(false); else setListLoading(false);
    }
  };

  // Fetch and transform ONE customer's full nested detail. Handles both the new
  // backend (`data` = single object) and the current one (`data` = array) so the
  // frontend keeps working before/after the backend is updated.
  const fetchCustomerDetail = async (id) => {
    const result = await api.customers.detail(id);
    const raw = Array.isArray(result.data)
      ? result.data.find(c => String(c.id) === String(id))
      : result.data;
    if (!raw) throw new Error('Customer detail not found');
    return transformCustomer(raw);
  };

  // Select a customer from the sidebar: show basic info instantly, then lazily
  // fetch that one customer's full nested detail (vehicles + invoices). This is
  // what keeps the payload small — we never ship every customer's deep data up
  // front, only the one being viewed.
  const handleSelectCustomer = async (slim) => {
    if (!slim) return;
    // Interim object so the detail header renders immediately without crashing
    // (vehicles must exist as an array for the vehicle filters/counts).
    setSelectedCustomer(prev =>
      prev && String(prev.id) === String(slim.id) ? prev : { ...slim, vehicles: [] }
    );
    setDetailLoading(true);
    try {
      const full = await fetchCustomerDetail(slim.id);
      setSelectedCustomer(full);
      // Keep the full-cache copy in sync so aggregate views stay consistent.
      setCustomers(prev => {
        const idx = prev.findIndex(c => String(c.id) === String(full.id));
        if (idx === -1) return [...prev, full];
        const next = prev.slice();
        next[idx] = full;
        return next;
      });
    } catch (error) {
      console.error('Error loading customer detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // After a customer/vehicle mutation, refresh the slim sidebar list (so names &
  // counts update) and re-fetch the open customer's detail. The full aggregate
  // cache is refreshed in the background by the callers' existing loadCustomers().
  const refreshAfterMutation = async (customerId) => {
    await loadCustomerList(1, searchTerm.trim(), false);
    if (customerId != null && selectedCustomer && String(selectedCustomer.id) === String(customerId)) {
      try {
        setSelectedCustomer(await fetchCustomerDetail(customerId));
      } catch (e) {
        console.error('Detail refresh failed:', e);
      }
    }
  };

  const loadInvoices = async () => {
    try {
      const result = await api.invoices.getAll();
      const transformedInvoices = result.data.map(inv => ({
        id: parseInt(inv.id),
        invoiceNumber: inv.invoice_number,
        customerId: parseInt(inv.customer_id),
        vehicleId: parseInt(inv.vehicle_id),
        customerName: inv.customer_name,
        vehicleNo: inv.vehicle_no,
        type: inv.type,
        amount: inv.amount,
        discount: parseFloat(inv.discount || 0),
        createdDate: inv.created_date,
        dueDate: inv.due_date,
        periodFrom: inv.period_from,
        periodTo: inv.period_to,
        status: inv.status,
        payments: inv.payments || []
      }));
      setInvoices(transformedInvoices);
      return transformedInvoices; // Return fresh data
    } catch (error) {
      console.error('Error loading invoices:', error);
      return [];
    }
  };

  const loadQuotations = async () => {
    try {
      const result = await api.quotations.getAll();
      setQuotations(result.data || []);
      return result.data || [];
    } catch (error) {
      console.error('Error loading quotations:', error);
      return [];
    }
  };

  const loadInventory = async () => {
    try {
      const result = await api.inventory.getAll();
      
      
      const transformedInventory = result.data.map(item => {
        
        return {
          id: parseInt(item.id),
          imei: item.imei,
          deviceType: item.device_type,
          iccid: item.iccid || '',
          simNumber: item.sim_number || '',
          vendor: item.vendor || '',
          amount: item.amount || '',
          dateAdded: item.created_at,
          usedCount: parseInt(item.used_count) || 0,
          status: item.status || 'Available',  // Fixed: was item.current_status
          location: item.location || 'Store',
          healthStatus: item.health_status || 'active',
          isIssued: item.is_issued === 1 || item.is_issued === true || item.is_issued === '1',
          issuedTo: item.issued_to || null,          // employee ID (FK)
          issuedToName: item.issued_to_name || ''    // derived from JOIN in API
        };
      });
      
      
      setInventory(transformedInventory);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const result = await api.vendors.getAll();
      setVendors(result.data);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadDevices = async () => {
    try {
      const result = await api.devices.getAll();
      setDevices(result.data);
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const loadVehicleModels = async () => {
    try {
      const result = await api.vehicleModels.getAll();
      setVehicleModels(result.data || []);
    } catch (error) {
      console.error('Error loading vehicle models:', error);
    }
  };

  const loadCities = async () => {
    try {
      const result = await api.cities.getAll();
      setCities(result.data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const result = await api.employees.getAll();
      const transformedEmployees = result.data.map(emp => ({
        ...emp,
        joiningDate: emp.joining_date
      }));
      setEmployees(transformedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  // Division Management Functions
  const loadDivisions = async (customerId = null) => {
    try {
      const result = await api.divisions.getAll(customerId);
      if (result.success) {
        setCustomerDivisions(result.data || []);
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
    }
  };

  // Divisions must reflect ONLY the selected customer — reload per selection.
  useEffect(() => {
    if (selectedCustomer?.id) loadDivisions(selectedCustomer.id);
    else setCustomerDivisions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id]);

  // ── Cashflow Functions ──────────────────────────────────────────────────
  const buildCashflowParams = (mode, month, from, to) => {
    const m = mode !== undefined ? mode : cashflowDateMode;
    if (m === 'all')    return { month: null, from: null, to: null };
    if (m === 'custom') return { month: null, from: from || cashflowDateFrom, to: to || cashflowDateTo };
    return { month: month || cashflowMonth, from: null, to: null };
  };

  const loadCashflow = async (month, mode, from, to) => {
    const params = buildCashflowParams(mode, month, from, to);
    setCfLoading(true);
    try {
      const qs = params.month ? `month=${params.month}` : (params.from && params.to ? `from=${params.from}&to=${params.to}` : '');
      const [sumRes, outRes, inRes, advRes] = await Promise.all([
        api.cashflow.getSummary(params.month, params.from, params.to),
        api.cashflow.getOutflows(params.month, params.from, params.to),
        api.cashflow.getInflows(params.month, params.from, params.to),
        api.cashflow.getAdvances(),
      ]);
      if (sumRes.success) setCashflowSummary(sumRes.data);
      if (outRes.success) setCfOutflows(outRes.data);
      if (inRes.success)  setCfInflows(inRes.data);
      if (advRes.success) setCfAdvances(advRes.data || { balances: [], transactions: [] });
    } catch (err) { console.error('Error loading cashflow:', err); }
    finally { setCfLoading(false); }
  };

  const loadCfCategories = async () => {
    try {
      const res = await api.cashflow.getCategories();
      if (res.success && Array.isArray(res.data)) setCfCategories(res.data);
    } catch (err) { console.error('Error loading cashflow categories:', err); }
  };

  // ── Cashflow category management (add / edit / delete) ───────────────────
  // flow = 'outflow' | 'inflow'. costType only applies to outflow (recurring/capital).
  const handleAddCfCategory = async (flow, costType, label, color) => {
    if (!label || !label.trim()) { alert('Enter a category name.'); return false; }
    try {
      const res = await api.cashflow.addCategory({ flow, costType, label: label.trim(), color });
      if (res.success) { await loadCfCategories(); return true; }
      alert(res.message || 'Could not add the category.'); return false;
    } catch (err) { console.error(err); alert('Could not add the category.'); return false; }
  };
  const handleUpdateCfCategory = async (id, label, color, flow, costType) => {
    if (!label || !label.trim()) { alert('Enter a category name.'); return false; }
    try {
      const payload = { id, label: label.trim(), color };
      if (flow === 'outflow' && costType) payload.costType = costType;
      const res = await api.cashflow.updateCategory(payload);
      if (res.success) { await loadCfCategories(); return true; }
      alert(res.message || 'Could not update.'); return false;
    } catch (err) { console.error(err); alert('Could not update.'); return false; }
  };
  const handleDeleteCfCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      const res = await api.cashflow.deleteCategory(id);
      if (res.success) { await loadCfCategories(); }
      else alert(res.message || 'Could not delete.');
    } catch (err) { console.error(err); alert('Could not delete.'); }
  };

  const handleSaveCfOutflow = async () => {
    const e = cfOutflowForm; // edit populates the form too; editingCfEntry only flags edit + carries id
    if (!e.description.trim() || !e.amount || parseFloat(e.amount) <= 0) { alert('Please fill Description and Amount.'); return; }
    try {
      const payload = { entryType: 'outflow', ...e, amount: parseFloat(e.amount) };
      const res = editingCfEntry
        ? await api.cashflow.updateEntry({ ...payload, id: editingCfEntry.id })
        : await api.cashflow.addEntry(payload);
      if (res.success) { await loadCashflow(); setShowAddCfEntry(false); setEditingCfEntry(null); setCfOutflowForm(CF_EMPTY_OUT); }
      else alert('Failed: ' + res.message);
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleSaveCfInflow = async () => {
    const e = cfInflowForm; // edit populates the form too; editingCfEntry only flags edit + carries id
    if (!e.description.trim() || !e.amount || parseFloat(e.amount) <= 0) { alert('Please fill Description and Amount.'); return; }
    try {
      const payload = { entryType: 'inflow', ...e, amount: parseFloat(e.amount) };
      const res = editingCfEntry
        ? await api.cashflow.updateEntry({ ...payload, id: editingCfEntry.id })
        : await api.cashflow.addEntry(payload);
      if (res.success) { await loadCashflow(); setShowAddCfEntry(false); setEditingCfEntry(null); setCfInflowForm(CF_EMPTY_IN); }
      else alert('Failed: ' + res.message);
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleDeleteCfEntry = async (id, entryType) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const res = await api.cashflow.deleteEntry({ id, entryType });
      if (res.success) await loadCashflow();
      else alert('Delete failed: ' + res.message);
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleEditCfEntry = (entry, entryType) => {
    setEditingCfEntry({ ...entry, entryType });
    if (entryType === 'outflow') {
      setCfOutflowForm({ entryDate: entry.entry_date, category: entry.category, costType: entry.cost_type, frequency: entry.frequency||'monthly', description: entry.description, amount: entry.amount, vendorId: entry.vendor_id||'', employeeId: entry.employee_id||'', referenceNo: entry.reference_no||'', notes: entry.notes||'' });
    } else {
      setCfInflowForm({ entryDate: entry.entry_date, category: entry.category, description: entry.description, amount: entry.amount, customerId: entry.customer_id||'', referenceNo: entry.reference_no||'', notes: entry.notes||'' });
    }
    setCfAddType(entryType);
    setShowAddCfEntry(true);
  };

  const cfFmt = (n) => 'Rs. ' + (n||0).toLocaleString('en-PK', { minimumFractionDigits: 0 });

  // ── Employee advances / petty cash handlers ──────────────────────────────
  const advAddedBy = () => (authUser?.full_name || authUser?.username || '');
  const balancesFor = (adv, employeeId) => (adv?.balances || []).find(b => String(b.employee_id) === String(employeeId)) || null;

  const openAdvModal = (kind, emp) => {
    if (kind === 'ledger') { setAdvLedgerEmp(emp || null); setAdvModal('ledger'); return; }
    setAdvForm({ ...ADV_EMPTY, employeeId: emp?.employee_id ? String(emp.employee_id) : '' });
    if (kind === 'adjust') {
      const firstCat = cfRecurringCats[0]?.value || CF_OUTFLOW_CATS[0]?.value || '';
      setAdvAdjustLines([{ category: firstCat, costType: 'recurring', description: '', amount: '' }]);
    }
    setAdvModal(kind);
  };

  const handleAdvIssueReturn = async (kind) => {
    const f = advForm;
    if (!f.employeeId) { alert('Select an employee.'); return; }
    if (!f.amount || parseFloat(f.amount) <= 0) { alert('Amount daalein.'); return; }
    try {
      const payload = { employeeId: f.employeeId, amount: parseFloat(f.amount), entryDate: f.entryDate, referenceNo: f.referenceNo, notes: f.notes, addedBy: advAddedBy() };
      const res = kind === 'issue' ? await api.cashflow.issueAdvance(payload) : await api.cashflow.returnAdvance(payload);
      if (res.success) { await loadCashflow(); setAdvModal(null); setAdvForm(ADV_EMPTY); }
      else alert('Failed: ' + res.message);
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleAdvAdjust = async () => {
    const f = advForm;
    if (!f.employeeId) { alert('Select an employee.'); return; }
    const lines = advAdjustLines
      .filter(l => l.category && l.amount && parseFloat(l.amount) > 0)
      .map(l => ({ category: l.category, costType: l.costType || 'recurring', description: l.description, amount: parseFloat(l.amount) }));
    if (lines.length === 0) { alert('Kam az kam ek expense line (category + amount) daalein.'); return; }
    try {
      const res = await api.cashflow.adjustAdvance({ employeeId: f.employeeId, entryDate: f.entryDate, lines, addedBy: advAddedBy() });
      if (res.success) { await loadCashflow(); setAdvModal(null); setAdvForm(ADV_EMPTY); setAdvAdjustLines([]); }
      else alert('Failed: ' + res.message);
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleDeleteAdvance = async (id) => {
    if (!window.confirm('Delete this entry? (If it is an adjustment, its linked expense will be removed too.)')) return;
    try {
      const res = await api.cashflow.deleteAdvance(id);
      if (res.success) await loadCashflow();
      else alert('Delete failed: ' + res.message);
    } catch (err) { alert('Error: ' + err.message); }
  };

  // ── Complaints Functions ────────────────────────────────────────────────
  const buildComplaintQS = (params) => {
    const qs = Object.entries(params).filter(([,v]) => v).map(([k,v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return qs ? '?' + qs : '';
  };

  const loadComplaints = async () => {
    setComplaintLoading(true);
    try {
      // Fetch ALL complaints; filtering (status/priority/assignee/search/date) is
      // done client-side against the unified status so the filters work instantly.
      const [listRes, statsRes] = await Promise.all([
        api.complaints.getAll(''),
        api.complaints.getStats(),
      ]);
      setComplaints(listRes.data || []);
      setComplaintStats(statsRes.data || null);
    } catch (e) { console.error('Error loading complaints:', e); }
    finally { setComplaintLoading(false); }
  };

  const openComplaintDetail = async (id) => {
    try {
      const res = await api.complaints.getOne(id);
      setSelectedComplaint(res.data);
      setComplaintView('detail');
    } catch (e) { alert('Failed to load complaint: ' + e.message); }
  };

  const handleComplaintStatusChange = async (id, newStatus) => {
    try {
      await api.complaints.setStatus({ id, status: newStatus, changed_by: 'Staff' });
      const res = await api.complaints.getOne(id);
      setSelectedComplaint(res.data);
      loadComplaints();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  // Office manually nudges the technician task stage (assigned/in_progress/submitted/completed/rejected).
  const handleComplaintTaskStage = async (id, taskStatus) => {
    try {
      await api.complaints.setTaskStatus({ id, task_status: taskStatus, changed_by: 'Staff' });
      const res = await api.complaints.getOne(id);
      setSelectedComplaint(res.data);
      loadComplaints();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  // Office converts an app/generic complaint into a real technician activity (same ticket).
  const handleComplaintConvert = async (id, payload) => {
    try {
      await api.complaints.convert({ ...payload, id, converted_by: 'Staff' });
      const res = await api.complaints.getOne(id);
      setSelectedComplaint(res.data);
      loadComplaints();
    } catch (e) { alert('Failed to convert: ' + e.message); }
  };

  const sendWhatsAppAssignment = async (complaint, emp) => {
    const phone = emp.phone?.replace(/\D/g, ''); // strip non-digits
    if (!phone) return; // no phone on file, skip silently
    const cat = (complaint.category || 'task').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const message =
`🔧 *New Activity Assigned*

📋 *Ticket:* ${complaint.ticket_no || '—'}
📂 *Type:* ${cat}
🚗 *Vehicle:* ${complaint.vehicle_name || '—'}
👤 *Customer:* ${complaint.customer_name || '—'}
📞 *Contact:* ${complaint.contact || complaint.customer_contact || '—'}
📍 *City:* ${complaint.city || '—'}
📝 *Subject:* ${complaint.subject || '—'}

Please open the SecureWheels app to view and update this activity.`;

    const url = `http://109.199.120.141:3001/api/message?token=u9495a5a6e7ca4ff.988a77ddf8f6484ab227a669d1bf87d0&phone=${encodeURIComponent(phone)}&message=${encodeURIComponent(message)}`;
    try {
      await fetch(url);
    } catch (e) {
      console.warn('WhatsApp notification failed:', e.message);
    }
  };

  const handleComplaintAssign = async (id, emp, remarks = '') => {
    if (!emp) return;
    try {
      await api.complaints.assign({ id, assigned_to: emp.id, assigned_name: emp.name, remarks });
      const res = await api.complaints.getOne(id);
      const complaint = res.data;
      setSelectedComplaint(complaint);
      loadComplaints();
      // Send WhatsApp notification to technician (fire-and-forget, won't block or throw)
      sendWhatsAppAssignment(complaint, emp);
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handleComplaintReply = async () => {
    if (!complaintReplyText.trim() || !selectedComplaint) return;
    setSendingComplaintReply(true);
    try {
      await api.complaints.addReply({
        complaint_id: selectedComplaint.id,
        author: 'Staff',
        author_role: 'staff',
        message: complaintReplyText.trim(),
      });
      setComplaintReplyText('');
      const res = await api.complaints.getOne(selectedComplaint.id);
      setSelectedComplaint(res.data);
      loadComplaints();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setSendingComplaintReply(false); }
  };

  const handleDeleteComplaint = async (id) => {
    if (!window.confirm('Delete this complaint permanently?')) return;
    try {
      await api.complaints.delete(id);
      setComplaintView('list');
      setSelectedComplaint(null);
      loadComplaints();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  // Bulk-delete the given complaint ids (+ their child rows). deleteSales also removes linked sales.
  const handleBulkDeleteComplaints = async (ids, deleteSales) => {
    try {
      const r = await api.complaints.bulkDelete({ ids, deleteSales: !!deleteSales });
      if (r && r.success) {
        await loadComplaints();
        const d = r.data || {};
        alert(`Deleted ${d.deleted ?? 0} complaint(s)` + (deleteSales ? ` and ${d.sales_deleted ?? 0} linked sale(s)` : ''));
      } else {
        alert(r?.message || 'Bulk delete failed');
      }
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handleComplaintSubmit = async (formData) => {
    try {
      // An array = one ticket per vehicle (multi-vehicle installation).
      if (Array.isArray(formData)) {
        let firstId = null;
        for (const p of formData) {
          const res = await api.complaints.create(p);
          if (!firstId && res && res.data) firstId = res.data.id;
        }
        await loadComplaints();
        if (firstId) await openComplaintDetail(firstId); else setComplaintView('list');
        return;
      }
      const res = await api.complaints.create(formData);
      await loadComplaints();
      // api.call returns the whole {success, data, message} — the new id is at data.id.
      const newId = res && res.data ? res.data.id : null;
      if (newId) await openComplaintDetail(newId);
      else setComplaintView('list');
    } catch (e) { alert('Failed to submit: ' + e.message); }
  };

  const loadFollowups = async () => {
    try {
      const result = await api.followups.getAll();
      if (result.success && result.data) {
        // { [customerId]: { followupDate, logs } }
        const dataMap = {};
        result.data.forEach(row => {
          dataMap[row.customer_id] = {
            followupDate: row.followup_date || null,
            logs: (row.logs || []).map(l => ({
              id:        l.id,
              date:      l.date,
              outcome:   l.outcome,
              note:      l.note || '',
              logged_by: l.logged_by || '',
            }))
          };
        });
        setFollowupData(dataMap);
      }
    } catch (error) {
      console.error('Error loading followups:', error);
    }
  };

  const loadVehicleFollowups = async () => {
    try {
      const result = await api.vehicleFollowups.getAll();
      if (result.success && result.data) {
        const map = {};
        result.data.forEach(row => {
          map[`${row.customer_id}:${row.vehicle_id}`] = {
            followupDate: row.followup_date || null,
            logs: (row.logs || []).map(l => ({ id: l.id, date: l.date, outcome: l.outcome, note: l.note || '', logged_by: l.logged_by || '' })),
          };
        });
        setVfData(map);
      }
    } catch (error) {
      console.error('Error loading vehicle follow-ups:', error);
    }
  };

  const handleAddDivision = async () => {
    if (!newDivisionName.trim()) {
      alert('Please enter a division name');
      return;
    }

    try {
      const result = await api.divisions.create({
        customerId: selectedCustomer.id,
        divisionName: newDivisionName.trim(),
        parentId: null   // top-level (Level 1)
      });

      if (result.success) {
        await loadDivisions(selectedCustomer?.id);
        setNewDivisionName('');
      } else {
        alert(result.message || 'Failed to add division');
      }
    } catch (error) {
      alert(error.message || 'Failed to add division');
    }
  };

  // Add a sub-group under a node (Level 2 / 3), name via prompt.
  const addSubDivision = async (parentId) => {
    const name = window.prompt('Sub-group name:');
    if (name === null || !name.trim()) return;
    try {
      const r = await api.divisions.create({ customerId: selectedCustomer.id, divisionName: name.trim(), parentId });
      if (r.success) await loadDivisions(selectedCustomer.id);
      else alert(r.message || 'Failed to add sub-group');
    } catch (e) { alert(e.message || 'Failed'); }
  };

  const renameDivisionPrompt = async (id, current) => {
    const name = window.prompt('Rename group:', current);
    if (name === null) return;
    if (!name.trim()) { alert('Name is required'); return; }
    try {
      const r = await api.divisions.update({ id, divisionName: name.trim() });
      if (r.success) await loadDivisions(selectedCustomer.id);
      else alert(r.message || 'Failed to rename');
    } catch (e) { alert(e.message || 'Failed'); }
  };

  const handleUpdateDivision = async () => {
    if (!editingDivision.division_name.trim()) {
      alert('Division name cannot be empty');
      return;
    }

    try {
      const result = await api.divisions.update({
        id: editingDivision.id,
        divisionName: editingDivision.division_name.trim()
      });

      if (result.success) {
        await loadDivisions(selectedCustomer?.id);
        setEditingDivision(null);
        alert('Division updated successfully!');
      }
    } catch (error) {
      alert(error.message || 'Failed to update division');
    }
  };

  const handleDeleteDivision = async (divisionId) => {
    if (!window.confirm('Are you sure you want to delete this division?')) {
      return;
    }

    try {
      const result = await api.divisions.delete(divisionId);
      if (result.success) {
        await loadDivisions(selectedCustomer?.id);
        alert('Division deleted successfully!');
      }
    } catch (error) {
      alert(error.message || 'Failed to delete division');
    }
  };


  // Helper function to calculate inventory status dynamically
  const getInventoryStatus = (imei) => {
    // First check if it exists in inventory
    const inventoryItem = inventory.find(item => item.imei === imei);
    if (!inventoryItem) return 'Not Found';
    
    // If status is explicitly set in database, use that
    if (inventoryItem.status) {
      return inventoryItem.status;
    }
    
    // Check if IMEI is installed in any active vehicle
    for (const customer of customers) {
      for (const vehicle of customer.vehicles) {
        if (vehicle.imei === imei && vehicle.status === 'active') {
          return 'Installed';
        }
      }
    }
    
    // Default to Available (which means it's in Store)
    return 'Available';
  };

  // Get location based on status
  const getInventoryLocation = (imei) => {
    const inventoryItem = inventory.find(item => item.imei === imei);
    if (!inventoryItem) return 'Unknown';
    
    // If location is set in database, use that
    if (inventoryItem.location) {
      return inventoryItem.location;
    }
    
    // Check if installed in vehicle
    for (const customer of customers) {
      for (const vehicle of customer.vehicles) {
        if (vehicle.imei === imei && vehicle.status === 'active') {
          return 'Installed';
        }
      }
    }
    
    // Default to Store
    return 'Store';
  };

  // Get inventory with calculated status and location
  const getInventoryWithStatus = () => {
    return inventory.map(item => ({
      ...item,
      status: getInventoryStatus(item.imei),
      location: getInventoryLocation(item.imei)
    }));
  };


  // --- Single source of truth: can this IMEI be installed in a vehicle? ---
  // Only update this function when business rules change.
  // Rules: device must be healthy AND issued to a technician.
  const isImeiReadyToInstall = (imei) => {
    const item = inventory.find(i => i.imei === imei);
    if (!item) return false;
    if (item.healthStatus !== 'active') return false; // damaged, under_repair, lost, etc.
    if (!item.isIssued) return false;                 // still sitting in store
    return true;
  };

  // Human-readable reason why an IMEI cannot be installed (used in alert messages)
  const getImeiNotReadyReason = (imei) => {
    const item = inventory.find(i => i.imei === imei);
    if (!item) return 'not found in inventory';
    if (item.healthStatus !== 'active') return `health status is "${item.healthStatus}" — only active devices can be installed`;
    if (!item.isIssued) return 'not issued to a technician yet — issue it first';
    return 'unknown reason';
  };
  // Calculate tracker counts
  const getTrackerCounts = () => {
    const inventoryWithStatus = getInventoryWithStatus();
    const total = inventoryWithStatus.length;
    // A lost device is NOT available for installation.
    const available = inventoryWithStatus.filter(item => item.status === 'Available' && (item.healthStatus || '').toLowerCase() !== 'lost').length;
    const installed = inventoryWithStatus.filter(item => item.status === 'Installed').length;
    const inStore = inventoryWithStatus.filter(item => item.location === 'Store').length;
    const issued = inventoryWithStatus.filter(item => item.isIssued).length;
    // Customer-owned = devices logged under the "Customer" vendor (client's own tracker).
    const customerOwned = inventoryWithStatus.filter(item => (item.vendor || '').trim().toLowerCase() === 'customer').length;
    // Lost = devices whose health status has been marked lost.
    const lost = inventoryWithStatus.filter(item => (item.healthStatus || '').toLowerCase() === 'lost').length;

    return { total, available, installed, inStore, issued, customerOwned, lost };
  };

  // ── Reports: Technician / Employee Wise Stock Report ─────────────────────
  // Aggregates devices currently issued to (held by) each technician/employee.
  const getTechnicianStockData = () => {
    const inv = getInventoryWithStatus();
    const allDeviceTypes = Array.from(new Set(inv.map(i => i.deviceType || 'Unspecified'))).sort();

    let issuedItems = inv.filter(item => item.isIssued);
    if (reportDeviceTypeFilter !== 'all') {
      issuedItems = issuedItems.filter(item => (item.deviceType || 'Unspecified') === reportDeviceTypeFilter);
    }

    const map = {};
    issuedItems.forEach(item => {
      const key = item.issuedTo != null ? String(item.issuedTo) : `name:${item.issuedToName || 'Unknown'}`;
      if (!map[key]) {
        const emp = employees.find(e => String(e.id) === String(item.issuedTo));
        map[key] = {
          techId: item.issuedTo,
          techName: item.issuedToName || emp?.name || 'Unknown',
          designation: emp?.designation || '',
          department: emp?.department || '',
          empStatus: emp?.status || '',
          totalDevices: 0,
          byType: {},
          items: []
        };
      }
      map[key].totalDevices += 1;
      const type = item.deviceType || 'Unspecified';
      map[key].byType[type] = (map[key].byType[type] || 0) + 1;
      map[key].items.push(item);
    });

    // Optionally include employees currently holding zero devices
    if (reportShowZeroStock) {
      employees.forEach(emp => {
        const key = String(emp.id);
        if (!map[key]) {
          map[key] = {
            techId: emp.id,
            techName: emp.name,
            designation: emp.designation || '',
            department: emp.department || '',
            empStatus: emp.status || '',
            totalDevices: 0,
            byType: {},
            items: []
          };
        }
      });
    }

    let rows = Object.values(map);

    if (reportTechSearch.trim()) {
      const q = reportTechSearch.toLowerCase();
      rows = rows.filter(r =>
        r.techName.toLowerCase().includes(q) ||
        (r.designation || '').toLowerCase().includes(q) ||
        (r.department || '').toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => b.totalDevices - a.totalDevices || a.techName.localeCompare(b.techName));

    const grandTotal = issuedItems.length;
    const techCount = rows.filter(r => r.totalDevices > 0).length;

    return { rows, grandTotal, techCount, deviceTypes: allDeviceTypes };
  };

  // Build Salesperson Wise Performance data from already-loaded customers/vehicles/invoices
  const getSalesPersonPerformanceData = () => {
    const fromDate = salesReportFrom ? new Date(salesReportFrom) : null;
    const toDate = salesReportTo ? new Date(salesReportTo) : null;

    const inRange = (dateStr) => {
      if (!fromDate && !toDate) return true;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    };

    const bucket = {}; // name -> { customers:Set, vehicles:Set, invoiced, paid, balance, invoiceCount }
    const getBucket = (name) => {
      const key = name && name.trim() ? name.trim() : 'Unassigned';
      if (!bucket[key]) {
        bucket[key] = { name: key, customerIds: new Set(), vehicleIds: new Set(), invoiced: 0, paid: 0, balance: 0, invoiceCount: 0 };
      }
      return bucket[key];
    };

    customers.forEach(cust => {
      (cust.vehicles || []).forEach(v => {
        // Attribute the sale to the vehicle's salesperson, falling back to the customer's salesperson
        const sp = v.salesperson || cust.salesperson;
        const b = getBucket(sp);

        // Only Setup invoices count as a "sale" — renewal/AMC invoices are recurring
        // billing, not a new sale, so they're excluded here. Customers/Vehicles are
        // only counted if they have a qualifying Setup invoice within the date range.
        const qualifyingInvoices = (v.invoices || []).filter(inv => {
          if (String(inv.type || '').toLowerCase() !== 'setup') return false;
          const dateForFilter = inv.issue_date || inv.created_date;
          return inRange(dateForFilter);
        });

        if (qualifyingInvoices.length === 0) return;

        b.customerIds.add(cust.id);
        b.vehicleIds.add(v.id);

        qualifyingInvoices.forEach(inv => {
          const effAmount = parseFloat(inv.amount || 0) - parseFloat(inv.discount || 0);
          b.invoiced += effAmount;
          b.paid += parseFloat(inv.paid_amount || 0);
          // Always derive outstanding as Invoiced - Collected for this invoice, rather than
          // trusting the backend's `balance` field (which can be stale/inconsistent when
          // an invoice is fully or over-paid).
          b.balance += (effAmount - parseFloat(inv.paid_amount || 0));
          b.invoiceCount += 1;
        });
      });
    });

    let rows = Object.values(bucket).map(b => ({
      name: b.name,
      customerCount: b.customerIds.size,
      vehicleCount: b.vehicleIds.size,
      invoiceCount: b.invoiceCount,
      invoiced: b.invoiced,
      paid: b.paid,
      balance: b.balance,
    }));

    const sortKey = { revenue: 'invoiced', customers: 'customerCount', vehicles: 'vehicleCount', outstanding: 'balance' }[salesReportSortBy] || 'invoiced';
    rows.sort((a, b) => b[sortKey] - a[sortKey] || a.name.localeCompare(b.name));

    const totals = rows.reduce((acc, r) => ({
      customerCount: acc.customerCount + r.customerCount,
      vehicleCount: acc.vehicleCount + r.vehicleCount,
      invoiceCount: acc.invoiceCount + r.invoiceCount,
      invoiced: acc.invoiced + r.invoiced,
      paid: acc.paid + r.paid,
      balance: acc.balance + r.balance,
    }), { customerCount: 0, vehicleCount: 0, invoiceCount: 0, invoiced: 0, paid: 0, balance: 0 });

    return { rows, totals };
  };

  // Export the Salesperson Wise Performance Report to CSV
  const exportSalesPerformanceToCSV = () => {
    const { rows, totals } = getSalesPersonPerformanceData();
    if (rows.length === 0) {
      alert('No data to export with current filters.');
      return;
    }
    const header = ['Salesperson', 'Customers', 'Vehicles', 'No of Sales', 'Total Invoiced', 'Total Paid', 'Outstanding'];
    const lines = rows.map(r => [
      r.name, r.customerCount, r.vehicleCount, r.invoiceCount,
      r.invoiced.toFixed(2), r.paid.toFixed(2), r.balance.toFixed(2)
    ]);
    lines.push(['TOTAL', totals.customerCount, totals.vehicleCount, totals.invoiceCount, totals.invoiced.toFixed(2), totals.paid.toFixed(2), totals.balance.toFixed(2)]);

    const csv = [header, ...lines].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Salesperson_Performance_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ---- Report: Sales Trend Over Time (month-wise Setup sales) ----
  const getSalesTrendData = () => {
    const fromDate = salesReportFrom ? new Date(salesReportFrom) : null;
    const toDate = salesReportTo ? new Date(salesReportTo) : null;
    const buckets = {};

    customers.forEach(cust => {
      (cust.vehicles || []).forEach(v => {
        (v.invoices || []).forEach(inv => {
          if (String(inv.type || '').toLowerCase() !== 'setup') return;
          const d = inv.issue_date || inv.created_date;
          if (!d) return;
          const dObj = new Date(d);
          if (fromDate && dObj < fromDate) return;
          if (toDate && dObj > toDate) return;
          const key = String(d).slice(0, 7); // YYYY-MM
          if (!buckets[key]) buckets[key] = { key, count: 0, invoiced: 0, paid: 0 };
          const effAmount = parseFloat(inv.amount || 0) - parseFloat(inv.discount || 0);
          buckets[key].count += 1;
          buckets[key].invoiced += effAmount;
          buckets[key].paid += parseFloat(inv.paid_amount || 0);
        });
      });
    });

    let months = Object.values(buckets).sort((a, b) => a.key.localeCompare(b.key));
    // With no explicit date filter, keep the chart readable by showing the last 12 months only
    if (!salesReportFrom && !salesReportTo && months.length > 12) {
      months = months.slice(-12);
    }
    const monthLabel = (key) => {
      const [y, m] = key.split('-');
      return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    };
    months = months.map(m => ({ ...m, label: monthLabel(m.key) }));
    const maxInvoiced = Math.max(1, ...months.map(m => m.invoiced));
    const totals = months.reduce((acc, m) => ({
      count: acc.count + m.count,
      invoiced: acc.invoiced + m.invoiced,
      paid: acc.paid + m.paid,
    }), { count: 0, invoiced: 0, paid: 0 });

    return { months, maxInvoiced, totals };
  };

  const exportSalesTrendToCSV = () => {
    const { months } = getSalesTrendData();
    if (months.length === 0) {
      alert('No data to export with current filters.');
      return;
    }
    const header = ['Month', 'No of Sales', 'Total Invoiced', 'Total Collected'];
    const lines = months.map(m => [m.label, m.count, m.invoiced.toFixed(2), m.paid.toFixed(2)]);
    const csv = [header, ...lines].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Sales_Trend_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ---- Report: Renewal Performance / Retention per Salesperson ----
  const getRenewalPerformanceData = () => {
    const fromDate = salesReportFrom ? new Date(salesReportFrom) : null;
    const toDate = salesReportTo ? new Date(salesReportTo) : null;
    const inRange = (dateStr) => {
      if (!fromDate && !toDate) return true;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const bucket = {};
    const getBucket = (name) => {
      const key = name && name.trim() ? name.trim() : 'Unassigned';
      if (!bucket[key]) bucket[key] = { name: key, completed: 0, revenue: 0, collected: 0, overdue: 0 };
      return bucket[key];
    };

    customers.forEach(cust => {
      (cust.vehicles || []).forEach(v => {
        const sp = v.salesperson || cust.salesperson;
        const b = getBucket(sp);

        // Renewals completed within the selected date range
        (v.invoices || []).forEach(inv => {
          if (String(inv.type || '').toLowerCase() !== 'renewal') return;
          const d = inv.issue_date || inv.created_date;
          if (!inRange(d)) return;
          const effAmount = parseFloat(inv.amount || 0) - parseFloat(inv.discount || 0);
          b.completed += 1;
          b.revenue += effAmount;
          b.collected += parseFloat(inv.paid_amount || 0);
        });

        // Currently overdue (as of today) — a live snapshot, not tied to the date filter,
        // since "who's overdue right now" is what needs following up on.
        if (v.setupInvoiceCreated && v.serviceUpto && v.status !== 'inactive' && v.status !== 'removed') {
          const due = new Date(v.serviceUpto);
          if (due < today) b.overdue += 1;
        }
      });
    });

    let rows = Object.values(bucket).filter(r => r.completed > 0 || r.overdue > 0);
    rows.forEach(r => {
      const considered = r.completed + r.overdue;
      r.renewalRate = considered > 0 ? (r.completed / considered) * 100 : 0;
    });
    rows.sort((a, b) => b.revenue - a.revenue || a.name.localeCompare(b.name));

    const totals = rows.reduce((acc, r) => ({
      completed: acc.completed + r.completed,
      revenue: acc.revenue + r.revenue,
      collected: acc.collected + r.collected,
      overdue: acc.overdue + r.overdue,
    }), { completed: 0, revenue: 0, collected: 0, overdue: 0 });

    return { rows, totals };
  };

  const exportRenewalPerformanceToCSV = () => {
    const { rows, totals } = getRenewalPerformanceData();
    if (rows.length === 0) {
      alert('No data to export with current filters.');
      return;
    }
    const header = ['Salesperson', 'Renewals Completed', 'Renewal Revenue', 'Collected', 'Currently Overdue', 'Renewal Rate %'];
    const lines = rows.map(r => [r.name, r.completed, r.revenue.toFixed(2), r.collected.toFixed(2), r.overdue, r.renewalRate.toFixed(1)]);
    lines.push(['TOTAL', totals.completed, totals.revenue.toFixed(2), totals.collected.toFixed(2), totals.overdue, '']);
    const csv = [header, ...lines].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Renewal_Performance_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ---- Report: Upcoming Renewals / At-Risk Customers ----
  const getUpcomingRenewalsData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const windowEnd = new Date(today);
    windowEnd.setDate(windowEnd.getDate() + parseInt(upcomingRenewalWindow || 30));

    const list = [];
    customers.forEach(cust => {
      (cust.vehicles || []).forEach(v => {
        if (!v.setupInvoiceCreated || !v.serviceUpto) return;
        if (v.status === 'inactive' || v.status === 'removed') return;
        const due = new Date(v.serviceUpto);
        due.setHours(0, 0, 0, 0);
        if (due < today || due > windowEnd) return;
        const sp = (v.salesperson || cust.salesperson || 'Unassigned').trim() || 'Unassigned';
        if (upcomingRenewalSalesperson !== 'all' && sp !== upcomingRenewalSalesperson) return;
        const daysLeft = Math.round((due - today) / (1000 * 60 * 60 * 24));
        list.push({
          customerName: cust.name,
          phone: cust.phone || cust.cell || '',
          vehicleNo: v.vehicleNo,
          salesperson: sp,
          serviceUpto: v.serviceUpto,
          daysLeft,
        });
      });
    });
    list.sort((a, b) => a.daysLeft - b.daysLeft);
    return list;
  };

  const exportUpcomingRenewalsToCSV = () => {
    const list = getUpcomingRenewalsData();
    if (list.length === 0) {
      alert('No data to export with current filters.');
      return;
    }
    const header = ['Customer', 'Phone', 'Vehicle No', 'Salesperson', 'Renewal Due', 'Days Left'];
    const lines = list.map(r => [r.customerName, r.phone, r.vehicleNo, r.salesperson, r.serviceUpto, r.daysLeft]);
    const csv = [header, ...lines].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Upcoming_Renewals_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ---- Report: Sales by City / Area ----
  const getSalesByAreaData = () => {
    const fromDate = salesReportFrom ? new Date(salesReportFrom) : null;
    const toDate = salesReportTo ? new Date(salesReportTo) : null;
    const inRange = (dateStr) => {
      if (!fromDate && !toDate) return true;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    };

    const bucket = {};
    const getBucket = (name) => {
      const key = name && name.trim() ? name.trim() : 'Unspecified';
      if (!bucket[key]) bucket[key] = { name: key, customerIds: new Set(), vehicleIds: new Set(), invoiced: 0, paid: 0, count: 0 };
      return bucket[key];
    };

    customers.forEach(cust => {
      const area = cust.city;
      (cust.vehicles || []).forEach(v => {
        const qualifying = (v.invoices || []).filter(inv => {
          if (String(inv.type || '').toLowerCase() !== 'setup') return false;
          const d = inv.issue_date || inv.created_date;
          return inRange(d);
        });
        if (qualifying.length === 0) return;
        const b = getBucket(area);
        b.customerIds.add(cust.id);
        b.vehicleIds.add(v.id);
        qualifying.forEach(inv => {
          const effAmount = parseFloat(inv.amount || 0) - parseFloat(inv.discount || 0);
          b.invoiced += effAmount;
          b.paid += parseFloat(inv.paid_amount || 0);
          b.count += 1;
        });
      });
    });

    let rows = Object.values(bucket).map(b => ({
      name: b.name,
      customerCount: b.customerIds.size,
      vehicleCount: b.vehicleIds.size,
      count: b.count,
      invoiced: b.invoiced,
      paid: b.paid,
    }));
    rows.sort((a, b) => b.invoiced - a.invoiced || a.name.localeCompare(b.name));

    const totals = rows.reduce((acc, r) => ({
      customerCount: acc.customerCount + r.customerCount,
      vehicleCount: acc.vehicleCount + r.vehicleCount,
      count: acc.count + r.count,
      invoiced: acc.invoiced + r.invoiced,
      paid: acc.paid + r.paid,
    }), { customerCount: 0, vehicleCount: 0, count: 0, invoiced: 0, paid: 0 });

    return { rows, totals };
  };

  const exportSalesByAreaToCSV = () => {
    const { rows, totals } = getSalesByAreaData();
    if (rows.length === 0) {
      alert('No data to export with current filters.');
      return;
    }
    const header = ['City / Area', 'Customers', 'Vehicles', 'No of Sales', 'Total Invoiced', 'Total Collected'];
    const lines = rows.map(r => [r.name, r.customerCount, r.vehicleCount, r.count, r.invoiced.toFixed(2), r.paid.toFixed(2)]);
    lines.push(['TOTAL', totals.customerCount, totals.vehicleCount, totals.count, totals.invoiced.toFixed(2), totals.paid.toFixed(2)]);
    const csv = [header, ...lines].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Sales_By_Area_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Export the Technician/Employee Wise Stock Report to CSV
  const exportTechStockReportToCSV = () => {
    const { rows, grandTotal } = getTechnicianStockData();
    const dataRows = rows.filter(r => r.totalDevices > 0);

    if (dataRows.length === 0) {
      alert('No data to export with current filters.');
      return;
    }

    const escapeCSV = (val) => {
      const str = val === null || val === undefined ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = ['Employee/Technician', 'Designation', 'Department', 'IMEI', 'Device Type', 'ICCID', 'SIM Number', 'Health Status'];

    const lines = [];
    dataRows.forEach(r => {
      r.items.forEach(item => {
        lines.push([
          r.techName,
          r.designation,
          r.department,
          item.imei,
          item.deviceType || 'Unspecified',
          item.iccid || '',
          item.simNumber || '',
          item.healthStatus || 'active',
        ].map(escapeCSV).join(','));
      });
    });

    const today = new Date().toLocaleDateString('en-PK');
    const csvContent = [
      `SecureWheels - Technician/Employee Wise Stock Report`,
      `Exported on: ${today}`,
      `Device Type Filter: ${reportDeviceTypeFilter === 'all' ? 'All' : reportDeviceTypeFilter}`,
      `Total Technicians: ${dataRows.length}`,
      `Total Devices Held: ${grandTotal}`,
      '',
      headers.join(','),
      ...lines,
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Technician_Stock_Report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Reports: Stock Issuance Report ────────────────────────────────────────
  // Query-based report — runs only when the user clicks "Load Report".
  // Backed by a single SQL query on the server (stock_issuance_report.php)
  // instead of looping imei_logs.php per device — fast even at thousands
  // of devices, since there's no N+1 API call pattern anymore.
  const runStockIssuanceReport = async () => {
    setReportIssuanceError('');
    setReportIssuanceResults(null);

    if (!reportIssuanceIncludeAvailable && !reportIssuanceIncludeInstalled) {
      setReportIssuanceError('Select at least one status to include (Available or Installed).');
      return;
    }

    setReportIssuanceLoading(true);
    try {
      const params = {
        employee_id: reportIssuanceEmployeeFilter !== 'all' ? reportIssuanceEmployeeFilter : '',
        date_from: reportIssuanceDateFrom || '',
        date_to: reportIssuanceDateTo || '',
        include_available: reportIssuanceIncludeAvailable ? '1' : '0',
        include_installed: reportIssuanceIncludeInstalled ? '1' : '0',
      };
      const res = await api.reports.stockIssuance(params);
      const rows = (res.data || []).map(r => ({
        employeeCode: r.employee_code || '',
        employeeId: r.employee_id ?? null,
        name: r.name || 'Unknown',
        designation: r.designation || '',
        deviceType: r.device_type || 'Unspecified',
        imei: r.imei,
        issuanceDate: r.issuance_date ? String(r.issuance_date).split(/[ T]/)[0] : '',
        status: r.status,
        vehicleNo: r.vehicle_no || '',
        installDate: r.installation_date || '',
      }));
      setReportIssuanceResults(rows);
    } catch (e) {
      console.error('Failed to load stock issuance report:', e);
      setReportIssuanceError('Failed to load report: ' + e.message);
    } finally {
      setReportIssuanceLoading(false);
    }
  };

  // Export the Stock Issuance Report to CSV
  const exportIssuanceReportToCSV = () => {
    if (!reportIssuanceResults || reportIssuanceResults.length === 0) {
      alert('No data to export. Load the report first.');
      return;
    }

    const escapeCSV = (val) => {
      const str = val === null || val === undefined ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = ['Employee Code', 'Name', 'Designation', 'Issued Device', 'IMEI', 'Issuance Date', 'Status', 'Vehicle Number', 'Installation Date'];

    const lines = reportIssuanceResults.map(r => [
      r.employeeCode,
      r.name,
      r.designation,
      r.deviceType,
      r.imei,
      r.issuanceDate,
      r.status,
      r.vehicleNo,
      r.installDate,
    ].map(escapeCSV).join(','));

    const today = new Date().toLocaleDateString('en-PK');
    const csvContent = [
      `SecureWheels - Stock Issuance Report`,
      `Exported on: ${today}`,
      `Total Records: ${reportIssuanceResults.length}`,
      '',
      headers.join(','),
      ...lines,
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Stock_Issuance_Report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Reports: IMEI Profit Report ───────────────────────────────────────────
  // Enter an IMEI, see total invoiced/paid/outstanding against that specific
  // device plus the itemized invoice list — powered by imei_revenue_report.php
  // (single indexed query on invoices.imei, captured at invoice-creation time).
  const runImeiProfitSearch = async (overrideImei) => {
    const imei = (overrideImei ?? imeiProfitSearch).trim();
    setImeiProfitError('');
    if (!imei) {
      setImeiProfitError('Enter an IMEI to search.');
      return;
    }
    setImeiProfitLoading(true);
    setImeiProfitResult(null);
    try {
      const res = await api.reports.imeiRevenue(imei);
      if (res.success) {
        setImeiProfitResult(res.data);
      } else {
        setImeiProfitError(res.message || 'No data found for this IMEI.');
      }
    } catch (e) {
      setImeiProfitError('Failed to load report: ' + e.message);
    } finally {
      setImeiProfitLoading(false);
    }
  };

  const exportImeiProfitToCSV = () => {
    if (!imeiProfitResult || !imeiProfitResult.invoices || imeiProfitResult.invoices.length === 0) {
      alert('No data to export.');
      return;
    }
    const escapeCSV = (val) => {
      const str = val === null || val === undefined ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const headers = ['Invoice Number', 'Type', 'Customer', 'Vehicle', 'Amount', 'Discount', 'Paid', 'Balance', 'Status', 'Issue Date'];
    const lines = imeiProfitResult.invoices.map(inv => [
      inv.invoice_number, inv.invoice_type, inv.customer_name, inv.vehicle_no,
      inv.amount, inv.discount, inv.paid_amount, inv.balance, inv.status, inv.issue_date,
    ].map(escapeCSV).join(','));
    const today = new Date().toLocaleDateString('en-PK');
    const csvContent = [
      `SecureWheels - IMEI Profit Report`,
      `IMEI: ${imeiProfitResult.imei}`,
      `Exported on: ${today}`,
      `Total Invoiced: Rs. ${imeiProfitResult.total_invoiced}`,
      `Total Paid: Rs. ${imeiProfitResult.total_paid}`,
      `Outstanding: Rs. ${imeiProfitResult.total_balance}`,
      '',
      headers.join(','),
      ...lines,
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `IMEI_Profit_Report_${imeiProfitResult.imei}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Reports: Vehicle Profit Report ────────────────────────────────────────
  // Same pattern as the IMEI Profit Report, keyed by vehicle registration
  // instead of IMEI. Powered by vehicle_revenue_report.php (invoices.vehicle_id
  // is already a proper FK, so no backfill needed for this one).
  const runVehicleProfitSearch = async (overrideVehicleNo) => {
    const vehicleNo = (overrideVehicleNo ?? vehicleProfitSearch).trim();
    setVehicleProfitError('');
    if (!vehicleNo) {
      setVehicleProfitError('Enter a vehicle number to search.');
      return;
    }
    setVehicleProfitLoading(true);
    setVehicleProfitResult(null);
    try {
      const res = await api.reports.vehicleRevenue(vehicleNo);
      if (res.success) {
        setVehicleProfitResult(res.data);
      } else {
        setVehicleProfitError(res.message || 'No data found for this vehicle.');
      }
    } catch (e) {
      setVehicleProfitError('Failed to load report: ' + e.message);
    } finally {
      setVehicleProfitLoading(false);
    }
  };

  const exportVehicleProfitToCSV = () => {
    if (!vehicleProfitResult || !vehicleProfitResult.invoices || vehicleProfitResult.invoices.length === 0) {
      alert('No data to export.');
      return;
    }
    const escapeCSV = (val) => {
      const str = val === null || val === undefined ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const headers = ['Invoice Number', 'Type', 'Customer', 'IMEI', 'Amount', 'Discount', 'Paid', 'Balance', 'Status', 'Issue Date'];
    const lines = vehicleProfitResult.invoices.map(inv => [
      inv.invoice_number, inv.invoice_type, inv.customer_name, inv.imei,
      inv.amount, inv.discount, inv.paid_amount, inv.balance, inv.status, inv.issue_date,
    ].map(escapeCSV).join(','));
    const today = new Date().toLocaleDateString('en-PK');
    const csvContent = [
      `SecureWheels - Vehicle Profit Report`,
      `Vehicle: ${vehicleProfitResult.vehicle_no}`,
      `Exported on: ${today}`,
      `Total Invoiced: Rs. ${vehicleProfitResult.total_invoiced}`,
      `Total Paid: Rs. ${vehicleProfitResult.total_paid}`,
      `Outstanding: Rs. ${vehicleProfitResult.total_balance}`,
      '',
      headers.join(','),
      ...lines,
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Vehicle_Profit_Report_${vehicleProfitResult.vehicle_no}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to check if vehicle is awaiting package/amc
  const isPackageAwaiting = (vehicle) => {
    // Check if packagePrice or amc is missing/empty (handle both string and number types)
    // Only a blank value counts as awaiting — 0 means the charge is deliberately free.
    const isBlank = (x) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '');
    const hasNoPackagePrice = isBlank(vehicle.packagePrice);
    const hasNoAmc = isBlank(vehicle.amc);
    return hasNoPackagePrice || hasNoAmc;
  };

  // Calculate vehicle counts
  const getVehicleCounts = () => {
    if (!selectedCustomer) return { active: 0, inactive: 0, packageAwaiting: 0 };
    
    const vehicles = selectedCustomer.vehicles;
    return {
      active: vehicles.filter(v => v.status === 'active').length,
      inactive: vehicles.filter(v => v.status === 'inactive').length,
      packageAwaiting: vehicles.filter(v => isPackageAwaiting(v)).length
    };
  };

  // Customer Management Functions
  const handleAddCustomer = async () => {
    try {
      const createRes = await api.customers.create(newCustomer);
      // Auto-create the mandatory division named after the customer, so it doesn't
      // have to be added by hand every time. Best-effort — never blocks the add.
      const newCustomerId = createRes?.data?.id;
      const customerName = (newCustomer.name || '').trim();
      if (newCustomerId && customerName) {
        try {
          await api.divisions.create({ customerId: newCustomerId, divisionName: customerName });
        } catch (e) {
          console.error('Failed to auto-create customer division:', e);
        }
      }
      await loadCustomerList(1, searchTerm.trim(), false); // new customer shows in the sidebar
      loadCustomers(); // keep the full aggregate cache in sync (background)
      setNewCustomer({
        name: '', type: 'Individual', gender: 'Male', phone: '', cell: '', whatsapp: '', 
        email: '', cnic: '', address: '', city: '', state: '', country: 'Pakistan', 
        contactPerson: '', cpContact: '', pocEmail: '', followUp: '', salesperson: '' 
      });
      setShowAddCustomer(false);
      alert('Customer added successfully!');
    } catch (error) {
      alert('Failed to add customer: ' + error.message);
    }
  };


  const handleEditCustomerClick = () => {
    if (selectedCustomer) {
      setEditCustomer({ ...selectedCustomer });
      setShowEditCustomer(true);
    }
  };

  const handleSaveEditCustomer = async () => {
    try {
      const editedId = selectedCustomer.id;
      await api.customers.update({ ...editCustomer, id: editedId });
      await refreshAfterMutation(editedId); // refresh sidebar row + open detail
      loadCustomers(); // keep the full aggregate cache in sync (background)
      setShowEditCustomer(false);
      alert('Customer updated successfully!');
    } catch (error) {
      alert('Failed to update customer: ' + error.message);
    }
  };

  // Open the Add Vehicle form pre-filled from a Reconciliation "GPS not in CRM"
  // row. No customer is selected yet — the user picks one via the form's built-in
  // customer search, then the vehicle is added to that customer.
  const handleReconAddVehicle = (r) => {
    setSelectedCustomer(null);
    // Auto-fill the device name from inventory if this IMEI is already stocked
    // (same behaviour as typing the IMEI in the form), so DEVICE isn't left blank.
    const invItem = inventory.find(i => i.imei === (r.imei || ''));
    setNewVehicle({
      vehicleNo: r.name || '', model: '', engineNo: '', chassisNo: '',
      activeDate: new Date().toISOString().split('T')[0],
      status: 'active', imei: r.imei || '', deviceName: invItem?.deviceType || '', packagePrice: '', amc: '', commission: '', invoiceFullSetup: false,
      invoicingPeriod: 'Yearly', division: '', division_id: null, salesperson: '', setupInvoiceCreated: false,
    });
    setVehicleCustomerSearch('');
    setShowVehicleCustomerPicker(true);
    setInstallTechId('');
    setInstallTechAmount('0');
    // Ensure the full customer list is available for the picker.
    if (customers.length <= 1) loadCustomers();
    setShowAddVehicle(true);
  };

  const handleAddVehicle = async () => {
    // Validate required fields
    if (!newVehicle.vehicleNo || !newVehicle.imei) {
      alert('Vehicle Number and IMEI are required fields!');
      return;
    }

    if (!selectedCustomer) {
      alert('Please select a customer for this vehicle first.');
      return;
    }
    
    // Check for duplicate vehicle number in active vehicles across all customers
    let duplicateVehicleNo = false;
    let duplicateImei = false;
    let imeiVehicle = '';
    
    for (const customer of customers) {
      for (const vehicle of customer.vehicles) {
        // Check vehicle number (case-insensitive)
        if (vehicle.status === 'active' && 
            vehicle.vehicleNo.toLowerCase() === newVehicle.vehicleNo.toLowerCase()) {
          duplicateVehicleNo = true;
          break;
        }
        
        // Check IMEI
        if (vehicle.status === 'active' && 
            vehicle.imei === newVehicle.imei) {
          duplicateImei = true;
          imeiVehicle = vehicle.vehicleNo;
          break;
        }
      }
      if (duplicateVehicleNo || duplicateImei) break;
    }
    
    if (duplicateVehicleNo) {
      alert(`Vehicle number '${newVehicle.vehicleNo}' already exists in active vehicles!`);
      return;
    }
    
    if (duplicateImei) {
      alert(`IMEI '${newVehicle.imei}' is already installed in active vehicle: ${imeiVehicle}`);
      return;
    }

    // Validate that the IMEI exists in the inventory store
    const imeiInInventory = inventory.find(item => item.imei === newVehicle.imei);
    if (!imeiInInventory) {
      alert(`IMEI '${newVehicle.imei}' is not found in the store inventory. Please add it to inventory first or check the IMEI number.`);
      return;
    }

    // Validate that the IMEI is ready to install — uses centralized isImeiReadyToInstall()
    // To add new statuses (Damaged, Lost, etc.), update only that one function above.
    if (!isImeiReadyToInstall(newVehicle.imei)) {
      alert(`Cannot assign IMEI '${newVehicle.imei}': ${getImeiNotReadyReason(newVehicle.imei)}.`);
      return;
    }
    
    if (selectedCustomer) {
      try {
        // Create vehicle first
        const createResult = await api.vehicles.create({
          customerId: selectedCustomer.id,
          vehicleNo: newVehicle.vehicleNo,
          make: newVehicle.make,
          model: newVehicle.model,
          engineNo: newVehicle.engineNo,
          chassisNo: newVehicle.chassisNo,
          activeDate: newVehicle.activeDate,
          status: newVehicle.status,
          invoiceStatus: 'PENDING',
          items: 0,
          imei: newVehicle.imei,
          deviceType: newVehicle.deviceName,  // Send as deviceType to match API expectation
          packagePrice: newVehicle.packagePrice,
          amc: newVehicle.amc,
          commission: newVehicle.commission,
          invoiceFullSetup: !!newVehicle.invoiceFullSetup,
          invoicingPeriod: newVehicle.invoicingPeriod,
          division: newVehicle.division,
          divisionId: newVehicle.division_id,
          salesperson: newVehicle.salesperson,
          setupInvoiceCreated: false
        });
        
        // Update inventory status, location and assigned_to_vehicle_id
        const newVehicleId = createResult?.data?.id || null;
        const inventoryItem = inventory.find(item => item.imei === newVehicle.imei);
        if (inventoryItem) {
          await api.inventory.update({
            id: inventoryItem.id,
            imei: inventoryItem.imei,
            device_type: inventoryItem.device_type,
            iccid: inventoryItem.iccid,
            sim_number: inventoryItem.sim_number,
            vendor: inventoryItem.vendor,
            amount: inventoryItem.amount,
            status: 'Installed',
            location: newVehicle.vehicleNo,
            assigned_to_vehicle_id: newVehicleId,
            is_issued: false,
            issued_to: null
          });
        }

        // Save technician payment record for installation
        if (installTechId && parseFloat(installTechAmount) >= 0) {
          const installTech = employees.find(e => String(e.id) === String(installTechId));
          try {
            await fetch(`${API_BASE_URL}/technician_payments.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                technician_id:   parseInt(installTechId),
                technician_name: installTech?.name || '',
                activity_type:   'installation',
                vehicle_id:      newVehicleId,
                vehicle_no:      newVehicle.vehicleNo,
                customer_name:   selectedCustomer.name,
                amount:          parseFloat(installTechAmount) || 0,
                activity_date:   newVehicle.activeDate || new Date().toISOString().split('T')[0],
                notes:           `Tracker installation — IMEI: ${newVehicle.imei}`
              })
            });
          } catch(e) { console.error('Failed to save installation tech payment', e); }
        }
        
        // Load fresh data and update selected customer
        const freshCustomers = await loadCustomers();
        const updatedCustomer = freshCustomers.find(c => c.id === selectedCustomer.id);
        setSelectedCustomer(updatedCustomer);
        
        // Reload inventory to reflect changes
        await loadInventory();

        // If this add came from the Reconciliation "GPS not in CRM" list, refresh the
        // recon data so the just-added vehicle drops off that list automatically.
        if (reconData) {
          try {
            const rres = await fetch(`${API_BASE_URL}/reconciliation.php`);
            setReconData(await rres.json());
          } catch (e) { /* best-effort — recon refresh must never block the add */ }
        }

        setNewVehicle({
          vehicleNo: '', model: '', engineNo: '', chassisNo: '', activeDate: '', status: 'active',
          imei: '', deviceName: '', packagePrice: '', amc: '', commission: '', invoiceFullSetup: false, invoicingPeriod: 'Yearly',
          division: '', division_id: null, salesperson: '', setupInvoiceCreated: false
        });
        setShowAddVehicle(false);
        setShowVehicleCustomerPicker(false);
        alert('Vehicle added successfully!');
      } catch (error) {
        alert('Failed to add vehicle: ' + error.message);
      }
    }
  };

  const handleEditVehicleClick = (vehicle) => {
    setEditingVehicle(vehicle);
    setEditVehicle({ ...vehicle });
    setShowEditVehicle(true);
  };

  const handleSaveEditVehicle = async () => {
    // Validate required fields
    if (!editVehicle.vehicleNo || !editVehicle.imei) {
      alert('Vehicle Number and IMEI are required fields!');
      return;
    }
    
    // Check if vehicle is being marked as inactive and has an IMEI
    if (editVehicle.status === 'inactive' && editingVehicle.status === 'active' && editVehicle.imei) {
      // Show popup to ask who removed the tracker
      setPendingInactiveVehicle(editVehicle);
      setShowRemovalPopup(true);
      return; // Don't proceed until employee is selected
    }
    
    // Proceed with normal save
    await performVehicleSave();
  };

  const performVehicleSave = async (removedBy = null, markLost = false) => {
    // removedBy can be a JSON string {id, name} or null.
    // markLost = true → the tracker was not recovered; mark it Lost instead of
    // issuing it back to a technician.
    let removedByName = null;
    let removedById = null;
    if (removedBy) {
      try {
        const parsed = JSON.parse(removedBy);
        removedByName = parsed.name;
        removedById = parsed.id;
      } catch(e) {
        removedByName = removedBy; // fallback to string
      }
    }
    // Validate required fields
    if (!editVehicle.vehicleNo || !editVehicle.imei) {
      alert('Vehicle Number and IMEI are required fields!');
      return;
    }
    
    // Check for duplicate vehicle number in active vehicles (excluding current vehicle)
    let duplicateVehicleNo = false;
    let duplicateImei = false;
    let imeiVehicle = '';
    
    for (const customer of customers) {
      for (const vehicle of customer.vehicles) {
        // Skip the current vehicle being edited
        if (vehicle.id === editingVehicle.id) continue;
        
        // Check vehicle number (case-insensitive)
        if (vehicle.status === 'active' && 
            vehicle.vehicleNo.toLowerCase() === editVehicle.vehicleNo.toLowerCase()) {
          duplicateVehicleNo = true;
          break;
        }
        
        // Check IMEI
        if (vehicle.status === 'active' && 
            vehicle.imei === editVehicle.imei) {
          duplicateImei = true;
          imeiVehicle = vehicle.vehicleNo;
          break;
        }
      }
      if (duplicateVehicleNo || duplicateImei) break;
    }
    
    if (duplicateVehicleNo) {
      alert(`Vehicle number '${editVehicle.vehicleNo}' already exists in active vehicles!`);
      return;
    }
    
    if (duplicateImei) {
      alert(`IMEI '${editVehicle.imei}' is already installed in active vehicle: ${imeiVehicle}`);
      return;
    }
    
    if (selectedCustomer && editingVehicle) {
      try {
        const oldImei = editingVehicle.imei;
        const newImei = editVehicle.imei;
        
        // Call API to update vehicle in database
        await api.vehicles.update({
          id: editingVehicle.id,
          vehicleNo: editVehicle.vehicleNo,
          make: editVehicle.make,
          model: editVehicle.model,
          engineNo: editVehicle.engineNo,
          chassisNo: editVehicle.chassisNo,
          activeDate: editVehicle.activeDate,
          status: editVehicle.status,
          imei: editVehicle.imei,
          deviceType: editVehicle.deviceName,  // Send as deviceType to match API expectation
          packagePrice: editVehicle.packagePrice,
          amc: editVehicle.amc,
          commission: editVehicle.commission,
          invoiceFullSetup: !!editVehicle.invoiceFullSetup,
          invoicingPeriod: editVehicle.invoicingPeriod,
          division: editVehicle.division,
          divisionId: editVehicle.division_id
        });

        // Handle IMEI changes in inventory
        if (oldImei && oldImei !== newImei) {
          // Old IMEI was removed - set Status=Available, Location=Store, clear vehicle link
          const oldInventoryItem = inventory.find(item => item.imei === oldImei);
          if (oldInventoryItem) {
            await api.inventory.update({
              id: oldInventoryItem.id,
              imei: oldInventoryItem.imei,
              device_type: oldInventoryItem.device_type,
              iccid: oldInventoryItem.iccid,
              sim_number: oldInventoryItem.sim_number,
              vendor: oldInventoryItem.vendor,
              amount: oldInventoryItem.amount,
              status: 'Available',
              location: 'Store',
              assigned_to_vehicle_id: null
            });
          }
        }
        
        // BUG FIX #2: If vehicle status changed to inactive, release the IMEI —
        // either issue it to the employee who removed it, or mark it Lost.
        if (editVehicle.status === 'inactive' && editingVehicle.status === 'active' && editVehicle.imei) {
          const inventoryItem = inventory.find(item => item.imei === editVehicle.imei);
          if (inventoryItem) {
            await api.inventory.update({
              id: inventoryItem.id,
              imei: inventoryItem.imei,
              device_type: inventoryItem.device_type,
              iccid: inventoryItem.iccid,
              sim_number: inventoryItem.sim_number,
              vendor: inventoryItem.vendor,
              amount: inventoryItem.amount,
              status: 'Available',
              // Lost devices are marked in health_status so they can't be re-installed.
              health_status: markLost ? 'lost' : (inventoryItem.health_status || inventoryItem.healthStatus || 'active'),
              location: markLost ? 'Lost' : (removedByName || 'Store'),
              is_issued: markLost ? false : (removedById ? true : false),
              issued_to: markLost ? null : (removedById || null),
              assigned_to_vehicle_id: null
            });
          }

          // Save technician payment record for removal (not applicable when lost)
          if (!markLost && removedById && parseFloat(removalTechAmount) >= 0) {
            try {
              await fetch(`${API_BASE_URL}/technician_payments.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  technician_id:   removedById,
                  technician_name: removedByName,
                  activity_type:   'removal',
                  vehicle_id:      editingVehicle.id,
                  vehicle_no:      editVehicle.vehicleNo,
                  customer_name:   selectedCustomer.name,
                  amount:          parseFloat(removalTechAmount) || 0,
                  activity_date:   new Date().toISOString().split('T')[0],
                  notes:           `Tracker removal — IMEI: ${editVehicle.imei}`
                })
              });
            } catch(e) { console.error('Failed to save removal tech payment', e); }
          }
        }
        
        if (newImei && oldImei !== newImei) {
          // New IMEI was installed - set Status=Installed, Location=Vehicle Number, link vehicle
          const newInventoryItem = inventory.find(item => item.imei === newImei);
          if (newInventoryItem) {
            await api.inventory.update({
              id: newInventoryItem.id,
              imei: newInventoryItem.imei,
              device_type: newInventoryItem.device_type,
              iccid: newInventoryItem.iccid,
              sim_number: newInventoryItem.sim_number,
              vendor: newInventoryItem.vendor,
              amount: newInventoryItem.amount,
              status: 'Installed',
              location: editVehicle.vehicleNo,
              assigned_to_vehicle_id: editingVehicle.id,
              is_issued: false,
              issued_to: null
            });
          }
        }
        
        // Vehicle reactivated with same IMEI — restore installed state and vehicle link
        if (editVehicle.status === 'active' && editingVehicle.status === 'inactive' && editVehicle.imei) {
          const inventoryItem = inventory.find(item => item.imei === editVehicle.imei);
          if (inventoryItem) {
            await api.inventory.update({
              id: inventoryItem.id,
              imei: inventoryItem.imei,
              device_type: inventoryItem.device_type,
              iccid: inventoryItem.iccid,
              sim_number: inventoryItem.sim_number,
              vendor: inventoryItem.vendor,
              amount: inventoryItem.amount,
              status: 'Installed',
              location: editVehicle.vehicleNo,
              assigned_to_vehicle_id: editingVehicle.id,
              is_issued: false,
              issued_to: null
            });
          }
        }
        
        // Reload customers to get fresh data from database
        const freshCustomers = await loadCustomers();
        const updatedCustomer = freshCustomers.find(c => c.id === selectedCustomer.id);
        setSelectedCustomer(updatedCustomer);
        
        // Reload inventory to reflect changes
        await loadInventory();
        
        setShowEditVehicle(false);
        setEditingVehicle(null);
        alert('Vehicle updated successfully!');
      } catch (error) {
        alert('Failed to update vehicle: ' + error.message);
      }
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!selectedCustomer) return;
    
    // Find the vehicle being deleted
    const vehicleToDelete = selectedCustomer.vehicles.find(v => v.id === vehicleId);
    
    // Check if setup invoice has been created
    if (vehicleToDelete && vehicleToDelete.setupInvoiceCreated) {
      alert('Cannot delete vehicle: Setup invoice has already been created for this vehicle.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        // Call API to delete vehicle from database
        await api.vehicles.delete(vehicleId);
        
        if (vehicleToDelete && vehicleToDelete.imei) {
          // Return IMEI to store: Status=Available, Location=Store
          const inventoryItem = inventory.find(item => item.imei === vehicleToDelete.imei);
          if (inventoryItem) {
            await api.inventory.update({
              id: inventoryItem.id,
              imei: inventoryItem.imei,
              device_type: inventoryItem.device_type,
              iccid: inventoryItem.iccid,
              sim_number: inventoryItem.sim_number,
              vendor: inventoryItem.vendor,
              amount: inventoryItem.amount,
              status: 'Available',
              location: 'Store'
            });
          }
        }
        
        // Reload customers to get fresh data from database
        const freshCustomers = await loadCustomers();
        const updatedCustomer = freshCustomers.find(c => c.id === selectedCustomer.id);
        setSelectedCustomer(updatedCustomer);
        
        // Reload inventory to reflect changes
        await loadInventory();
        
        alert('Vehicle deleted successfully!');
      } catch (error) {
        alert('Failed to delete vehicle: ' + error.message);
      }
    }
  };

  const loadVehicleNumberChanges = async (vehicleId) => {
    try {
      const result = await api.vehicles.getNumberChanges(vehicleId);
      if (result.success && result.data) {
        // The backend doesn't implement action=number_changes (no such table/handler),
        // so it falls through to the default GET and returns unrelated vehicle rows.
        // Only keep records that are genuinely number-change entries — i.e. they carry
        // an old/new vehicle number — so we never render that garbage as "history".
        const changes = (Array.isArray(result.data) ? result.data : []).filter(
          row => row && (row.old_vehicle_no != null || row.new_vehicle_no != null)
        );
        setVehicleNumberChanges(prev => ({
          ...prev,
          [vehicleId]: changes
        }));
      }
    } catch (error) {
      console.error('Error loading vehicle number changes:', error);
    }
  };

  // Load Removal&Transfer / Ownership-Transfer records for a vehicle so the
  // expanded row can show where it came from (and where it went).
  const loadVehicleTransfers = async (vehicleId) => {
    try {
      const result = await api.call(`service.php?vehicle_id=${vehicleId}`);
      if (result.success && result.data) {
        setVehicleTransfers(prev => ({ ...prev, [vehicleId]: { rt: result.data.rt || [], ot: result.data.ot || [] } }));
      }
    } catch (error) {
      console.error('Error loading vehicle transfers:', error);
    }
  };

  // Inventory Management Functions

  // FIXED: Add Inventory - Now calls API and saves to database
  const handleViewImeiLog = async (item) => {
    setImeiLogDrawer({ imei: item.imei, deviceType: item.deviceType });
    setImeiLogs([]);
    setImeiLogsLoading(true);
    setImeiRevenue(null);
    setImeiRevenueLoading(true);

    // Independent lookups — run concurrently, fail independently so a
    // problem with one (e.g. revenue) never blocks the other (history).
    api.imeiLogs.getByImei(item.imei)
      .then(res => setImeiLogs(res.data || []))
      .catch(e => console.error('Failed to load IMEI logs:', e))
      .finally(() => setImeiLogsLoading(false));

    api.reports.imeiRevenue(item.imei)
      .then(res => setImeiRevenue(res.data || null))
      .catch(e => console.error('Failed to load IMEI revenue:', e))
      .finally(() => setImeiRevenueLoading(false));
  };

  const handleAddInventory = async () => {
    // Validation
    if (!newInventoryItem.imei || !newInventoryItem.deviceType || !newInventoryItem.iccid || 
        !newInventoryItem.simNumber || !newInventoryItem.vendor || !newInventoryItem.amount) {
      alert('Please fill all required fields');
      return;
    }

    // IMEI validation - must be exactly 15 digits
    if (newInventoryItem.imei.length !== 15 || !/^\d+$/.test(newInventoryItem.imei)) {
      alert('IMEI must be exactly 15 digits');
      return;
    }

    try {
      setLoading(true);
      
      // Call API to save to database
      await api.inventory.create({
        imei: newInventoryItem.imei,
        device_type: newInventoryItem.deviceType,
        iccid: newInventoryItem.iccid,
        sim_number: newInventoryItem.simNumber,
        vendor: newInventoryItem.vendor,
        amount: parseFloat(newInventoryItem.amount),
        purchase_date: new Date().toISOString().split('T')[0],
        status: 'Available',
        health_status: newInventoryItem.healthStatus || 'active',
        is_issued: false,
        issued_to: null,
        assigned_to_vehicle_id: null,
        location: 'Store'
      });
      
      // Reload inventory from database
      await loadInventory();
      
      // Reset form
      setNewInventoryItem({ 
        imei: '', 
        deviceType: '', 
        iccid: '', 
        simNumber: '', 
        vendor: '', 
        amount: '',
        healthStatus: 'active'
      });
      
      try {
        await api.call('imei_logs.php', 'POST', {
          imei: newInventoryItem.imei,
          event_type: 'added',
          actor_name: 'Staff',
          location: 'Store',
          notes: `Added. Device: ${newInventoryItem.deviceType||''}. Vendor: ${newInventoryItem.vendor||''}`,
        });
      } catch(e) { console.warn('IMEI log failed:', e); }
      setShowAddInventory(false);
      alert('Inventory item added successfully!');
    } catch (error) {
      console.error('Add inventory error:', error);
      alert('Failed to add inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInventoryClick = (item) => {
    setEditingInventory(item);
    setEditInventoryItem({ ...item });
    setShowEditInventory(true);
  };

  // FIXED: Save Edit Inventory - Now calls API and updates database
  const handleSaveEditInventory = async () => {
    if (!editingInventory) return;

    // Validation
    if (!editInventoryItem.imei || !editInventoryItem.deviceType || !editInventoryItem.iccid || 
        !editInventoryItem.simNumber || !editInventoryItem.vendor || !editInventoryItem.amount) {
      alert('Please fill all required fields');
      return;
    }

    // IMEI validation
    if (editInventoryItem.imei.length !== 15 || !/^\d+$/.test(editInventoryItem.imei)) {
      alert('IMEI must be exactly 15 digits');
      return;
    }

    try {
      setLoading(true);
      
      // Call API to update in database
      await api.inventory.update({
        id: editingInventory.id,
        imei: editInventoryItem.imei,
        device_type: editInventoryItem.deviceType,
        iccid: editInventoryItem.iccid,
        sim_number: editInventoryItem.simNumber,
        vendor: editInventoryItem.vendor,
        amount: parseFloat(editInventoryItem.amount),
        health_status: editInventoryItem.healthStatus || 'active'
      });
      
      // Reload inventory from database
      await loadInventory();
      
      setShowEditInventory(false);
      setEditingInventory(null);
      alert('Inventory item updated successfully!');
    } catch (error) {
      console.error('Update inventory error:', error);
      alert('Failed to update inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Delete Inventory - Now calls API and deletes from database
  const handleDeleteInventory = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Call API to delete from database
      await api.inventory.delete(itemId);
      
      // Reload inventory from database
      await loadInventory();
      
      alert('Inventory item deleted successfully!');
    } catch (error) {
      console.error('Delete inventory error:', error);
      alert('Failed to delete inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  // Vendor handlers
  const handleAddVendor = async () => {
    if (newVendor.name && newVendor.contact) {
      try {
        setLoading(true);
        
        // Call API to save to database
        await api.vendors.create({
          name: newVendor.name,
          contact: newVendor.contact,
          email: newVendor.email || '',
          address: newVendor.address || '',
          city: newVendor.city || ''
        });
        
        // Reload vendors from database
        await loadVendors();
        
        setNewVendor({ name: '', contact: '', email: '', address: '', city: '' });
        setShowAddVendor(false);
        alert('Vendor added successfully!');
      } catch (error) {
        console.error('Add vendor error:', error);
        alert('Failed to add vendor: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateVendor = async () => {
    if (editVendorData.name && editVendorData.contact) {
      try {
        setLoading(true);
        
        // Call API to update in database
        await api.vendors.update({
          id: editingVendor.id,
          name: editVendorData.name,
          contact: editVendorData.contact,
          email: editVendorData.email || '',
          address: editVendorData.address || '',
          city: editVendorData.city || ''
        });
        
        // Reload vendors from database
        await loadVendors();
        
        setShowEditVendor(false);
        setEditingVendor(null);
        alert('Vendor updated successfully!');
      } catch (error) {
        console.error('Update vendor error:', error);
        alert('Failed to update vendor: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        setLoading(true);
        
        // Call API to delete from database
        await api.vendors.delete(vendorId);
        
        // Reload vendors from database
        await loadVendors();
        
        alert('Vendor deleted successfully!');
      } catch (error) {
        console.error('Delete vendor error:', error);
        alert('Failed to delete vendor: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Device handlers
  const handleAddDevice = async () => {
    if (newDevice.name && newDevice.category) {
      try {
        setLoading(true);
        
        // Call API to save to database
        await api.devices.create({
          name: newDevice.name,
          category: newDevice.category,
          description: newDevice.description || ''
        });
        
        // Reload devices from database
        await loadDevices();
        
        setNewDevice({ name: '', category: '', description: '' });
        setShowAddDevice(false);
        alert('Device added successfully!');
      } catch (error) {
        console.error('Add device error:', error);
        alert('Failed to add device: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateDevice = async () => {
    if (editDeviceData.name && editDeviceData.category) {
      try {
        setLoading(true);
        
        // Call API to update in database
        await api.devices.update({
          id: editingDevice.id,
          name: editDeviceData.name,
          category: editDeviceData.category,
          description: editDeviceData.description || ''
        });
        
        // Reload devices from database
        await loadDevices();
        
        setShowEditDevice(false);
        setEditingDevice(null);
        alert('Device updated successfully!');
      } catch (error) {
        console.error('Update device error:', error);
        alert('Failed to update device: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        setLoading(true);
        
        // Call API to delete from database
        await api.devices.delete(deviceId);
        
        // Reload devices from database
        await loadDevices();
        
        alert('Device deleted successfully!');
      } catch (error) {
        console.error('Delete device error:', error);
        alert('Failed to delete device: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Employee handlers
  const handleAddEmployee = async () => {
    // Validate CNIC
    const cnicDigits = newEmployee.cnic.replace(/\D/g, '');
    if (cnicDigits.length !== 13) {
      alert('⚠️ CNIC must be exactly 13 digits in format: xxxxx-xxxxxxx-x');
      return;
    }

    if (newEmployee.name && newEmployee.designation) {
      try {
        setLoading(true);
        
        // Call API to save to database
        await api.employees.create({
          name: newEmployee.name,
          designation: newEmployee.designation,
          department: newEmployee.department || '',
          phone: newEmployee.phone || '',
          email: newEmployee.email || '',
          role: newEmployee.role,
          joiningDate: newEmployee.joiningDate || new Date().toISOString().split('T')[0],
          cnic: newEmployee.cnic || '',
          city: newEmployee.city || '',
          type: newEmployee.emp_type || 'permanent',
          photo: newEmployee.photo || ''
        });
        
        // Reload employees from database
        await loadEmployees();
        
        setNewEmployee({ name: '', designation: '', department: '', phone: '', email: '', role: 'Sales', joiningDate: '', cnic: '', city: '', emp_type: 'permanent' });
        setShowAddEmployee(false);
        alert('Employee added successfully!');
      } catch (error) {
        console.error('Add employee error:', error);
        alert('Failed to add employee: ' + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      alert('Please fill in Name and Designation fields');
    }
  };

  const handleUpdateEmployee = async () => {
    // Validate CNIC
    const cnicDigits = editEmployeeData.cnic.replace(/\D/g, '');
    if (cnicDigits.length !== 13) {
      alert('⚠️ CNIC must be exactly 13 digits in format: xxxxx-xxxxxxx-x');
      return;
    }

    if (editEmployeeData.name && editEmployeeData.designation) {
      try {
        setLoading(true);
        
        // Call API to update in database
        await api.employees.update({
          id: editingEmployee.id,
          name: editEmployeeData.name,
          designation: editEmployeeData.designation,
          department: editEmployeeData.department || '',
          phone: editEmployeeData.phone || '',
          email: editEmployeeData.email || '',
          role: editEmployeeData.role,
          joiningDate: editEmployeeData.joiningDate,
          cnic: editEmployeeData.cnic || '',
          city: editEmployeeData.city || '',
          type: editEmployeeData.emp_type || 'permanent',
          ...(editEmployeeData.photo !== undefined ? { photo: editEmployeeData.photo || '' } : {})
        });
        
        // Reload employees from database
        await loadEmployees();
        
        setShowEditEmployee(false);
        setEditingEmployee(null);
        alert('Employee updated successfully!');
      } catch (error) {
        console.error('Update employee error:', error);
        alert('Failed to update employee: ' + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      alert('Please fill in Name and Designation fields');
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    const response = window.confirm(
      'What would you like to do?\n\n' +
      'Click OK to DEACTIVATE (soft delete - keeps data in database)\n' +
      'Click CANCEL, then use the secondary delete button for permanent deletion'
    );
    
    if (response === true) {
      // User clicked OK - deactivate
      try {
        setLoading(true);
        await api.employees.deactivate(employeeId);
        await loadEmployees();
        alert('Employee deactivated successfully!');
      } catch (error) {
        console.error('Deactivate employee error:', error);
        alert('Failed to deactivate employee: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePermanentDeleteEmployee = async (employeeId) => {
    if (window.confirm('⚠️ WARNING: This will PERMANENTLY DELETE the employee record from the database. This action cannot be undone. Are you sure?')) {
      try {
        setLoading(true);
        await api.employees.delete(employeeId);
        await loadEmployees();
        alert('Employee permanently deleted successfully!');
      } catch (error) {
        console.error('Permanent delete employee error:', error);
        alert('Failed to delete employee: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Invoice Utility Functions
  const generateInvoiceNumber = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return '';
    
    const now = new Date();
    const yymm = String(now.getFullYear()).slice(-2) + String(now.getMonth() + 1).padStart(2, '0');
    const custId = String(customerId).padStart(5, '0');
    const count = String((customer.invoiceCount || 0) + 1).padStart(3, '0');
    
    return `${yymm}-${custId}-${count}`;
  };

  const calculateDueDate = (supposedDueDate, days = INVOICE_DUE_DAYS) => {
    // Due date is configurable days after the invoice issue date (set by INVOICE_DUE_DAYS)
    const date = new Date(supposedDueDate);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const calculateRenewalAmount = (amc, period) => {
    const amcValue = parseFloat(amc) || 0;
    switch(period) {
      case 'Monthly': return (amcValue / 12).toFixed(2);
      case 'Quarterly': return (amcValue / 4).toFixed(2);
      case 'Annual': return amcValue.toFixed(2);
      default: return amcValue.toFixed(2);
    }
  };

  const getNextRenewalDate = (activeDate, period, renewalNumber = 1) => {
    const date = new Date(activeDate);
    switch(period) {
      case 'Monthly':
        date.setMonth(date.getMonth() + renewalNumber);
        break;
      case 'Quarterly':
        date.setMonth(date.getMonth() + (renewalNumber * 3));
        break;
      case 'Annual':
        date.setFullYear(date.getFullYear() + renewalNumber);
        break;
    }
    return date.toISOString().split('T')[0];
  };

  // Check if renewal invoice can be created (configurable days before due date - see INVOICE_DUE_DAYS)




const canCreateRenewalInvoice = (vehicle) => {
  // Must have active_date and invoicing_period set
  if (!vehicle.activeDate || !vehicle.invoicingPeriod) {
    return { allowed: false, reason: 'Active date or invoicing period not set', daysUntilAllowed: 0 };
  }
  
  // Must have service_upto set (means setup invoice was created)
  if (!vehicle.serviceUpto && !vehicle.service_upto) {
    return { allowed: false, reason: 'Setup invoice not created yet', daysUntilAllowed: 0 };
  }
  
  // The service_upto is the current due date
  const serviceUptoDate = new Date(vehicle.serviceUpto || vehicle.service_upto);
  
  // Can create invoice (configurable days) before service_upto (the due date - see INVOICE_DUE_DAYS)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to compare dates only
  
  const allowedDate = new Date(serviceUptoDate);
  allowedDate.setDate(allowedDate.getDate() - 21); // 21 days before service_upto
  
  const daysUntilDue = Math.ceil((serviceUptoDate - today) / (1000 * 60 * 60 * 24));
  const daysUntilAllowed = Math.ceil((allowedDate - today) / (1000 * 60 * 60 * 24));
  
  // Debug logging
  
  // Compare dates directly instead of using calculated days
  const todayStr = today.toISOString().split('T')[0];
  const allowedStr = allowedDate.toISOString().split('T')[0];
  const serviceUptoStr = serviceUptoDate.toISOString().split('T')[0];
  
  
  // Allow if today is greater than or EQUAL to allowed date
  if (todayStr >= allowedStr) {
    return { 
      allowed: true, 
      nextDueDate: serviceUptoStr,
      allowedDate: allowedStr,
      daysUntilDue 
    };
  }
  
  // Block only if today is BEFORE allowed date
  return { 
    allowed: false, 
    reason: `Next invoice due on ${formatDate(serviceUptoDate)}`,
    daysUntilAllowed: Math.max(1, daysUntilAllowed), // Ensure minimum 1 day when blocked
    nextDueDate: serviceUptoStr,
    allowedDate: allowedStr
  };
};




  const getInvoiceStatus = (invoice) => {
    if (invoice.status === 'cancelled') return 'CANCELLED';
    const totalPaid = invoice.payments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
    const discount = parseFloat(invoice.discount || 0);
    const total = parseFloat(invoice.amount) - discount;
    const dueDate = new Date(invoice.dueDate);
    const today = new Date();
    
    if (totalPaid >= total) return 'PAID';
    if (totalPaid > 0 && totalPaid < total) {
      return today > dueDate ? 'OVERDUE (Partial)' : 'PARTIALLY PAID';
    }
    return today > dueDate ? 'OVERDUE' : 'UNPAID';
  };

  // Load GPS expiry for every vehicle of the invoice shown in the details popup.
  const loadInvGps = React.useCallback(async (inv) => {
    if (!inv) { setInvGps(null); return; }
    const num = inv.invoiceNumber || inv.invoice_number;
    const ids = [...new Set(invoices
      .filter(x => (x.invoiceNumber || x.invoice_number) === num && x.status !== 'cancelled')
      .map(x => x.vehicleId).concat(inv.vehicleId).filter(Boolean))];
    if (!ids.length) { setInvGps([]); return; }
    setInvGps(null);
    try {
      const res = await fetch(`${API_BASE_URL}/reconciliation.php?action=gps_status&vehicle_ids=${ids.join(',')}`);
      const j = await res.json();
      setInvGps(j.success ? (j.data || []) : []);
    } catch (e) { setInvGps([]); }
  }, [invoices]);
  useEffect(() => {
    if (showInvoiceDetails && selectedInvoice) loadInvGps(selectedInvoice); else setInvGps(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInvoiceDetails, selectedInvoice?.id]);

  // One color per invoice status — drives the left status bar + detail badge.
  const invoiceStatusTheme = (status) => {
    if (status === 'PAID')           return { bar: '#1D9E75', bg: '#E1F5EE', fg: '#0F6E56', label: 'Paid' };
    if (status === 'PARTIALLY PAID') return { bar: '#EF9F27', bg: '#FAEEDA', fg: '#854F0B', label: 'Partially paid' };
    if (status === 'UNPAID')         return { bar: '#378ADD', bg: '#E6F1FB', fg: '#185FA5', label: 'Unpaid' };
    if (status === 'CANCELLED')      return { bar: '#B4B2A9', bg: '#F1EFE8', fg: '#5F5E5A', label: 'Cancelled' };
    return { bar: '#E24B4A', bg: '#FCEBEB', fg: '#A32D2D', label: status === 'OVERDUE (Partial)' ? 'Overdue (partial)' : 'Overdue' };
  };

  // Get customers with vehicles due for renewal invoice
const getCustomersWithDueVehicles = () => {
  const customersWithDue = [];
  
  customers.forEach(customer => {
    const dueVehicles = customer.vehicles.filter(vehicle => {
      // Only show active vehicles
      if (vehicle.status !== 'active') {
        return false;
      }
      
      // Only show if setup invoice is created
      if (!vehicle.setupInvoiceCreated) {
        return false;
      }
      
      const check = canCreateRenewalInvoice(vehicle);
      return check.allowed;
    });

    if (dueVehicles.length > 0) {
      customersWithDue.push({
        ...customer,
        dueVehicles,
        dueCount: dueVehicles.length
      });
    }
  });

  return customersWithDue;
};
  
  //Create a new function for Setup Due Vehicles
  const getCustomersWithSetupDueVehicles = () => {
  const customersWithSetupDue = [];
  
  customers.forEach(customer => {
    const setupDueVehicles = customer.vehicles.filter(vehicle => {
      // Only show if setup invoice NOT created yet AND has a valid package price
      return !vehicle.setupInvoiceCreated && 
             vehicle.status === 'active' && 
             vehicle.packagePrice && 
             (typeof vehicle.packagePrice === 'number' ? vehicle.packagePrice > 0 : vehicle.packagePrice.trim() !== '');
    });

    if (setupDueVehicles.length > 0) {
      customersWithSetupDue.push({
        ...customer,
        setupDueVehicles: setupDueVehicles,
        dueCount: setupDueVehicles.length
      });
    }
  });

  return customersWithSetupDue;
};

// Group invoices by invoice number for summary view
  const getGroupedInvoices = () => {
    const grouped = {};
    
    invoices.forEach(inv => {
      if (!grouped[inv.invoiceNumber]) {
        grouped[inv.invoiceNumber] = {
          invoiceNumber: inv.invoiceNumber,
          createdDate: inv.createdDate,
          dueDate: inv.dueDate,
          customerName: inv.customerName,
          type: inv.type,
          vehicles: [],
          totalAmount: 0,
          totalDiscount: 0,
          payments: [], // Initialize as empty array, will aggregate from all vehicles
          customerId: inv.customerId,
          firstId: inv.id // reference to first flat line for row-level actions
        };
      }

      grouped[inv.invoiceNumber].vehicles.push({
        vehicleNo: inv.vehicleNo,
        amount: parseFloat(inv.amount)
      });
      grouped[inv.invoiceNumber].totalAmount += parseFloat(inv.amount);
      grouped[inv.invoiceNumber].totalDiscount += parseFloat(inv.discount || 0);
      
      // Aggregate payments from ALL vehicles in this invoice
      if (inv.payments && inv.payments.length > 0) {
        grouped[inv.invoiceNumber].payments.push(...inv.payments);
      }
    });
    
    return Object.values(grouped);
  };

  // Status for a GROUPED invoice (net of discount). Single source of truth so the
  // Dashboard summary and the Detail tab always agree on amount/paid/balance/status.
  const groupedPaid = (g) => g.payments?.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0) || 0;
  const groupedNet = (g) => (parseFloat(g.totalAmount) || 0) - (parseFloat(g.totalDiscount) || 0);
  const getGroupedStatus = (g) => {
    const paid = groupedPaid(g);
    const total = groupedNet(g);
    const due = new Date(g.dueDate);
    const today = new Date();
    if (paid >= total) return 'PAID';
    if (paid > 0 && paid < total) return today > due ? 'OVERDUE (Partial)' : 'PARTIALLY PAID';
    return today > due ? 'OVERDUE' : 'UNPAID';
  };


  // ── Export Dashboard Detail to Excel ─────────────────────────────────────
  const exportDashboardToExcel = () => {
    // Apply same filters as the dashboard detail table
    const filteredData = invoices.filter(inv => {
      const matchesSearch =
        inv.invoiceNumber.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
        inv.customerName.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
        inv.vehicleNo.toLowerCase().includes(invoiceSearch.toLowerCase());

      const status = getInvoiceStatus(inv);

      let matchesStatus = false;
      if (invoiceStatusFilter === 'all') {
        matchesStatus = true;
      } else if (invoiceStatusFilter === 'UNPAID') {
        matchesStatus = status.includes('UNPAID') || status.includes('PARTIALLY PAID') || status.includes('OVERDUE');
      } else if (invoiceStatusFilter === 'OVERDUE' && status.includes('OVERDUE')) {
        matchesStatus = true;
      } else if (invoiceStatusFilter === 'PARTIALLY PAID' && (status === 'PARTIALLY PAID' || status === 'OVERDUE (Partial)')) {
        matchesStatus = true;
      } else if (status === invoiceStatusFilter) {
        matchesStatus = true;
      }

      const customer = customers.find(c => parseInt(c.id) === inv.customerId);
      const followUpPerson = customer?.followUp || 'Unassigned';
      const matchesSalesperson = invoiceFollowUpPersonFilter === 'all' || followUpPerson === invoiceFollowUpPersonFilter;

      return matchesSearch && matchesStatus && matchesSalesperson;
    });

    if (filteredData.length === 0) {
      alert('No data to export with current filters.');
      return;
    }

    // Determine header label for follow-up person filter
    const followUpLabel = invoiceFollowUpPersonFilter === 'all'
      ? 'All Follow-Up Persons'
      : `Follow-Up Person: ${invoiceFollowUpPersonFilter}`;

    // Build CSV rows
    const escapeCSV = (val) => {
      const str = val === null || val === undefined ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = [
      'Vehicle Number',
      'Invoice Date',
      'Customer Name',
      'Customer Contact',
      'Sales Person',
      'Invoice Amount (Rs.)',
      'Received Amount (Rs.)',
      'Pending Amount (Rs.)',
    ];

    const rows = filteredData.map(inv => {
      const customer = customers.find(c => parseInt(c.id) === inv.customerId);
      const contact = customer?.cell || customer?.phone || customer?.whatsapp || customer?.cpContact || '';
      const salesPerson =
        customer?.vehicles?.find(v => v.vehicleNo === inv.vehicleNo)?.salesperson ||
        customer?.salesperson || '';
      const totalPaid = inv.payments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
      const discount = parseFloat(inv.discount || 0);
      const effectiveAmount = parseFloat(inv.amount) - discount;
      const balance = effectiveAmount - totalPaid;

      return [
        inv.vehicleNo,
        inv.createdDate,
        inv.customerName,
        contact,
        salesPerson,
        effectiveAmount.toFixed(0),
        totalPaid.toFixed(0),
        balance.toFixed(0),
      ].map(escapeCSV).join(',');
    });

    // Build totals row
    const totalInvoiced = filteredData.reduce((s, inv) => s + parseFloat(inv.amount) - parseFloat(inv.discount || 0), 0);
    const totalReceived = filteredData.reduce((s, inv) => {
      const paid = inv.payments?.reduce((ps, p) => ps + parseFloat(p.amount), 0) || 0;
      return s + paid;
    }, 0);
    const totalPending = totalInvoiced - totalReceived;

    const totalsRow = [
      'TOTAL', '', '', '', '',
      totalInvoiced.toFixed(0),
      totalReceived.toFixed(0),
      totalPending.toFixed(0),
    ].map(escapeCSV).join(',');

    // Build full CSV with header info at top
    const today = new Date().toLocaleDateString('en-PK');
    const csvContent = [
      `SecureWheels - Invoice Dashboard Detail Export`,
      `Exported on: ${today}`,
      `Filter: ${followUpLabel}`,
      `Status Filter: ${invoiceStatusFilter === 'all' ? 'All' : invoiceStatusFilter}`,
      `Total Records: ${filteredData.length}`,
      '',
      headers.join(','),
      ...rows,
      '',
      totalsRow,
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filterSuffix = invoiceFollowUpPersonFilter !== 'all' ? `_${invoiceFollowUpPersonFilter.replace(/\s+/g, '_')}` : '';
    link.download = `Invoice_Dashboard${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Invoice Handlers
  // Single source of truth for the setup-invoice amount, based on the vehicle's
  // invoicing period. For Monthly/Quarterly the recurring AMC portion is charged
  // pro-rata (1 month / 1 quarter); Annual (and anything else) charges the full
  // package price. Used by BOTH the display and the actual invoice creation so
  // the shown amount and the created amount can never diverge.
  const calculateSetupAmount = (vehicle) => {
    const packagePrice = parseFloat(vehicle.packagePrice || 0);
    const amc = parseFloat(vehicle.amc || 0);
    if (vehicle.invoicingPeriod === 'Quarterly') return (packagePrice - amc) + (amc / 4);
    if (vehicle.invoicingPeriod === 'Monthly') return (packagePrice - amc) + (amc / 12);
    return packagePrice; // Annual / default
  };
  // Setup invoice amount after commission (matches invoices.php swSetupInvoiceAmount).
  const calculateSetupInvoiceAmount = (vehicle) => {
    const gross = calculateSetupAmount(vehicle);
    const comm = vehicle.invoiceFullSetup ? 0 : (parseFloat(vehicle.commission) || 0);
    return Math.max(0, gross - comm);
  };

  // No invoice may be created for a customer until BOTH a salesperson and a
  // follow-up person are defined on that customer. Returns false + alerts if missing.
  const assertCustomerAttributed = (customer) => {
    const sp = (customer?.salesperson || '').trim();
    const fu = (customer?.followUp || '').trim();
    if (sp && fu) return true;
    const missing = [!sp && 'Salesperson', !fu && 'Follow-up Person'].filter(Boolean).join(' & ');
    alert(`Cannot create the invoice — first set ${missing} for customer "${customer?.name || ''}".`);
    return false;
  };

  // Void an unpaid invoice (e.g. tracker removed). Kept in history, out of receivables.
  const handleCancelInvoice = async (inv) => {
    // Invoices of an ACTIVE vehicle can't be cancelled (server enforces it too).
    const veh = customers.flatMap(c => c.vehicles || []).find(v => String(v.id) === String(inv.vehicleId ?? inv.vehicle_id));
    if (veh && veh.status === 'active') {
      alert(`Cannot cancel: vehicle ${veh.vehicleNo} is still active. Deactivate the vehicle first, then cancel the invoice.`);
      return;
    }
    const reason = window.prompt(
      `Cancel invoice ${inv.invoice_number || inv.invoiceNumber || ''}?\nIt stays in history but is removed from receivables. (Only unpaid invoices can be cancelled.)\n\nOptional reason:`, ''
    );
    if (reason === null) return;
    try {
      const r = await api.invoices.cancel(inv.id, reason);
      if (r && r.success) {
        await loadInvoices();
        const freshCustomers = await loadCustomers();
        if (selectedCustomer?.id) setSelectedCustomer(freshCustomers.find(c => c.id === selectedCustomer.id) || selectedCustomer);
      } else {
        alert(r?.message || 'Failed to cancel invoice');
      }
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const handleCreateSetupInvoice = async (customerId, vehicleId) => {
    const customer = customers.find(c => c.id === customerId);
    const vehicle = customer?.vehicles.find(v => v.id === vehicleId);

    if (!customer || !vehicle || !vehicle.packagePrice) {
      alert('Cannot create invoice: Missing package price');
      return;
    }
    if (isPricingPending(vehicleId)) { alert(`${vehicle.vehicleNo}: ${PRICING_PENDING_MSG}`); return; }
    if (!assertCustomerAttributed(customer)) return;

    try {
      // Calculate setup invoice amount based on invoicing period
      const setupAmount = calculateSetupInvoiceAmount(vehicle);

      const today = new Date().toISOString().split('T')[0];
      const dueDate = calculateDueDate(today);
      
      // Create invoice in database via API
      await api.invoices.create({
        customerId: parseInt(customerId),
        vehicleId: vehicleId,
        invoiceType: 'setup',
        amount: setupAmount,
        issueDate: today,
        dueDate: dueDate,
        paidAmount: 0
      });

      // Reload data from database
      await loadInvoices();
      const freshCustomers = await loadCustomers();
      
      // Update selected customer if viewing
      if (selectedCustomer?.id === customerId) {
        const updatedCustomer = freshCustomers.find(c => c.id === customerId);
        setSelectedCustomer(updatedCustomer);
      }

      alert('Setup invoice created successfully!');
    } catch (error) {
      alert('Failed to create invoice: ' + error.message);
    }
  };

  const handleGenerateRenewalInvoice = async (customerId, vehicleId) => {
    const customer = customers.find(c => c.id === customerId);
    const vehicle = customer?.vehicles.find(v => v.id === vehicleId);
    
    if (!customer || !vehicle || !vehicle.amc || !vehicle.activeDate) {
      alert('Cannot generate renewal: Missing AMC or Active Date');
      return;
    }
    if (isPricingPending(vehicleId)) { alert(`${vehicle.vehicleNo}: ${PRICING_PENDING_MSG}`); return; }
    if (!assertCustomerAttributed(customer)) return;

    const check = canCreateRenewalInvoice(vehicle);
    if (!check.allowed) {
      alert(`Cannot create renewal invoice: ${check.reason}`);
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const renewalAmount = calculateRenewalAmount(vehicle.amc, vehicle.invoicingPeriod);
      const supposedDueDate = check.nextDueDate; // The date the invoice was supposed to be due
      const actualDueDate = calculateDueDate(supposedDueDate); // Configurable days after supposed due date (see INVOICE_DUE_DAYS)
      
      // Create invoice in database via API
      await api.invoices.create({
        customerId: parseInt(customerId),
        vehicleId: vehicleId,
        invoiceType: 'renewal',
        amount: renewalAmount,
        issueDate: today,
        dueDate: actualDueDate,
        paidAmount: 0
      });

      // Reload data from database
      await loadInvoices();
      const freshCustomers = await loadCustomers();
      
      // Update selected customer if viewing
      if (selectedCustomer?.id === customerId) {
        const updatedCustomer = freshCustomers.find(c => c.id === customerId);
        setSelectedCustomer(updatedCustomer);
      }

      alert('Renewal invoice created successfully!');
    } catch (error) {
      alert('Failed to create renewal invoice: ' + error.message);
    }
  };

  // Bulk create renewal invoices for selected vehicles
  const handleBulkCreateRenewalInvoices = async () => {
    if (!selectedCustomerForBulk || selectedVehiclesForInvoice.length === 0) {
      alert('Please select vehicles to create invoices');
      return;
    }
    if (!assertCustomerAttributed(selectedCustomerForBulk)) return;

    try {
      // Generate one invoice number for all vehicles
      const invoiceNumberResponse = await api.invoices.generateInvoiceNumber();
      const sharedInvoiceNumber = invoiceNumberResponse.data.invoiceNumber;
      
      let successCount = 0;
      let failedCount = 0;
      const errors = [];

      // Create invoices one by one with the same invoice number
      for (const vehicleId of selectedVehiclesForInvoice) {
        const vehicle = selectedCustomerForBulk.dueVehicles.find(v => v.id === vehicleId);
        if (!vehicle) continue;
        if (isPricingPending(vehicle.id)) { failedCount++; errors.push(`${vehicle.vehicleNo}: pricing approval pending`); continue; }

        try {
          const renewalAmount = calculateRenewalAmount(vehicle.amc, vehicle.invoicingPeriod);
          
          await api.invoices.create({
            customerId: selectedCustomerForBulk.id,
            vehicleId: vehicle.id,
            invoiceType: 'renewal',
            amount: renewalAmount,
            paidAmount: 0,
            dueDate: calculateDueDate(new Date().toISOString().split('T')[0]),
            issueDate: new Date().toISOString().split('T')[0],
            invoiceNumber: sharedInvoiceNumber
          });
          
          successCount++;
        } catch (error) {
          failedCount++;
          errors.push(`${vehicle.registration}: ${error.message}`);
        }
      }

      await loadInvoices();
      await loadCustomers();
      
      setShowBulkInvoiceCreate(false);
      setSelectedCustomerForBulk(null);
      setSelectedVehiclesForInvoice([]);

      if (failedCount === 0) {
        alert(`Successfully created ${successCount} renewal invoice(s) with invoice number: ${sharedInvoiceNumber}`);
      } else {
        alert(`Created ${successCount} invoice(s) with number ${sharedInvoiceNumber}. Failed: ${failedCount}\n\nErrors:\n${errors.join('\n')}`);
      }
    } catch (error) {
      alert('Failed to create invoices: ' + error.message);
    }
  };

  const calculateEndDate = (period) => {
    const start = new Date();
    switch(period) {
      case 'Monthly': return new Date(start.setMonth(start.getMonth() + 1)).toISOString().split('T')[0];
      case 'Quarterly': return new Date(start.setMonth(start.getMonth() + 3)).toISOString().split('T')[0];
      case 'Half-Yearly': return new Date(start.setMonth(start.getMonth() + 6)).toISOString().split('T')[0];
      case 'Yearly': return new Date(start.setFullYear(start.getFullYear() + 1)).toISOString().split('T')[0];
      default: return new Date(start.setMonth(start.getMonth() + 1)).toISOString().split('T')[0];
    }
  };

  // NEW: Function to handle bulk setup invoice creation
  const handleBulkCreateSetupInvoices = async () => {
    if (!selectedCustomerForBulk || selectedVehiclesForInvoice.length === 0) {
      alert('Please select vehicles to create setup invoices');
      return;
    }
    if (!assertCustomerAttributed(selectedCustomerForBulk)) return;

    try {
      // Generate one invoice number for all vehicles
      const invoiceNumberResponse = await api.invoices.generateInvoiceNumber();
      const sharedInvoiceNumber = invoiceNumberResponse.data.invoiceNumber;
      
      let successCount = 0;
      let failedCount = 0;
      const errors = [];

      // Create invoices one by one with the same invoice number
      for (const vehicleId of selectedVehiclesForInvoice) {
        const vehicle = selectedCustomerForBulk.setupDueVehicles.find(v => v.id === vehicleId);
        if (!vehicle) continue;
        if (isPricingPending(vehicle.id)) { failedCount++; errors.push(`${vehicle.vehicleNo}: pricing approval pending`); continue; }

        try {
          await api.invoices.create({
            customerId: selectedCustomerForBulk.id,
            vehicleId: vehicle.id,
            invoiceType: 'setup',
            amount: calculateSetupInvoiceAmount(vehicle),
            paidAmount: 0,
            dueDate: calculateDueDate(new Date().toISOString().split('T')[0]),
            issueDate: new Date().toISOString().split('T')[0],
            invoiceNumber: sharedInvoiceNumber
          });
          
          successCount++;
        } catch (error) {
          failedCount++;
          errors.push(`${vehicle.registration}: ${error.message}`);
        }
      }

      await loadInvoices();
      await loadCustomers();
      
      setShowBulkInvoiceCreate(false);
      setSelectedCustomerForBulk(null);
      setSelectedVehiclesForInvoice([]);

      if (failedCount === 0) {
        alert(`Successfully created ${successCount} setup invoice(s) with invoice number: ${sharedInvoiceNumber}`);
      } else {
        alert(`Created ${successCount} invoice(s) with number ${sharedInvoiceNumber}. Failed: ${failedCount}\n\nErrors:\n${errors.join('\n')}`);
      }
    } catch (error) {
      alert('Failed to create setup invoices: ' + error.message);
    }
  };

  // Generate PDF Invoice
  const generatePDFInvoice = (invoice) => {
    
    // Handle both camelCase and snake_case field names
    const customerId = parseInt(invoice.customerId || invoice.customer_id);
    const vehicleId = parseInt(invoice.vehicleId || invoice.vehicle_id);
    const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number;
    const periodFrom = invoice.periodFrom || invoice.period_from;
    const periodTo = invoice.periodTo || invoice.period_to;
    
    
    // Find customer - use parseInt for both sides to ensure match
    const customer = customers.find(c => parseInt(c.id) === customerId);
    
    if (!customer) {
      console.error('Customer not found! customerId:', customerId);
      console.error('Available customer IDs:', customers.map(c => ({ id: c.id, name: c.name })));
      alert(`Customer not found (ID: ${customerId}). Please ensure customer data is loaded.`);
      return;
    }
    
    
    // Get ALL invoices with same invoice number
    const allInvoicesWithSameNumber = invoices.filter(inv => 
      (inv.invoiceNumber || inv.invoice_number) === invoiceNumber
    );
    
    
    // Get ALL vehicles for these invoices
    const allVehicles = allInvoicesWithSameNumber.map(inv => {
      const vId = parseInt(inv.vehicleId || inv.vehicle_id);
      const vehicle = customer?.vehicles.find(v => parseInt(v.id) === vId);
      
      if (!vehicle) {
      }
      
      return vehicle ? { 
        ...vehicle, 
        invoiceAmount: inv.amount, 
        periodFrom: inv.periodFrom || inv.period_from, 
        periodTo: inv.periodTo || inv.period_to 
      } : null;
    }).filter(v => v !== null);
    
    
    if (!customer || allVehicles.length === 0) {
      console.error('Error: Customer or vehicles not found');
      console.error('Customer exists?', !!customer);
      console.error('Vehicles found:', allVehicles.length);
      alert('Customer or vehicle data not found. Please check console for details.');
      return;
    }

    // Calculate total amount for all vehicles
    const totalAmount = allInvoicesWithSameNumber.reduce((sum, inv) => sum + parseFloat(inv.amount), 0);

    // FIXED: Calculate total paid from ALL invoices with same invoice number
    const totalPaid = allInvoicesWithSameNumber.reduce((sum, inv) => {
      const invoicePayments = inv.payments?.reduce((pSum, p) => pSum + parseFloat(p.amount), 0) || 0;
      return sum + invoicePayments;
    }, 0);
    
    const balance = totalAmount - totalPaid;
    
    // Calculate status for multi-vehicle invoice
    const dueDate = new Date(invoice.dueDate);
    const today = new Date();
    let status;
    if (totalPaid >= totalAmount) {
      status = 'PAID';
    } else if (totalPaid > 0 && totalPaid < totalAmount) {
      status = today > dueDate ? 'OVERDUE (Partial)' : 'PARTIALLY PAID';
    } else {
      status = today > dueDate ? 'OVERDUE' : 'UNPAID';
    }

    // Create branded HTML for PDF — mirrors the quotation format
    const fmtDt = (d) => { if (!d) return '—'; const x = new Date(d); return isNaN(x.getTime()) ? d : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };
    const statusColor = status === 'PAID' ? '#16a34a' : status.includes('OVERDUE') ? '#dc2626' : (status === 'PARTIALLY PAID' ? '#d97706' : '#dc2626');
    const rowsHtml = allVehicles.map((v, i) => { const mm = [v.make, v.model].filter(Boolean).join(' '); return `<tr><td class="c">${i + 1}</td><td><div class="iname">${v.vehicleNo || ''}</div>${mm ? `<div class="idesc">${mm}</div>` : ''}</td><td class="c">${v.periodFrom || '—'}</td><td class="c">${v.periodTo || '—'}</td><td class="r b">${rsFmt(v.invoiceAmount)}</td></tr>`; }).join('');
    const pdfContent = `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${invoiceNumber}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Segoe UI',Arial,sans-serif;color:#1e293b;background:#eef1f4;padding:24px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .page{max-width:820px;margin:0 auto;background:#fff;box-shadow:0 8px 30px rgba(0,0,0,.12);border-radius:10px;overflow:hidden}
    .top{background:#fff;color:#0f172a;padding:22px 34px;display:flex;justify-content:space-between;align-items:center}
    .logo{height:66px;width:auto}
    .accent{height:6px;background:linear-gradient(90deg,#b91c1c,#ef4444)}
    .qtitle{text-align:right}
    .qtitle h1{font-size:28px;font-weight:800;letter-spacing:3px;color:#b91c1c}
    .qtitle .qn{margin-top:6px;font-size:13px;font-weight:700;background:#f1f5f9;color:#334155;padding:5px 12px;border-radius:20px;display:inline-block}
    .cmeta2{font-size:10.5px;color:#64748b;margin-top:7px;line-height:1.5}
    .band{display:flex;justify-content:space-between;padding:18px 34px;background:#fff7f7;border-bottom:1px solid #fde2e2}
    .to .lbl,.meta .lbl{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#b91c1c;font-weight:700;margin-bottom:3px}
    .to .name{font-size:17px;font-weight:800;color:#0f172a}
    .to .city{font-size:12.5px;color:#475569;margin-top:2px}
    .meta{text-align:right;font-size:12px;color:#334155}
    .meta .row{margin-bottom:4px}
    .meta b{color:#0f172a}
    .stbadge{display:inline-block;font-size:11px;font-weight:800;padding:5px 12px;border-radius:20px;letter-spacing:.5px}
    .body{padding:24px 34px}
    .pricehd{color:#b91c1c;font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:1px;margin-bottom:14px;border-left:4px solid #ef4444;padding-left:10px}
    table{width:100%;border-collapse:collapse;font-size:12.5px}
    thead th{background:#0f172a;color:#fff;text-align:left;padding:11px 12px;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
    thead th.r{text-align:right}thead th.c{text-align:center}
    tbody td{padding:11px 12px;border-bottom:1px solid #eef2f6;vertical-align:top}
    tbody tr:nth-child(even){background:#fafbfc}
    td.r{text-align:right}td.c{text-align:center}td.b{font-weight:700;color:#0f172a}
    .iname{font-weight:700;color:#0f172a}.idesc{font-size:11px;color:#64748b;margin-top:2px}
    .muted{color:#94a3b8;font-size:11px}
    .totwrap{display:flex;justify-content:flex-end;margin-top:18px}
    .tot{width:320px}
    .tot .l{display:flex;justify-content:space-between;padding:8px 4px;font-size:13px;color:#475569}
    .tot .l b{color:#0f172a}
    .tot .grand{margin-top:6px;color:#fff;padding:13px 16px;border-radius:10px;display:flex;justify-content:space-between;font-size:16px;font-weight:800}
    .sign{display:flex;justify-content:space-between;margin-top:34px;padding:0 4px}
    .sign .box{width:220px;border-top:2px solid #cbd5e1;padding-top:6px;font-size:12px;color:#64748b;text-align:center}
    .foot{padding:16px 34px;background:#0f172a;color:#cbd5e1;font-size:11px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px}
    .foot b{color:#fff}
    @media print{body{background:#fff;padding:0}.page{box-shadow:none;border-radius:0;max-width:100%}}
  </style></head><body>
  <div class="page">
    <div class="top">
      <img class="logo" src="${SW_LOGO}" alt="Secure Wheels"/>
      <div class="qtitle"><h1>INVOICE</h1><div class="qn">${invoiceNumber}</div>
        <div class="cmeta2">${SW_COMPANY.address}<br>${SW_COMPANY.phone} &nbsp;·&nbsp; ${SW_COMPANY.email} &nbsp;·&nbsp; ${SW_COMPANY.web}</div>
      </div>
    </div>
    <div class="accent"></div>
    <div class="band">
      <div class="to"><div class="lbl">Bill To</div>
        <div class="name">${customer.name}</div>
        <div class="city">${formatCustomerCode(customer.id, customer.type)} &nbsp;·&nbsp; ${[customer.address, customer.city].filter(Boolean).join(', ')}</div>
        <div class="city">${customer.cell || customer.phone || ''}${customer.email ? ' · ' + customer.email : ''}</div>
      </div>
      <div class="meta">
        <div class="row"><span class="lbl" style="display:inline">Type</span> &nbsp; <b>${invoice.type}</b></div>
        <div class="row"><span class="lbl" style="display:inline">Invoice Date</span> &nbsp; <b>${fmtDt(invoice.createdDate || invoice.created_date)}</b></div>
        <div class="row"><span class="lbl" style="display:inline">Due Date</span> &nbsp; <b>${fmtDt(invoice.dueDate || invoice.due_date)}</b></div>
        <div class="row" style="margin-top:8px"><span class="stbadge" style="background:${statusColor}1a;color:${statusColor}">${status}</span></div>
      </div>
    </div>
    <div class="body">
      <div class="pricehd">Invoice Details</div>
      <table>
        <thead><tr><th class="c">#</th><th>Vehicle</th><th class="c">From</th><th class="c">To</th><th class="r">Amount</th></tr></thead>
        <tbody>${rowsHtml || '<tr><td colspan="5" class="c muted" style="padding:24px">No items</td></tr>'}</tbody>
      </table>
      <div class="totwrap"><div class="tot">
        <div class="l"><span>Subtotal</span><b>${rsFmt(totalAmount)}</b></div>
        ${totalPaid > 0 ? `<div class="l"><span style="color:#16a34a">Amount Paid</span><b style="color:#16a34a">${rsFmt(totalPaid)}</b></div>` : ''}
        <div class="grand" style="background:${balance > 0 ? 'linear-gradient(135deg,#b91c1c,#ef4444)' : 'linear-gradient(135deg,#15803d,#22c55e)'}"><span>${balance > 0 ? 'Balance Due' : 'Paid in Full'}</span><span>${rsFmt(balance)}</span></div>
      </div></div>
      <div class="sign"><div class="box">Received By</div><div class="box">For ${SW_COMPANY.name}</div></div>
    </div>
    <div class="foot"><div>Thank you for your business. <b>${SW_COMPANY.name}</b></div><div>This is a computer-generated invoice.</div></div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},350);}</script>
  </body></html>`;

    // Open in new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice || !paymentAmount || !paymentDate) {
      alert('Please fill all required fields');
      return;
    }

    // Max payment validation
    const totalAmt    = selectedInvoice.totalAmount || parseFloat(selectedInvoice.amount);
    const discount    = parseFloat(selectedInvoice.discount || 0);
    const effectiveAmt = totalAmt - discount;
    const alreadyPaid = selectedInvoice.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
    const maxAllowed  = Math.max(0, effectiveAmt - alreadyPaid);
    const enteredAmt  = parseFloat(paymentAmount);

    if (enteredAmt > maxAllowed) {
      alert(`Payment cannot exceed remaining balance of Rs. ${maxAllowed.toLocaleString()}`);
      return;
    }
    if (enteredAmt <= 0) {
      alert('Payment amount must be greater than 0');
      return;
    }

    try {
      // Handle both field name formats (camelCase and snake_case)
      const invoiceId = selectedInvoice.id;
      const invoiceNumber = selectedInvoice.invoiceNumber || selectedInvoice.invoice_number;
      const isBulkInvoice = selectedInvoice.isBulkInvoice || false;
      
      
      // For bulk invoices, use invoice number so payment applies to ALL vehicles
      // For single invoices, use invoice ID
      const paymentData = {
        amount: parseFloat(paymentAmount),
        paymentDate: paymentDate,
        paymentMethod: paymentMethod,
        reference: paymentReference
      };
      
      if (isBulkInvoice) {
        // Bulk invoice: send invoice number to apply payment to all vehicles
        paymentData.invoiceNumber = invoiceNumber;
      } else {
        // Single invoice: send invoice ID
        paymentData.invoiceId = invoiceId;
      }
      
      const paymentRes = await api.payments.create(paymentData);

      // Surface GPS tracking expiry-sync diagnostics (added while debugging
      // why expiration_date wasn't updating). Doesn't change payment flow —
      // just makes sync failures visible immediately instead of only in
      // server logs.
      const syncResults = Array.isArray(paymentRes?.data?.expirySync)
        ? paymentRes.data.expirySync
        : (paymentRes?.data?.expirySync ? [paymentRes.data.expirySync] : []);
      console.log('Expiry sync result:', syncResults);
      const failedSyncs = syncResults.filter(r => r && r.synced === false);
      
      // Reload both invoices and customers to refresh all data
      const [freshInvoices, freshCustomers] = await Promise.all([
        loadInvoices(),
        loadCustomers()
      ]);
      
      // If we have a selected customer, update it with fresh data
      if (selectedCustomer) {
        const updatedCustomer = freshCustomers.find(c => c.id === selectedCustomer.id);
        if (updatedCustomer) {
          setSelectedCustomer(updatedCustomer);
        }
      }
      
      setShowRecordPayment(false);
      setSelectedInvoice(null);
      setPaymentAmount('');
      setPaymentDate('');
      setPaymentReference('');
      
      if (isBulkInvoice) {
        alert(`Payment of Rs. ${parseFloat(paymentAmount).toLocaleString()} recorded successfully for ${selectedInvoice.totalVehicles} vehicles!` +
          (failedSyncs.length > 0 ? `\n\n⚠️ Tracking expiry did not sync for ${failedSyncs.length} device(s): ${failedSyncs.map(r => r.reason).join(', ')}. Check console for details.` : ''));
      } else {
        alert('Payment recorded successfully!' +
          (failedSyncs.length > 0 ? `\n\n⚠️ Tracking expiry did not sync: ${failedSyncs[0].message || failedSyncs[0].reason}` : ''));
      }
    } catch (error) {
      alert('Failed to record payment: ' + error.message);
    }
  };


  const handleApplyDiscount = async () => {
    if (!discountInvoice || !discountAmount || parseFloat(discountAmount) <= 0) {
      alert('Please enter a valid discount amount');
      return;
    }
    const disc = parseFloat(discountAmount);
    const maxDisc = parseFloat(discountInvoice.amount);
    if (disc > maxDisc) {
      alert(`Discount cannot exceed invoice amount (Rs. ${maxDisc.toLocaleString()})`);
      return;
    }

    try {
      const newBalance = maxDisc - disc - (discountInvoice.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0);
      const newStatus = newBalance <= 0 ? 'paid' : (discountInvoice.payments?.length > 0 ? 'partial' : 'unpaid');

      await fetch(`${API_BASE_URL}/invoices.php`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: discountInvoice.id,
          discount: disc,
          balance: Math.max(0, newBalance),
          status: newStatus,
          notes: discountNote || `Discount applied: Rs. ${disc.toLocaleString()}`
        })
      });

      // Direct DB update via a simple PUT action
      await fetch(`${API_BASE_URL}/invoices.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'apply_discount',
          id: discountInvoice.id,
          discount: disc,
          notes: discountNote || ''
        })
      });

      await Promise.all([loadInvoices(), loadCustomers()]);
      setShowDiscountModal(false);
      setDiscountInvoice(null);
      setDiscountAmount('');
      setDiscountNote('');
      alert(`Discount of Rs. ${disc.toLocaleString()} applied successfully!`);
    } catch (error) {
      alert('Failed to apply discount: ' + error.message);
    }
  };


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBulkUploadFile(file);
    }
  };

  // Load SheetJS (XLSX) library dynamically if not already loaded
  const loadSheetJS = () => new Promise((resolve, reject) => {
    if (window.XLSX) { resolve(window.XLSX); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => resolve(window.XLSX);
    script.onerror = () => reject(new Error('Failed to load SheetJS'));
    document.head.appendChild(script);
  });

  const processRows = async (dataRows) => {
    const today = new Date().toISOString().split('T')[0];
    let successCount = 0;
    const failedRows = [];

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const rowNumber = i + 2; // row 1 = header

      const imei       = String(row[0] ?? '').trim();
      const deviceType = String(row[1] ?? '').trim();
      const iccid      = String(row[2] ?? '').trim();
      const simNumber  = String(row[3] ?? '').trim();
      const vendor     = String(row[4] ?? '').trim();
      const amount     = String(row[5] ?? '').trim();

      if (!imei)       { failedRows.push({ row: rowNumber, imei: 'N/A', reason: 'IMEI is missing' }); continue; }
      if (!deviceType) { failedRows.push({ row: rowNumber, imei, reason: 'Device Type is missing' }); continue; }
      if (!iccid)      { failedRows.push({ row: rowNumber, imei, reason: 'ICCID is missing' }); continue; }
      if (!simNumber)  { failedRows.push({ row: rowNumber, imei, reason: 'SIM Number is missing' }); continue; }
      if (!vendor)     { failedRows.push({ row: rowNumber, imei, reason: 'Vendor is missing' }); continue; }
      if (!amount || isNaN(parseFloat(amount))) {
        failedRows.push({ row: rowNumber, imei, reason: 'Amount is missing or invalid' }); continue;
      }

      try {
        await api.inventory.create({
          imei,
          device_type: deviceType,
          iccid,
          sim_number: simNumber,
          vendor,
          amount: parseFloat(amount),
          purchase_date: today,
          status: 'Available',
          location: 'Store',
          health_status: 'active',
          is_issued: false,
          issued_to: null,
          assigned_to_vehicle_id: null
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to add IMEI ${imei}:`, error);
        failedRows.push({ row: rowNumber, imei, reason: error?.message || 'Server error' });
      }
    }

    return { successCount, failedRows };
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      alert('Please select a file first');
      return;
    }

    const fileName = bulkUploadFile.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    try {
      setLoading(true);

      let dataRows = []; // array of arrays

      if (isExcel) {
        // ── Excel parsing via SheetJS ──────────────────────────
        const XLSX = await loadSheetJS();
        const arrayBuffer = await bulkUploadFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        // header:1 returns array-of-arrays; defval:'' fills empty cells
        const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        dataRows = allRows.slice(1).filter(r => r.some(cell => String(cell).trim() !== ''));
      } else {
        // ── CSV parsing ────────────────────────────────────────
        const text = await bulkUploadFile.text();
        const lines = text.split('\n').filter(l => l.trim() !== '');
        dataRows = lines.slice(1).map(line => line.split(','));
      }

      const { successCount, failedRows } = await processRows(dataRows);
      await loadInventory();
      setBulkUploadFile(null);
      setBulkUploadResults({ success: successCount, failed: failedRows });

    } catch (error) {
      console.error('Bulk upload error:', error);
      alert('Failed to complete bulk upload: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Device Issuance with Validation
  const handleIssuance = async () => {
    if (!issuanceTechnician || selectedDevicesForIssuance.length === 0) {
      alert('Please select a technician and enter at least one IMEI');
      return;
    }

    try {
      setLoading(true);

      // Validate each IMEI
      const inventoryWithStatus = getInventoryWithStatus();
      const validIMEIs = [];
      const invalidIMEIs = [];
      const notFoundIMEIs = [];

      selectedDevicesForIssuance.forEach(imei => {
        const device = inventoryWithStatus.find(item => item.imei === imei);
        
        if (!device) {
          notFoundIMEIs.push(imei);
        } else if (device.status !== 'Available') {
          invalidIMEIs.push({ imei, status: device.status, location: device.location });
        } else {
          validIMEIs.push(imei);
        }
      });

      // Show detailed error if any invalid devices
      if (notFoundIMEIs.length > 0 || invalidIMEIs.length > 0) {
        let errorMessage = 'Cannot issue the following devices:\n\n';
        
        if (notFoundIMEIs.length > 0) {
          errorMessage += `❌ NOT FOUND IN INVENTORY (${notFoundIMEIs.length}):\n`;
          notFoundIMEIs.slice(0, 5).forEach(imei => {
            errorMessage += `   • ${imei}\n`;
          });
          if (notFoundIMEIs.length > 5) {
            errorMessage += `   ... and ${notFoundIMEIs.length - 5} more\n`;
          }
          errorMessage += '\n';
        }

        if (invalidIMEIs.length > 0) {
          errorMessage += `❌ NOT AVAILABLE (${invalidIMEIs.length}):\n`;
          invalidIMEIs.slice(0, 5).forEach(({ imei, status, location }) => {
            errorMessage += `   • ${imei} - ${status} (${location})\n`;
          });
          if (invalidIMEIs.length > 5) {
            errorMessage += `   ... and ${invalidIMEIs.length - 5} more\n`;
          }
          errorMessage += '\n';
        }

        if (validIMEIs.length > 0) {
          errorMessage += `✅ Valid devices that can be issued: ${validIMEIs.length}\n\n`;
          errorMessage += 'Do you want to proceed with issuing only the valid devices?';
          
          if (!window.confirm(errorMessage)) {
            setLoading(false);
            return;
          }
        } else {
          alert(errorMessage + '\n⚠️ No valid devices to issue!');
          setLoading(false);
          return;
        }
      }

      if (validIMEIs.length === 0) {
        alert('No valid devices to issue!');
        setLoading(false);
        return;
      }

      // Update inventory for each issued IMEI
      for (const imei of validIMEIs) {
        const inventoryItem = inventory.find(item => item.imei === imei);
        if (inventoryItem) {
          await api.inventory.update({
            id: inventoryItem.id,
            imei: inventoryItem.imei,
            device_type: inventoryItem.device_type,
            iccid: inventoryItem.iccid,
            sim_number: inventoryItem.sim_number,
            vendor: inventoryItem.vendor,
            amount: inventoryItem.amount,
            status: 'Available',
            is_issued: true,
            issued_to: issuanceTechnician,
            location: employees.find(e => String(e.id) === String(issuanceTechnician))?.name || issuanceTechnician
          });
        }
      }

      // Reload inventory
      await loadInventory();

      // Log: issued to technician
      try {
        const techNameLog = employees.find(e => String(e.id) === String(issuanceTechnician))?.name || String(issuanceTechnician);
        const issuedItems = getInventoryWithStatus().filter(i => selectedDevicesForIssuance.includes(i.id));
        for (const dev of issuedItems) {
          await api.call('imei_logs.php', 'POST', {
            imei: dev.imei, event_type: 'issued', actor_name: techNameLog,
            location: 'With Technician',
            notes: `Issued on ${issuanceDate}${issuanceNotes ? '. ' + issuanceNotes : ''}`,
          });
        }
      } catch(e) { console.warn('IMEI log failed:', e); }

      // Alert the technician in the SW Crew app (push + in-app). Best-effort — never blocks issuance.
      try {
        await api.call('notifications.php?action=notify_issued', 'POST', {
          employee_id: Number(issuanceTechnician),
          count: validIMEIs.length,
          imeis: validIMEIs.slice(0, 10),
          by: authUser?.full_name || authUser?.username || 'Office',
        });
      } catch (e) { console.warn('Issuance notify failed:', e); }

      // Close modal and reset
      setShowIssuance(false);
      setSelectedDevicesForIssuance([]);
      setIssuanceTechnician('');
      setIssuanceNotes('');

      const techName = employees.find(e => String(e.id) === String(issuanceTechnician))?.name || issuanceTechnician;
      alert(`✅ Successfully issued ${validIMEIs.length} device(s) to ${techName}!`);
    } catch (error) {
      console.error('Issuance error:', error);
      alert('Failed to issue devices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Device Return with Validation
  const handleReturn = async () => {
    if (!returnTechnician || selectedDevicesForReturn.length === 0) {
      alert('Please select a technician and enter at least one IMEI');
      return;
    }

    try {
      setLoading(true);

      // Validate each IMEI
      const inventoryWithStatus = getInventoryWithStatus();
      const validIMEIs = [];
      const invalidIMEIs = [];
      const notFoundIMEIs = [];

      selectedDevicesForReturn.forEach(imei => {
        const device = inventoryWithStatus.find(item => item.imei === imei);
        
        if (!device) {
          notFoundIMEIs.push(imei);
        } else if (device.status === 'Available' && device.location === 'Store') {
          // Device is already in store, cannot return
          invalidIMEIs.push({ imei, reason: 'Already in Store', location: device.location });
        } else if (device.location === 'Installed' || device.status === 'Installed') {
          // Device is installed in vehicle, cannot be returned by technician
          invalidIMEIs.push({ imei, reason: 'Installed in Vehicle', location: device.location });
        } else {
          validIMEIs.push(imei);
        }
      });

      // Show detailed error if any invalid devices
      if (notFoundIMEIs.length > 0 || invalidIMEIs.length > 0) {
        let errorMessage = 'Cannot return the following devices:\n\n';
        
        if (notFoundIMEIs.length > 0) {
          errorMessage += `❌ NOT FOUND IN INVENTORY (${notFoundIMEIs.length}):\n`;
          notFoundIMEIs.slice(0, 5).forEach(imei => {
            errorMessage += `   • ${imei}\n`;
          });
          if (notFoundIMEIs.length > 5) {
            errorMessage += `   ... and ${notFoundIMEIs.length - 5} more\n`;
          }
          errorMessage += '\n';
        }

        if (invalidIMEIs.length > 0) {
          errorMessage += `❌ CANNOT BE RETURNED (${invalidIMEIs.length}):\n`;
          invalidIMEIs.slice(0, 5).forEach(({ imei, reason, location }) => {
            errorMessage += `   • ${imei} - ${reason} (${location})\n`;
          });
          if (invalidIMEIs.length > 5) {
            errorMessage += `   ... and ${invalidIMEIs.length - 5} more\n`;
          }
          errorMessage += '\n';
        }

        if (validIMEIs.length > 0) {
          errorMessage += `✅ Valid devices that can be returned: ${validIMEIs.length}\n\n`;
          errorMessage += 'Do you want to proceed with returning only the valid devices?';
          
          if (!window.confirm(errorMessage)) {
            setLoading(false);
            return;
          }
        } else {
          alert(errorMessage + '\n⚠️ No valid devices to return!');
          setLoading(false);
          return;
        }
      }

      if (validIMEIs.length === 0) {
        alert('No valid devices to return!');
        setLoading(false);
        return;
      }

      // Update inventory for each returned IMEI
      for (const imei of validIMEIs) {
        const inventoryItem = inventory.find(item => item.imei === imei);
        if (inventoryItem) {
          await api.inventory.update({
            id: inventoryItem.id,
            imei: inventoryItem.imei,
            device_type: inventoryItem.device_type,
            iccid: inventoryItem.iccid,
            sim_number: inventoryItem.sim_number,
            vendor: inventoryItem.vendor,
            amount: inventoryItem.amount,
            status: 'Available',
            is_issued: false,
            issued_to: null,
            location: 'Store'
          });
        }
      }

      // Reload inventory
      await loadInventory();

      // Log: returned to store
      try {
        const retTechNameLog = employees.find(e => String(e.id) === String(returnTechnician))?.name || String(returnTechnician);
        const returnedItems = getInventoryWithStatus().filter(i => selectedDevicesForReturn.includes(i.id));
        for (const dev of returnedItems) {
          await api.call('imei_logs.php', 'POST', {
            imei: dev.imei, event_type: 'returned', actor_name: retTechNameLog,
            location: 'Store',
            notes: `Returned on ${returnDate}${returnNotes ? '. ' + returnNotes : ''}`,
          });
        }
      } catch(e) { console.warn('IMEI log failed:', e); }
      // Close modal and reset
      setShowReturn(false);
      setSelectedDevicesForReturn([]);
      setReturnTechnician('');
      setReturnNotes('');

      const retTechName = employees.find(e => String(e.id) === String(returnTechnician))?.name || returnTechnician;
      alert(`✅ Successfully returned ${validIMEIs.length} device(s) from ${retTechName} to store!`);
    } catch (error) {
      console.error('Return error:', error);
      alert('Failed to return devices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ===============================================
  // FOLLOW-UP SYSTEM HELPERS
  // ===============================================
  const todayStr = new Date().toISOString().split('T')[0];

  const daysAgo = (str) => Math.round((new Date(todayStr) - new Date(str)) / 86400000);
  const daysUntil = (str) => Math.round((new Date(str) - new Date(todayStr)) / 86400000);
  const fmtDate = (str) => { const d = new Date(str); return d.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const fmtAmt = (n) => `Rs. ${parseFloat(n).toLocaleString()}`;

  // ── Follow-Up: has any call been logged for this invoice on todayStr?
  // Groups all unpaid invoices by customer — one card per customer, not per invoice
  const getFollowupCustomers = () => {
    // Step 1: collect all unpaid invoices
    const pending = invoices.filter(inv => {
      const paid = inv.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
      return paid < parseFloat(inv.amount);
    });

    // Step 2: group by customer
    const byCustomer = {};
    pending.forEach(inv => {
      const cid = String(inv.customerId);
      if (!byCustomer[cid]) byCustomer[cid] = [];
      byCustomer[cid].push(inv);
    });

    // Step 3: build one record per customer
    return Object.entries(byCustomer).map(([cid, invList]) => {
      const customer = customers.find(c => String(c.id) === cid);
      const fd = followupData[cid] || {};
      const logs = fd.logs || [];
      const followupDate = fd.followupDate || null;
      const callCount = logs.length;
      const lastLog = logs[logs.length - 1] || null;
      const lastNote = lastLog?.note || '';
      const followUpPerson = customer?.followUp || '';

      // Financials across all invoices
      const totalOutstanding = invList.reduce((sum, inv) => {
        const paid = inv.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
        return sum + (parseFloat(inv.amount) - paid);
      }, 0);
      const invoiceCount = invList.length;

      // Oldest due date (most urgent)
      const oldestDue = invList
        .map(inv => inv.dueDate)
        .filter(Boolean)
        .sort()[0] || null;

      const phone = customer?.cell || customer?.phone || '';

      return {
        id: cid,                    // use customerId as the key
        customerId: cid,
        customerName: customer?.name || invList[0]?.customerName || '—',
        customerObj: customer,
        phone,
        followUpPerson,
        followupDate,
        logs,
        callCount,
        lastNote,
        lastLog,
        totalOutstanding,
        invoiceCount,
        dueDate: oldestDue,         // oldest unpaid due date
        invoices: invList,          // full list for expanding detail
      };
    });
  };

  // Keep old name as alias so categorizeFollowups still works
  const getFollowupInvoices = getFollowupCustomers;

  // One record per (customer, vehicle) with unpaid, non-cancelled invoices.
  const getFollowupVehicles = () => {
    const balOf = (inv) => {
      const paid = inv.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
      return parseFloat(inv.amount || 0) - parseFloat(inv.discount || 0) - paid;
    };
    const groups = {};
    invoices.forEach(inv => {
      if (inv.status === 'cancelled' || !inv.vehicleId || balOf(inv) <= 0.01) return;
      const key = `${inv.customerId}:${inv.vehicleId}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(inv);
    });
    return Object.entries(groups).map(([key, invList]) => {
      const cid = String(invList[0].customerId);
      const customer = customers.find(c => String(c.id) === cid);
      const fd = vfData[key] || {};
      const logs = fd.logs || [];
      return {
        key,
        customerId: cid,
        customerName: customer?.name || invList[0]?.customerName || '—',
        phone: customer?.cell || customer?.phone || '',
        followUpPerson: customer?.followUp || '',
        vehicleId: invList[0].vehicleId,
        vehicleNo: invList[0].vehicleNo,
        invoices: invList,
        outstanding: invList.reduce((s, inv) => s + balOf(inv), 0),
        dueDate: invList.map(inv => inv.dueDate).filter(Boolean).sort()[0] || null,
        followupDate: fd.followupDate || null,
        logs,
        lastLog: logs[logs.length - 1] || null,
      };
    });
  };

  // GPS expiry for every follow-up vehicle (loaded when the Follow-Up tab is opened).
  const loadFollowupGps = async (vehicleIds) => {
    const ids = [...new Set(vehicleIds.filter(Boolean))];
    const out = {};
    for (let i = 0; i < ids.length; i += 100) {
      try {
        const res = await fetch(`${API_BASE_URL}/reconciliation.php?action=gps_status&vehicle_ids=${ids.slice(i, i + 100).join(',')}`);
        const j = await res.json();
        (j.data || []).forEach(g => { out[g.vehicle_id] = g; });
      } catch (e) { console.warn('follow-up GPS status failed', e); }
    }
    setFuGps(prev => ({ ...prev, ...out }));
  };
  useEffect(() => {
    if (activeModule === 'invoices' && invoiceTab === 'followup') loadFollowupGps(getFollowupVehicles().map(v => v.vehicleId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModule, invoiceTab, invoices.length]);

  const categorizeFollowups = (list) => {
    const today_ = [], missed_ = [], upcoming_ = [], newItems_ = [];
    list.forEach(inv => {
      if (!inv.followupDate) {
        // Rule 1: No follow-up date assigned → New Follow-Ups
        newItems_.push(inv);
      } else if (inv.followupDate === todayStr) {
        // Rule 4: Follow-up date is exactly today → Today's Follow-Ups
        today_.push(inv);
      } else if (inv.followupDate > todayStr) {
        // Rule 2: Follow-up date is in the future → Upcoming Follow-Ups
        upcoming_.push(inv);
      } else {
        // Rule 3: Follow-up date is in the past → Missed Follow-Ups
        missed_.push(inv);
      }
    });
    // Sort missed oldest-first (most urgent at top), upcoming soonest-first
    missed_.sort((a, b) => a.followupDate.localeCompare(b.followupDate));
    upcoming_.sort((a, b) => a.followupDate.localeCompare(b.followupDate));
    return { today: today_, missed: missed_, upcoming: upcoming_, newItems: newItems_ };
  };

  const getFollowupPersons = () => {
    const persons = new Set();
    customers.forEach(c => { if (c.followUp) persons.add(c.followUp); });
    return Array.from(persons).sort();
  };

  const handleSaveReschedule = async (updates) => {
    // updates: { followupDate, outcome, note, vehicleIds }
    const target = rescheduleTarget;   // { customerId, choices, preselected, ... }
    const cid = String(target.customerId);
    const vehicleIds = updates.vehicleIds && updates.vehicleIds.length ? updates.vehicleIds : (target.preselected || []);
    const newLog = { date: todayStr, outcome: updates.outcome, note: updates.note, logged_by: authUser?.full_name || authUser?.username || '' };

    // ── Optimistic UI update (each ticked vehicle) ────────────────────────
    setVfData(prev => {
      const next = { ...prev };
      vehicleIds.forEach(vid => {
        const key = `${cid}:${vid}`;
        const existing = next[key] || {};
        next[key] = { followupDate: updates.followupDate, logs: [...(existing.logs || []), newLog] };
      });
      return next;
    });
    setRescheduleTarget(null);

    // ── Persist ───────────────────────────────────────────────────────────
    try {
      await api.vehicleFollowups.save({
        customerId:   cid,
        vehicleIds,
        followupDate: updates.followupDate,
        logDate:      todayStr,
        outcome:      updates.outcome,
        note:         updates.note || '',
        loggedBy:     authUser?.full_name || authUser?.username || '',
      });
    } catch (error) {
      console.error('Failed to save follow-up to DB:', error);
      await loadVehicleFollowups();
    }
  };

  // Note: the customer sidebar now uses the server-paginated `customerList`
  // (searched/filtered on the backend), so no client-side filter over the full
  // `customers` cache is needed here anymore.

  const inventoryWithStatus = getInventoryWithStatus();
  const filteredInventory = inventoryWithStatus.filter(item => {
    const q = inventorySearch.trim().toLowerCase();
    // Null-safe, case-insensitive match across IMEI, ICCID, SIM, device type,
    // vendor, status, and the "WHERE" location (Store or the vehicle number).
    const matchesSearch = q === '' ||
      (item.imei || '').toLowerCase().includes(q) ||
      (item.iccid || '').toLowerCase().includes(q) ||
      (item.simNumber || '').toLowerCase().includes(q) ||
      (item.deviceType || '').toLowerCase().includes(q) ||
      (item.vendor || '').toLowerCase().includes(q) ||
      (item.status || '').toLowerCase().includes(q) ||
      (item.location || '').toLowerCase().includes(q);
    
    const matchesFilter = inventoryStatusFilter === 'all' ||
      (inventoryStatusFilter === 'Available' && item.status === 'Available' && (item.healthStatus || '').toLowerCase() !== 'lost') ||
      (inventoryStatusFilter === 'Installed' && item.status === 'Installed') ||
      (inventoryStatusFilter === 'Store' && item.location === 'Store') ||
      (inventoryStatusFilter === 'Issued' && item.status === 'Available' && item.location !== 'Store') ||
      (inventoryStatusFilter === 'Customer' && (item.vendor || '').trim().toLowerCase() === 'customer') ||
      (inventoryStatusFilter === 'Lost' && (item.healthStatus || '').toLowerCase() === 'lost');

    return matchesSearch && matchesFilter;
  });

  // Invoice list filter (search + status + follow-up person). Extracted so both
  // the table and its "Load more" pager share one computed, filtered list.
  const invoiceMatchesFilters = (inv) => {
    const matchesSearch =
      inv.invoiceNumber.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      inv.customerName.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      (inv.vehicles || []).some(v => (v.vehicleNo || '').toLowerCase().includes(invoiceSearch.toLowerCase()));

    const status = getGroupedStatus(inv);

    let matchesStatus = false;
    if (invoiceStatusFilter === 'all') {
      matchesStatus = true;
    } else if (invoiceStatusFilter === 'UNPAID') {
      matchesStatus = status === 'UNPAID' || status === 'PARTIALLY PAID' || status.includes('OVERDUE');
    } else if (invoiceStatusFilter === 'PAID') {
      matchesStatus = status === 'PAID';
    } else if (invoiceStatusFilter === 'OVERDUE') {
      matchesStatus = status.includes('OVERDUE');
    } else if (invoiceStatusFilter === 'PARTIALLY PAID') {
      matchesStatus = status === 'PARTIALLY PAID' || status === 'OVERDUE (Partial)';
    } else {
      matchesStatus = status === invoiceStatusFilter;
    }

    const customer = customers.find(c => parseInt(c.id) === inv.customerId);
    const followUpPerson = customer?.followUp || 'Unassigned';
    const matchesSalesperson = invoiceFollowUpPersonFilter === 'all' || followUpPerson === invoiceFollowUpPersonFilter;

    return matchesSearch && matchesStatus && matchesSalesperson;
  };
  const filteredGroupedInvoices = getGroupedInvoices().filter(invoiceMatchesFilters);

  // Detail tab is VEHICLE-WISE (one row per vehicle/line). A 7-vehicle invoice
  // shows 7 rows here vs 1 grouped row in the Dashboard summary — by design.
  // Payment/balance shown per line are for that vehicle only.
  const filteredDetailInvoices = invoices.filter(inv => {
    const matchesSearch =
      inv.invoiceNumber.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      inv.customerName.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      inv.vehicleNo.toLowerCase().includes(invoiceSearch.toLowerCase());

    const status = getInvoiceStatus(inv);
    let matchesStatus = false;
    if (invoiceStatusFilter === 'all') {
      matchesStatus = true;
    } else if (invoiceStatusFilter === 'UNPAID') {
      matchesStatus = status.includes('UNPAID') || status.includes('PARTIALLY PAID') || status.includes('OVERDUE');
    } else if (invoiceStatusFilter === 'OVERDUE' && status.includes('OVERDUE')) {
      matchesStatus = true;
    } else if (invoiceStatusFilter === 'PARTIALLY PAID' && (status === 'PARTIALLY PAID' || status === 'OVERDUE (Partial)')) {
      matchesStatus = true;
    } else if (status === invoiceStatusFilter) {
      matchesStatus = true;
    }

    const customer = customers.find(c => parseInt(c.id) === inv.customerId);
    const followUpPerson = customer?.followUp || 'Unassigned';
    const matchesSalesperson = invoiceFollowUpPersonFilter === 'all' || followUpPerson === invoiceFollowUpPersonFilter;

    return matchesSearch && matchesStatus && matchesSalesperson;
  });

  // Invoices that carry a discount — powers the Discounts tab (grouped per invoice).
  const discountedInvoices = getGroupedInvoices().filter(g => (parseFloat(g.totalDiscount) || 0) > 0);

  const filteredVehicles = selectedCustomer?.vehicles.filter(vehicle => {
    if (vehicleFilter === 'all') return true;
    if (vehicleFilter === 'active') return vehicle.status === 'active';
    if (vehicleFilter === 'inactive') return vehicle.status === 'inactive';
    if (vehicleFilter === 'packageAwaiting') return isPackageAwaiting(vehicle);
    return true;
  }) || [];

  const vehicleCounts = getVehicleCounts();

  // Filtered arrays for management modules
  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
    vendor.contact.toLowerCase().includes(vendorSearch.toLowerCase()) ||
    vendor.email.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const filteredDevices = devices.filter(device =>
    device.name.toLowerCase().includes(deviceSearch.toLowerCase())
  );

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    employee.designation.toLowerCase().includes(employeeSearch.toLowerCase())
  );

  // Role has no CRM (web) access → block the whole app with a friendly notice.
  if (roleAccess && !roleAccess.crmAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-8 max-w-md text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <Lock className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">No CRM Access</h2>
          <p className="text-sm text-slate-600">
            Your role{roleAccess.role?.name ? ` (${roleAccess.role.name})` : ''} does not have
            access to the CRM web application. Please contact an administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Company Logo */}
              <div className="w-11 h-11 rounded-xl bg-red-500 flex items-center justify-center shadow-sm shadow-red-200">
                <div className="text-sm font-extrabold text-white tracking-tight">SW</div>
              </div>
              <div>
                <h1 className="text-[15px] font-bold text-slate-800 leading-tight">
                  Secure Wheels
                </h1>
                <p className="text-[11px] text-slate-400">Tracking &amp; Management</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              {canSee('customers') && (
              <button
                onClick={() => setActiveModule('customers')}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  activeModule === 'customers'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <User className="inline mr-2" size={18} />
                Customers
              </button>
              )}
              {canSee('inventory') && (
              <button
                onClick={() => setActiveModule('inventory')}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  activeModule === 'inventory'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Package className="inline mr-2" size={18} />
                Inventory
              </button>
              )}
              {canSee('invoices') && (
              <button
                onClick={() => setActiveModule('invoices')}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  activeModule === 'invoices'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <FileText className="inline mr-2" size={18} />
                Invoices
              </button>
              )}
              {canSee('cashflow') && (
              <button
                onClick={() => setActiveModule('cashflow')}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  activeModule === 'cashflow'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Wallet className="inline mr-2" size={18} />
                Cashflow
              </button>
              )}
              {canSee('complaints') && (
              <button
                onClick={() => { setActiveModule('complaints'); setComplaintView('list'); loadComplaints(); }}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all relative ${
                  activeModule === 'complaints'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <MessageSquare className="inline mr-2" size={18} />
                Complaints
                {(() => {
                  // Count only genuinely-open complaints (excludes completed/submitted/in-progress via task_status).
                  const n = (complaints || []).filter(c => deriveUStatus(c) === 'open').length;
                  return n > 0 ? (
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center shadow">
                      {n}
                    </span>
                  ) : null;
                })()}
              </button>
              )}
              {canSee('reports') && (
              <button
                onClick={() => setActiveModule('reports')}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  activeModule === 'reports'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <BarChart2 className="inline mr-2" size={18} />
                Reports
              </button>
              )}
              {canSee('redo') && (
              <button
                onClick={() => setActiveModule('redo')}
                className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  activeModule === 'redo'
                    ? 'bg-red-500 text-white shadow-md shadow-red-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Briefcase className="inline mr-2" size={18} />
                Services
              </button>
              )}

              {/* Management Dropdown */}
              {canSeeAnyManagement && (
              <div className="relative" ref={managementMenuRef}>
                <button
                  onClick={() => setShowManagementMenu(!showManagementMenu)}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                    activeModule === 'vendors' || activeModule === 'devices' || activeModule === 'vehicle_models' || activeModule === 'cities' || activeModule === 'quotations' || activeModule === 'employees' || activeModule === 'reconciliation' || activeModule === 'nr_vehicles' || activeModule === 'live_map' || activeModule === 'pipeline' || activeModule === 'user_management'
                      ? 'bg-red-500 text-white shadow-md shadow-red-200'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Filter className="inline mr-2" size={18} />
                  Management
                  <svg className={`inline ml-1 w-4 h-4 transition-transform ${showManagementMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showManagementMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                    {canSee('vendors') && (
                    <button
                      onClick={() => {
                        setActiveModule('vendors');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <Briefcase size={18} className="text-slate-600" />
                      <span className="font-medium text-slate-700">Vendors</span>
                    </button>
                    )}
                    {canSee('devices') && (
                    <button
                      onClick={() => {
                        setActiveModule('devices');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <Box size={18} className="text-slate-600" />
                      <span className="font-medium text-slate-700">Devices</span>
                    </button>
                    )}
                    {canSee('vehicle_models') && (
                    <button
                      onClick={() => {
                        setActiveModule('vehicle_models');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <Car size={18} className="text-slate-600" />
                      <span className="font-medium text-slate-700">Vehicle Models</span>
                    </button>
                    )}
                    {canSee('cities') && (
                    <button
                      onClick={() => {
                        setActiveModule('cities');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <MapPin size={18} className="text-slate-600" />
                      <span className="font-medium text-slate-700">Cities</span>
                    </button>
                    )}
                    {canSee('quotations') && (
                    <button
                      onClick={() => {
                        setActiveModule('quotations');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <FileText size={18} className="text-red-500" />
                      <span className="font-medium text-red-600">Quotations</span>
                    </button>
                    )}
                    {canSee('employees') && (
                    <button
                      onClick={() => {
                        setActiveModule('employees');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <Users size={18} className="text-slate-600" />
                      <span className="font-medium text-slate-700">Employees</span>
                    </button>
                    )}
                    {canSee('user_management') && (
                    <button
                      onClick={() => {
                        setActiveModule('user_management');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <Lock size={18} className="text-slate-600" />
                      <span className="font-medium text-slate-700">User Management</span>
                    </button>
                    )}
                    {canSee('nr_vehicles') && (
                    <>
                    <div className="border-t border-slate-100 my-1" />
                    <button
                      onClick={() => {
                        setActiveModule('nr_vehicles');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-orange-50 transition-all flex items-center gap-3"
                    >
                      <AlertCircle size={18} className="text-orange-500" />
                      <span className="font-medium text-orange-600">NR Vehicles</span>
                    </button>
                    </>
                    )}
                    {canSee('live_map') && (
                    <button
                      onClick={() => {
                        setActiveModule('live_map');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <MapPin size={18} className="text-red-500" />
                      <span className="font-medium text-slate-700">Live Map</span>
                    </button>
                    )}
                    {canSee('pipeline') && (
                    <button
                      onClick={() => {
                        setActiveModule('pipeline');
                        setShowManagementMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-all flex items-center gap-3"
                    >
                      <PhoneCall size={18} className="text-red-500" />
                      <span className="font-medium text-slate-700">Sales Pipeline</span>
                    </button>
                    )}
                    {canSee('reconciliation') && (
                    <>
                    <div className="border-t border-slate-100 my-1" />
                    <button
                      onClick={() => {
                        setActiveModule('reconciliation');
                        setShowManagementMenu(false);
                        setReconData(null);
                        setReconError(null);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-red-50 transition-all flex items-center gap-3"
                    >
                      <RefreshCw size={18} className="text-red-500" />
                      <span className="font-medium text-red-600">Reconciliation</span>
                    </button>
                    </>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* User menu (merged from the old top bar) */}
              <div className="flex items-center gap-2 pl-3 ml-1 border-l border-slate-200">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {(authUser?.full_name || authUser?.username || 'U').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                </div>
                <div className="hidden xl:block leading-tight mr-1">
                  <p className="text-xs font-semibold text-slate-800 max-w-[130px] truncate">{authUser?.full_name || authUser?.username || 'User'}</p>
                  <p className="text-[10px] text-slate-500 capitalize">{authUser?.role || authUser?.role_name || 'user'}</p>
                </div>
                <NotificationBell onOpenTicket={() => setActiveModule('complaints')} />
                {onChangePassword && <button onClick={onChangePassword} title="Change Password" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Key size={16} /></button>}
                {onLogout && <button onClick={onLogout} title="Logout" className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"><LogOut size={16} /></button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-5">
        {activeModule === 'customers' && (
          <div className="grid grid-cols-12 gap-4">
            {/* Customer List — server-paginated, only the current page is fetched & rendered */}
            <div className="col-span-3 bg-white rounded-xl shadow-md border border-slate-200 flex flex-col max-h-[calc(100vh-160px)]">
              <div className="p-5 border-b border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-800">Customers</h2>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                      {listTotal}
                    </span>
                  </div>
                  {canDo('customers', 'create') && (
                  <button
                    onClick={() => setShowAddCustomer(true)}
                    title="Add customer"
                    className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-2 rounded-xl hover:shadow-md hover:shadow-red-200 transition-all"
                  >
                    <Plus size={20} />
                  </button>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search name, phone, vehicle no…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-9 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all text-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      title="Clear"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  {listLoading ? 'Loading…' : `Showing ${customerList.length} of ${listTotal}`}
                </p>
                {/* Quick filters */}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {[['all', 'All'], ['corporate', 'Corporate'], ['individual', 'Individual']].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setCustTypeFilter(k)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${custTypeFilter === k ? 'bg-red-50 text-red-600 border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-y-auto flex-1">
                {listLoading ? (
                  /* Skeleton rows while the page loads */
                  <div className="p-4 space-y-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="animate-pulse flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 shrink-0" />
                        <div className="flex-1 space-y-2 pt-1">
                          <div className="h-3 bg-slate-200 rounded w-3/4" />
                          <div className="h-2.5 bg-slate-100 rounded w-1/2" />
                          <div className="h-2.5 bg-slate-100 rounded w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : customerList.length === 0 ? (
                  /* Empty state */
                  <div className="p-10 text-center">
                    <Users size={36} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 font-medium">No customers found</p>
                    {searchTerm && (
                      <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                    )}
                  </div>
                ) : (
                  <>
                    {customerList.filter(c => custTypeFilter === 'all' || (custTypeFilter === 'corporate' ? c.type === 'Corporate' : c.type !== 'Corporate')).map((customer) => {
                      const isSel = String(selectedCustomer?.id) === String(customer.id);
                      const initials = (customer.name || '?')
                        .trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
                      return (
                        <div
                          key={customer.id}
                          onClick={() => handleSelectCustomer(customer)}
                          className={`px-4 py-3 border-b border-slate-100 cursor-pointer transition-all ${
                            isSel
                              ? 'bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-l-red-500'
                              : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                              customer.type === 'Corporate' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {initials || <User size={16} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-800 truncate leading-tight">{customer.name}</h3>
                              <p className="text-xs text-slate-400 tabular-nums truncate mt-0.5">{formatCustomerCode(customer.id, customer.type)} · {customer.cell || customer.phone || '—'}</p>
                              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">
                                  <Car size={12} />{customer.activeVehicles} active
                                </span>
                                {customer.type === 'Corporate' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700">Corporate</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {customerList.length < listTotal && (
                      <div className="p-3">
                        <button
                          onClick={() => loadCustomerList(listPage + 1, searchTerm.trim(), true)}
                          disabled={listLoadingMore}
                          className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {listLoadingMore ? (
                            <><RefreshCw size={14} className="animate-spin" />Loading…</>
                          ) : (
                            <>Load more ({listTotal - customerList.length} left)</>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Customer Details (Plan A — header + KPIs + tabs) */}
            <div className="col-span-9">
              {selectedCustomer ? (
                <div className="space-y-4">
                  {/* Sticky header + KPIs + tabs */}
                  {(() => {
                    let billed = 0, received = 0, outstanding = 0, renewDue = 0, invCount = 0;
                    (selectedCustomer.vehicles || []).forEach(v => {
                      (v.invoices || []).forEach(inv => { billed += parseFloat(inv.amount) || 0; received += parseFloat(inv.paid_amount) || 0; outstanding += parseFloat(inv.balance) || 0; invCount++; });
                      try { if (v.status === 'active' && canCreateRenewalInvoice(v).allowed) renewDue++; } catch (e) {}
                    });
                    const fmt = (n) => 'Rs ' + Math.round(n).toLocaleString();
                    const TABS = [{ k: 'overview', label: 'Overview' }, { k: 'vehicles', label: 'Vehicles', n: selectedCustomer.vehicles.length }, { k: 'invoices', label: 'Invoices', n: invCount }, { k: 'ledger', label: 'Ledger' }, { k: 'complaints', label: 'Complaints' }];
                    return (
                    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden sticky top-2 z-20">
                      <div className="p-4 flex items-start gap-4" style={{ background: 'linear-gradient(120deg, rgba(226,60,46,.06), transparent 60%)' }}>
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-extrabold shrink-0 ${selectedCustomer.type === 'Corporate' ? 'bg-gradient-to-br from-blue-500 to-indigo-600' : 'bg-gradient-to-br from-slate-500 to-slate-600'}`}>{(selectedCustomer.name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</div>
                        <div className="min-w-0 flex-1">
                          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 flex-wrap">{selectedCustomer.name}<span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${selectedCustomer.type === 'Corporate' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{selectedCustomer.type}</span>{detailLoading && <RefreshCw size={14} className="animate-spin text-red-400" />}</h2>
                          <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500"><span className="tabular-nums">🆔 {formatCustomerCode(selectedCustomer.id, selectedCustomer.type)}</span><span className="tabular-nums">📞 {selectedCustomer.cell || selectedCustomer.phone || '—'}</span><span>📍 {[selectedCustomer.city, selectedCustomer.country].filter(Boolean).join(', ') || '—'}</span>{selectedCustomer.salesperson && <span>👤 {selectedCustomer.salesperson}</span>}</div>
                        </div>
                        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                          {canDo('customers', 'edit') && (<button onClick={() => setShowManageDivisions(true)} className="bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-all flex items-center gap-1.5 text-sm font-semibold"><Briefcase size={15} /> Divisions</button>)}
                          {canDo('customers', 'edit') && (<button onClick={handleEditCustomerClick} className="bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-all flex items-center gap-1.5 text-sm font-semibold"><Edit2 size={15} /> Edit</button>)}
                          {canDo('customers', 'create') && (<button onClick={() => setShowAddVehicle(true)} className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1.5 rounded-lg hover:shadow-md transition-all flex items-center gap-1.5 text-sm font-semibold"><Plus size={15} /> Add Vehicle</button>)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 px-4 py-2.5 border-t border-slate-100">
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 border-l-4 border-l-blue-500"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Vehicles</div><div className="text-lg font-extrabold text-slate-800 tabular-nums leading-tight">{selectedCustomer.vehicles.length} <span className="text-[10px] font-semibold text-green-600">{vehicleCounts.active} active</span></div></div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 border-l-4 border-l-green-500"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Active trackers</div><div className="text-lg font-extrabold text-green-600 tabular-nums leading-tight">{vehicleCounts.active}</div></div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 border-l-4 border-l-rose-500"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Outstanding</div><div className="text-lg font-extrabold tabular-nums leading-tight" style={{ color: outstanding > 0 ? '#e11d48' : '#16a34a' }}>{fmt(outstanding)}</div></div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 border-l-4 border-l-amber-500"><div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Renewals due</div><div className="text-lg font-extrabold tabular-nums leading-tight" style={{ color: renewDue > 0 ? '#c9820f' : '#16a34a' }}>{renewDue}</div></div>
                      </div>
                      <div className="flex gap-1 px-3 border-t border-slate-100 overflow-x-auto">
                        {TABS.map(t => (<button key={t.k} onClick={() => setCustTab(t.k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${custTab === t.k ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{t.label}{t.n != null && <span className={`ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${custTab === t.k ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{t.n}</span>}</button>))}
                      </div>
                    </div>
                    );
                  })()}

                  {/* OVERVIEW tab */}
                  {custTab === 'overview' && (() => {
                    let billed = 0, received = 0, outstanding = 0;
                    const events = [];
                    (selectedCustomer.vehicles || []).forEach(v => (v.invoices || []).forEach(inv => {
                      billed += parseFloat(inv.amount) || 0;
                      received += parseFloat(inv.paid_amount) || 0;
                      outstanding += parseFloat(inv.balance) || 0;
                      const bal = parseFloat(inv.balance) || 0, amt = parseFloat(inv.amount) || 0;
                      events.push({ date: inv.issue_date || inv.created_date || '', title: `${inv.type || 'Invoice'} · Rs ${Math.round(amt).toLocaleString()}`, meta: `${inv.invoice_number || ''}${v.vehicleNo ? ' · ' + v.vehicleNo : ''}`, color: bal <= 0 ? '#16a34a' : (bal < amt ? '#c9820f' : '#e11d48') });
                    }));
                    events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                    const recent = events.slice(0, 8);
                    const fmt = (n) => 'Rs ' + Math.round(n).toLocaleString();
                    const pct = billed > 0 ? Math.round(received / billed * 100) : 0;
                    const fmtDate = (d) => { if (!d) return '—'; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); };
                    return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Left column */}
                      <div className="space-y-4">
                        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
                          <div className="flex items-center justify-between mb-3"><h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Contact &amp; account</h3><button onClick={() => setShowCustomerDetail(true)} className="text-xs text-red-600 font-semibold hover:underline">View all</button></div>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                            {[['Type', selectedCustomer.type], ['Cell', selectedCustomer.cell || selectedCustomer.phone || '—'], ['Follow-up', selectedCustomer.followUp || '—'], ['Salesperson', selectedCustomer.salesperson || '—'], ['City', selectedCustomer.city || '—'], ['Member since', selectedCustomer.created_at ? fmtDate(selectedCustomer.created_at) : '—']].map(([k, val]) => (<div key={k}><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{k}</dt><dd className="font-semibold text-slate-800 truncate">{val}</dd></div>))}
                          </dl>
                          <div className="mt-3 pt-3 border-t border-slate-100"><dt className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Address</dt><dd className="font-medium text-slate-700 text-sm mt-0.5">{[selectedCustomer.address, selectedCustomer.city, selectedCustomer.country].filter(Boolean).join(', ') || '—'}</dd></div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
                          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Financial snapshot</h3>
                          <div className="text-2xl font-extrabold tabular-nums" style={{ color: outstanding > 0 ? '#e11d48' : '#16a34a' }}>{fmt(outstanding)} <span className="text-xs font-semibold text-slate-400">outstanding</span></div>
                          <div className="mt-3 space-y-2.5">{[['Billed', billed, 'bg-blue-500', 100], ['Received', received, 'bg-green-500', pct], ['Outstanding', outstanding, 'bg-rose-500', billed > 0 ? Math.round(outstanding / billed * 100) : 0]].map(([k, val, clr, w]) => (<div key={k}><div className="flex justify-between text-xs text-slate-500 mb-1"><span>{k}</span><span className="tabular-nums font-mono">{fmt(val)}</span></div><div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${clr}`} style={{ width: Math.max(2, Math.min(100, w)) + '%' }} /></div></div>))}</div>
                        </div>

                        {customerDivisions && customerDivisions.length > 0 && (<div className="bg-white rounded-xl shadow-md border border-slate-200 p-4"><div className="flex items-center justify-between mb-3"><h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Divisions</h3>{canDo('customers', 'edit') && <button onClick={() => setShowManageDivisions(true)} className="text-xs text-red-600 font-semibold hover:underline">Manage</button>}</div><DivisionTree nodes={buildDivTree(customerDivisions.filter(d => String(d.customer_id) === String(selectedCustomer.id)))} level={1} can={{}} /></div>)}
                      </div>

                      {/* Right column — recent activity */}
                      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Recent activity</h3>
                        {recent.length === 0 ? (
                          <div className="text-center text-slate-400 text-sm py-10">No invoice activity yet.</div>
                        ) : (
                          <div className="relative pl-5">
                            <div className="absolute left-[5px] top-1 bottom-1 w-0.5 bg-slate-200" />
                            {recent.map((e, i) => (
                              <div key={i} className="relative pb-4 last:pb-0">
                                <div className="absolute -left-[18px] top-1 w-2.5 h-2.5 rounded-full bg-white" style={{ border: `2px solid ${e.color}` }} />
                                <div className="text-sm font-semibold text-slate-800">{e.title}</div>
                                <div className="text-xs text-slate-400 tabular-nums">{e.meta} · {fmtDate(e.date)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}

                  {/* INVOICES tab */}
                  {custTab === 'invoices' && (() => {
                    const fmt = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString();
                    const rows = [];
                    (selectedCustomer.vehicles || []).forEach(v => (v.invoices || []).forEach(inv => rows.push({ ...inv, _veh: v.vehicleNo })));
                    return (
                    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                      <div className="overflow-x-auto"><table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Invoice #</th><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Type</th><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Vehicle</th><th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Amount</th><th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Balance</th><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Status</th><th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Actions</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">{rows.length === 0 ? (<tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">No invoices for this customer yet.</td></tr>) : rows.map(inv => {
                          const st = (inv.status || '').toLowerCase();
                          const cancelled = st === 'cancelled';
                          const paidSoFar = parseFloat(inv.paid_amount) || 0;
                          const canCancel = !cancelled && paidSoFar <= 0 && canDo('invoices', 'edit');
                          return (<tr key={inv.id} className={`hover:bg-slate-50 ${cancelled ? 'opacity-60' : ''}`}><td className="px-4 py-2.5 font-mono text-slate-700">{inv.invoice_number}</td><td className="px-4 py-2.5">{inv.type}</td><td className="px-4 py-2.5 font-mono text-slate-600">{inv._veh}</td><td className="px-4 py-2.5 text-right tabular-nums font-mono">{fmt(inv.amount)}</td><td className="px-4 py-2.5 text-right tabular-nums font-mono" style={{ color: cancelled ? '#94a3b8' : ((parseFloat(inv.balance) || 0) > 0 ? '#e11d48' : '#16a34a') }}>{fmt(inv.balance)}</td><td className="px-4 py-2.5">{cancelled ? (<span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">CANCELLED</span>) : (<span className={`text-xs font-bold px-2 py-0.5 rounded-full ${(parseFloat(inv.balance) || 0) <= 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status || ((parseFloat(inv.balance) || 0) <= 0 ? 'PAID' : 'UNPAID')}</span>)}</td><td className="px-4 py-2.5 text-right">{canCancel ? (<button onClick={() => handleCancelInvoice(inv)} className="text-xs font-semibold text-red-600 hover:underline">Cancel</button>) : (<span className="text-xs text-slate-300">—</span>)}</td></tr>);
                        })}</tbody>
                      </table></div>
                    </div>
                    );
                  })()}

                  {/* LEDGER tab */}
                  {custTab === 'ledger' && (() => {
                    const fmt = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString();
                    let billed = 0, received = 0, outstanding = 0;
                    const rows = [];
                    (selectedCustomer.vehicles || []).forEach(v => (v.invoices || []).forEach(inv => {
                      billed += parseFloat(inv.amount) || 0; received += parseFloat(inv.paid_amount) || 0; outstanding += parseFloat(inv.balance) || 0;
                      rows.push({ date: inv.issue_date || inv.created_date || '', desc: `${inv.type || 'Invoice'} — ${inv.invoice_number || ''}`, veh: v.vehicleNo, amount: parseFloat(inv.amount) || 0, paid: parseFloat(inv.paid_amount) || 0, balance: parseFloat(inv.balance) || 0 });
                    }));
                    rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
                    const fmtDate = (d) => { if (!d) return '—'; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); };
                    return (
                    <div className="space-y-4">
                      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4 flex flex-wrap gap-8">
                        <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Total billed</div><div className="text-xl font-extrabold tabular-nums font-mono">{fmt(billed)}</div></div>
                        <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Total received</div><div className="text-xl font-extrabold tabular-nums font-mono text-green-600">{fmt(received)}</div></div>
                        <div><div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Outstanding</div><div className="text-xl font-extrabold tabular-nums font-mono" style={{ color: outstanding > 0 ? '#e11d48' : '#16a34a' }}>{fmt(outstanding)}</div></div>
                      </div>
                      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto"><table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Date</th><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Description</th><th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Vehicle</th><th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Billed</th><th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Received</th><th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Balance</th></tr></thead>
                          <tbody className="divide-y divide-slate-100">{rows.length === 0 ? (<tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">No ledger entries yet.</td></tr>) : rows.map((r, i) => (<tr key={i} className="hover:bg-slate-50"><td className="px-4 py-2.5 tabular-nums text-slate-600">{fmtDate(r.date)}</td><td className="px-4 py-2.5">{r.desc}</td><td className="px-4 py-2.5 font-mono text-slate-600">{r.veh}</td><td className="px-4 py-2.5 text-right tabular-nums font-mono">{fmt(r.amount)}</td><td className="px-4 py-2.5 text-right tabular-nums font-mono text-green-600">{fmt(r.paid)}</td><td className="px-4 py-2.5 text-right tabular-nums font-mono" style={{ color: r.balance > 0 ? '#e11d48' : '#16a34a' }}>{fmt(r.balance)}</td></tr>))}</tbody>
                        </table></div>
                      </div>
                    </div>
                    );
                  })()}

                  {/* COMPLAINTS tab */}
                  {custTab === 'complaints' && (
                    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-8 text-center">
                      <MessageSquare size={40} className="mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-600 font-semibold mb-1">Complaints are handled in the Complaints module</p>
                      <p className="text-slate-400 text-sm mb-4">Open the full complaints board to view or file tickets for this customer.</p>
                      {canSee('complaints') && <button onClick={() => { setActiveModule('complaints'); setComplaintView('list'); }} className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-xl text-sm font-semibold inline-flex items-center gap-2"><MessageSquare size={16} /> Open Complaints →</button>}
                    </div>
                  )}

                  {/* VEHICLES tab */}
                  {custTab === 'vehicles' && (
                  <div className="bg-white rounded-xl shadow-md border border-slate-200">
                    <div className="p-4 border-b border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <h2 className="text-xl font-bold text-slate-800">Vehicles</h2>
                          {/* Vehicle Counts */}
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-600">Total:</span>
                              <span className="px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 text-sm font-bold">
                                {selectedCustomer.vehicles.length}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-600">Active:</span>
                              <span className="px-2.5 py-1 rounded-lg bg-green-100 text-green-700 text-sm font-bold">
                                {vehicleCounts.active}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-600">Inactive:</span>
                              <span className="px-2.5 py-1 rounded-lg bg-red-100 text-red-700 text-sm font-bold">
                                {vehicleCounts.inactive}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-600">Package Awaiting:</span>
                              <span className="px-2.5 py-1 rounded-lg bg-orange-100 text-orange-700 text-sm font-bold">
                                {vehicleCounts.packageAwaiting}
                              </span>
                            </div>
                          </div>
                        </div>
                        {canDo('customers', 'create') && (
                        <button
                          onClick={() => setShowAddVehicle(true)}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                        >
                          <Plus size={18} />
                          Add Vehicle
                        </button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {['all', 'active', 'inactive', 'packageAwaiting'].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setVehicleFilter(filter)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                              vehicleFilter === filter
                                ? 'bg-red-500 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {filter === 'packageAwaiting' ? 'Package Awaiting' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Vehicle No</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Active Date</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">IMEI</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Device</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredVehicles.map((vehicle) => (
                            <React.Fragment key={vehicle.id}>
                              <tr 
                                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                                onClick={() => {
                                  const newId = expandedVehicle === vehicle.id ? null : vehicle.id;
                                  setExpandedVehicle(newId);
                                  if (newId === vehicle.id) {
                                    loadVehicleNumberChanges(vehicle.id);
                                    loadVehicleTransfers(vehicle.id);
                                  }
                                }}
                              >
                                <td className="px-4 py-2.5 font-semibold text-slate-800">{vehicle.vehicleNo}</td>
                                <td className="px-4 py-2.5 text-slate-600 text-sm">{vehicle.activeDate ? vehicle.activeDate : <span className="text-orange-400 italic">Not Set</span>}</td>
                                <td className="px-4 py-2.5 text-slate-600 font-mono text-sm">{vehicle.imei}</td>
                                <td className="px-4 py-2.5 text-slate-600">{vehicle.deviceName}</td>
                                <td className="px-4 py-2.5">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    vehicle.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {vehicle.status}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5">
                                  <div className="flex gap-2">
                                    {canDo('customers', 'edit') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (vehicle.status !== 'inactive') {
                                          handleEditVehicleClick(vehicle);
                                        }
                                      }}
                                      disabled={vehicle.status === 'inactive'}
                                      className={`p-1.5 rounded-lg transition-all ${
                                        vehicle.status === 'inactive'
                                          ? 'text-slate-300 bg-slate-50 cursor-not-allowed'
                                          : 'text-blue-600 hover:bg-blue-50'
                                      }`}
                                      title={vehicle.status === 'inactive' ? 'Cannot edit - Vehicle is inactive' : 'Edit Vehicle'}
                                    >
                                      <Edit2 size={16} />
                                    </button>
                                    )}
                                    {canDo('customers', 'delete') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!vehicle.setupInvoiceCreated) {
                                          handleDeleteVehicle(vehicle.id);
                                        }
                                      }}
                                      disabled={vehicle.setupInvoiceCreated}
                                      className={`p-1.5 rounded-lg transition-all ${
                                        vehicle.setupInvoiceCreated
                                          ? 'text-slate-300 bg-slate-50 cursor-not-allowed'
                                          : 'text-red-600 hover:bg-red-50'
                                      }`}
                                      title={vehicle.setupInvoiceCreated ? 'Cannot delete - Setup invoice created' : 'Delete Vehicle'}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedVehicle === vehicle.id && (
                                <tr className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-slate-200">
                                  <td colSpan="6" className="px-4 py-2.5">
                                    <div className="grid grid-cols-4 gap-4 mb-4">
                                      <div>
                                        <p className="text-xs text-slate-500 mb-1 font-semibold uppercase">Active Date</p>
                                        <p className="text-base font-semibold text-slate-800">{vehicle.activeDate || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500 mb-1 font-semibold uppercase">Package Price</p>
                                        <p className="text-base font-semibold text-slate-800">
                                          {vehicle.packagePrice === '' || vehicle.packagePrice == null ? <span className="text-orange-500">Not Set</span> : parseFloat(vehicle.packagePrice) === 0 ? <span className="text-emerald-600">Free</span> : `Rs. ${vehicle.packagePrice}`}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500 mb-1 font-semibold uppercase">AMC</p>
                                        <p className="text-base font-semibold text-slate-800">
                                          {vehicle.amc === '' || vehicle.amc == null ? <span className="text-orange-500">Not Set</span> : parseFloat(vehicle.amc) === 0 ? <span className="text-emerald-600">Free</span> : `Rs. ${vehicle.amc}`}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500 mb-1 font-semibold uppercase">Invoicing Period</p>
                                        <p className="text-base font-semibold text-slate-800">{vehicle.invoicingPeriod}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Invoice Actions */}
                                    <div className="flex gap-3 pt-4 border-t border-blue-200">
                                      {!vehicle.setupInvoiceCreated && vehicle.packagePrice ? (
                                        vehicle.status === 'active' ? (
                                        canDo('invoices', 'create') ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCreateSetupInvoice(selectedCustomer.id, vehicle.id);
                                          }}
                                          className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2.5 rounded-xl hover:shadow-md transition-all flex items-center justify-center gap-2 font-semibold"
                                        >
                                          <FileText size={18} />
                                          Create Setup Invoice (Rs. {calculateSetupInvoiceAmount(vehicle).toLocaleString()})
                                        </button>
                                        ) : null
                                        ) : (
                                        <div className="flex-1 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold cursor-not-allowed" title="Vehicle is inactive">
                                          <AlertCircle size={18} />
                                          Vehicle Inactive
                                        </div>
                                        )
                                      ) : vehicle.setupInvoiceCreated ? (
                                        <div className="flex-1 bg-green-100 text-green-700 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold">
                                          <Check size={18} />
                                          Setup Invoice Created
                                        </div>
                                      ) : (
                                        <div className="flex-1 bg-orange-100 text-orange-700 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold">
                                          <AlertCircle size={18} />
                                          Set Package Price to Create Invoice
                                        </div>
                                      )}
                                      
                                      {(() => {
                                        const check = canCreateRenewalInvoice(vehicle);
                                        
                                        // Check if vehicle is inactive
                                        if (vehicle.status !== 'active') {
                                          return (
                                            <div className="flex-1 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold cursor-not-allowed" title="Vehicle is inactive">
                                              <AlertCircle size={18} />
                                              Vehicle Inactive
                                            </div>
                                          );
                                        }
                                        
                                        // Check if setup invoice has been created
                                        if (!vehicle.setupInvoiceCreated) {
                                          return (
                                            <div className="flex-1 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold cursor-not-allowed" title="Create setup invoice first">
                                              <Clock size={18} />
                                              Create Setup Invoice First
                                            </div>
                                          );
                                        }
                                        
                                        if (vehicle.amc && vehicle.activeDate) {
                                          if (check.allowed) {
                                            if (!canDo('invoices', 'create')) return null;
                                            return (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleGenerateRenewalInvoice(selectedCustomer.id, vehicle.id);
                                                }}
                                                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2.5 rounded-xl hover:shadow-md transition-all flex items-center justify-center gap-2 font-semibold"
                                              >
                                                <DollarSign size={18} />
                                                Generate Renewal Invoice (Rs. {calculateRenewalAmount(vehicle.amc, vehicle.invoicingPeriod)})
                                              </button>
                                            );
                                          } else {
                                            return (
                                              <div className="flex-1 bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold cursor-not-allowed" title={check.reason}>
                                                <Clock size={18} />
                                                {check.reason.includes('already created')
												 ? 'Invoice Already Created' 
												  : check.allowedDate && check.daysUntilAllowed !== undefined
													? `Available on ${new Date(check.allowedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })} in ${check.daysUntilAllowed} ${check.daysUntilAllowed === 1 ? 'day' : 'days'}` 
													: check.daysUntilAllowed !== undefined
														? `Available in ${check.daysUntilAllowed} ${check.daysUntilAllowed === 1 ? 'day' : 'days'}`
														: check.reason || 'Not yet available'
												}
                                              </div>
                                            );
                                          }
                                        } else {
                                          return (
                                            <div className="flex-1 bg-orange-100 text-orange-700 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold">
                                              <AlertCircle size={18} />
                                              Set AMC & Active Date for Renewals
                                            </div>
                                          );
                                        }
                                      })()}
                                    </div>
                                    
                                    {/* Vehicle Invoices */}
                                    {(() => {
                                      // Use invoices from vehicle object (already loaded from API)
                                      const vehicleInvoices = vehicle.invoices || [];
                                      
                                      if (vehicleInvoices.length > 0) {
                                        return (
                                          <div className="mt-4 pt-4 border-t border-blue-200">
                                            <p className="text-sm font-semibold text-slate-700 mb-3">Vehicle Invoices:</p>
                                            <div className="space-y-2">
                                              {vehicleInvoices.map(inv => {
                                                const totalPaid = inv.payments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
                                                const status = getInvoiceStatus(inv);
                                                return (
                                                  <div key={inv.id} className="flex items-center justify-between bg-white px-4 py-2.5 rounded-lg">
                                                    <div className="flex items-center gap-4">
                                                      <span className="font-mono text-sm font-semibold text-slate-700">{inv.invoice_number}</span>
                                                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                        inv.type === 'Setup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                                      }`}>
                                                        {inv.type}
                                                      </span>
                                                      {inv.period_from && inv.period_to && (
                                                        <span className="text-xs text-slate-600 font-medium">
                                                          {inv.period_from} to {inv.period_to}
                                                        </span>
                                                      )}
                                                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                        status === 'PAID' ? 'bg-green-100 text-green-700' :
                                                        status.includes('OVERDUE') ? 'bg-red-100 text-red-700' :
                                                        status === 'PARTIALLY PAID' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-slate-100 text-slate-700'
                                                      }`}>
                                                        {status}
                                                      </span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                      <span className="text-sm font-semibold text-slate-700">Rs. {parseFloat(inv.amount).toLocaleString()}</span>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}

                                    {/* Removal&Transfer / Ownership-Transfer origin & history */}
                                    {(() => {
                                      const t = vehicleTransfers[vehicle.id];
                                      if (!t || ((t.rt?.length || 0) === 0 && (t.ot?.length || 0) === 0)) return null;
                                      const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                                      const incoming = (t.rt || []).filter(r => String(r.to_vehicle_id) === String(vehicle.id));
                                      const outgoing = (t.rt || []).filter(r => String(r.from_vehicle_id) === String(vehicle.id));
                                      return (
                                        <div className="mt-4 pt-4 border-t border-amber-200 space-y-2">
                                          <p className="text-sm font-semibold text-slate-700 mb-1">🔁 Transfer History:</p>
                                          {incoming.map(r => (
                                            <div key={'in'+r.id} className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm">
                                              <span className="font-semibold text-amber-800">Received via Removal &amp; Transfer</span>
                                              <span className="text-slate-600"> from <b>{r.from_vehicle_no}</b> on <b>{fmtD(r.rt_date)}</b>.</span>
                                              <span className="text-slate-500"> Billing (AMC/setup) inherited.</span>
                                            </div>
                                          ))}
                                          {outgoing.map(r => (
                                            <div key={'out'+r.id} className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5 text-sm">
                                              <span className="font-semibold text-orange-800">Tracker removed &amp; transferred out</span>
                                              <span className="text-slate-600"> to <b>{r.to_vehicle_no}</b> on <b>{fmtD(r.rt_date)}</b>. Vehicle inactive.</span>
                                            </div>
                                          ))}
                                          {(t.ot || []).map(o => (
                                            <div key={'ot'+o.id} className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 text-sm">
                                              <span className="font-semibold text-indigo-800">Ownership transferred</span>
                                              <span className="text-slate-600"> {o.from_customer_name} → <b>{o.to_customer_name}</b> on <b>{fmtD(o.ot_date)}</b>.</span>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}

                                    {/* Vehicle Number Change History */}
                                    {vehicleNumberChanges[vehicle.id]?.length > 0 && (
                                      <div className="mt-4 pt-4 border-t border-blue-200">
                                        <p className="text-sm font-semibold text-slate-700 mb-3">📋 Number Change History:</p>
                                        <div className="space-y-2">
                                          {vehicleNumberChanges[vehicle.id]
                                            .sort((a, b) => new Date(b.change_date) - new Date(a.change_date))
                                            .map(change => (
                                              <div key={change.id} className="flex items-center justify-between bg-white px-4 py-2.5 rounded-lg border border-slate-100">
                                                <div className="flex items-center gap-4">
                                                  <span className="text-xs text-slate-500 font-semibold uppercase">
                                                    {new Date(change.change_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                  </span>
                                                  <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-700">{change.old_vehicle_no}</span>
                                                    <ArrowRight size={16} className="text-slate-400" />
                                                    <span className="font-semibold text-slate-700">{change.new_vehicle_no}</span>
                                                  </div>
                                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                    change.change_reason === 'legal_change' ? 'bg-green-100 text-green-700' :
                                                    change.change_reason === 'correction' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-blue-100 text-blue-700'
                                                  }`}>
                                                    {change.change_reason === 'legal_change' ? '🏛️ Legal' : 
                                                     change.change_reason === 'correction' ? '✏️ Correction' : 
                                                     'Other'}
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-md border border-slate-200 p-12 text-center">
                  <Search size={64} className="mx-auto text-slate-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-600 mb-2">No customer selected</h3>
                  <p className="text-slate-400">Pick a customer from the list on the left to open their profile.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeModule === 'inventory' && (
          /* Inventory Module */
          <div className="bg-white rounded-xl shadow-md border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">IMEI Inventory <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full tabular-nums">{filteredInventory.length} devices</span></h2>
                <div className="flex gap-3">
                  {canDo('inventory', 'edit') && (
                  <button
                    onClick={() => {
                      setShowIssuance(true);
                      setIssuanceDate(new Date().toISOString().split('T')[0]);
                    }}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Package size={18} />
                    Issuance
                  </button>
                  )}
                  {canDo('inventory', 'edit') && (
                  <button
                    onClick={() => {
                      setShowReturn(true);
                      setReturnDate(new Date().toISOString().split('T')[0]);
                    }}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Download size={18} />
                    Return
                  </button>
                  )}
                  {canDo('inventory', 'create') && (
                  <button
                    onClick={() => setShowBulkUpload(true)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Upload size={18} />
                    Bulk Upload
                  </button>
                  )}
                  {canDo('inventory', 'create') && (
                  <button
                    onClick={() => setShowAddInventory(true)}
                    className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Plus size={18} />
                    Add Device
                  </button>
                  )}
                </div>
              </div>
              
              {/* Tracker Statistics */}
              <div className="grid grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                {(() => {
                  const counts = getTrackerCounts();
                  const tiles = [
                    { key: 'all',       label: 'Total',          value: counts.total,         color: '#64748b' },
                    { key: 'Available', label: 'Available',      value: counts.available,     color: '#1f9d63' },
                    { key: 'Installed', label: 'Installed',      value: counts.installed,     color: '#3b6fd2' },
                    { key: 'Issued',    label: 'Issued',         value: counts.issued,        color: '#c9820f' },
                    { key: 'Store',     label: 'In Store',       value: counts.inStore,       color: '#7c53d6' },
                    { key: 'Customer',  label: 'Customer Owned', value: counts.customerOwned, color: '#128a86' },
                    { key: 'Lost',      label: 'Lost',           value: counts.lost,          color: '#d23b4e' },
                  ];
                  return tiles.map(t => {
                    const active = inventoryStatusFilter === t.key;
                    return (
                      <div
                        key={t.key}
                        onClick={() => setInventoryStatusFilter(t.key === 'all' ? 'all' : (active ? 'all' : t.key))}
                        title={t.label}
                        className="rounded-xl p-3 border border-slate-200 bg-white cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5"
                        style={{ borderTop: `3px solid ${t.color}`, boxShadow: active ? `0 0 0 2px ${t.color}` : undefined }}
                      >
                        <div className="text-2xl font-extrabold leading-none tabular-nums" style={{ color: t.color }}>{t.value}</div>
                        <div className="text-[11px] font-semibold text-slate-500 mt-1.5 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: t.color }} />
                          {t.label}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-3 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by IMEI, ICCID, SIM, vehicle no, device type, vendor..."
                  value={inventorySearch}
                  onChange={(e) => setInventorySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">IMEI / Device</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Held by / Location</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Health</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Used</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Added</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.slice(0, inventoryVisibleCount).map((item) => (
                    <React.Fragment key={item.id}>
                    <tr onClick={() => setExpandedInvId(expandedInvId === item.id ? null : item.id)} className={`border-b border-slate-100 cursor-pointer transition-colors ${expandedInvId === item.id ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ChevronDown size={14} className={`text-slate-300 shrink-0 transition-transform ${expandedInvId === item.id ? 'rotate-180 text-slate-500' : ''}`} />
                          <div>
                            <div className="font-mono font-semibold text-slate-800 text-sm leading-tight">{item.imei}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{[item.deviceType, item.vendor].filter(Boolean).join(' · ') || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {item.status === 'Installed' ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold bg-blue-50 text-blue-600"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Installed</span>
                        ) : item.isIssued ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold bg-amber-50 text-amber-600"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Issued</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-bold bg-violet-50 text-violet-600"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />In Store</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {item.status === 'Installed' ? (<span>🚗 {item.location || 'Vehicle'}</span>)
                          : item.isIssued ? (<span>👤 {item.issuedToName || item.location || '—'}</span>)
                          : (<span className="text-slate-400">🏪 Main store</span>)}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const map = { active: ['#1f9d63', '● Active'], lost: ['#d23b4e', '● Lost'], under_repair: ['#c9820f', '🔧 Under Repair'], repaired: ['#3b6fd2', '✔ Repaired'], damaged: ['#f5732b', '⚠ Damaged'] };
                          const [c, label] = map[item.healthStatus] || map.active;
                          return <span className="font-semibold text-[12.5px]" style={{ color: c }}>{label}</span>;
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${(item.usedCount || 0) === 0 ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`} title="Times this IMEI was installed on a vehicle">{item.usedCount || 0}</span>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-slate-500 text-sm whitespace-nowrap">{item.dateAdded}</td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => handleViewImeiLog(item)} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-violet-600 transition-all" title="View IMEI History"><Clock size={16} /></button>
                          {canDo('inventory', 'edit') && (
                          <button onClick={() => handleEditInventoryClick(item)} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-blue-600 transition-all" title="Edit"><Edit2 size={16} /></button>
                          )}
                          {canDo('inventory', 'delete') && (
                          <button onClick={() => handleDeleteInventory(item.id)} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-red-600 transition-all" title="Delete"><Trash2 size={16} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedInvId === item.id && (
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <td colSpan={7} className="px-4 pb-3 pt-0">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-white rounded-lg border border-slate-200 p-3">
                            <div><div className="text-[10px] uppercase text-slate-400 font-bold tracking-wide">ICCID</div><div className="font-mono text-sm text-slate-700 break-all">{item.iccid || '—'}</div></div>
                            <div><div className="text-[10px] uppercase text-slate-400 font-bold tracking-wide">SIM Number</div><div className="font-mono text-sm text-slate-700">{item.simNumber || '—'}</div></div>
                            <div><div className="text-[10px] uppercase text-slate-400 font-bold tracking-wide">Vendor</div><div className="text-sm text-slate-700">{item.vendor || '—'}</div></div>
                            <div><div className="text-[10px] uppercase text-slate-400 font-bold tracking-wide">Held by / Location</div><div className="text-sm text-slate-700">{item.issuedToName || item.location || '—'}</div></div>
                            <div><div className="text-[10px] uppercase text-slate-400 font-bold tracking-wide">Times installed</div><div className="text-sm text-slate-700 tabular-nums">{item.usedCount || 0}</div></div>
                            <div><div className="text-[10px] uppercase text-slate-400 font-bold tracking-wide">Added</div><div className="text-sm text-slate-700 tabular-nums">{item.dateAdded}</div></div>
                            <div className="md:col-span-2 flex items-end justify-end gap-2">
                              <button onClick={(e) => { e.stopPropagation(); handleViewImeiLog(item); }} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"><Clock size={14} /> Full IMEI history</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredInventory.length > inventoryVisibleCount && (
              <div className="p-3 border-t border-slate-100">
                <button
                  onClick={() => setInventoryVisibleCount(c => c + TABLE_PAGE_SIZE)}
                  className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                >
                  Load more ({filteredInventory.length - inventoryVisibleCount} more of {filteredInventory.length})
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── IMEI History Drawer ── */}
        {imeiLogDrawer && (
          <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setImeiLogDrawer(null)}>
            <div className="bg-white w-full max-w-lg h-full flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 flex items-start justify-between">
                <div>
                  <p className="text-purple-200 text-xs font-semibold uppercase tracking-widest mb-1">IMEI Activity Log</p>
                  <h2 className="text-white font-bold text-lg font-mono">{imeiLogDrawer.imei}</h2>
                  <p className="text-purple-200 text-sm mt-0.5">{imeiLogDrawer.deviceType}</p>
                </div>
                <button onClick={() => setImeiLogDrawer(null)} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                  <X size={20}/>
                </button>
              </div>

              {/* Revenue Earned — total invoiced/paid against this specific device */}
              <div className="border-b border-slate-200 bg-slate-50 p-5">
                {imeiRevenueLoading ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <RefreshCw size={14} className="animate-spin"/> Loading revenue...
                  </div>
                ) : !imeiRevenue || imeiRevenue.invoice_count === 0 ? (
                  <p className="text-sm text-slate-400">No invoices billed against this device yet.</p>
                ) : (
                  <div>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-slate-500 font-semibold">Total Invoiced</p>
                        <p className="text-lg font-bold text-slate-800">Rs. {imeiRevenue.total_invoiced.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-semibold">Earned (Paid)</p>
                        <p className="text-lg font-bold text-green-600">Rs. {imeiRevenue.total_paid.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-semibold">Outstanding</p>
                        <p className={`text-lg font-bold ${imeiRevenue.total_balance > 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                          Rs. {imeiRevenue.total_balance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-purple-700 font-semibold text-xs">
                        {imeiRevenue.invoice_count} invoice{imeiRevenue.invoice_count !== 1 ? 's' : ''} — view breakdown
                      </summary>
                      <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto pr-1">
                        {imeiRevenue.invoices.map(inv => (
                          <div key={inv.id} className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                            <div>
                              <p className="font-semibold text-slate-700">{inv.invoice_number} <span className="text-slate-400 font-normal capitalize">({inv.invoice_type})</span></p>
                              <p className="text-slate-400">{inv.issue_date}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-700">Rs. {Number(inv.amount - (inv.discount || 0)).toLocaleString()}</p>
                              <p className={inv.balance > 0 ? 'text-orange-500' : 'text-green-600'}>
                                {inv.balance > 0 ? `Rs. ${Number(inv.balance).toLocaleString()} due` : 'Paid'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {imeiLogsLoading ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <RefreshCw size={28} className="animate-spin mb-3"/>
                    <p className="text-sm font-medium">Loading history...</p>
                  </div>
                ) : imeiLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Clock size={36} className="mb-3 text-slate-300"/>
                    <p className="text-sm font-semibold">No activity logged yet</p>
                    <p className="text-xs mt-1">Events appear here as the device is used</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200"/>
                    <div className="space-y-6">
                      {imeiLogs.map((log, i) => {
                        const cfgMap = {
                          added:     { icon: '➕', color: 'bg-green-500',  label: 'Added to System',    border: 'border-green-200',  bg: 'bg-green-50'  },
                          issued:    { icon: '📤', color: 'bg-blue-500',   label: 'Issued to Tech',      border: 'border-blue-200',   bg: 'bg-blue-50'   },
                          returned:  { icon: '📥', color: 'bg-slate-500',  label: 'Returned to Store',   border: 'border-slate-200',  bg: 'bg-slate-50'  },
                          installed: { icon: '🔧', color: 'bg-orange-500', label: 'Installed on Vehicle', border: 'border-orange-200', bg: 'bg-orange-50' },
                          removed:   { icon: '🔴', color: 'bg-red-500',    label: 'Removed from Vehicle', border: 'border-red-200',    bg: 'bg-red-50'    },
                          repaired:  { icon: '✅', color: 'bg-teal-500',   label: 'Repaired',            border: 'border-teal-200',   bg: 'bg-teal-50'   },
                          damaged:   { icon: '⚠️', color: 'bg-yellow-500', label: 'Marked Damaged',      border: 'border-yellow-200', bg: 'bg-yellow-50' },
                          lost:      { icon: '❌', color: 'bg-red-700',    label: 'Marked Lost',         border: 'border-red-300',    bg: 'bg-red-50'    },
                          extended:  { icon: '⏳', color: 'bg-emerald-600', label: 'Service Extended',    border: 'border-emerald-200', bg: 'bg-emerald-50' },
                        };
                        const cfg = cfgMap[log.event_type] || { icon: '📋', color: 'bg-slate-400', label: log.event_type, border: 'border-slate-200', bg: 'bg-slate-50' };
                        return (
                          <div key={i} className="relative flex gap-4">
                            <div className={`w-10 h-10 rounded-full ${cfg.color} flex items-center justify-center text-base z-10 flex-shrink-0 shadow-sm`}>
                              {cfg.icon}
                            </div>
                            <div className={`flex-1 rounded-xl border ${cfg.border} ${cfg.bg} p-4`}>
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <p className="text-sm font-bold text-slate-800">{cfg.label}</p>
                                <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                                  {log.created_at ? new Date(log.created_at).toLocaleString('en-PK', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
                                </span>
                              </div>
                              <div className="space-y-1 text-xs text-slate-600">
                                {log.actor_name   && <p><span className="font-semibold text-slate-500 w-16 inline-block">By:</span> <span className="font-semibold text-slate-700">{log.actor_name}</span></p>}
                                {log.vehicle_name && <p><span className="font-semibold text-slate-500 w-16 inline-block">Vehicle:</span> <span className="font-mono font-bold text-slate-700">{log.vehicle_name}</span></p>}
                                {log.location     && <p><span className="font-semibold text-slate-500 w-16 inline-block">Location:</span> <span className="text-slate-700">{log.location}</span></p>}
                                {log.notes        && <p className="mt-1.5 pt-1.5 border-t border-white/60"><span className="font-semibold text-slate-500">Notes:</span> <span className="text-slate-600">{log.notes}</span></p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-400 text-center">{imeiLogs.length} event{imeiLogs.length !== 1 ? 's' : ''} recorded</p>
              </div>
            </div>
          </div>
        )}

        {activeModule === 'vendors' && (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">Vendors <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full tabular-nums">{filteredVendors.length}</span></h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                    <input type="text" placeholder="Search vendors…" value={vendorSearch} onChange={(e) => setVendorSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm w-56" />
                  </div>
                  {canDo('vendors', 'create') && (
                  <button onClick={() => setShowAddVendor(true)} className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-md transition-all flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Vendor
                  </button>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Name</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Contact</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Email</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">City</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Address</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredVendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-sm font-semibold text-slate-800">{vendor.name}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{vendor.contact}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{vendor.email}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{vendor.city}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{vendor.address}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-2 justify-end">
                          {canDo('vendors', 'edit') && (
                          <button
                            onClick={() => {
                              setEditVendorData(vendor);
                              setEditingVendor(vendor);
                              setShowEditVendor(true);
                            }}
                            className="text-slate-400 hover:text-blue-600 transition-all"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          )}
                          {canDo('vendors', 'delete') && (
                          <button
                            onClick={() => handleDeleteVendor(vendor.id)}
                            className="text-slate-600 hover:text-red-600 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredVendors.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No vendors found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeModule === 'devices' && (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">Devices <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full tabular-nums">{filteredDevices.length}</span></h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                    <input type="text" placeholder="Search devices…" value={deviceSearch} onChange={(e) => setDeviceSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm w-56" />
                  </div>
                  {canDo('devices', 'create') && (
                  <button onClick={() => setShowAddDevice(true)} className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-md transition-all flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Device
                  </button>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Device Name</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Category</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Description</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredDevices.map((device) => (
                    <tr key={device.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-sm font-semibold text-slate-800">{device.name}</td>
                      <td className="px-4 py-2.5">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          {device.category}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{device.description}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-2 justify-end">
                          {canDo('devices', 'edit') && (
                          <button
                            onClick={() => {
                              setEditDeviceData(device);
                              setEditingDevice(device);
                              setShowEditDevice(true);
                            }}
                            className="text-slate-400 hover:text-blue-600 transition-all"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          )}
                          {canDo('devices', 'delete') && (
                          <button
                            onClick={() => handleDeleteDevice(device.id)}
                            className="text-slate-600 hover:text-red-600 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredDevices.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Box className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No devices found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeModule === 'vehicle_models' && (
          <VehicleModelsModule vehicleModels={vehicleModels} reload={loadVehicleModels}
            can={{ create: canDo('vehicle_models','create'), edit: canDo('vehicle_models','edit'), delete: canDo('vehicle_models','delete') }} />
        )}

        {activeModule === 'cities' && (
          <CitiesModule cities={cities} reload={loadCities}
            can={{ create: canDo('cities','create'), edit: canDo('cities','edit'), delete: canDo('cities','delete') }} />
        )}

        {activeModule === 'quotations' && (
          <QuotationsModule
            quotations={quotations}
            customers={customers}
            reload={loadQuotations}
            can={{ create: canDo('quotations','create'), edit: canDo('quotations','edit'), delete: canDo('quotations','delete') }}
          />
        )}

        {activeModule === 'employees' && (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">Employees <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full tabular-nums">{filteredEmployees.length}</span></h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
                    <input type="text" placeholder="Search employees…" value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 rounded-lg bg-slate-50 border border-slate-200 focus:border-red-400 outline-none text-sm w-56" />
                  </div>
                  {canDo('employees', 'create') && (
                  <button onClick={() => setShowAddEmployee(true)} className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-md transition-all flex items-center gap-2 text-sm">
                    <Plus size={16} /> Add Employee
                  </button>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Code</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Name</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Designation</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Department</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Phone</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Email</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Role</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredEmployees.map((employee) => (
                    <tr key={employee.id} className={`hover:bg-slate-50 ${employee.status === 'inactive' ? 'bg-slate-50' : ''}`}>
                      <td className="px-4 py-2.5 text-sm font-bold text-slate-900 bg-slate-50">
                        <span className="inline-block px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 font-mono text-xs font-bold">
                          {employee.employee_code || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm font-semibold text-slate-800">{employee.name}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{employee.designation}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{employee.department}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{employee.phone}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{employee.email}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          employee.role === 'Sales' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {employee.role}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          employee.status === 'active' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {employee.status === 'active' ? '✓ Active' : '⊗ Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-2">
                          {canDo('employees', 'edit') && (
                          <button
                            onClick={() => {
                              setEditEmployeeData(employee);
                              setEditingEmployee(employee);
                              setShowEditEmployee(true);
                            }}
                            className="text-slate-600 hover:text-blue-600 transition-all"
                            title="Edit employee"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          )}
                          {canDo('employees', 'delete') && (
                          <button
                            onClick={() => handleDeleteEmployee(employee.id)}
                            className="text-slate-600 hover:text-orange-600 transition-all"
                            title="Deactivate employee"
                          >
                            <AlertCircle className="w-4 h-4" />
                          </button>
                          )}
                          {canDo('employees', 'delete') && (
                          <button
                            onClick={() => handlePermanentDeleteEmployee(employee.id)}
                            className="text-slate-600 hover:text-red-600 transition-all"
                            title="Permanently delete employee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredEmployees.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No employees found</p>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Cashflow Module */}
        {activeModule === 'cashflow' && (
          <div>
            {/* ── Header (mockup-clean) ── */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: '#fff1f3' }}>
                    <Wallet className="text-rose-500" size={22} />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-slate-800 tracking-tight leading-tight">Cashflow Management</h2>
                    <p className="text-slate-500 text-xs mt-0.5">Track all inflows and outflows · <span className="font-semibold text-slate-600">{cashflowDateMode === 'all' ? 'All time' : cashflowDateMode === 'custom' ? (cashflowDateFrom && cashflowDateTo ? `${cashflowDateFrom} → ${cashflowDateTo}` : 'Custom range') : (() => { try { return new Date(cashflowMonth + '-01T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }); } catch (e) { return cashflowMonth; } })()}</span></p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Mode segmented */}
                  <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
                    {[['month','Month'],['all','All'],['custom','Custom']].map(([m,l]) => (
                      <button key={m} onClick={() => {
                        setCashflowDateMode(m);
                        if (m !== 'custom') loadCashflow(m === 'month' ? cashflowMonth : null, m);
                      }} className={`px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-all ${cashflowDateMode===m ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  {/* Month picker */}
                  {cashflowDateMode === 'month' && (
                    <input type="month" value={cashflowMonth}
                      onChange={e => { setCashflowMonth(e.target.value); loadCashflow(e.target.value, 'month'); }}
                      className="px-3 py-2 rounded-lg border border-slate-200 focus:border-rose-400 outline-none font-semibold text-sm text-slate-700"
                    />
                  )}
                  {/* Custom date range */}
                  {cashflowDateMode === 'custom' && (
                    <div className="flex items-center gap-2">
                      <input type="date" value={cashflowDateFrom}
                        onChange={e => setCashflowDateFrom(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:border-rose-400 outline-none text-sm text-slate-700"
                      />
                      <span className="text-slate-400 text-sm font-medium">to</span>
                      <input type="date" value={cashflowDateTo}
                        onChange={e => setCashflowDateTo(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-slate-200 focus:border-rose-400 outline-none text-sm text-slate-700"
                      />
                      <button
                        onClick={() => { if (cashflowDateFrom && cashflowDateTo) loadCashflow(null, 'custom', cashflowDateFrom, cashflowDateTo); else alert('Please select both dates.'); }}
                        className="px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all" style={{ background: 'linear-gradient(135deg,#d23b4e,#e8637a)' }}>
                        Apply
                      </button>
                    </div>
                  )}
                  <button onClick={() => loadCashflow(null, cashflowDateMode, cashflowDateFrom, cashflowDateTo)} className="px-3.5 py-2 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 flex items-center gap-2 transition-all">
                    <RefreshCw size={15} className={cfLoading ? 'animate-spin' : ''} /> Refresh
                  </button>
                  {canDo('cashflow', 'create') && (
                  <button onClick={() => { setCfCatModalFlow('outflow'); setShowCfCatModal(true); }} className="px-3.5 py-2 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 flex items-center gap-2 transition-all">
                    <Settings size={15} /> Categories
                  </button>
                  )}
                  {canDo('cashflow', 'create') && (
                  <button
                    onClick={() => { setCfAddType('outflow'); setEditingCfEntry(null); setCfOutflowForm(CF_EMPTY_OUT); setShowAddCfEntry(true); }}
                    className="px-4 py-2 rounded-lg text-white font-semibold flex items-center gap-2 shadow-sm text-sm transition-all" style={{ background: 'linear-gradient(135deg,#d23b4e,#e8637a)' }}
                  >
                    <ArrowDownCircle size={16} /> Add Outflow
                  </button>
                  )}
                  {canDo('cashflow', 'create') && (
                  <button
                    onClick={() => { setCfAddType('inflow'); setEditingCfEntry(null); setCfInflowForm(CF_EMPTY_IN); setShowAddCfEntry(true); }}
                    className="px-4 py-2 rounded-lg text-white font-semibold flex items-center gap-2 shadow-sm text-sm transition-all" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}
                  >
                    <ArrowUpCircle size={16} /> Add Inflow
                  </button>
                  )}
                </div>
              </div>
            </div>

            {/* ── Tabs (underline) ── */}
            <div className="flex gap-1 border-b border-slate-200 mb-4">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'outflows', label: 'Outflows', n: cfOutflows.length },
                { key: 'inflows',  label: 'Inflows',  n: (cfInflows?.invoice_payments?.length || 0) + (cfInflows?.other_inflows?.length || 0) },
                { key: 'advances', label: 'Advances / Petty Cash', n: (cfAdvances?.balances || []).length },
              ].map(tab => (
                <button key={tab.key} onClick={() => setCashflowTab(tab.key)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${cashflowTab === tab.key ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                  {tab.label}{tab.n != null && <span className={`ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${cashflowTab === tab.key ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{tab.n}</span>}
                </button>
              ))}
            </div>

            {/* ── OVERVIEW TAB ── */}
            {cashflowTab === 'overview' && cashflowSummary && (() => {
              const inTot = cashflowSummary.total_inflows || 0;
              const outTot = cashflowSummary.total_outflows || 0;
              const net = cashflowSummary.net_cashflow || 0;
              const maxIO = Math.max(inTot, outTot, 1);
              const OV_PALETTE = ['#ef4444','#3b82f6','#f97316','#8b5cf6','#e11d48','#06b6d4','#f59e0b','#6366f1','#14b8a6','#0ea5e9','#84cc16','#64748b'];
              const cats = CF_OUTFLOW_CATS
                .map(cat => { const items = cfOutflows.filter(o => o.category === cat.value); return { cat, total: items.reduce((s, o) => s + o.amount, 0), count: items.length }; })
                .filter(x => x.total > 0)
                .sort((a, b) => b.total - a.total);
              const maxCat = Math.max(...cats.map(c => c.total), 1);
              const txCount = cfOutflows.length + (cfInflows?.invoice_payments?.length || 0) + (cfInflows?.other_inflows?.length || 0);
              return (
                <div className="space-y-4">
                  {/* Summary stat tiles (Overview only) */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 border-l-[3px] border-l-green-500">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Inflows</div>
                      <div className="text-lg font-extrabold text-green-600 tabular-nums font-mono leading-tight">{cfFmt(inTot)}</div>
                      <div className="text-[10px] text-slate-400">Setup {cfFmt(cashflowSummary.invoice_setup_total)} · AMC {cfFmt(cashflowSummary.invoice_amc_total)} · Svc {cfFmt(cashflowSummary.invoice_service_total || 0)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 border-l-[3px] border-l-rose-500">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Outflows</div>
                      <div className="text-lg font-extrabold tabular-nums font-mono leading-tight" style={{ color: '#e11d48' }}>{cfFmt(outTot)}</div>
                      <div className="text-[10px] text-slate-400">all categories</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2" style={{ borderLeft: `3px solid ${net >= 0 ? '#3b6fd2' : '#c9820f'}` }}>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Net cashflow</div>
                      <div className="text-lg font-extrabold tabular-nums font-mono leading-tight" style={{ color: net >= 0 ? '#3b6fd2' : '#c9820f' }}>{cfFmt(net)}</div>
                      <div className="text-[10px] text-slate-400">{net >= 0 ? '▲ surplus' : '▼ deficit'}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 border-l-[3px] border-l-amber-500">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Transactions</div>
                      <div className="text-lg font-extrabold text-slate-800 tabular-nums leading-tight">{txCount}</div>
                      <div className="text-[10px] text-slate-400">this period</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Money in vs out */}
                  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Money in vs out</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1.5"><span className="text-slate-500">Inflows</span><span className="font-mono font-bold text-green-600">{cfFmt(inTot)}</span></div>
                        <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-green-500" style={{ width: Math.round(inTot / maxIO * 100) + '%' }} /></div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1.5"><span className="text-slate-500">Outflows</span><span className="font-mono font-bold text-rose-600">{cfFmt(outTot)}</span></div>
                        <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-rose-500" style={{ width: Math.round(outTot / maxIO * 100) + '%' }} /></div>
                      </div>
                    </div>
                    <div className="mt-4 rounded-xl px-4 py-3 text-white flex items-center justify-between" style={{ background: net >= 0 ? 'linear-gradient(135deg,#3b6fd2,#5a86e0)' : 'linear-gradient(135deg,#c9820f,#e0a53f)' }}>
                      <span className="font-semibold text-sm">Net Cashflow · {net >= 0 ? 'Surplus' : 'Deficit'}</span>
                      <span className="text-xl font-extrabold font-mono">{cfFmt(net)}</span>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-4 gap-2 text-center">
                      <div><div className="text-[10px] uppercase text-slate-400 font-bold">Setup</div><div className="font-mono text-sm font-bold text-violet-600">{cfFmt(cashflowSummary.invoice_setup_total)}</div></div>
                      <div><div className="text-[10px] uppercase text-slate-400 font-bold">AMC</div><div className="font-mono text-sm font-bold text-green-600">{cfFmt(cashflowSummary.invoice_amc_total)}</div></div>
                      <div><div className="text-[10px] uppercase text-slate-400 font-bold">Service</div><div className="font-mono text-sm font-bold text-orange-600">{cfFmt(cashflowSummary.invoice_service_total || 0)}</div></div>
                      <div><div className="text-[10px] uppercase text-slate-400 font-bold">Other</div><div className="font-mono text-sm font-bold text-emerald-600">{cfFmt((cfInflows?.other_inflows || []).reduce((s, i) => s + i.amount, 0))}</div></div>
                    </div>
                  </div>
                  {/* Outflow by category */}
                  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Outflow by category</h3>
                    {cats.length === 0 ? (
                      <div className="text-center py-10 text-slate-400"><Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">No outflows this period</p></div>
                    ) : (
                      <div className="space-y-3">
                        {cats.map(({ cat, total, count }, i) => (
                          <div key={cat.value} className="cursor-pointer group" onClick={() => { setCfOutCatFilter(cat.value); setCfOutTypeFilter('all'); setCashflowTab('outflows'); }} title={`${count} entr${count === 1 ? 'y' : 'ies'} · click to view`}>
                            <div className="flex justify-between items-baseline mb-1">
                              <span className="text-xs font-semibold text-slate-700 group-hover:text-rose-600 transition-colors">{cat.label}</span>
                              <span className="font-mono text-xs font-bold text-slate-700 tabular-nums">{cfFmt(total)}</span>
                            </div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: Math.max(4, Math.round(total / maxCat * 100)) + '%', background: OV_PALETTE[i % OV_PALETTE.length] }} /></div>
                            <div className="text-[10px] text-slate-400 mt-0.5">{Math.round(total / (outTot || 1) * 100)}% · {count} entr{count === 1 ? 'y' : 'ies'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                </div>
              );
            })()}

            {/* ── OUTFLOWS TAB (redesigned) ── */}
            {cashflowTab === 'outflows' && (() => {
              const BAR_PALETTE = ['#ef4444','#3b82f6','#f97316','#8b5cf6','#e11d48','#06b6d4','#f59e0b','#6366f1','#14b8a6','#0ea5e9','#84cc16','#64748b'];
              const periodTotal = cfOutflows.reduce((s,o)=>s+o.amount,0);
              const recTotal = cfOutflows.filter(o=>o.cost_type!=='capital').reduce((s,o)=>s+o.amount,0);
              const capTotal = cfOutflows.filter(o=>o.cost_type==='capital').reduce((s,o)=>s+o.amount,0);
              const recPct = periodTotal>0 ? Math.round(recTotal/periodTotal*100) : 0;
              const capPct = periodTotal>0 ? 100-recPct : 0;
              // category breakdown (period)
              const catAgg = {};
              cfOutflows.forEach(o => { catAgg[o.category] = (catAgg[o.category]||0) + o.amount; });
              const catRows = Object.entries(catAgg).map(([slug,total])=>({ slug, total, label: CF_CAT_MAP[slug]?.label || slug }))
                .sort((a,b)=>b.total-a.total);
              const topCat = catRows[0];
              const catMax = catRows.length ? catRows[0].total : 1;
              // filtered table
              const q = cfOutSearch.trim().toLowerCase();
              const filtered = cfOutflows.filter(o => {
                if (cfOutTypeFilter !== 'all' && (o.cost_type||'recurring') !== cfOutTypeFilter) return false;
                if (cfOutCatFilter !== 'all' && o.category !== cfOutCatFilter) return false;
                if (!q) return true;
                return (o.description||'').toLowerCase().includes(q) || (o.vendor_name||'').toLowerCase().includes(q) || (o.employee_name||'').toLowerCase().includes(q) || (o.reference_no||'').toLowerCase().includes(q) || (CF_CAT_MAP[o.category]?.label||o.category||'').toLowerCase().includes(q);
              });
              const filteredTotal = filtered.reduce((s,o)=>s+o.amount,0);
              return (
              <div>
                {/* KPI stripe tiles */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {[
                    { l:'TOTAL OUTFLOWS', v: cfFmt(periodTotal), h:`${cfOutflows.length} entries`, c:'#e11d48' },
                    { l:'RECURRING', v: cfFmt(recTotal), h:`${recPct}% · monthly costs`, c:'#64748b' },
                    { l:'CAPITAL', v: cfFmt(capTotal), h:`${capPct}% · one-time`, c:'#7c3aed' },
                    { l:'TOP CATEGORY', v: topCat ? topCat.label : '—', h: topCat ? `${cfFmt(topCat.total)} · ${Math.round(topCat.total/(periodTotal||1)*100)}%` : 'no data', c:'#c9820f', small:true },
                  ].map(t=>(
                    <div key={t.l} className="bg-white rounded-xl shadow-md border border-slate-200 px-3 py-2" style={{ borderLeft:`3px solid ${t.c}` }}>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t.l}</p>
                      <p className={`font-extrabold leading-tight ${t.small?'text-sm pt-0.5':'text-lg tabular-nums font-mono'}`} style={{ color:t.c }}>{t.v}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">{t.h}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-start">
                  {/* Left: donut + category breakdown */}
                  <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                    <div className="flex items-center gap-4 p-4 border-b border-slate-100">
                      <svg width="80" height="80" viewBox="0 0 42 42" className="shrink-0">
                        <circle cx="21" cy="21" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="6"/>
                        <circle cx="21" cy="21" r="15.9" fill="none" stroke="#e11d48" strokeWidth="6" strokeDasharray={`${recPct} ${100-recPct}`} strokeDashoffset="25" strokeLinecap="round"/>
                        <circle cx="21" cy="21" r="15.9" fill="none" stroke="#7c3aed" strokeWidth="6" strokeDasharray={`${capPct} ${100-capPct}`} strokeDashoffset={`${100-recPct+25}`} strokeLinecap="round"/>
                      </svg>
                      <div className="text-xs space-y-1.5">
                        <div className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-sm" style={{background:'#e11d48'}}/>Recurring <b className="text-slate-800">{recPct}%</b></div>
                        <div className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-sm" style={{background:'#7c3aed'}}/>Capital <b className="text-slate-800">{capPct}%</b></div>
                        <div className="text-slate-400">Cost structure</div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">By category</h3>
                      {catRows.length === 0 ? (
                        <div className="text-center py-6 text-slate-400"><Receipt className="w-9 h-9 mx-auto mb-2 opacity-30"/><p className="text-xs">No outflows this period</p></div>
                      ) : (
                        <div className="space-y-3">
                          {catRows.slice(0,7).map((r,i)=>(
                            <div key={r.slug} className="cursor-pointer" onClick={()=>setCfOutCatFilter(cfOutCatFilter===r.slug?'all':r.slug)}>
                              <div className="flex justify-between items-baseline mb-1">
                                <span className={`text-xs font-semibold ${cfOutCatFilter===r.slug?'text-rose-600':'text-slate-700'}`}>{r.label}</span>
                                <span className="text-[11px] font-bold font-mono tabular-nums text-rose-600">{cfFmt(r.total)}</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width:`${Math.max(4,r.total/catMax*100)}%`, background: BAR_PALETTE[i%BAR_PALETTE.length] }}/></div>
                              <div className="text-[10px] text-slate-400 mt-0.5">{Math.round(r.total/(periodTotal||1)*100)}%</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: filters + table */}
                  <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><ArrowDownCircle size={18} className="text-rose-500"/> All Outflows</h3>
                        <p className="text-slate-500 text-xs mt-0.5">{filtered.length} of {cfOutflows.length} · {cfFmt(filteredTotal)}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => downloadCSV(`outflows-${cashflowMonth || 'all'}.csv`, ['Date','Category','Cost Type','Description','Vendor/Employee','Reference','Amount'], filtered.map(o => [o.entry_date, CF_CAT_MAP[o.category]?.label || o.category, o.cost_type, o.description, o.vendor_name || o.employee_name || '', o.reference_no || '', o.amount]))}
                          className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 flex items-center gap-2 transition-all"><Download size={15}/> Export</button>
                        {canDo('cashflow','create') && <button onClick={() => { setCfAddType('outflow'); setEditingCfEntry(null); setCfOutflowForm(CF_EMPTY_OUT); setShowAddCfEntry(true); }}
                          className="text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-sm transition-all text-sm" style={{ background: 'linear-gradient(135deg,#d23b4e,#e8637a)' }}>
                          <Plus size={15}/> Add Outflow
                        </button>}
                      </div>
                    </div>

                    {/* toolbar: search + type chips + category dropdown */}
                    <div className="p-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[180px] relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15}/>
                        <input value={cfOutSearch} onChange={e=>setCfOutSearch(e.target.value)} placeholder="Search description, vendor, reference…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:border-red-400 outline-none"/>
                      </div>
                      <div className="flex gap-1.5">
                        {[['all','All types'],['recurring','Recurring'],['capital','Capital']].map(([v,l])=>(
                          <button key={v} onClick={()=>{ setCfOutTypeFilter(v); if (v!=='all' && cfOutCatFilter!=='all' && !(v==='capital'?cfCapitalCats:cfRecurringCats).some(c=>c.value===cfOutCatFilter)) setCfOutCatFilter('all'); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${cfOutTypeFilter===v?(v==='capital'?'bg-purple-50 text-purple-700 border-transparent':'bg-rose-50 text-rose-600 border-transparent'):'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{l}</button>
                        ))}
                      </div>
                      <select value={cfOutCatFilter} onChange={e=>setCfOutCatFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:border-red-400 outline-none max-w-[220px]">
                        <option value="all">All categories</option>
                        {(cfOutTypeFilter==='capital'?cfCapitalCats:cfOutTypeFilter==='recurring'?cfRecurringCats:CF_OUTFLOW_CATS).map(c=>(
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>

                    {filtered.length === 0 ? (
                      <div className="text-center py-16 text-slate-400"><Receipt className="w-16 h-16 mx-auto mb-4 opacity-30"/><p className="text-lg">No outflows found</p></div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-5 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Date</th>
                              <th className="px-5 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Category</th>
                              <th className="px-5 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Type</th>
                              <th className="px-5 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Description</th>
                              <th className="px-5 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Reference</th>
                              <th className="px-5 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Amount</th>
                              <th className="px-5 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {filtered.map(o => (
                              <tr key={o.id} className="hover:bg-slate-50 transition-all">
                                <td className="px-5 py-3.5 text-sm text-slate-600 whitespace-nowrap">{o.entry_date}</td>
                                <td className="px-5 py-3.5"><span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${CF_CAT_MAP[o.category]?.color || 'bg-slate-100 text-slate-600'}`}>{CF_CAT_MAP[o.category]?.label || o.category}</span></td>
                                <td className="px-5 py-3.5"><span className={`text-xs px-2 py-0.5 rounded font-semibold ${o.cost_type==='capital'?'bg-purple-100 text-purple-700':'bg-slate-100 text-slate-600'}`}>{o.cost_type}</span></td>
                                <td className="px-5 py-3.5 text-sm text-slate-800 font-medium max-w-xs">{o.description}{o.vendor_name&&<span className="text-xs text-slate-400 ml-2">({o.vendor_name})</span>}{o.employee_name&&<span className="text-xs text-slate-400 ml-2">({o.employee_name})</span>}</td>
                                <td className="px-5 py-3.5 text-sm text-slate-500">{o.reference_no||'-'}</td>
                                <td className="px-5 py-3.5 text-right font-bold text-red-700 font-mono tabular-nums">{cfFmt(o.amount)}</td>
                                <td className="px-5 py-3.5">
                                  <div className="flex gap-2 justify-center">
                                    {canDo('cashflow','edit') && <button onClick={() => handleEditCfEntry(o,'outflow')} className="text-slate-400 hover:text-blue-600 transition-all"><Edit2 size={15}/></button>}
                                    {canDo('cashflow','delete') && <button onClick={() => handleDeleteCfEntry(o.id,'outflow')} className="text-slate-400 hover:text-red-600 transition-all"><Trash2 size={15}/></button>}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                            <tr>
                              <td colSpan={5} className="px-5 py-3.5 text-sm font-bold text-slate-700">Total{(cfOutTypeFilter!=='all'||cfOutCatFilter!=='all'||q)?' (filtered)':''}</td>
                              <td className="px-5 py-3.5 text-right font-bold text-red-700 text-base font-mono tabular-nums">{cfFmt(filteredTotal)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })()}

            {/* ── INFLOWS TAB (redesigned) ── */}
            {cashflowTab === 'inflows' && (() => {
              const setupTot   = (cfInflows?.invoice_setup_payments   || []).reduce((s,p)=>s+p.amount,0);
              const amcTot     = (cfInflows?.invoice_amc_payments     || []).reduce((s,p)=>s+p.amount,0);
              const serviceTot = (cfInflows?.invoice_service_payments || []).reduce((s,p)=>s+p.amount,0);
              const otherTot   = (cfInflows?.other_inflows            || []).reduce((s,i)=>s+i.amount,0);
              const inTotal  = setupTot + amcTot + serviceTot + otherTot;
              const inCount  = (cfInflows?.invoice_setup_payments?.length||0) + (cfInflows?.invoice_amc_payments?.length||0) + (cfInflows?.invoice_service_payments?.length||0) + (cfInflows?.other_inflows?.length||0);
              const exportInflows = () => {
                const rows = [];
                (cfInflows?.invoice_setup_payments || []).forEach(p => rows.push([p.entry_date, 'Setup', p.customer_name, p.invoice_number, p.reference_no || '', p.amount]));
                (cfInflows?.invoice_amc_payments || []).forEach(p => rows.push([p.entry_date, 'AMC', p.customer_name, p.invoice_number, p.reference_no || '', p.amount]));
                (cfInflows?.invoice_service_payments || []).forEach(p => rows.push([p.entry_date, 'Service', p.customer_name, p.invoice_number, p.reference_no || '', p.amount]));
                (cfInflows?.other_inflows || []).forEach(i => rows.push([i.entry_date, 'Other', i.description || i.customer_name || '', '', i.reference_no || '', i.amount]));
                downloadCSV(`inflows-${cashflowMonth || 'all'}.csv`, ['Date', 'Type', 'Customer/Description', 'Invoice', 'Reference', 'Amount'], rows);
              };
              return (
              <div className="space-y-4">
                {/* KPI stripe tiles */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { l:'TOTAL INFLOWS', v: cfFmt(inTotal), h:`${inCount} payments`, c:'#059669' },
                    { l:'SETUP CHARGES', v: cfFmt(setupTot), h:`${cfInflows?.invoice_setup_payments?.length||0} · from system`, c:'#7c3aed' },
                    { l:'AMC PAYMENTS', v: cfFmt(amcTot), h:`${cfInflows?.invoice_amc_payments?.length||0} · from system`, c:'#16a34a' },
                    { l:'SERVICES', v: cfFmt(serviceTot), h:`${cfInflows?.invoice_service_payments?.length||0} · redo/removal/transfer`, c:'#ea580c' },
                    { l:'OTHER INFLOWS', v: cfFmt(otherTot), h:`${cfInflows?.other_inflows?.length||0} · manual`, c:'#0d9488' },
                  ].map(t=>(
                    <div key={t.l} className="bg-white rounded-xl shadow-md border border-slate-200 px-3 py-2" style={{ borderLeft:`3px solid ${t.c}` }}>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t.l}</p>
                      <p className="text-lg font-extrabold leading-tight tabular-nums font-mono" style={{ color:t.c }}>{t.v}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">{t.h}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={exportInflows} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 flex items-center gap-2 transition-all"><Download size={16}/> Export Excel</button>
                  {canDo('cashflow','create') && <button onClick={() => { setCfAddType('inflow'); setEditingCfEntry(null); setCfInflowForm(CF_EMPTY_IN); setShowAddCfEntry(true); }} className="text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-sm transition-all text-sm" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}><Plus size={16}/> Add Inflow</button>}
                </div>
                {/* Setup Charges (from system invoices) */}
                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><Receipt size={18} className="text-violet-500"/> Tracker Setup Charges <span className="text-[10px] font-bold uppercase bg-violet-50 text-violet-600 px-2 py-0.5 rounded-full">from system</span></h3>
                    <p className="text-slate-500 text-xs mt-0.5">{cfInflows.invoice_setup_payments.length} payments · Total {cfFmt(cfInflows.invoice_setup_payments.reduce((s,p)=>s+p.amount,0))}</p>
                  </div>
                  {cfInflows.invoice_setup_payments.length === 0 ? (
                    <div className="text-center py-10 text-slate-400"><Receipt className="w-12 h-12 mx-auto mb-3 opacity-30"/><p>No setup charge payments in {cashflowMonth}</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Customer</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Invoice</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Method</th>
                            <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {cfInflows.invoice_setup_payments.map(p => (
                            <tr key={p.id} className="hover:bg-slate-50">
                              <td className="px-5 py-3 text-sm text-slate-600">{p.entry_date}</td>
                              <td className="px-5 py-3 text-sm font-medium text-slate-800">{p.customer_name}</td>
                              <td className="px-5 py-3"><span className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded font-medium">{p.invoice_number}</span></td>
                              <td className="px-5 py-3 text-sm text-slate-500">{p.reference_no||'-'}</td>
                              <td className="px-5 py-3 text-right font-bold text-violet-700">{cfFmt(p.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr>
                            <td colSpan={4} className="px-5 py-3 text-sm font-bold text-slate-700">Total Setup Charges</td>
                            <td className="px-5 py-3 text-right font-bold text-violet-700 text-base">{cfFmt(cfInflows.invoice_setup_payments.reduce((s,p)=>s+p.amount,0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* AMC Payments (from system invoices) */}
                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><Receipt size={18} className="text-green-500"/> AMC Payments <span className="text-[10px] font-bold uppercase bg-green-50 text-green-600 px-2 py-0.5 rounded-full">from system</span></h3>
                    <p className="text-slate-500 text-xs mt-0.5">{cfInflows.invoice_amc_payments.length} payments · Total {cfFmt(cfInflows.invoice_amc_payments.reduce((s,p)=>s+p.amount,0))}</p>
                  </div>
                  {cfInflows.invoice_amc_payments.length === 0 ? (
                    <div className="text-center py-10 text-slate-400"><Receipt className="w-12 h-12 mx-auto mb-3 opacity-30"/><p>No AMC payments in {cashflowMonth}</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Customer</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Invoice</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Method</th>
                            <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {cfInflows.invoice_amc_payments.map(p => (
                            <tr key={p.id} className="hover:bg-slate-50">
                              <td className="px-5 py-3 text-sm text-slate-600">{p.entry_date}</td>
                              <td className="px-5 py-3 text-sm font-medium text-slate-800">{p.customer_name}</td>
                              <td className="px-5 py-3"><span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">{p.invoice_number}</span></td>
                              <td className="px-5 py-3 text-sm text-slate-500">{p.reference_no||'-'}</td>
                              <td className="px-5 py-3 text-right font-bold text-green-700">{cfFmt(p.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr>
                            <td colSpan={4} className="px-5 py-3 text-sm font-bold text-slate-700">Total AMC Payments</td>
                            <td className="px-5 py-3 text-right font-bold text-green-700 text-base">{cfFmt(cfInflows.invoice_amc_payments.reduce((s,p)=>s+p.amount,0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Service Payments (redo / removal / transfer / ownership — from system) */}
                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><Wrench size={18} className="text-orange-500"/> Service Payments <span className="text-[10px] font-bold uppercase bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">redo / removal / transfer</span></h3>
                    <p className="text-slate-500 text-xs mt-0.5">{(cfInflows.invoice_service_payments||[]).length} payments · Total {cfFmt((cfInflows.invoice_service_payments||[]).reduce((s,p)=>s+p.amount,0))}</p>
                  </div>
                  {(cfInflows.invoice_service_payments||[]).length === 0 ? (
                    <div className="text-center py-10 text-slate-400"><Wrench className="w-12 h-12 mx-auto mb-3 opacity-30"/><p>No service payments in {cashflowMonth}</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Customer</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Invoice</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Method</th>
                            <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(cfInflows.invoice_service_payments||[]).map(p => (
                            <tr key={p.id} className="hover:bg-slate-50">
                              <td className="px-5 py-3 text-sm text-slate-600">{p.entry_date}</td>
                              <td className="px-5 py-3 text-sm font-medium text-slate-800">{p.customer_name}</td>
                              <td className="px-5 py-3"><span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded font-medium">{p.invoice_number}</span></td>
                              <td className="px-5 py-3 text-sm text-slate-500">{p.reference_no||'-'}</td>
                              <td className="px-5 py-3 text-right font-bold text-orange-700">{cfFmt(p.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr>
                            <td colSpan={4} className="px-5 py-3 text-sm font-bold text-slate-700">Total Service Payments</td>
                            <td className="px-5 py-3 text-right font-bold text-orange-700 text-base">{cfFmt((cfInflows.invoice_service_payments||[]).reduce((s,p)=>s+p.amount,0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* Other Inflows (manual entry) */}
                <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><ArrowUpCircle size={18} className="text-emerald-500"/> Other Inflows</h3>
                      <p className="text-slate-500 text-xs mt-0.5">{cfInflows.other_inflows.length} entries · Total {cfFmt(cfInflows.other_inflows.reduce((s,i)=>s+i.amount,0))}</p>
                    </div>
                    {canDo('cashflow','create') && <button onClick={() => { setCfAddType('inflow'); setEditingCfEntry(null); setCfInflowForm(CF_EMPTY_IN); setShowAddCfEntry(true); }}
                      className="text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-sm transition-all text-sm" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}>
                      <Plus size={16}/> Add Inflow
                    </button>}
                  </div>
                  {cfInflows.other_inflows.length === 0 ? (
                    <div className="text-center py-10 text-slate-400"><ArrowUpCircle className="w-12 h-12 mx-auto mb-3 opacity-30"/><p>No other inflows in {cashflowMonth}</p></div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Category</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Description</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Customer</th>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Reference</th>
                            <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Amount</th>
                            <th className="px-5 py-3 text-center text-xs font-bold text-slate-600 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {cfInflows.other_inflows.map(i => (
                            <tr key={i.id} className="hover:bg-slate-50">
                              <td className="px-5 py-3 text-sm text-slate-600">{i.entry_date}</td>
                              <td className="px-5 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${CF_CAT_MAP[i.category]?.color||'bg-slate-100 text-slate-600'}`}>{CF_CAT_MAP[i.category]?.label||i.category}</span></td>
                              <td className="px-5 py-3 text-sm font-medium text-slate-800">{i.description}</td>
                              <td className="px-5 py-3 text-sm text-slate-500">{i.customer_name||'-'}</td>
                              <td className="px-5 py-3 text-sm text-slate-500">{i.reference_no||'-'}</td>
                              <td className="px-5 py-3 text-right font-bold text-emerald-700">{cfFmt(i.amount)}</td>
                              <td className="px-5 py-3">
                                <div className="flex gap-2 justify-center">
                                  {canDo('cashflow','edit') && <button onClick={() => handleEditCfEntry(i,'inflow')} className="text-slate-400 hover:text-blue-600 transition-all"><Edit2 size={15}/></button>}
                                  {canDo('cashflow','delete') && <button onClick={() => handleDeleteCfEntry(i.id,'inflow')} className="text-slate-400 hover:text-red-600 transition-all"><Trash2 size={15}/></button>}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr>
                            <td colSpan={5} className="px-5 py-3 text-sm font-bold text-slate-700">Total Other Inflows</td>
                            <td className="px-5 py-3 text-right font-bold text-emerald-700 text-base">{cfFmt(cfInflows.other_inflows.reduce((s,i)=>s+i.amount,0))}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              );
            })()}

            {/* ── ADVANCES / PETTY CASH TAB ── */}
            {cashflowTab === 'advances' && (() => {
              const inPeriod = (d) => {
                if (!d) return false;
                if (cashflowDateMode === 'all') return true;
                if (cashflowDateMode === 'custom') return (!cashflowDateFrom || d >= cashflowDateFrom) && (!cashflowDateTo || d <= cashflowDateTo);
                return String(d).slice(0,7) === cashflowMonth;
              };
              const balances = cfAdvances?.balances || [];
              const txns = cfAdvances?.transactions || [];
              const periodTx = txns.filter(t => inPeriod(t.txn_date));
              const issued   = periodTx.filter(t=>t.txn_type==='issue').reduce((s,t)=>s+t.amount,0);
              const adjusted = periodTx.filter(t=>t.txn_type==='adjustment').reduce((s,t)=>s+t.amount,0);
              const returned = periodTx.filter(t=>t.txn_type==='return').reduce((s,t)=>s+t.amount,0);
              const outstanding = balances.reduce((s,b)=>s+(b.outstanding||0),0);
              const netCash = (cashflowSummary?.net_cashflow || 0) - outstanding;
              const initials = (nm) => (nm||'?').split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase();
              const canWrite = canDo('cashflow','create');
              return (
              <div className="space-y-4">
                {/* KPI tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 border-l-[3px] border-l-blue-500">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Issued</div>
                    <div className="text-lg font-extrabold text-blue-600 tabular-nums font-mono leading-tight">{cfFmt(issued)}</div>
                    <div className="text-[10px] text-slate-400">this period</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 border-l-[3px] border-l-rose-500">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Adjusted (expensed)</div>
                    <div className="text-lg font-extrabold tabular-nums font-mono leading-tight" style={{color:'#e11d48'}}>{cfFmt(adjusted)}</div>
                    <div className="text-[10px] text-slate-400">this period</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 border-l-[3px] border-l-emerald-500">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Returned</div>
                    <div className="text-lg font-extrabold text-emerald-600 tabular-nums font-mono leading-tight">{cfFmt(returned)}</div>
                    <div className="text-[10px] text-slate-400">this period</div>
                  </div>
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 border-l-[3px] border-l-amber-500">
                    <div className="text-[10px] uppercase tracking-wide text-amber-600 font-bold">Outstanding with staff</div>
                    <div className="text-lg font-extrabold text-amber-700 tabular-nums font-mono leading-tight">{cfFmt(outstanding)}</div>
                    <div className="text-[10px] text-amber-600">left the cash box, not an expense yet</div>
                  </div>
                </div>

                {/* Net cash note */}
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <Info size={14} className="text-slate-400 shrink-0" />
                  <span>Real cash in hand = Net cashflow ({cfFmt(cashflowSummary?.net_cashflow || 0)}) − Outstanding advances ({cfFmt(outstanding)}) = <b className="text-slate-700">{cfFmt(netCash)}</b></span>
                </div>

                {/* Header + Issue button */}
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-slate-700">Staff accounts</h3>
                  {canWrite && (
                    <button onClick={() => openAdvModal('issue', null)} className="px-3.5 py-2 rounded-lg text-white font-semibold flex items-center gap-2 shadow-sm text-sm" style={{ background: 'linear-gradient(135deg,#3b6fd2,#5a86e0)' }}>
                      <Plus size={16}/> Issue Cash
                    </button>
                  )}
                </div>

                {/* Employee cards */}
                {balances.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-slate-200 text-slate-400">
                    <Wallet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No petty cash has been issued yet.</p>
                    {canWrite && <button onClick={() => openAdvModal('issue', null)} className="mt-3 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold">Issue cash</button>}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {balances.map(b => {
                      const out = b.outstanding || 0;
                      const color = out > 0 ? '#b45309' : out < 0 ? '#e11d48' : '#16a34a';
                      return (
                      <div key={b.employee_id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full grid place-items-center font-bold text-sm bg-blue-50 text-blue-700 shrink-0">{initials(b.employee_name)}</div>
                          <div className="min-w-0">
                            <div className="text-sm font-bold text-slate-800 truncate">{b.employee_name}</div>
                            <div className="text-xs text-slate-400 truncate">{b.designation || 'Employee'}</div>
                          </div>
                        </div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Outstanding</div>
                        <div className="text-2xl font-extrabold font-mono tabular-nums leading-tight" style={{ color }}>{cfFmt(out)}</div>
                        <div className="text-[11px] text-slate-400 mt-1 mb-3">Issued {cfFmt(b.issued)} · Adjusted {cfFmt(b.adjusted)}{b.returned ? ` · Returned ${cfFmt(b.returned)}` : ''}</div>
                        <div className="flex gap-1.5">
                          {canWrite && <button onClick={() => openAdvModal('issue', b)} className="flex-1 px-2 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold">Issue</button>}
                          {canWrite && <button onClick={() => openAdvModal('adjust', b)} className="flex-1 px-2 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold">Adjust</button>}
                          <button onClick={() => openAdvModal('ledger', b)} className="flex-1 px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold">Ledger</button>
                        </div>
                        {canWrite && out > 0 && <button onClick={() => openAdvModal('return', b)} className="w-full mt-1.5 px-2 py-1 rounded-lg text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold">Return cash</button>}
                      </div>
                    );})}
                  </div>
                )}
              </div>
              );
            })()}

            {/* ── ADVANCE: ISSUE / RETURN MODAL ── */}
            {(advModal === 'issue' || advModal === 'return') && (() => {
              const isIssue = advModal === 'issue';
              return (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                  <div className={`p-5 rounded-t-2xl ${isIssue ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : 'bg-gradient-to-r from-emerald-600 to-teal-600'}`}>
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Wallet size={20}/> {isIssue ? 'Issue cash' : 'Return cash'}
                      </h3>
                      <button onClick={() => { setAdvModal(null); setAdvForm(ADV_EMPTY); }} className="text-white/70 hover:text-white"><X size={22}/></button>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Employee *</label>
                      <EmployeeSearchSelect employees={employees} value={advForm.employeeId}
                        onChange={(id)=>setAdvForm({...advForm,employeeId:id})} activeOnly placeholder="Search employee by name or code…" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Amount (Rs.) *</label>
                        <input type="number" min="0" step="1" placeholder="0" value={advForm.amount} onChange={e=>setAdvForm({...advForm,amount:e.target.value})}
                          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-blue-400 outline-none text-sm"/>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Date *</label>
                        <input type="date" value={advForm.entryDate} onChange={e=>setAdvForm({...advForm,entryDate:e.target.value})}
                          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-blue-400 outline-none text-sm"/>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Note {isIssue ? '' : '/ reference'}</label>
                      <input type="text" placeholder={isIssue ? 'e.g. site visits — Lahore' : 'e.g. unused cash wapas'} value={advForm.notes} onChange={e=>setAdvForm({...advForm,notes:e.target.value})}
                        className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-blue-400 outline-none text-sm"/>
                    </div>
                    {isIssue && (
                      <div className="flex items-start gap-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <Info size={13} className="mt-0.5 shrink-0"/>
                        <span>Not an expense — added to the employee's advance balance and recorded as cash out of the cash box.</span>
                      </div>
                    )}
                    <div className="flex gap-3 pt-1">
                      <button onClick={() => { setAdvModal(null); setAdvForm(ADV_EMPTY); }} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50">Cancel</button>
                      <button onClick={() => handleAdvIssueReturn(isIssue ? 'issue' : 'return')} className={`flex-1 py-3 rounded-xl text-white font-semibold shadow-md flex items-center justify-center gap-2 ${isIssue ? 'bg-blue-500 hover:bg-blue-600' : 'bg-emerald-500 hover:bg-emerald-600'}`}>
                        <Save size={16}/> {isIssue ? 'Issue' : 'Return'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* ── ADVANCE: ADJUSTMENT MODAL ── */}
            {advModal === 'adjust' && (() => {
              const emp = balancesFor(cfAdvances, advForm.employeeId);
              const total = advAdjustLines.reduce((s,l)=> s + (parseFloat(l.amount)||0), 0);
              const newOut = (emp?.outstanding || 0) - total;
              return (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
                  <div className="p-5 rounded-t-2xl bg-gradient-to-r from-rose-600 to-red-600">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2"><Receipt size={20}/> Adjust — {emp?.employee_name || 'employee'}</h3>
                      <button onClick={() => { setAdvModal(null); setAdvForm(ADV_EMPTY); setAdvAdjustLines([]); }} className="text-white/70 hover:text-white"><X size={22}/></button>
                    </div>
                    {emp && <p className="text-white/80 text-xs mt-1">Advance outstanding: <b>{cfFmt(emp.outstanding)}</b></p>}
                  </div>
                  <div className="p-4 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Employee *</label>
                        <EmployeeSearchSelect employees={employees} value={advForm.employeeId}
                          onChange={(id)=>setAdvForm({...advForm,employeeId:id})} activeOnly placeholder="Search employee by name or code…" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Date *</label>
                        <input type="date" value={advForm.entryDate} onChange={e=>setAdvForm({...advForm,entryDate:e.target.value})}
                          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-rose-400 outline-none text-sm"/>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-slate-600 uppercase">Expense lines (which category it was spent on)</label>
                        <button type="button" onClick={()=>setAdvAdjustLines([...advAdjustLines, { category: cfRecurringCats[0]?.value || CF_OUTFLOW_CATS[0]?.value || '', costType:'recurring', description:'', amount:'' }])} className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 flex items-center gap-1"><Plus size={12}/> Add line</button>
                      </div>
                      <div className="space-y-2">
                        {advAdjustLines.map((ln, i) => {
                          const catList = ln.costType === 'capital' ? cfCapitalCats : cfRecurringCats;
                          return (
                          <div key={i} className="grid grid-cols-[90px_1fr_110px_28px] gap-2 items-center">
                            <select value={ln.costType} onChange={e=>{ const ct=e.target.value; const list=ct==='capital'?cfCapitalCats:cfRecurringCats; const valid=list.some(c=>c.value===ln.category); const nl=[...advAdjustLines]; nl[i]={...ln,costType:ct,category:valid?ln.category:(list[0]?.value||'')}; setAdvAdjustLines(nl); }}
                              className="px-2 py-2 rounded-lg border-2 border-slate-200 focus:border-rose-400 outline-none text-xs bg-white">
                              <option value="recurring">Recurring</option>
                              <option value="capital">Capital</option>
                            </select>
                            <select value={ln.category} onChange={e=>{ const nl=[...advAdjustLines]; nl[i]={...ln,category:e.target.value}; setAdvAdjustLines(nl); }}
                              className="px-2 py-2 rounded-lg border-2 border-slate-200 focus:border-rose-400 outline-none text-xs bg-white">
                              {catList.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                            <input type="number" min="0" step="1" placeholder="Amount" value={ln.amount} onChange={e=>{ const nl=[...advAdjustLines]; nl[i]={...ln,amount:e.target.value}; setAdvAdjustLines(nl); }}
                              className="px-2 py-2 rounded-lg border-2 border-slate-200 focus:border-rose-400 outline-none text-xs"/>
                            <button type="button" onClick={()=>setAdvAdjustLines(advAdjustLines.filter((_,idx)=>idx!==i))} className="text-slate-400 hover:text-rose-500 grid place-items-center"><X size={16}/></button>
                          </div>
                        );})}
                      </div>
                      <input type="text" placeholder="Detail / note (optional) — e.g. bill no, purpose" value={advAdjustLines[0]?.description || ''} onChange={e=>{ const nl=[...advAdjustLines]; if(nl[0]) nl[0]={...nl[0],description:e.target.value}; setAdvAdjustLines(nl); }}
                        className="w-full mt-2 px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-rose-400 outline-none text-xs"/>
                    </div>

                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Adjusting</span><span className="font-bold font-mono text-rose-600">{cfFmt(total)}</span></div>
                      {emp && <div className="flex justify-between"><span className="text-slate-500">New outstanding</span><span className="font-bold font-mono" style={{color: newOut < 0 ? '#e11d48' : '#b45309'}}>{cfFmt(newOut)}</span></div>}
                      {emp && newOut < 0 && <div className="text-[11px] text-rose-600">⚠ Adjustment is more than the outstanding amount — please check.</div>}
                    </div>
                    <div className="flex items-start gap-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                      <Check size={13} className="mt-0.5 shrink-0"/>
                      <span>Each line becomes a categorized outflow and shows up in Outflows and reports automatically.</span>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button onClick={() => { setAdvModal(null); setAdvForm(ADV_EMPTY); setAdvAdjustLines([]); }} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50">Cancel</button>
                      <button onClick={handleAdvAdjust} className="flex-1 py-3 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-semibold shadow-md flex items-center justify-center gap-2"><Save size={16}/> Save adjustment</button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* ── ADVANCE: LEDGER MODAL ── */}
            {advModal === 'ledger' && advLedgerEmp && (() => {
              const rows = (cfAdvances?.transactions || []).filter(t => t.employee_id === advLedgerEmp.employee_id);
              const chrono = [...rows].sort((a,b)=> (a.txn_date<b.txn_date?-1:a.txn_date>b.txn_date?1:a.id-b.id));
              let run = 0; const withBal = chrono.map(t => { run += t.txn_type==='issue' ? t.amount : -t.amount; return { ...t, balance: run }; }).reverse();
              return (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
                  <div className="p-5 border-b border-slate-100 flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">{advLedgerEmp.employee_name} — ledger</h3>
                      <p className="text-xs text-slate-400">Outstanding: <b className="text-amber-700">{cfFmt(advLedgerEmp.outstanding || 0)}</b></p>
                    </div>
                    <button onClick={() => { setAdvModal(null); setAdvLedgerEmp(null); }} className="text-slate-400 hover:text-slate-700"><X size={22}/></button>
                  </div>
                  <div className="overflow-y-auto">
                    {withBal.length === 0 ? (
                      <div className="text-center py-10 text-slate-400 text-sm">No transactions.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs sticky top-0">
                          <tr>
                            <th className="px-4 py-2.5 text-left font-semibold">Date</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Type</th>
                            <th className="px-4 py-2.5 text-left font-semibold">Detail</th>
                            <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                            <th className="px-4 py-2.5 text-right font-semibold">Balance</th>
                            <th className="px-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {withBal.map(t => {
                            const badge = t.txn_type==='issue' ? 'bg-blue-100 text-blue-700' : t.txn_type==='adjustment' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700';
                            const sign = t.txn_type==='issue' ? '+' : '−';
                            const amtColor = t.txn_type==='issue' ? 'text-blue-600' : t.txn_type==='adjustment' ? 'text-rose-600' : 'text-emerald-600';
                            const detail = t.txn_type==='adjustment' ? (CF_CAT_MAP[t.category]?.label || t.category || '') + (t.description ? ` · ${t.description}` : '') : (t.notes || t.reference_no || '');
                            return (
                            <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                              <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{t.txn_date}</td>
                              <td className="px-4 py-2.5"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold capitalize ${badge}`}>{t.txn_type}</span></td>
                              <td className="px-4 py-2.5 text-slate-600">{detail || '—'}</td>
                              <td className={`px-4 py-2.5 text-right font-mono font-semibold ${amtColor}`}>{sign}{cfFmt(t.amount).replace('Rs. ','')}</td>
                              <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-700">{cfFmt(t.balance).replace('Rs. ','')}</td>
                              <td className="px-2">{canDo('cashflow','create') && <button onClick={()=>handleDeleteAdvance(t.id)} className="text-slate-300 hover:text-rose-500"><Trash2 size={14}/></button>}</td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                    {canDo('cashflow','create') && <button onClick={()=>openAdvModal('adjust', advLedgerEmp)} className="px-4 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold text-sm">Adjust</button>}
                    {canDo('cashflow','create') && <button onClick={()=>openAdvModal('issue', advLedgerEmp)} className="px-4 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold text-sm">Issue</button>}
                    <button onClick={() => { setAdvModal(null); setAdvLedgerEmp(null); }} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm">Close</button>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* ── MANAGE CATEGORIES MODAL ── */}
            {showCfCatModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
                  <div className="p-5 border-b border-slate-100">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">Manage Categories</h3>
                        <p className="text-slate-500 text-xs mt-0.5">{cfCatModalFlow==='outflow' ? 'Define / edit Recurring and Capital categories separately' : 'Define / edit inflow (income) categories'}</p>
                      </div>
                      <button onClick={()=>{ setShowCfCatModal(false); setCfCatEdit(null); }} className="text-slate-400 hover:text-slate-700"><X size={22}/></button>
                    </div>
                    {/* Flow toggle */}
                    <div className="flex bg-slate-100 rounded-xl p-1 gap-1 mt-3 w-fit">
                      {[['outflow','Outflow','#e11d48'],['inflow','Inflow','#059669']].map(([f,l,c])=>(
                        <button key={f} onClick={()=>{ setCfCatModalFlow(f); setCfCatEdit(null); }} className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${cfCatModalFlow===f?'bg-white shadow-sm':'text-slate-500 hover:text-slate-700'}`} style={cfCatModalFlow===f?{color:c}:{}}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className={`grid grid-cols-1 overflow-y-auto ${cfCatModalFlow==='outflow'?'sm:grid-cols-2':''}`}>
                    {(cfCatModalFlow==='outflow'
                      ? [
                          { title:'Recurring', accent:'text-rose-600', btn:'#e11d48', list:cfRecurringCats, draft:cfCatNewRec, setDraft:setCfCatNewRec, flow:'outflow', costType:'recurring' },
                          { title:'Capital',   accent:'text-purple-600', btn:'#7c3aed', list:cfCapitalCats, draft:cfCatNewCap, setDraft:setCfCatNewCap, flow:'outflow', costType:'capital' },
                        ]
                      : [
                          { title:'Inflow', accent:'text-emerald-600', btn:'#059669', list:CF_INFLOW_CATS, draft:cfCatNewIn, setDraft:setCfCatNewIn, flow:'inflow', costType:null },
                        ]
                    ).map((col,ci) => (
                      <div key={col.title} className={`p-4 ${ci>0?'border-t sm:border-t-0 sm:border-l border-slate-100':''}`}>
                        <div className={`text-[11px] font-extrabold uppercase tracking-wider mb-3 ${col.accent}`}>{col.title} <span className="text-slate-400 font-bold">· {col.list.length}</span></div>
                        <div className="space-y-2">
                          {col.list.map(c => (
                            <div key={c.value} className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-slate-200 bg-slate-50">
                              <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${(c.color||'').replace(/text-[a-z]+-\d+/,'')}`}/>
                              {cfCatEdit && c.id != null && cfCatEdit.id === c.id ? (
                                <>
                                  <input autoFocus value={cfCatEdit.label} onChange={e=>setCfCatEdit({...cfCatEdit,label:e.target.value})} onKeyDown={e=>{ if(e.key==='Enter'){ handleUpdateCfCategory(c.id, cfCatEdit.label, c.color, col.flow, col.costType).then(ok=>{ if(ok) setCfCatEdit(null); }); } }} className="flex-1 min-w-0 px-2 py-1 rounded border border-slate-300 text-sm outline-none focus:border-red-400"/>
                                  <button onClick={()=>handleUpdateCfCategory(c.id, cfCatEdit.label, c.color, col.flow, col.costType).then(ok=>{ if(ok) setCfCatEdit(null); })} className="text-green-600 hover:text-green-700 shrink-0"><Check size={16}/></button>
                                  <button onClick={()=>setCfCatEdit(null)} className="text-slate-400 hover:text-slate-600 shrink-0"><X size={16}/></button>
                                </>
                              ) : (
                                <>
                                  <span className="flex-1 min-w-0 text-sm font-medium text-slate-700 truncate">{c.label}</span>
                                  {c.id != null && <button onClick={()=>setCfCatEdit({ id:c.id, label:c.label })} className="text-slate-400 hover:text-blue-600 shrink-0"><Edit2 size={14}/></button>}
                                  {c.id != null && <button onClick={()=>handleDeleteCfCategory(c.id)} className="text-slate-400 hover:text-red-600 shrink-0"><Trash2 size={14}/></button>}
                                </>
                              )}
                            </div>
                          ))}
                          {col.list.length === 0 && <p className="text-xs text-slate-400 py-2">No categories yet.</p>}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <input value={col.draft} onChange={e=>col.setDraft(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter' && col.draft.trim()){ handleAddCfCategory(col.flow, col.costType, col.draft.trim(), CF_CAT_COLORS[col.list.length % CF_CAT_COLORS.length]).then(ok=>{ if(ok) col.setDraft(''); }); } }} placeholder={`Nayi ${col.title.toLowerCase()} category…`} className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-sm outline-none focus:border-red-400 bg-transparent"/>
                          <button onClick={()=>{ if(col.draft.trim()){ handleAddCfCategory(col.flow, col.costType, col.draft.trim(), CF_CAT_COLORS[col.list.length % CF_CAT_COLORS.length]).then(ok=>{ if(ok) col.setDraft(''); }); } }} className="px-3 rounded-lg text-white text-sm font-bold shrink-0" style={{ background: col.btn }}>Add</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-slate-100 flex justify-between items-center">
                    <p className="text-[11px] text-slate-400">A category that is used by any entry cannot be deleted.</p>
                    <button onClick={()=>{ setShowCfCatModal(false); setCfCatEdit(null); }} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm">Done</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── ADD / EDIT MODAL ── */}
            {showAddCfEntry && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                  <div className={`p-5 rounded-t-2xl ${cfAddType==='outflow'?'bg-gradient-to-r from-red-600 to-red-700':'bg-gradient-to-r from-emerald-600 to-teal-600'}`}>
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        {cfAddType==='outflow'?<ArrowDownCircle size={22}/>:<ArrowUpCircle size={22}/>}
                        {editingCfEntry ? 'Edit' : 'Add'} {cfAddType==='outflow'?'Outflow':'Inflow'}
                      </h3>
                      <button onClick={() => { setShowAddCfEntry(false); setEditingCfEntry(null); }} className="text-white/70 hover:text-white transition-all"><X size={22}/></button>
                    </div>
                  </div>

                  <div className="p-4 space-y-4">
                    {cfAddType === 'outflow' ? (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Date *</label>
                            <input type="date" value={cfOutflowForm.entryDate} onChange={e=>setCfOutflowForm({...cfOutflowForm,entryDate:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm"/>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Cost Type *</label>
                            <select value={cfOutflowForm.costType} onChange={e=>{const ct=e.target.value; const list=ct==='capital'?cfCapitalCats:cfRecurringCats; const stillValid=list.some(c=>c.value===cfOutflowForm.category); setCfOutflowForm({...cfOutflowForm,costType:ct,category:stillValid?cfOutflowForm.category:(list[0]?.value||'')});}}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm bg-white">
                              <option value="recurring">Recurring</option>
                              <option value="capital">Capital</option>
                            </select>
                          </div>
                        </div>
                        {cfOutflowForm.costType === 'recurring' && (
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Frequency *</label>
                            <select value={cfOutflowForm.frequency || 'monthly'} onChange={e=>setCfOutflowForm({...cfOutflowForm,frequency:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm bg-white">
                              <option value="monthly">Monthly</option>
                              <option value="fortnightly">Fortnightly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="annually">Annually</option>
                            </select>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-semibold text-slate-600 uppercase">Category * <span className="text-slate-400 normal-case font-medium">({cfOutflowForm.costType})</span></label>
                            {canDo('cashflow','create') && <button type="button" onClick={()=>{ setCfCatModalFlow('outflow'); setShowCfCatModal(true); }} className="text-[11px] font-semibold text-red-600 hover:text-red-700 flex items-center gap-1"><Settings size={12}/> Manage</button>}
                          </div>
                          <select value={cfOutflowForm.category} onChange={e=>setCfOutflowForm({...cfOutflowForm,category:e.target.value})}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm bg-white">
                            {(cfOutflowForm.costType==='capital'?cfCapitalCats:cfRecurringCats).map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Description *</label>
                          <input type="text" placeholder="e.g. Office rent April 2026" value={cfOutflowForm.description} onChange={e=>setCfOutflowForm({...cfOutflowForm,description:e.target.value})}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm"/>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Amount (Rs.) *</label>
                            <input type="number" min="0" step="1" placeholder="0" value={cfOutflowForm.amount} onChange={e=>setCfOutflowForm({...cfOutflowForm,amount:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm"/>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Reference No.</label>
                            <input type="text" placeholder="Cheque / bill no." value={cfOutflowForm.referenceNo} onChange={e=>setCfOutflowForm({...cfOutflowForm,referenceNo:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm"/>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Notes</label>
                          <textarea rows={2} placeholder="Optional notes..." value={cfOutflowForm.notes} onChange={e=>setCfOutflowForm({...cfOutflowForm,notes:e.target.value})}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm resize-none"/>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button onClick={() => { setShowAddCfEntry(false); setEditingCfEntry(null); }} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                          <button onClick={handleSaveCfOutflow} className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold shadow-md shadow-red-200 transition-all flex items-center justify-center gap-2">
                            <Save size={16}/> {editingCfEntry?'Update':'Save'} Outflow
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Date *</label>
                            <input type="date" value={cfInflowForm.entryDate} onChange={e=>setCfInflowForm({...cfInflowForm,entryDate:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-400 outline-none text-sm"/>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-1.5">
                              <label className="block text-xs font-semibold text-slate-600 uppercase">Category *</label>
                              {canDo('cashflow','create') && <button type="button" onClick={()=>{ setCfCatModalFlow('inflow'); setShowCfCatModal(true); }} className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"><Settings size={12}/> Manage</button>}
                            </div>
                            <select value={cfInflowForm.category} onChange={e=>setCfInflowForm({...cfInflowForm,category:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-400 outline-none text-sm bg-white">
                              {CF_INFLOW_CATS.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Description *</label>
                          <input type="text" placeholder="e.g. Website development for client" value={cfInflowForm.description} onChange={e=>setCfInflowForm({...cfInflowForm,description:e.target.value})}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-400 outline-none text-sm"/>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Amount (Rs.) *</label>
                            <input type="number" min="0" step="1" placeholder="0" value={cfInflowForm.amount} onChange={e=>setCfInflowForm({...cfInflowForm,amount:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-400 outline-none text-sm"/>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Reference No.</label>
                            <input type="text" placeholder="Receipt / ref no." value={cfInflowForm.referenceNo} onChange={e=>setCfInflowForm({...cfInflowForm,referenceNo:e.target.value})}
                              className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-400 outline-none text-sm"/>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase">Notes</label>
                          <textarea rows={2} placeholder="Optional notes..." value={cfInflowForm.notes} onChange={e=>setCfInflowForm({...cfInflowForm,notes:e.target.value})}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-400 outline-none text-sm resize-none"/>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button onClick={() => { setShowAddCfEntry(false); setEditingCfEntry(null); }} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                          <button onClick={handleSaveCfInflow} className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-md shadow-emerald-200 transition-all flex items-center justify-center gap-2">
                            <Save size={16}/> {editingCfEntry?'Update':'Save'} Inflow
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invoices Module */}
        {activeModule === 'invoices' && (
          <div>
            {/* Compact money KPIs — always visible */}
            {(() => {
              // Cancelled invoices are void — keep them out of billed / outstanding.
              const isLive = (inv) => getInvoiceStatus(inv) !== 'CANCELLED';
              const billed = invoices.filter(isLive).reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0);
              const collected = invoices.reduce((s, inv) => s + (inv.payments?.reduce((ps, p) => ps + (parseFloat(p.amount) || 0), 0) || 0), 0);
              const balOf = (inv) => {
                const paid = inv.payments?.reduce((ps, p) => ps + (parseFloat(p.amount) || 0), 0) || 0;
                const disc = parseFloat(inv.discount || 0);
                return Math.max(0, (parseFloat(inv.amount) || 0) - disc - paid);
              };
              const outstanding = invoices.reduce((s, inv) => ['PAID', 'CANCELLED'].includes(getInvoiceStatus(inv)) ? s : s + balOf(inv), 0);
              const overdue = invoices.filter(inv => getInvoiceStatus(inv).includes('OVERDUE'));
              const overdueAmt = overdue.reduce((s, inv) => s + balOf(inv), 0);
              const collPct = billed > 0 ? Math.round(collected / billed * 100) : 0;
              const fmt = (n) => 'Rs ' + Math.round(n).toLocaleString();
              const tiles = [
                { l: 'Total Billed', v: fmt(billed),      h: 'all invoices',           c: '#3b6fd2', vc: '#1e293b' },
                { l: 'Received',     v: fmt(collected),    h: `${collPct}% collected`,  c: '#1f9d63', vc: '#16a34a' },
                { l: 'Outstanding',  v: fmt(outstanding),  h: 'to collect',             c: '#e11d48', vc: '#e11d48' },
                { l: 'Overdue',      v: fmt(overdueAmt),   h: `${overdue.length} invoices`, c: '#d23b4e', vc: '#e11d48' },
                { l: 'Invoices',     v: invoices.length.toLocaleString(), h: 'total',    c: '#c9820f', vc: '#1e293b' },
              ];
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  {tiles.map(t => (
                    <div key={t.l} className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2" style={{ borderLeft: `3px solid ${t.c}` }}>
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t.l}</p>
                      <p className="text-lg font-extrabold leading-tight tabular-nums font-mono" style={{ color: t.vc }}>{t.v}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">{t.h}</p>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Tab Navigation — underline text tabs (mockup style) */}
            <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
              {(() => {
                const dueN = getCustomersWithDueVehicles().reduce((sum, c) => sum + c.dueCount, 0);
                const setupN = getCustomersWithSetupDueVehicles().reduce((sum, c) => sum + c.dueCount, 0);
                let followN = 0;
                try { followN = categorizeFollowups(getFollowupVehicles()).today.length; } catch (e) {}
                const TABS = [
                  { k: 'summary',   label: 'Dashboard' },
                  { k: 'dashboard', label: 'Detail', n: filteredDetailInvoices.length },
                  { k: 'ledger',    label: 'Customer Ledger' },
                  { k: 'due',       label: 'Renewal Due', n: dueN },
                  { k: 'setup-due', label: 'Setup Due', n: setupN },
                  { k: 'followup',  label: 'Follow-Up', n: followN > 0 ? followN : null, red: true },
                  { k: 'aging',     label: 'Aging' },
                  { k: 'discounts', label: 'Discounts', n: discountedInvoices.length || null },
                  { k: 'commissions', label: 'Commissions' },
                ];
                return TABS.map(t => (
                  <button
                    key={t.k}
                    onClick={() => setInvoiceTab(t.k)}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${invoiceTab === t.k ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                  >
                    {t.label}
                    {t.n != null && <span className={`ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${t.red ? 'bg-red-100 text-red-600' : invoiceTab === t.k ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{t.n}</span>}
                  </button>
                ));
              })()}
              {/* WhatsApp invoice messages — Setup / AMC switches (Master / Admin) */}
              {(() => {
                const role = String(authUser?.role_name || authUser?.role || '').toLowerCase();
                const canToggle = role === 'master' || role === 'admin';
                const sw = (kind, label) => {
                  const st = waSettings?.[kind];
                  const on = !!st?.enabled;
                  const tip = waSettings == null ? 'Loading…'
                    : `${label} WhatsApp messages are ${on ? 'ON' : 'OFF'}` + (st?.updated_by ? ` (changed by ${st.updated_by})` : '')
                      + (canToggle ? '' : ' — only Master/Admin can change this');
                  return (
                    <div key={kind} className="flex items-center gap-1.5" title={tip}>
                      <span className="text-xs font-semibold text-slate-500">{label} WhatsApp</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={on}
                        disabled={!canToggle || waSettings == null || waSaving}
                        onClick={() => toggleWhatsApp(kind)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60 ${on ? 'bg-green-500' : 'bg-slate-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                      <span className={`text-xs font-bold ${on ? 'text-green-600' : 'text-slate-400'}`}>{on ? 'ON' : 'OFF'}</span>
                    </div>
                  );
                };
                return (
                  <div className="ml-auto flex items-center gap-4 pl-4 pb-1 whitespace-nowrap">
                    {sw('setup', 'Setup')}
                    {sw('renewal', 'AMC')}
                  </div>
                );
              })()}
            </div>

            {/* Dashboard Tab */}
			{/* Dashboard Summary Tab */}
            {/* AGING tab */}
            {/* COMMISSIONS tab — salesperson commission payables */}
            {invoiceTab === 'commissions' && <CommissionsPanel apiBaseUrl={API_BASE_URL} canPay={canDo('invoices', 'edit')} />}

            {invoiceTab === 'aging' && (() => {
              const balOf = (inv) => { const paid = inv.payments?.reduce((ps, p) => ps + (parseFloat(p.amount) || 0), 0) || 0; const disc = parseFloat(inv.discount || 0); return Math.max(0, (parseFloat(inv.amount) || 0) - disc - paid); };
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const b = { cur: 0, b30: 0, b60: 0, b60p: 0 };
              const rows = [];
              invoices.forEach(inv => {
                if (getInvoiceStatus(inv) === 'PAID') return;
                const bal = balOf(inv); if (bal <= 0) return;
                const due = new Date(inv.dueDate); due.setHours(0, 0, 0, 0);
                const days = Math.round((today - due) / 86400000);
                let bucket;
                if (days <= 0) { b.cur += bal; bucket = 'Current'; }
                else if (days <= 30) { b.b30 += bal; bucket = '1–30'; }
                else if (days <= 60) { b.b60 += bal; bucket = '31–60'; }
                else { b.b60p += bal; bucket = '60+'; }
                rows.push({ inv, bal, days, bucket });
              });
              rows.sort((x, y) => y.days - x.days);
              const total = b.cur + b.b30 + b.b60 + b.b60p;
              const fmt = (n) => 'Rs ' + Math.round(n).toLocaleString();
              const pct = (n) => total > 0 ? Math.round(n / total * 100) : 0;
              const cells = [['Current', b.cur, '#1f9d63', 'Current'], ['1–30 days', b.b30, '#c9820f', '1–30'], ['31–60 days', b.b60, '#f5732b', '31–60'], ['60+ days', b.b60p, '#d23b4e', '60+']];
              const bucketColor = { 'Current': '#1f9d63', '1–30': '#c9820f', '31–60': '#f5732b', '60+': '#d23b4e' };
              const shown = rows.filter(r => agingFilter === 'all' || r.bucket === agingFilter);
              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {cells.map(([l, v, c, bk]) => {
                      const active = agingFilter === bk;
                      return (
                      <div key={l} onClick={() => setAgingFilter(active ? 'all' : bk)} title={`Filter: ${l}`} className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2 cursor-pointer transition-all hover:shadow-md" style={{ borderLeft: `3px solid ${c}`, boxShadow: active ? `0 0 0 2px ${c}` : undefined }}>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{l}{active && <span className="ml-1 text-[9px]" style={{ color: c }}>●</span>}</p>
                          <span className="text-[10px] font-bold tabular-nums" style={{ color: c }}>{pct(v)}%</span>
                        </div>
                        <p className="text-base font-extrabold tabular-nums font-mono leading-tight" style={{ color: c }}>{fmt(v)}</p>
                        <div className="h-1 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: Math.max(2, pct(v)) + '%', background: c }} /></div>
                      </div>
                      );
                    })}
                  </div>
                  <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-bold text-slate-800">Outstanding invoices by age</h3>
                      <span className="text-xs text-slate-500 tabular-nums">
                        {agingFilter === 'all' ? `${rows.length} unpaid · ${fmt(total)} total` : `${shown.length} in "${agingFilter}"`}
                        {agingFilter !== 'all' && <button onClick={() => setAgingFilter('all')} className="ml-2 text-red-600 font-semibold hover:underline">Clear</button>}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200"><tr>
                          <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Invoice #</th>
                          <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Customer</th>
                          <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicle</th>
                          <th className="text-right px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Balance</th>
                          <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Due date</th>
                          <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Age</th>
                          <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Bucket</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {shown.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">{rows.length === 0 ? 'No outstanding invoices 🎉' : 'No invoices in this age bucket'}</td></tr>
                          ) : shown.slice(0, invoiceVisibleCount).map(r => (
                            <tr key={r.inv.id} className="hover:bg-slate-50">
                              <td className="px-4 py-2.5 font-mono font-semibold text-slate-700">{r.inv.invoiceNumber}</td>
                              <td className="px-4 py-2.5 text-slate-700">{r.inv.customerName}</td>
                              <td className="px-4 py-2.5 font-mono text-slate-600">{r.inv.vehicleNo}</td>
                              <td className="px-4 py-2.5 text-right font-mono tabular-nums" style={{ color: '#e11d48' }}>{fmt(r.bal)}</td>
                              <td className="px-4 py-2.5 tabular-nums text-slate-500">{r.inv.dueDate}</td>
                              <td className="px-4 py-2.5 tabular-nums font-semibold" style={{ color: bucketColor[r.bucket] }}>{r.days <= 0 ? `${-r.days}d left` : `${r.days}d overdue`}</td>
                              <td className="px-4 py-2.5"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: bucketColor[r.bucket] + '22', color: bucketColor[r.bucket] }}>{r.bucket}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {shown.length > invoiceVisibleCount && (
                      <div className="p-3 border-t border-slate-100">
                        <button onClick={() => setInvoiceVisibleCount(c => c + TABLE_PAGE_SIZE)} className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Load more ({shown.length - invoiceVisibleCount} more)</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {invoiceTab === 'summary' && (
              <div>
                {/* Dashboard Stats hidden — the compact KPI tiles at the top now cover these */}
                {false && (() => {
                  const totalBilled    = invoices.reduce((s, inv) => s + parseFloat(inv.amount), 0);
                  const totalDiscounts = invoices.reduce((s, inv) => s + parseFloat(inv.discount || 0), 0);
                  const netReceivable  = totalBilled - totalDiscounts;
                  const totalCollected = invoices.reduce((s, inv) => s + (inv.payments?.reduce((ps, p) => ps + parseFloat(p.amount), 0) || 0), 0);
                  const outstanding    = invoices.reduce((s, inv) => {
                    const status = getInvoiceStatus(inv);
                    if (status === 'PAID') return s;
                    const paid = inv.payments?.reduce((ps, p) => ps + parseFloat(p.amount), 0) || 0;
                    const disc = parseFloat(inv.discount || 0);
                    return s + Math.max(0, parseFloat(inv.amount) - disc - paid);
                  }, 0);
                  const overdueCount   = invoices.filter(inv => getInvoiceStatus(inv).includes('OVERDUE')).length;
                  return (
                    <div className="grid grid-cols-5 gap-4 mb-5">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <TrendingUp className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Total Billed</p>
                          <p className="text-lg font-bold leading-tight">Rs. {totalBilled.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">Original amounts</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <Tag className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Total Discounts</p>
                          <p className="text-lg font-bold leading-tight">Rs. {totalDiscounts.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">Net: Rs. {netReceivable.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <DollarSign className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Total Collected</p>
                          <p className="text-lg font-bold leading-tight">Rs. {totalCollected.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">of Rs. {netReceivable.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <Clock className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Outstanding</p>
                          <p className="text-lg font-bold leading-tight">Rs. {outstanding.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">After discounts</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <AlertCircle className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Overdue</p>
                          <p className="text-lg font-bold leading-tight">{overdueCount}</p>
                          <p className="text-xs opacity-75 mt-0.5">invoices</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Search and Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search by invoice number or customer..."
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {[['all', 'All'], ['PAID', 'Paid'], ['PARTIALLY PAID', 'Partial'], ['OVERDUE', 'Overdue'], ['UNPAID', 'Unpaid']].map(([val, label]) => (
                      <button key={val} onClick={() => setInvoiceStatusFilter(val)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${invoiceStatusFilter === val ? 'bg-red-50 text-red-600 border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                        {val !== 'all' && <span className="inline-block w-1 h-3.5 rounded-sm" style={{ background: invoiceStatusTheme(val).bar }} />}
                        {label}
                      </button>
                    ))}
                  </div>
                  <select
                    value={invoiceFollowUpPersonFilter}
                    onChange={(e) => setInvoiceFollowUpPersonFilter(e.target.value)}
                    className="px-4 py-2 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                  >
                    <option value="all">All Follow-Up Persons</option>
                    {(() => {
                      const followUpPersons = customers
                        .filter(c => c.followUp && c.followUp.trim())
                        .map(c => c.followUp)
                        .filter((item, index, arr) => arr.indexOf(item) === index)
                        .sort();
                      console.log('[DEBUG] Dashboard Summary - Follow-Up Persons:', followUpPersons);
                      console.log('[DEBUG] Dashboard Summary - Sample customers with followUp:', 
                        customers.filter(c => c.followUp).slice(0, 3).map(c => ({
                          id: c.id, 
                          name: c.name, 
                          followUp: c.followUp
                        }))
                      );
                      return followUpPersons.map(person => (
                        <option key={person} value={person}>{person}</option>
                      ));
                    })()}
                  </select>
                </div>

                {/* Summary Table */}
                <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Invoice #</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Date</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Customer</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Type</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicles</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Amount</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Paid</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Balance</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredGroupedInvoices
                        .slice(0, invoiceVisibleCount)
                        .map((invoice) => {
                          const totalPaid = groupedPaid(invoice);
                          const discount = parseFloat(invoice.totalDiscount || 0);
                          const effectiveAmount = groupedNet(invoice);
                          const balance = effectiveAmount - totalPaid;
                          const status = getGroupedStatus(invoice);

                          return (
                            <tr key={invoice.invoiceNumber} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-700">{invoice.invoiceNumber}</td>
                              <td className="px-4 py-3 text-sm text-slate-500 tabular-nums whitespace-nowrap">{invoice.createdDate}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] font-bold shrink-0">{(invoice.customerName || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</div>
                                  <div className="min-w-0">
                                    <div className="font-medium text-slate-800 text-sm truncate">{invoice.customerName}</div>
                                    {invoice.customerId != null && <div className="text-[11px] text-slate-400 tabular-nums">{formatCustomerCode(invoice.customerId, customers.find(c => String(c.id) === String(invoice.customerId))?.type)}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3"><span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{invoice.type}</span></td>
                              <td className="px-4 py-3 text-center"><span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">{invoice.vehicles.length}</span></td>
                              <td className="px-4 py-3 text-right font-mono text-sm text-slate-700 tabular-nums">Rs {effectiveAmount.toLocaleString()}{discount > 0 && <span className="block text-[10px] text-slate-400">-{discount.toLocaleString()} disc</span>}</td>
                              <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-green-600">Rs {totalPaid.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-mono text-sm tabular-nums" style={{ color: balance > 0 ? '#e11d48' : '#16a34a' }}>Rs {balance.toLocaleString()}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${status === 'PAID' ? 'bg-green-100 text-green-700' : status.includes('OVERDUE') ? 'bg-red-100 text-red-700' : status === 'PARTIALLY PAID' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${status === 'PAID' ? 'bg-green-500' : status.includes('OVERDUE') ? 'bg-red-500' : status === 'PARTIALLY PAID' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                  {status}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-1.5 justify-end items-center">
                                  {status !== 'PAID' && canDo('invoices', 'edit') && (
                                    <button
                                      onClick={() => {
                                        const firstInvoice = invoices.find(inv => inv.invoiceNumber === invoice.invoiceNumber);
                                        setSelectedInvoice({ ...firstInvoice, isBulkInvoice: invoice.vehicles.length > 1, totalVehicles: invoice.vehicles.length, totalAmount: invoice.totalAmount, bulkBalance: balance });
                                        setShowRecordPayment(true);
                                        setPaymentDate(new Date().toISOString().split('T')[0]);
                                        setPaymentAmount(balance > 0 ? balance.toString() : '');
                                      }}
                                      className="text-xs font-bold px-3 py-1.5 rounded-lg text-white shadow-sm whitespace-nowrap"
                                      style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}
                                      title="Record payment"
                                    ><span className="2xl:hidden">Pay</span><span className="hidden 2xl:inline">Record payment</span></button>
                                  )}
                                  <button onClick={() => { const firstInvoice = invoices.find(inv => inv.invoiceNumber === invoice.invoiceNumber); generatePDFInvoice(firstInvoice); }} className="w-8 h-8 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-red-600 transition-all" title="Print Invoice"><Download size={15} /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  {filteredGroupedInvoices.length === 0 && (
                    <div className="text-center py-16 text-slate-500">
                      <FileText className="mx-auto mb-4 opacity-50" size={64} />
                      <p className="text-lg">No invoices found</p>
                    </div>
                  )}
                  {filteredGroupedInvoices.length > invoiceVisibleCount && (
                    <div className="p-3 border-t border-slate-100">
                      <button
                        onClick={() => setInvoiceVisibleCount(c => c + TABLE_PAGE_SIZE)}
                        className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                      >
                        Load more ({filteredGroupedInvoices.length - invoiceVisibleCount} more of {filteredGroupedInvoices.length})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {invoiceTab === 'dashboard' && (
              <div>
                {/* Dashboard Stats hidden — the compact KPI tiles at the top now cover these */}
                {false && (() => {
                  const totalBilled    = invoices.reduce((s, inv) => s + parseFloat(inv.amount), 0);
                  const totalDiscounts = invoices.reduce((s, inv) => s + parseFloat(inv.discount || 0), 0);
                  const netReceivable  = totalBilled - totalDiscounts;
                  const totalCollected = invoices.reduce((s, inv) => s + (inv.payments?.reduce((ps, p) => ps + parseFloat(p.amount), 0) || 0), 0);
                  const outstanding    = invoices.reduce((s, inv) => {
                    const status = getInvoiceStatus(inv);
                    if (status === 'PAID') return s;
                    const paid = inv.payments?.reduce((ps, p) => ps + parseFloat(p.amount), 0) || 0;
                    const disc = parseFloat(inv.discount || 0);
                    return s + Math.max(0, parseFloat(inv.amount) - disc - paid);
                  }, 0);
                  const overdueCount   = invoices.filter(inv => getInvoiceStatus(inv).includes('OVERDUE')).length;
                  return (
                    <div className="grid grid-cols-5 gap-4 mb-5">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <TrendingUp className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Total Billed</p>
                          <p className="text-lg font-bold leading-tight">Rs. {totalBilled.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">Original amounts</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <Tag className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Total Discounts</p>
                          <p className="text-lg font-bold leading-tight">Rs. {totalDiscounts.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">Net: Rs. {netReceivable.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <DollarSign className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Total Collected</p>
                          <p className="text-lg font-bold leading-tight">Rs. {totalCollected.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">of Rs. {netReceivable.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <Clock className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Outstanding</p>
                          <p className="text-lg font-bold leading-tight">Rs. {outstanding.toLocaleString()}</p>
                          <p className="text-xs opacity-75 mt-0.5">After discounts</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-3 text-white shadow-md flex items-center gap-3">
                        <AlertCircle className="w-6 h-6 opacity-80 shrink-0" />
                        <div>
                          <p className="text-xs font-medium opacity-90">Overdue</p>
                          <p className="text-lg font-bold leading-tight">{overdueCount}</p>
                          <p className="text-xs opacity-75 mt-0.5">invoices</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Search and Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search by invoice number, customer, or vehicle..."
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {[['all', 'All'], ['PAID', 'Paid'], ['PARTIALLY PAID', 'Partial'], ['OVERDUE', 'Overdue'], ['UNPAID', 'Unpaid']].map(([val, label]) => (
                      <button key={val} onClick={() => setInvoiceStatusFilter(val)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${invoiceStatusFilter === val ? 'bg-red-50 text-red-600 border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                        {val !== 'all' && <span className="inline-block w-1 h-3.5 rounded-sm" style={{ background: invoiceStatusTheme(val).bar }} />}
                        {label}
                      </button>
                    ))}
                  </div>
                  <select
                    value={invoiceFollowUpPersonFilter}
                    onChange={(e) => setInvoiceFollowUpPersonFilter(e.target.value)}
                    className="px-4 py-2 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                  >
                    <option value="all">All Follow-Up Persons</option>
                    {customers
                      .filter(c => c.followUp && c.followUp.trim())
                      .map(c => c.followUp)
                      .filter((item, index, arr) => arr.indexOf(item) === index)
                      .sort()
                      .map(person => (
                        <option key={person} value={person}>{person}</option>
                      ))}
                  </select>
                  <button
                    onClick={exportDashboardToExcel}
                    className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-md hover:from-green-600 hover:to-emerald-700 transition-all whitespace-nowrap"
                    title="Export filtered data to Excel/CSV"
                  >
                    <Download size={18} />
                    Export Excel
                  </button>
                </div>

                {/* Invoices Table */}
                <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Invoice #</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Customer</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicle</th>
                        <th className="hidden xl:table-cell px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Type</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Amount</th>
                        <th className="hidden xl:table-cell px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Received</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Balance</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Due</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredDetailInvoices
                        .slice(0, invoiceVisibleCount)
                        .map(invoice => {
                          const totalPaid = invoice.payments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
                          const discount = parseFloat(invoice.discount || 0);
                          const effectiveAmount = parseFloat(invoice.amount) - discount;
                          const balance = effectiveAmount - totalPaid;
                          const status = getInvoiceStatus(invoice);
                          const theme = invoiceStatusTheme(status);

                          return (
                            <tr key={invoice.id} onClick={() => { setSelectedInvoice(invoice); setShowInvoiceDetails(true); }} title={`${theme.label} — click for details`} className="hover:bg-slate-50 transition-colors cursor-pointer">
                              <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-700 whitespace-nowrap" style={{ boxShadow: `inset 4px 0 0 ${theme.bar}` }}>{invoice.invoiceNumber}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] font-bold shrink-0">{(invoice.customerName || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</div>
                                  <div className="min-w-0">
                                    <div className="font-medium text-slate-800 text-sm truncate">{invoice.customerName}</div>
                                    {invoice.customerId != null && <div className="text-[11px] text-slate-400 tabular-nums">{formatCustomerCode(invoice.customerId, customers.find(c => String(c.id) === String(invoice.customerId))?.type)}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 font-mono text-sm text-slate-600 whitespace-nowrap">{invoice.vehicleNo}<span className="xl:hidden block text-[10px] font-sans font-bold text-slate-400">{invoice.type}</span></td>
                              <td className="hidden xl:table-cell px-4 py-3"><span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{invoice.type}</span></td>
                              <td className="px-4 py-3 text-right font-mono text-sm text-slate-700 tabular-nums whitespace-nowrap">Rs {effectiveAmount.toLocaleString()}{discount > 0 && <span className="block text-[10px] text-slate-400">-{discount.toLocaleString()} disc</span>}</td>
                              <td className="hidden xl:table-cell px-4 py-3 text-right font-mono text-sm tabular-nums text-green-600 whitespace-nowrap">Rs {totalPaid.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-mono text-sm tabular-nums whitespace-nowrap" style={{ color: balance > 0 ? '#e11d48' : '#16a34a' }}>Rs {balance.toLocaleString()}{totalPaid > 0 && balance > 0 && <span className="xl:hidden block text-[10px] text-green-600">{totalPaid.toLocaleString()} rcvd</span>}</td>
                              <td className="px-4 py-3 text-sm tabular-nums whitespace-nowrap">
                                {(() => {
                                  const d = new Date(invoice.dueDate); d.setHours(0, 0, 0, 0);
                                  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
                                  const days = Math.round((t0 - d) / 86400000);
                                  if (status === 'CANCELLED') return <span className="text-slate-400">Cancelled</span>;
                                  if (status === 'PAID') return <span className="text-slate-400">{invoice.dueDate}</span>;
                                  const partialTag = status === 'OVERDUE (Partial)' && <span className="block text-[10px] font-medium text-amber-600">partial</span>;
                                  if (days > 0) return <span style={{ color: '#d23b4e', fontWeight: 600 }}>Overdue {days}d{partialTag}</span>;
                                  const left = -days;
                                  return <span style={{ color: left <= 7 ? '#c9820f' : '#64748b', fontWeight: 600 }}>{left === 0 ? 'Due today' : `Due ${left}d`}</span>;
                                })()}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {status !== 'PAID' && status !== 'CANCELLED' && canDo('invoices', 'edit') && (
                                  <button onClick={(e) => { e.stopPropagation(); setSelectedInvoice(invoice); setShowRecordPayment(true); setPaymentDate(new Date().toISOString().split('T')[0]); }} title="Record payment" className="text-xs font-bold px-3 py-1.5 rounded-lg text-white shadow-sm whitespace-nowrap" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}>Pay</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                    {filteredDetailInvoices.length > 0 && (() => {
                      // Subtotal of everything matching the current filters (not just the rows shown).
                      const live = filteredDetailInvoices.filter(inv => getInvoiceStatus(inv) !== 'CANCELLED');
                      const tot = live.reduce((acc, inv) => {
                        const paid = inv.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
                        const net = parseFloat(inv.amount) - parseFloat(inv.discount || 0);
                        acc.amount += net; acc.paid += paid; acc.balance += net - paid;
                        return acc;
                      }, { amount: 0, paid: 0, balance: 0 });
                      return (
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr>
                            <td colSpan={3} className="px-4 py-3 text-sm font-bold text-slate-700">
                              Subtotal <span className="font-medium text-slate-400">({live.length} invoice{live.length !== 1 ? 's' : ''})</span>
                            </td>
                            <td className="hidden xl:table-cell px-4 py-3"></td>
                            <td className="px-4 py-3 text-right font-mono text-sm font-bold text-slate-800 tabular-nums whitespace-nowrap">Rs {tot.amount.toLocaleString()}</td>
                            <td className="hidden xl:table-cell px-4 py-3 text-right font-mono text-sm font-bold text-green-600 tabular-nums whitespace-nowrap">Rs {tot.paid.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right font-mono text-sm font-bold tabular-nums whitespace-nowrap" style={{ color: tot.balance > 0 ? '#e11d48' : '#16a34a' }}>
                              Rs {tot.balance.toLocaleString()}
                              {tot.paid > 0 && <span className="xl:hidden block text-[10px] font-medium text-green-600">{tot.paid.toLocaleString()} rcvd</span>}
                            </td>
                            <td colSpan={2} className="px-4 py-3"></td>
                          </tr>
                        </tfoot>
                      );
                    })()}
                  </table>

                  {filteredDetailInvoices.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">No invoices found</p>
                    </div>
                  )}
                  {filteredDetailInvoices.length > invoiceVisibleCount && (
                    <div className="p-3 border-t border-slate-100">
                      <button
                        onClick={() => setInvoiceVisibleCount(c => c + TABLE_PAGE_SIZE)}
                        className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                      >
                        Load more ({filteredDetailInvoices.length - invoiceVisibleCount} more of {filteredDetailInvoices.length})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Discounts Tab */}
            {invoiceTab === 'discounts' && (() => {
              const rows = discountedInvoices
                .filter(g => invoiceFollowUpPersonFilter === 'all' || (customers.find(c => parseInt(c.id) === g.customerId)?.followUp || 'Unassigned') === invoiceFollowUpPersonFilter)
                .filter(g => {
                  const q = invoiceSearch.toLowerCase();
                  return !q || g.invoiceNumber.toLowerCase().includes(q) || g.customerName.toLowerCase().includes(q) || (g.vehicles || []).some(v => (v.vehicleNo || '').toLowerCase().includes(q));
                })
                .sort((a, b) => (parseFloat(b.totalDiscount) || 0) - (parseFloat(a.totalDiscount) || 0));
              const totalDisc = rows.reduce((s, g) => s + (parseFloat(g.totalDiscount) || 0), 0);
              const totalOrig = rows.reduce((s, g) => s + (parseFloat(g.totalAmount) || 0), 0);
              const avgPct = totalOrig > 0 ? (totalDisc / totalOrig) * 100 : 0;
              return (
                <div>
                  {/* KPI stripe tiles */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { l: 'TOTAL DISCOUNTS', v: `Rs ${totalDisc.toLocaleString()}`, h: `${rows.length} invoice${rows.length === 1 ? '' : 's'}`, c: '#e11d48' },
                      { l: 'DISCOUNTED INVOICES', v: rows.length, h: 'with a discount applied', c: '#ea580c' },
                      { l: 'AVG DISCOUNT', v: `${avgPct.toFixed(1)}%`, h: 'of original billed', c: '#7c3aed' },
                    ].map(t => (
                      <div key={t.l} className="bg-white rounded-xl shadow-md border border-slate-200 px-3 py-2" style={{ borderLeft: `3px solid ${t.c}` }}>
                        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t.l}</p>
                        <p className="text-lg font-extrabold leading-tight tabular-nums font-mono" style={{ color: t.c }}>{t.v}</p>
                        <p className="text-[10px] text-slate-400 leading-tight">{t.h}</p>
                      </div>
                    ))}
                  </div>

                  {/* Search */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                      <input type="text" placeholder="Search by invoice number, customer, or vehicle..." value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)} className="w-full pl-11 pr-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Invoice #</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Customer</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicle</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Original</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Discount</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">%</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Net</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rows.map(g => {
                          const orig = parseFloat(g.totalAmount) || 0;
                          const disc = parseFloat(g.totalDiscount) || 0;
                          const net = orig - disc;
                          const pct = orig > 0 ? (disc / orig) * 100 : 0;
                          const status = getGroupedStatus(g);
                          const isBulk = g.vehicles.length > 1;
                          return (
                            <tr key={g.invoiceNumber} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 font-mono text-sm font-semibold text-slate-700">{g.invoiceNumber}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] font-bold shrink-0">{(g.customerName || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</div>
                                  <div className="min-w-0">
                                    <div className="font-medium text-slate-800 text-sm truncate">{g.customerName}</div>
                                    {g.customerId != null && <div className="text-[11px] text-slate-400 tabular-nums">{formatCustomerCode(g.customerId, customers.find(c => String(c.id) === String(g.customerId))?.type)}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 font-mono text-sm text-slate-600 whitespace-nowrap">{g.vehicles[0]?.vehicleNo || '—'}{isBulk && <span className="ml-1.5 text-[10px] font-sans font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">+{g.vehicles.length - 1}</span>}</td>
                              <td className="px-4 py-3 text-right font-mono text-sm text-slate-500 tabular-nums">Rs {orig.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-mono text-sm font-semibold tabular-nums text-rose-600">Rs {disc.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-500">{pct.toFixed(1)}%</td>
                              <td className="px-4 py-3 text-right font-mono text-sm text-slate-700 tabular-nums">Rs {net.toLocaleString()}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${status === 'PAID' ? 'bg-green-100 text-green-700' : status.includes('OVERDUE') ? 'bg-red-100 text-red-700' : status === 'PARTIALLY PAID' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${status === 'PAID' ? 'bg-green-500' : status.includes('OVERDUE') ? 'bg-red-500' : status === 'PARTIALLY PAID' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                  {status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {rows.length === 0 && (
                      <div className="text-center py-16 text-slate-400">
                        <Tag className="mx-auto mb-4 opacity-50" size={64} />
                        <p className="text-lg">No discounts applied yet</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Due Invoices Tab */}
            {invoiceTab === 'due' && (
              <div>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800">Vehicles due for renewal invoice</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Eligible 21 days before due date</p>
                  </div>

                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Customer</th>
                        <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">ID</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicles Due</th>
                        <th className="px-4 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wide">Active</th>
                        <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {getCustomersWithDueVehicles().slice(0, invoiceVisibleCount).map(customer => (
                        <tr key={customer.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-2.5 font-semibold text-slate-800">{customer.name}</td>
                          <td className="px-4 py-2.5 text-slate-600 tabular-nums">{formatCustomerCode(customer.id, customer.type)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-bold">
                              {customer.dueCount}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center text-slate-600">{customer.vehicles.filter(v => v.status === 'active').length}</td>
                          <td className="px-4 py-2.5 text-center">
                            {canDo('invoices', 'create') ? (
                            <button
                              onClick={() => {
                                setSelectedCustomerForBulk(customer);
                                setSelectedVehiclesForInvoice([]);
                                setBulkInvoiceType('renewal'); // Set to renewal type
                                setShowBulkInvoiceCreate(true);
                              }}
                              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-md transition-all font-semibold"
                            >
                              <FileText className="inline mr-2" size={16} />
                              Create Invoices
                            </button>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {getCustomersWithDueVehicles().length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-semibold">No Vehicles Due for Renewal</p>
                      <p className="text-sm mt-2">All vehicles are up to date or not yet eligible for renewal invoice</p>
                    </div>
                  )}
                  {getCustomersWithDueVehicles().length > invoiceVisibleCount && (
                    <div className="p-3 border-t border-slate-100">
                      <button
                        onClick={() => setInvoiceVisibleCount(c => c + TABLE_PAGE_SIZE)}
                        className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                      >
                        Load more ({getCustomersWithDueVehicles().length - invoiceVisibleCount} more of {getCustomersWithDueVehicles().length})
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
			{/* Setup Due Invoices Tab */}
            {invoiceTab === 'setup-due' && (
				<div>
				<div className="bg-white rounded-xl shadow-md overflow-hidden">
				<div className="p-4 border-b border-slate-100">
				<h3 className="text-base font-bold text-slate-800">Vehicles due for setup invoice</h3>
				<p className="text-xs text-slate-500 mt-0.5">These vehicles need their initial setup invoice created</p>
				</div>

				<table className="w-full">
				<thead className="bg-slate-50 border-b border-slate-200">
				<tr>
				<th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Customer</th>
				<th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">ID</th>
				<th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Salesperson</th>
				<th className="px-4 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wide">Vehicles Due</th>
				<th className="px-4 py-2.5 text-center text-[11px] font-bold text-slate-500 uppercase tracking-wide">Active</th>
				<th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
				</tr>
				</thead>
				<tbody className="divide-y divide-slate-100">
				{getCustomersWithSetupDueVehicles().slice(0, invoiceVisibleCount).map(customer => (
				<tr key={customer.id} className="hover:bg-slate-50 transition-colors">
				<td className="px-4 py-2.5 font-semibold text-slate-800">{customer.name}</td>
				<td className="px-4 py-2.5 text-slate-600 tabular-nums">{formatCustomerCode(customer.id, customer.type)}</td>
				<td className="px-4 py-2.5 text-slate-600">{customer.salesperson || <span className="text-slate-400 italic">N/A</span>}</td>
				<td className="px-4 py-2.5 text-center">
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-bold">
                  {customer.dueCount}
                </span>
              </td>
              <td className="px-4 py-2.5 text-center text-slate-600">{customer.vehicles.filter(v => v.status === 'active').length}</td>
              <td className="px-4 py-2.5 text-center">
                {canDo('invoices', 'create') ? (
                <button
                  onClick={() => {
                    setSelectedCustomerForBulk(customer);
                    setSelectedVehiclesForInvoice([]);
                    setBulkInvoiceType('setup'); // Set to setup type
                    setShowBulkInvoiceCreate(true);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-md transition-all font-semibold"
                >
                  <FileText className="inline mr-2" size={16} />
                  Create Invoices
					</button>
                ) : <span className="text-slate-300">—</span>}
				</td>
				</tr>
				))}
					</tbody>
				</table>

				{getCustomersWithSetupDueVehicles().length === 0 && (
				<div className="text-center py-12 text-slate-400">
					<Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
				<p className="text-lg font-semibold">No Vehicles Due for Setup</p>
				<p className="text-sm mt-2">All vehicles have their setup invoices created</p>
				</div>
				)}
				{getCustomersWithSetupDueVehicles().length > invoiceVisibleCount && (
				<div className="p-3 border-t border-slate-100">
					<button
						onClick={() => setInvoiceVisibleCount(c => c + TABLE_PAGE_SIZE)}
						className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
					>
						Load more ({getCustomersWithSetupDueVehicles().length - invoiceVisibleCount} more of {getCustomersWithSetupDueVehicles().length})
					</button>
				</div>
				)}
				</div>
				</div>
			)}

            {/* ── Follow-Up Tab ── */}
            {invoiceTab === 'followup' && (() => {
              // ── Vehicle-wise follow-ups, grouped by customer ──────────────────
              const q = followupSearch.trim().toLowerCase();
              const allVehicles = getFollowupVehicles().filter(v => {
                const matchPerson = followupPersonFilter === 'all' || v.followUpPerson === followupPersonFilter;
                const matchSearch = !q ||
                  (v.customerName || '').toLowerCase().includes(q) ||
                  (v.phone || '').includes(q) ||
                  (v.vehicleNo || '').toLowerCase().includes(q) ||
                  v.invoices.some(inv => (inv.invoiceNumber || '').toLowerCase().includes(q));
                return matchPerson && matchSearch;
              });
              const cats = categorizeFollowups(allVehicles);
              const tabOf = (v) => !v.followupDate ? 'new' : v.followupDate === todayStr ? 'today' : v.followupDate > todayStr ? 'upcoming' : 'missed';
              const tabName = { today: "Today's", missed: 'Missed', upcoming: 'Upcoming', new: 'New' };
              const followupPersons = getFollowupPersons();
              const custCount = (list) => new Set(list.map(v => v.customerId)).size;

              const subTabs = [
                { key: 'today', label: "Today's Follow-Up", icon: Zap, list: cats.today, color: 'red' },
                { key: 'missed', label: 'Missed Follow-Ups', icon: PhoneMissed, list: cats.missed, color: 'amber' },
                { key: 'upcoming', label: 'Upcoming Follow-Ups', icon: Calendar, list: cats.upcoming, color: 'blue' },
                { key: 'new', label: 'New Follow-Ups', icon: Target, list: cats.newItems, color: 'emerald' },
              ];
              const subColorMap = {
                red: { active: 'bg-red-600 text-white', badge: 'bg-red-100 text-red-700', border: 'border-b-2 border-red-600', icon: 'text-red-600' },
                amber: { active: 'bg-amber-500 text-white', badge: 'bg-amber-100 text-amber-700', border: 'border-b-2 border-amber-500', icon: 'text-amber-600' },
                blue: { active: 'bg-blue-600 text-white', badge: 'bg-blue-100 text-blue-700', border: 'border-b-2 border-blue-600', icon: 'text-blue-600' },
                emerald: { active: 'bg-emerald-600 text-white', badge: 'bg-emerald-100 text-emerald-700', border: 'border-b-2 border-emerald-600', icon: 'text-emerald-600' },
              };
              const activeSub = subTabs.find(t => t.key === followupTab) || subTabs[0];
              const activeVehicles = activeSub.list;

              // Customer cards (in the order their first vehicle appears in this tab)
              const groups = [];
              const byCust = {};
              activeVehicles.forEach(v => {
                if (!byCust[v.customerId]) {
                  byCust[v.customerId] = { customerId: v.customerId, inTab: [], all: allVehicles.filter(x => x.customerId === v.customerId) };
                  groups.push(byCust[v.customerId]);
                }
                byCust[v.customerId].inTab.push(v);
              });

              const statusText = (v) => ({
                today: `Follow-up today · invoice due ${fmtDate(v.dueDate)}`,
                missed: `Missed by ${daysAgo(v.followupDate)} day(s) · was ${fmtDate(v.followupDate)}`,
                upcoming: `In ${daysUntil(v.followupDate)} day(s) · ${fmtDate(v.followupDate)}`,
                new: `No follow-up set · due ${fmtDate(v.dueDate)}`,
              })[tabOf(v)];

              // GPS line per vehicle
              const fmtD = (s) => new Date(s.replace(' ', 'T')).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
              const gpsOf = (v) => fuGps[v.vehicleId];
              const gpsLabel = (g) => !g ? 'GPS …' : !g.has_device ? 'No GPS device' : !g.expiry_pk ? 'GPS: no expiry set'
                : g.expired ? `GPS expired ${fmtD(g.expiry_pk)}` : `GPS valid till ${fmtD(g.expiry_pk)}`;
              const gpsVeh = (v) => ({ vehicle_id: v.vehicleId, vehicle_no: v.vehicleNo, expiry_label: gpsLabel(gpsOf(v)) });
              const canExtend = (v) => { const g = gpsOf(v); return g && g.has_device && g.expired; };
              const canBlock  = (v) => { const g = gpsOf(v); return g && g.has_device && !g.expired; };

              const outcomeColors = { no_answer: 'bg-slate-100 text-slate-600', promised: 'bg-green-100 text-green-700', dispute: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', note: 'bg-blue-50 text-blue-700' };
              const outcomeLabels = { no_answer: 'No Answer', promised: 'Promised', dispute: 'Dispute', paid: 'Paid', note: 'Note' };

              const openLogCall = (group, preselect) => setRescheduleTarget({
                customerId: group.customerId,
                customerName: group.all[0]?.customerName,
                totalOutstanding: group.all.reduce((s, v) => s + v.outstanding, 0),
                vehicleCount: group.all.length,
                // previous calls of the vehicle(s) this call starts from, newest last
                logs: group.all.filter(v => preselect.includes(v.vehicleId))
                  .flatMap(v => v.logs.map(l => ({ ...l, note: `${v.vehicleNo}: ${l.note || ''}` })))
                  .sort((x, y) => String(x.date).localeCompare(String(y.date))),
                choices: group.all.map(v => ({ vehicleId: v.vehicleId, vehicleNo: v.vehicleNo, label: `${fmtAmt(v.outstanding)} · ${tabName[tabOf(v)]}` })),
                preselected: preselect,
              });

              return (
                <div>
                  {/* Stats Row — counted in vehicles */}
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    {subTabs.map(t => (
                      <div key={t.key} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${
                          t.color === 'red' ? 'from-red-500 to-red-600' :
                          t.color === 'amber' ? 'from-amber-500 to-amber-600' :
                          t.color === 'blue' ? 'from-blue-500 to-blue-600' : 'from-emerald-500 to-emerald-600'
                        }`}>
                          <t.icon size={18} className="text-white" />
                        </div>
                        <div>
                          <p className="text-xl font-black text-slate-800 leading-none">{t.list.length}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{t.label}</p>
                          <p className="text-[10px] text-slate-400">vehicles · {custCount(t.list)} customer{custCount(t.list) !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Filters Row */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        placeholder="Search customer, vehicle or invoice..."
                        value={followupSearch}
                        onChange={e => setFollowupSearch(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all"
                      />
                    </div>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <select
                        value={followupPersonFilter}
                        onChange={e => setFollowupPersonFilter(e.target.value)}
                        className="pl-10 pr-10 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all appearance-none bg-white font-medium text-slate-700"
                      >
                        <option value="all">All Follow-Up Persons</option>
                        {followupPersons.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                    </div>
                  </div>

                  {/* Sub-tabs */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-4">
                    <div className="flex border-b border-slate-200 overflow-x-auto">
                      {subTabs.map(tab => {
                        const isActive = followupTab === tab.key;
                        const sc = subColorMap[tab.color];
                        return (
                          <button key={tab.key} onClick={() => setFollowupTab(tab.key)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all ${
                              isActive ? `${sc.border} text-slate-900` : 'border-b-2 border-transparent text-slate-500 hover:text-slate-700'
                            }`}>
                            <tab.icon size={15} className={isActive ? sc.icon : ''} />
                            {tab.label}
                            {tab.list.length > 0 && (
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? sc.active : sc.badge}`}>
                                {tab.list.length}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Customer cards */}
                    <div className="p-4 space-y-3">
                      {groups.length === 0 ? (
                        <div className="text-center py-12 text-slate-400">
                          <activeSub.icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p className="font-semibold">
                            {followupTab === 'today' ? '🎉 No follow-ups scheduled for today!' :
                             followupTab === 'missed' ? '✅ No missed follow-ups!' :
                             followupTab === 'upcoming' ? '📅 No upcoming follow-ups.' : '✨ All vehicles have a follow-up date set.'}
                          </p>
                        </div>
                      ) : groups.map(group => {
                        const first = group.all[0] || group.inTab[0];
                        const initials = (first.customerName || '').split(' ').map(w => w[0]).slice(0, 2).join('');
                        const total = group.all.reduce((s, v) => s + v.outstanding, 0);
                        const others = group.all.filter(v => !group.inTab.includes(v));
                        const extAll = group.all.filter(canExtend);
                        const blkAll = group.all.filter(canBlock);
                        const vehicleRow = (v, dim) => {
                          const overdue = daysAgo(v.dueDate);
                          const isExpanded = expandedFollowup === v.key;
                          const g = gpsOf(v);
                          return (
                            <div key={v.key} className={dim ? 'bg-slate-50/70' : ''}>
                              <div className="grid grid-cols-12 gap-3 items-center px-4 py-3">
                                <div className="col-span-12 md:col-span-3 min-w-0">
                                  <p className={`font-mono font-semibold ${dim ? 'text-slate-400' : 'text-slate-800'}`}>{v.vehicleNo || '—'}</p>
                                  <div className="flex flex-wrap gap-1 mt-0.5">
                                    {[...new Map(v.invoices.map(inv => [inv.invoiceNumber, inv])).values()].map(inv => (
                                      <button key={inv.invoiceNumber}
                                        onClick={() => setInvoicePopup({ invoiceNumber: inv.invoiceNumber, createdDate: inv.createdDate, vehicles: invoices.filter(x => x.invoiceNumber === inv.invoiceNumber) })}
                                        className="text-[11px] text-violet-600 hover:underline font-mono">{inv.invoiceNumber}</button>
                                    ))}
                                  </div>
                                </div>
                                <div className="col-span-4 md:col-span-2">
                                  <p className={`font-mono font-semibold text-sm ${dim ? 'text-slate-400' : 'text-slate-800'}`}>{fmtAmt(v.outstanding)}</p>
                                  {v.dueDate && <p className={`text-[11px] font-semibold ${overdue > 30 ? 'text-red-600' : overdue > 15 ? 'text-amber-600' : 'text-slate-500'}`}>{overdue > 0 ? `${overdue}d overdue` : overdue === 0 ? 'due today' : `due in ${-overdue}d`}</p>}
                                </div>
                                <div className="col-span-8 md:col-span-3 min-w-0">
                                  {dim ? (
                                    <p className="text-xs text-slate-400">{tabName[tabOf(v)]} follow-up{v.followupDate ? ` · ${fmtDate(v.followupDate)}` : ''} (other tab)</p>
                                  ) : (
                                    <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><Clock size={10} /> {statusText(v)}</p>
                                  )}
                                  <button onClick={() => setExpandedFollowup(isExpanded ? null : v.key)}
                                    className="text-[11px] text-indigo-600 font-medium hover:underline flex items-center gap-1 mt-0.5 max-w-full">
                                    <PhoneCall size={10} /> {v.logs.length} call{v.logs.length !== 1 ? 's' : ''}
                                    {v.lastLog?.note && <span className="text-slate-500 truncate">· "{v.lastLog.note}"</span>}
                                    {v.logs.length > 0 && <span>{isExpanded ? '▲' : '▼'}</span>}
                                  </button>
                                </div>
                                <div className="col-span-7 md:col-span-2 flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-[11px] font-semibold ${!g || !g.has_device ? 'text-slate-400' : g.expired ? 'text-red-600' : 'text-green-600'}`}>● {gpsLabel(g)}</span>
                                  {canExtend(v) && (
                                    <button onClick={() => setGpsDialog({ mode: 'extend', vehicles: [gpsVeh(v)] })}
                                      className="text-[10px] font-bold px-2 py-0.5 rounded text-white" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}>Extend</button>
                                  )}
                                  {canBlock(v) && (
                                    <button onClick={() => setGpsDialog({ mode: 'block', vehicles: [gpsVeh(v)] })}
                                      className="text-[10px] font-bold px-2 py-0.5 rounded border border-red-200 text-red-600 bg-white hover:bg-red-50">Block</button>
                                  )}
                                </div>
                                <div className="col-span-5 md:col-span-2 text-right">
                                  <button onClick={() => openLogCall(group, [v.vehicleId])}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-all border border-red-200">
                                    <PhoneCall size={12} /> Log Call
                                  </button>
                                </div>
                              </div>
                              {isExpanded && v.logs.length > 0 && (
                                <div className="mx-4 mb-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 space-y-1.5">
                                  {[...v.logs].reverse().map((log, i) => (
                                    <div key={i} className="flex items-start gap-3 text-xs">
                                      <span className="text-slate-400 whitespace-nowrap pt-0.5">{fmtDate(log.date)}</span>
                                      <span className={`px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${outcomeColors[log.outcome] || 'bg-slate-100 text-slate-600'}`}>
                                        {outcomeLabels[log.outcome] || log.outcome}
                                      </span>
                                      {log.note && <span className="text-slate-600 flex-1">{log.note}</span>}
                                      {log.logged_by && <span className="text-slate-400 whitespace-nowrap">— {log.logged_by}</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        };
                        return (
                          <div key={group.customerId} className="border border-slate-200 rounded-xl bg-white hover:border-red-200 transition-all overflow-hidden">
                            {/* Customer header */}
                            <div className="flex gap-4 items-start p-4 bg-slate-50/60 border-b border-slate-100">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {initials}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-800 text-sm">{first.customerName}</p>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                  {first.phone && <span className="text-xs flex items-center gap-1 text-slate-500"><Phone size={11} /> {first.phone}</span>}
                                  {first.followUpPerson && <span className="text-xs flex items-center gap-1 text-indigo-600 font-medium"><User size={11} /> {first.followUpPerson}</span>}
                                  <span className="text-xs text-slate-500">
                                    <b className="text-slate-700">{group.all.length} vehicle{group.all.length !== 1 ? 's' : ''}</b> with dues
                                    {others.length > 0 && ` (${group.inTab.length} in this tab)`}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="font-bold text-slate-800 text-sm">{fmtAmt(total)}</p>
                                <p className="text-xs text-slate-400">total outstanding</p>
                                <div className="flex gap-1.5 justify-end flex-wrap mt-2">
                                  {group.all.length > 1 && extAll.length > 0 && (
                                    <button onClick={() => setGpsDialog({ mode: 'extend', vehicles: extAll.map(gpsVeh) })}
                                      className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-white" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}>Extend all ({extAll.length})</button>
                                  )}
                                  {group.all.length > 1 && blkAll.length > 0 && (
                                    <button onClick={() => setGpsDialog({ mode: 'block', vehicles: blkAll.map(gpsVeh) })}
                                      className="text-[11px] font-bold px-2.5 py-1 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50">Block all ({blkAll.length})</button>
                                  )}
                                  {group.all.length > 1 && (
                                    <button onClick={() => openLogCall(group, group.all.map(v => v.vehicleId))}
                                      className="text-[11px] font-bold px-2.5 py-1 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 inline-flex items-center gap-1">
                                      <PhoneCall size={11} /> Log call (all vehicles)
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Vehicles in this tab, then the customer's other vehicles (greyed) */}
                            <div className="divide-y divide-slate-100">
                              {group.inTab.map(v => vehicleRow(v, false))}
                              {others.map(v => vehicleRow(v, true))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Customer Ledger Tab ── */}
            {invoiceTab === 'ledger' && (() => {
              // Build per-customer ledger data from invoices
              const ledgerCustomers = customers.map(cust => {
                const custInvoices = invoices.filter(inv => inv.customerId === parseInt(cust.id));
                const totalBilled  = custInvoices.reduce((s, inv) => s + parseFloat(inv.amount || 0), 0);
                const totalPaid    = custInvoices.reduce((s, inv) => {
                  return s + (inv.payments || []).reduce((ps, p) => ps + parseFloat(p.amount || 0), 0);
                }, 0);
                const balance      = totalBilled - totalPaid;
                const overdueCount = custInvoices.filter(inv => {
                  const paid = (inv.payments || []).reduce((ps, p) => ps + parseFloat(p.amount || 0), 0);
                  return paid < parseFloat(inv.amount || 0) && new Date(inv.dueDate) < new Date();
                }).length;
                return { ...cust, custInvoices, totalBilled, totalPaid, balance, overdueCount };
              }).filter(c => c.custInvoices.length > 0);

              const filteredLedger = ledgerCustomers.filter(c =>
                !ledgerCustomerSearch ||
                c.name.toLowerCase().includes(ledgerCustomerSearch.toLowerCase()) ||
                String(c.id).includes(ledgerCustomerSearch)
              );

              const grandBilled    = filteredLedger.reduce((s, c) => s + c.totalBilled, 0);
              const grandPaid      = filteredLedger.reduce((s, c) => s + c.totalPaid, 0);
              const grandBalance   = filteredLedger.reduce((s, c) => s + c.balance, 0);

              return (
                <div className="space-y-5">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 shrink-0">
                        <TrendingUp size={18} className="text-white" />
                      </div>
                      <div>
                        <p className="text-xl font-black text-slate-800 leading-none">Rs. {grandBilled.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 mt-0.5">Total Billed</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-green-500 to-green-600 shrink-0">
                        <DollarSign size={18} className="text-white" />
                      </div>
                      <div>
                        <p className="text-xl font-black text-slate-800 leading-none">Rs. {grandPaid.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 mt-0.5">Total Collected</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-500 shrink-0">
                        <AlertCircle size={18} className="text-white" />
                      </div>
                      <div>
                        <p className="text-xl font-black text-slate-800 leading-none">Rs. {grandBalance.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 mt-0.5">Total Outstanding</p>
                      </div>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      placeholder="Search customer by name or ID..."
                      value={ledgerCustomerSearch}
                      onChange={e => { setLedgerCustomerSearch(e.target.value); setSelectedLedgerCustomer(null); }}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all"
                    />
                  </div>

                  {/* Ledger Table */}
                  {!selectedLedgerCustomer ? (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
                      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Wallet size={20} className="text-red-500" /> Customer Ledger
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5">{filteredLedger.length} customer{filteredLedger.length !== 1 ? 's' : ''} with invoices</p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">Customer</th>
                              <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">ID</th>
                              <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">Type</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wide">Invoices</th>
                              <th className="px-4 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">Total Billed</th>
                              <th className="px-4 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">Paid</th>
                              <th className="px-4 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">Balance</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wide">Overdue</th>
                              <th className="px-4 py-2 text-center text-xs font-bold text-slate-600 uppercase tracking-wide">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {filteredLedger.length === 0 ? (
                              <tr>
                                <td colSpan={9} className="px-6 py-12 text-center text-slate-400">
                                  <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                  <p className="font-semibold">No customers with invoices found</p>
                                </td>
                              </tr>
                            ) : filteredLedger.slice(0, invoiceVisibleCount).map(cust => (
                              <tr key={cust.id}
                                className={`hover:bg-slate-50 transition-colors ${cust.balance > 0 ? '' : ''}`}
                              >
                                <td className="px-4 py-2.5">
                                  <p className="font-semibold text-slate-800">{cust.name}</p>
                                  <p className="text-xs text-slate-500">{cust.cell || cust.phone || '—'}</p>
                                </td>
                                <td className="px-4 py-2.5 text-slate-500 text-sm">{cust.id}</td>
                                <td className="px-4 py-2.5">
                                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                    cust.type === 'Corporate' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                                  }`}>{cust.type}</span>
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold text-sm">
                                    {cust.custInvoices.length}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-right font-semibold text-slate-800">
                                  Rs. {cust.totalBilled.toLocaleString()}
                                </td>
                                <td className="px-4 py-2.5 text-right font-semibold text-green-600">
                                  Rs. {cust.totalPaid.toLocaleString()}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                  <span className={`font-bold text-base ${
                                    cust.balance <= 0 ? 'text-green-600' : cust.overdueCount > 0 ? 'text-red-600' : 'text-orange-600'
                                  }`}>
                                    {cust.balance <= 0 ? '✓ Cleared' : `Rs. ${cust.balance.toLocaleString()}`}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  {cust.overdueCount > 0 ? (
                                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                      {cust.overdueCount} overdue
                                    </span>
                                  ) : (
                                    <span className="text-green-500 text-xs font-semibold">✓ On time</span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  <button
                                    onClick={() => setSelectedLedgerCustomer(cust)}
                                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all text-xs font-semibold border border-red-200 flex items-center gap-1 mx-auto"
                                  >
                                    <FileText size={12} /> View Ledger
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {filteredLedger.length > 0 && (
                            <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                              <tr>
                                <td colSpan={4} className="px-4 py-2.5 font-bold text-slate-700 text-sm">Grand Total</td>
                                <td className="px-4 py-2.5 text-right font-bold text-slate-800">Rs. {grandBilled.toLocaleString()}</td>
                                <td className="px-4 py-2.5 text-right font-bold text-green-600">Rs. {grandPaid.toLocaleString()}</td>
                                <td className="px-4 py-2.5 text-right font-bold text-orange-600">Rs. {grandBalance.toLocaleString()}</td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                      {filteredLedger.length > invoiceVisibleCount && (
                        <div className="p-3 border-t border-slate-100">
                          <button
                            onClick={() => setInvoiceVisibleCount(c => c + TABLE_PAGE_SIZE)}
                            className="w-full py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all"
                          >
                            Load more ({filteredLedger.length - invoiceVisibleCount} more of {filteredLedger.length})
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ── Individual Customer Ledger Detail ── */
                    <div className="space-y-4">
                      {/* Back + Header */}
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setSelectedLedgerCustomer(null)}
                          className="flex items-center gap-2 text-slate-600 hover:text-red-600 font-semibold transition-colors"
                        >
                          <ArrowRight size={16} className="rotate-180" /> Back to All Customers
                        </button>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Customer Code: {formatCustomerCode(selectedLedgerCustomer.id, selectedLedgerCustomer.type || customers.find(c => String(c.id) === String(selectedLedgerCustomer.id))?.type)}</p>
                        </div>
                      </div>

                      {/* Customer Info + Balance Summary */}
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="bg-gradient-to-r from-red-600 to-red-700 p-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xl font-bold text-white">{selectedLedgerCustomer.name}</h3>
                              <p className="text-red-100 text-sm mt-0.5">
                                {selectedLedgerCustomer.type} · {selectedLedgerCustomer.cell || selectedLedgerCustomer.phone || 'No phone'} · {selectedLedgerCustomer.city || ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-red-200 text-xs uppercase tracking-wide font-semibold mb-1">Outstanding Balance</p>
                              <p className={`text-2xl font-black ${selectedLedgerCustomer.balance <= 0 ? 'text-green-300' : 'text-white'}`}>
                                {selectedLedgerCustomer.balance <= 0 ? '✓ Cleared' : `Rs. ${selectedLedgerCustomer.balance.toLocaleString()}`}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Mini Stats */}
                        <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100">
                          <div className="p-4 text-center">
                            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Total Billed</p>
                            <p className="text-lg font-bold text-slate-800 mt-0.5">Rs. {selectedLedgerCustomer.totalBilled.toLocaleString()}</p>
                          </div>
                          <div className="p-4 text-center">
                            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Total Paid</p>
                            <p className="text-lg font-bold text-green-600 mt-0.5">Rs. {selectedLedgerCustomer.totalPaid.toLocaleString()}</p>
                          </div>
                          <div className="p-4 text-center">
                            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Invoices</p>
                            <p className="text-lg font-bold text-slate-800 mt-0.5">{selectedLedgerCustomer.custInvoices.length}</p>
                          </div>
                        </div>
                      </div>

                      {/* Invoice Ledger Table */}
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="p-4 border-b border-slate-100">
                          <h4 className="font-bold text-slate-800 flex items-center gap-2">
                            <Receipt size={16} className="text-red-500" /> Invoice History
                          </h4>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-100">
                              <tr>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Invoice #</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Vehicle</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Type</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Period</th>
                                <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Amount</th>
                                <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Paid</th>
                                <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Balance</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Status</th>
                                <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Due Date</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {(() => {
                                let runningBalance = 0;
                                return [...selectedLedgerCustomer.custInvoices]
                                  .sort((a, b) => new Date(a.createdDate || a.created_date) - new Date(b.createdDate || b.created_date))
                                  .map(inv => {
                                    const paid    = (inv.payments || []).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
                                    const balance = parseFloat(inv.amount) - paid;
                                    runningBalance += balance;
                                    const status  = getInvoiceStatus(inv);
                                    return (
                                      <tr key={inv.id} className={`hover:bg-slate-50 transition-colors ${balance > 0 && new Date(inv.dueDate) < new Date() ? 'bg-red-50/30' : ''}`}>
                                        <td className="px-5 py-3 font-mono text-sm font-semibold text-slate-700">{inv.invoiceNumber}</td>
                                        <td className="px-5 py-3 text-sm text-slate-600">{inv.createdDate}</td>
                                        <td className="px-5 py-3 text-sm font-medium text-slate-700">{inv.vehicleNo}</td>
                                        <td className="px-5 py-3">
                                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${inv.type === 'Setup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                            {inv.type}
                                          </span>
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-500">
                                          {inv.periodFrom && inv.periodTo ? `${inv.periodFrom} → ${inv.periodTo}` : '—'}
                                        </td>
                                        <td className="px-5 py-3 text-right font-semibold text-slate-800">
                                          Rs. {parseFloat(inv.amount).toLocaleString()}
                                        </td>
                                        <td className="px-5 py-3 text-right font-semibold text-green-600">
                                          Rs. {paid.toLocaleString()}
                                        </td>
                                        <td className="px-5 py-3 text-right font-bold">
                                          <span className={balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                                            Rs. {balance.toLocaleString()}
                                          </span>
                                        </td>
                                        <td className="px-5 py-3">
                                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                            status === 'PAID' ? 'bg-green-100 text-green-700' :
                                            status.includes('OVERDUE') ? 'bg-red-100 text-red-700' :
                                            status === 'PARTIALLY PAID' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-slate-100 text-slate-600'
                                          }`}>{status}</span>
                                        </td>
                                        <td className="px-5 py-3 text-sm text-slate-500">{inv.dueDate || '—'}</td>
                                      </tr>
                                    );
                                  });
                              })()}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                              <tr>
                                <td colSpan={5} className="px-5 py-3 font-bold text-slate-700 text-sm">Totals</td>
                                <td className="px-5 py-3 text-right font-bold text-slate-800">
                                  Rs. {selectedLedgerCustomer.totalBilled.toLocaleString()}
                                </td>
                                <td className="px-5 py-3 text-right font-bold text-green-600">
                                  Rs. {selectedLedgerCustomer.totalPaid.toLocaleString()}
                                </td>
                                <td className="px-5 py-3 text-right font-bold text-red-600">
                                  Rs. {selectedLedgerCustomer.balance.toLocaleString()}
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>

                      {/* Payment History */}
                      {(() => {
                        const allPayments = selectedLedgerCustomer.custInvoices.flatMap(inv =>
                          (inv.payments || []).map(p => ({ ...p, invoiceNumber: inv.invoiceNumber, vehicleNo: inv.vehicleNo }))
                        ).sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date));
                        if (allPayments.length === 0) return null;
                        return (
                          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-slate-100">
                              <h4 className="font-bold text-slate-800 flex items-center gap-2">
                                <DollarSign size={16} className="text-green-500" /> Payment History ({allPayments.length} payments)
                              </h4>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                  <tr>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Invoice #</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Vehicle</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Method</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-slate-600 uppercase">Reference</th>
                                    <th className="px-5 py-3 text-right text-xs font-bold text-slate-600 uppercase">Amount</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {allPayments.map((p, i) => (
                                    <tr key={i} className="hover:bg-green-50/30 transition-colors">
                                      <td className="px-5 py-3 text-sm text-slate-600">{p.payment_date || p.date || '—'}</td>
                                      <td className="px-5 py-3 font-mono text-sm text-slate-700">{p.invoiceNumber}</td>
                                      <td className="px-5 py-3 text-sm text-slate-600">{p.vehicleNo}</td>
                                      <td className="px-5 py-3">
                                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-600">
                                          {p.payment_method || p.method || 'Cash'}
                                        </span>
                                      </td>
                                      <td className="px-5 py-3 text-sm text-slate-500">{p.reference || p.reference_no || '—'}</td>
                                      <td className="px-5 py-3 text-right font-bold text-green-600">
                                        Rs. {parseFloat(p.amount || 0).toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                                  <tr>
                                    <td colSpan={5} className="px-5 py-3 font-bold text-slate-700 text-sm">Total Received</td>
                                    <td className="px-5 py-3 text-right font-bold text-green-600 text-base">
                                      Rs. {allPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0).toLocaleString()}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {activeModule === 'nr_vehicles' && (
          <NRVehiclesModule customers={customers} apiBaseUrl={API_BASE_URL} setActiveModule={setActiveModule} setComplaintView={setComplaintView} setComplaintPrefill={setComplaintPrefill} />
        )}

        {activeModule === 'live_map' && (
          <LiveMap apiBaseUrl={API_BASE_URL} />
        )}

        {activeModule === 'pipeline' && (
          <PipelineModule apiBaseUrl={API_BASE_URL} canImport={true} />
        )}

        {activeModule === 'reconciliation' && (() => {
          const RECON_API = API_BASE_URL + '/reconciliation.php';

          const fetchRecon = async () => {
            setReconLoading(true);
            setReconError(null);
            try {
              const res = await fetch(RECON_API);
              const json = await res.json();
              if (!json.success) throw new Error(json.error || 'Failed');
              setReconData(json);
            } catch(e) {
              setReconError(e.message);
            } finally {
              setReconLoading(false);
            }
          };

          const tabs = [
            { key: 'gps_not_in_crm',  label: 'GPS not in CRM',    color: 'orange', count: reconData?.summary?.gps_not_in_crm  ?? 0 },
            { key: 'crm_not_in_gps',  label: 'CRM not in GPS',    color: 'blue',   count: reconData?.summary?.crm_not_in_gps  ?? 0 },
            { key: 'imei_mismatch',   label: 'IMEI Mismatch',      color: 'red',    count: reconData?.summary?.imei_mismatch   ?? 0 },
            { key: 'missing_pricing', label: 'Missing Pricing',    color: 'purple', count: reconData?.summary?.missing_pricing ?? 0 },
            { key: 'low_pricing',     label: 'Low Pricing',        color: 'amber',  count: reconData?.summary?.low_pricing ?? 0 },
            { key: 'expired_vehicles',label: 'Expired Vehicles',   color: 'gray',   count: reconData?.summary?.expired_vehicles ?? 0 },
          ];

          // Only Master / Admin may approve below-standard pricing.
          const reconRole = String(authUser?.role_name || authUser?.role || '').toLowerCase();
          const canApprovePricing = reconRole === 'master' || reconRole === 'admin';
          const lowLimits = reconData?.low_pricing_thresholds || { setup: 8000, amc: 7000 };

          const approvePricing = async (r) => {
            if (!window.confirm(`Approve pricing for ${r.vehicle_no}?\nSetup: ${r.package_price ?? '—'} · AMC: ${r.amc ?? '—'}`)) return;
            setReconApproving(r.crm_id);
            try {
              const res = await fetch(RECON_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'approve_pricing', vehicle_id: r.crm_id, user_id: authUser?.id }),
              });
              const json = await res.json();
              if (!json.success) throw new Error(json.error || 'Failed');
              // Invoicing for this vehicle is unblocked straight away.
              setPricingPendingIds(prev => { const n = new Set(prev); n.delete(Number(r.crm_id)); return n; });
              // Drop the row locally — no need to re-run the whole GPS check.
              setReconData(d => d ? {
                ...d,
                low_pricing: (d.low_pricing || []).filter(x => x.crm_id !== r.crm_id),
                summary: { ...d.summary, low_pricing: Math.max(0, (d.summary?.low_pricing || 1) - 1) },
              } : d);
            } catch (e) {
              alert('Could not approve: ' + e.message);
            } finally {
              setReconApproving(null);
            }
          };

          const rows = reconData ? reconData[reconTab] : [];
          const q = reconSearch.toLowerCase();
          const filtered = rows.filter(r => {
            const str = JSON.stringify(r).toLowerCase();
            return !q || str.includes(q);
          });

          return (
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <RefreshCw size={20} className="text-red-500" /> GPS ↔ CRM Reconciliation
                    </h2>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {reconData ? `Last run: ${reconData.generated_at} · GPS: ${reconData.summary.gps_total} · CRM Active: ${reconData.summary.crm_active}` : 'Compare GPSWox devices against CRM vehicles'}
                    </p>
                  </div>
                  <button
                    onClick={fetchRecon}
                    disabled={reconLoading}
                    className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-md transition-all flex items-center gap-2 text-sm disabled:opacity-60"
                  >
                    <RefreshCw size={16} className={reconLoading ? 'animate-spin' : ''} />
                    {reconLoading ? 'Running…' : reconData ? 'Re-Run' : 'Run Check'}
                  </button>
                </div>

                {/* Summary tiles (clickable filters) */}
                {reconData && (
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-4">
                    {tabs.map(t => {
                      const cmap = { red: '#d23b4e', orange: '#f5732b', purple: '#7c53d6', blue: '#3b6fd2', green: '#1f9d63', gray: '#64748b', amber: '#c9820f' };
                      const cc = cmap[t.color] || '#64748b';
                      const active = reconTab === t.key;
                      return (
                        <div key={t.key} onClick={() => setReconTab(t.key)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 cursor-pointer transition-all hover:shadow-md"
                          style={{ borderLeft: `3px solid ${cc}`, boxShadow: active ? `0 0 0 2px ${cc}` : undefined }}>
                          <div className="text-xl font-extrabold tabular-nums leading-none" style={{ color: cc }}>{t.count}</div>
                          <div className="text-[11px] text-slate-500 font-semibold mt-1">{t.label}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Error */}
              {reconError && (
                <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium flex items-center gap-2">
                  <AlertCircle size={16} /> {reconError}
                </div>
              )}

              {/* Empty state */}
              {!reconData && !reconLoading && !reconError && (
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                  <RefreshCw size={48} className="mb-4 opacity-30" />
                  <p className="text-lg font-semibold">Click "Run Check" to start reconciliation</p>
                  <p className="text-sm mt-1">Compares GPSWox devices with CRM active vehicles</p>
                </div>
              )}

              {/* Loading */}
              {reconLoading && (
                <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                  <RefreshCw size={48} className="mb-4 animate-spin text-red-400" />
                  <p className="text-lg font-semibold">Running reconciliation...</p>
                </div>
              )}

              {/* Results */}
              {reconData && !reconLoading && (
                <div>
                  {/* Search */}
                  <div className="flex items-center justify-end px-6 pt-4 pb-3 border-b border-slate-100">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-2.5 text-slate-400" size={15} />
                      <input
                        value={reconSearch}
                        onChange={e => setReconSearch(e.target.value)}
                        placeholder="Search..."
                        className="w-full pl-9 pr-4 py-2 rounded-xl border-2 border-slate-200 focus:border-red-400 outline-none text-sm"
                      />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    {reconTab === 'gps_not_in_crm' && (
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">GPS ID</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle Name</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">IMEI</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Expiry Date</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Active</th>
                            <th className="px-4 py-2 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No records found</td></tr>
                          ) : filtered.map((r, i) => (
                            <tr key={i} className="hover:bg-orange-50 transition-colors">
                              <td className="px-4 py-2 text-sm font-mono text-slate-500">{r.gps_id}</td>
                              <td className="px-4 py-2 text-sm font-semibold text-slate-800">{r.name}</td>
                              <td className="px-4 py-2 text-sm font-mono text-slate-600">{r.imei || '—'}</td>
                              <td className="px-4 py-2 text-sm text-slate-600">{r.expiration_date || '—'}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${r.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                  {r.active ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  onClick={() => handleReconAddVehicle(r)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-semibold hover:shadow-md transition-all"
                                  title="Add this vehicle to CRM"
                                >
                                  <Plus size={14} /> Add
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {reconTab === 'crm_not_in_gps' && (
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">CRM ID</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle No</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Device IMEI</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filtered.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No records found</td></tr>
                          ) : filtered.map((r, i) => (
                            <tr key={i} className="hover:bg-blue-50 transition-colors">
                              <td className="px-4 py-2 text-sm font-mono text-slate-500">{r.crm_id}</td>
                              <td className="px-4 py-2 text-sm font-semibold text-slate-800">{r.vehicle_no}</td>
                              <td className="px-4 py-2 text-sm font-mono text-slate-600">{r.device_imei || '—'}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{r.customer_name || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {reconTab === 'imei_mismatch' && (
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle Name</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">CRM IMEI</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">GPS IMEI</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filtered.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No records found</td></tr>
                          ) : filtered.map((r, i) => (
                            <tr key={i} className="hover:bg-red-50 transition-colors">
                              <td className="px-4 py-2 text-sm font-semibold text-slate-800">{r.name}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{r.customer_name || '—'}</td>
                              <td className="px-4 py-2 text-sm font-mono text-blue-600">{r.crm_imei}</td>
                              <td className="px-4 py-2 text-sm font-mono text-orange-600">{r.gps_imei}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {reconTab === 'missing_pricing' && (
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle No</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Setup Charges</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">AMC</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filtered.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No records found</td></tr>
                          ) : filtered.map((r, i) => (
                            <tr key={i} className="hover:bg-purple-50 transition-colors">
                              <td className="px-4 py-2 text-sm font-semibold text-slate-800">{r.vehicle_no}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{r.customer_name || '—'}</td>
                              <td className="px-4 py-2 text-sm">
                                {r.package_price == null
                                  ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Missing</span>
                                  : parseFloat(r.package_price) === 0
                                    ? <span className="text-emerald-600">Free</span>
                                    : <span className="text-slate-700">Rs. {parseFloat(r.package_price).toLocaleString()}</span>
                                }
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {r.amc == null
                                  ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Missing</span>
                                  : parseFloat(r.amc) === 0
                                    ? <span className="text-emerald-600">Free</span>
                                    : <span className="text-slate-700">Rs. {parseFloat(r.amc).toLocaleString()}</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {reconTab === 'low_pricing' && (
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle No</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Setup Charges <span className="normal-case font-medium text-slate-400">(std {Number(lowLimits.setup).toLocaleString()})</span></th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">AMC <span className="normal-case font-medium text-slate-400">(std {Number(lowLimits.amc).toLocaleString()})</span></th>
                            <th className="px-4 py-2 text-right text-xs font-bold text-slate-500 uppercase tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filtered.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No records found</td></tr>
                          ) : filtered.map((r, i) => {
                            const price = (v, low) => v == null
                              ? <span className="text-slate-400">—</span>
                              : <span className={low ? 'font-semibold text-amber-700' : 'text-slate-700'}>
                                  {parseFloat(v) === 0 ? 'Free' : `Rs. ${parseFloat(v).toLocaleString()}`}
                                </span>;
                            return (
                              <tr key={r.crm_id || i} className="hover:bg-amber-50 transition-colors">
                                <td className="px-4 py-2 text-sm font-semibold text-slate-800">{r.vehicle_no}</td>
                                <td className="px-4 py-2 text-sm text-slate-700">{r.customer_name || '—'}</td>
                                <td className="px-4 py-2 text-sm">
                                  {price(r.net_setup ?? r.package_price, r.low_setup)}
                                  {parseFloat(r.commission) > 0 && (
                                    <span className="block text-[11px] text-slate-400">{parseFloat(r.package_price).toLocaleString()} − {parseFloat(r.commission).toLocaleString()} commission</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm">{price(r.amc, r.low_amc)}</td>
                                <td className="px-4 py-2 text-right">
                                  {canApprovePricing ? (
                                    <button
                                      onClick={() => approvePricing(r)}
                                      disabled={reconApproving === r.crm_id}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-60"
                                      style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}
                                    ><CheckCircle size={14} />{reconApproving === r.crm_id ? 'Approving…' : 'Approve'}</button>
                                  ) : (
                                    <span className="text-xs text-slate-400">Needs Master/Admin approval</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}

                    {reconTab === 'expired_vehicles' && (
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Vehicle No</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">IMEI</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Expired On</th>
                            <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Since</th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filtered.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No expired vehicles</td></tr>
                          ) : filtered.map((r, i) => (
                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-2 text-sm font-semibold text-slate-800">{r.vehicle_no}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{r.customer_name || '—'}</td>
                              <td className="px-4 py-2 text-sm text-slate-600 font-mono">{r.device_imei || '—'}</td>
                              <td className="px-4 py-2 text-sm text-slate-700">{r.expiration_date ? String(r.expiration_date).slice(0, 10) : '—'}</td>
                              <td className="px-4 py-2 text-sm">
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{r.days_expired}d ago</span>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  onClick={() => { setReconExtend(r); setExtUntil(''); setExtReason(''); }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-white whitespace-nowrap"
                                  style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}
                                >Extend</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* Extend service window (Expired Vehicles) */}
                    {reconExtend && (() => {
                      const pkToday = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
                      const plus = (n) => { const d = new Date(pkToday + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };
                      const maxDate = plus(30);
                      const fmt = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                      const save = async () => {
                        if (!extUntil) { alert('Choose how long to extend.'); return; }
                        if (!extReason.trim()) { alert('Please write a reason.'); return; }
                        setExtSaving(true);
                        try {
                          const res = await fetch(RECON_API, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ action: 'extend_service', vehicle_id: reconExtend.crm_id, until_date: extUntil, reason: extReason.trim(), user_id: authUser?.id }),
                          });
                          const j = await res.json();
                          if (!j.success) throw new Error(j.error || 'Failed');
                          setReconData(d => d ? {
                            ...d,
                            expired_vehicles: (d.expired_vehicles || []).filter(x => x.crm_id !== reconExtend.crm_id),
                            summary: { ...d.summary, expired_vehicles: Math.max(0, (d.summary?.expired_vehicles || 1) - 1) },
                          } : d);
                          setReconExtend(null);
                        } catch (e) {
                          alert('Could not extend: ' + e.message);
                        } finally {
                          setExtSaving(false);
                        }
                      };
                      return (
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                            <div className="flex items-center justify-between p-5 border-b border-slate-200">
                              <h3 className="font-bold text-slate-800 text-lg">Extend Service</h3>
                              <button onClick={() => setReconExtend(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
                            </div>
                            <div className="p-5 space-y-4">
                              <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
                                <p className="text-slate-600">Vehicle: <span className="font-semibold text-slate-800">{reconExtend.vehicle_no}</span> · {reconExtend.customer_name || '—'}</p>
                                <p className="text-slate-600">Expired: <span className="font-semibold text-red-600">{reconExtend.expiration_date ? String(reconExtend.expiration_date).slice(0, 10) : '—'}</span> ({reconExtend.days_expired}d ago)</p>
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Extend until *</label>
                                <div className="flex gap-2 mb-2">
                                  {[3, 7, 15].map(n => (
                                    <button key={n} type="button" onClick={() => setExtUntil(plus(n))}
                                      className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition-colors ${extUntil === plus(n) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                      +{n} days
                                    </button>
                                  ))}
                                </div>
                                <input type="date" value={extUntil} min={pkToday} max={maxDate} onChange={e => setExtUntil(e.target.value)}
                                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all" />
                                <p className="text-xs text-slate-400 mt-1">
                                  {extUntil ? `Service will run until ${fmt(extUntil)}, 11:59 PM.` : 'Pick +3 / +7 / +15 days or a date.'} Max {fmt(maxDate)}.
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Reason *</label>
                                <textarea value={extReason} onChange={e => setExtReason(e.target.value)} rows={2}
                                  placeholder="e.g. customer promised payment on 5 Nov"
                                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all resize-none text-sm" />
                                <p className="text-xs text-slate-400 mt-1">Saved with your name in the vehicle's IMEI Activity Log.</p>
                              </div>
                              <div className="flex gap-3 pt-1">
                                <button onClick={save} disabled={extSaving || !extUntil || !extReason.trim()}
                                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-md disabled:opacity-40 transition-all">
                                  {extSaving ? 'Saving...' : 'Extend Service'}
                                </button>
                                <button onClick={() => setReconExtend(null)} className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-all">Cancel</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {filtered.length > 0 && (
                    <div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-400">
                      Showing {filtered.length} of {rows.length} records
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {activeModule === 'complaints' && (
          <ComplaintsModule
            complaints={complaints}
            complaintStats={complaintStats}
            complaintView={complaintView}
            setComplaintView={setComplaintView}
            selectedComplaint={selectedComplaint}
            loading={complaintLoading}
            search={complaintSearch} setSearch={setComplaintSearch}
            filterStatus={complaintStatusFilter} setFilterStatus={setComplaintStatusFilter}
            filterPriority={complaintPriorityFilter} setFilterPriority={setComplaintPriorityFilter}
            filterAssigned={complaintAssignedFilter} setFilterAssigned={setComplaintAssignedFilter}
            dateFrom={complaintDateFrom} setDateFrom={setComplaintDateFrom}
            dateTo={complaintDateTo} setDateTo={setComplaintDateTo}
            replyText={complaintReplyText} setReplyText={setComplaintReplyText}
            sendingReply={sendingComplaintReply}
            customers={customers}
            employees={employees}
            onOpenDetail={openComplaintDetail}
            onDelete={handleDeleteComplaint}
            onBulkDelete={handleBulkDeleteComplaints}
            onStatusChange={handleComplaintStatusChange}
            onTaskStage={handleComplaintTaskStage}
            onConvert={handleComplaintConvert}
            onAssign={handleComplaintAssign}
            onSendReply={handleComplaintReply}
            onSubmitNew={handleComplaintSubmit}
            complaintPrefill={complaintPrefill}
            onRefresh={loadComplaints}
            can={{ create: canDo('complaints','create'), edit: canDo('complaints','edit'), delete: canDo('complaints','delete') }}
          />
        )}

        {activeModule === 'user_management' && (
          <UserManagement />
        )}

        {activeModule === 'redo' && (
          <ServicesModule
            customers={customers}
            employees={employees}
            inventory={inventory}
            vehicleModels={vehicleModels}
            apiBaseUrl={API_BASE_URL}
            currentUser={authUser}
            onInvoiceCreated={async () => { await loadInvoices(); await loadInventory(); await loadCustomers(); }}
          />
        )}

        {activeModule === 'reports' && (() => {
          const reportData = inventoryReportType === 'tech_stock' ? getTechnicianStockData() : null;
          const visibleRows = reportData ? reportData.rows.filter(r => reportShowZeroStock || r.totalDevices > 0) : [];

          return (
          <div className="bg-white rounded-xl shadow-md border border-slate-200">
            {/* Reports Header — compact head tabs + sub-report chips (frees full width for content) */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-4 flex-wrap mb-3">
                <h2 className="text-xl font-bold text-slate-800">Reports</h2>
                <div className="flex gap-1.5">
                  {canReportHead('inventory') && (
                    <button onClick={() => setReportsHead('inventory')} className={`px-3.5 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-all ${reportsHead === 'inventory' ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}><Package size={15} /> Inventory</button>
                  )}
                  {canReportHead('invoices') && (
                    <button onClick={() => setReportsHead('invoices')} className={`px-3.5 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-all ${reportsHead === 'invoices' ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}><DollarSign size={15} /> Financials</button>
                  )}
                  {canReportHead('sales') && (
                    <button onClick={() => setReportsHead('sales')} className={`px-3.5 py-1.5 rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-all ${reportsHead === 'sales' ? 'bg-red-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}><Target size={15} /> Sales</button>
                  )}
                </div>
              </div>
              {/* Sub-report chips for the active head */}
              {(() => {
                const head = reportsHead;
                const curType = head === 'inventory' ? inventoryReportType : head === 'invoices' ? invoiceReportType : salesReportType;
                const setType = (t) => { if (head === 'inventory') setInventoryReportType(t); else if (head === 'invoices') setInvoiceReportType(t); else setSalesReportType(t); };
                const secs = REPORT_SECTIONS.filter(s => s.head === head && canReport(s.key));
                if (secs.length === 0) return null;
                return (
                  <div className="flex gap-2 flex-wrap">
                    {secs.map(s => (
                      <button key={s.key} onClick={() => setType(s.type)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${curType === s.type ? 'bg-red-50 text-red-600 border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{s.label}</button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {reportsHead === 'inventory' && (
              <div className="flex">
                {/* Reports sub-menu — moved to header chips, hidden */}
                <div className="hidden">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">Inventory Reports</p>
                  {canReport('inv_tech_stock') && (
                  <button
                    onClick={() => setInventoryReportType('tech_stock')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      inventoryReportType === 'tech_stock'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Users size={16} />
                    Technician/Employee Wise Stock
                  </button>
                  )}
                  {canReport('inv_stock_issuance') && (
                  <button
                    onClick={() => setInventoryReportType('stock_issuance')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      inventoryReportType === 'stock_issuance'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <FileText size={16} />
                    Stock Issuance Report
                  </button>
                  )}
                  <p className="text-xs text-slate-400 px-3 pt-3">More reports coming soon</p>
                </div>

                {/* Report content */}
                <div className="flex-1 p-4">
                  {inventoryReportType === 'tech_stock' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Technician/Employee Wise Stock Report</h3>
                          <p className="text-sm text-slate-500">Devices currently issued to (held by) each technician/employee</p>
                        </div>
                        <button
                          onClick={exportTechStockReportToCSV}
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold"
                        >
                          <Download size={16} />
                          Export CSV
                        </button>
                      </div>

                      {/* Summary cards */}
                      <div className="grid grid-cols-3 gap-4 mb-5">
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                          <div className="flex items-center gap-3">
                            <div className="bg-blue-500 p-1.5 rounded-lg">
                              <Users size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="text-xs text-blue-700 font-semibold">Technicians Holding Stock</p>
                              <p className="text-xl font-bold text-blue-800">{reportData.techCount}</p>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                          <div className="flex items-center gap-3">
                            <div className="bg-orange-500 p-1.5 rounded-lg">
                              <Package size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="text-xs text-orange-700 font-semibold">Total Devices Held</p>
                              <p className="text-xl font-bold text-orange-800">{reportData.grandTotal}</p>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className="bg-slate-500 p-1.5 rounded-lg">
                              <Box size={20} className="text-white" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-600 font-semibold">Avg. Devices / Technician</p>
                              <p className="text-xl font-bold text-slate-800">
                                {reportData.techCount > 0 ? (reportData.grandTotal / reportData.techCount).toFixed(1) : '0.0'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Filters */}
                      <div className="flex gap-3 mb-4">
                        <div className="flex-1 relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <input
                            type="text"
                            placeholder="Search technician/employee..."
                            value={reportTechSearch}
                            onChange={(e) => setReportTechSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        <select
                          value={reportDeviceTypeFilter}
                          onChange={(e) => setReportDeviceTypeFilter(e.target.value)}
                          className="px-4 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-red-400 outline-none text-sm"
                        >
                          <option value="all">All Device Types</option>
                          {reportData.deviceTypes.map(dt => (
                            <option key={dt} value={dt}>{dt}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-50 border-2 border-slate-200 text-sm text-slate-600 cursor-pointer whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={reportShowZeroStock}
                            onChange={(e) => setReportShowZeroStock(e.target.checked)}
                          />
                          Show zero-stock employees
                        </label>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 w-8"></th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Employee/Technician</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Designation</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Device Types Held</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Total Devices</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {visibleRows.map((r) => {
                              const rowKey = r.techId != null ? String(r.techId) : r.techName;
                              const isExpanded = expandedReportTech === rowKey;
                              return (
                                <React.Fragment key={rowKey}>
                                  <tr
                                    className={`hover:bg-slate-50 cursor-pointer ${r.totalDevices === 0 ? 'opacity-60' : ''}`}
                                    onClick={() => r.totalDevices > 0 && setExpandedReportTech(isExpanded ? null : rowKey)}
                                  >
                                    <td className="px-4 py-3 text-slate-400">
                                      {r.totalDevices > 0 && (
                                        <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                      )}
                                    </td>
                                    <td className="px-4 py-3">
                                      <p className="text-sm font-semibold text-slate-800">{r.techName}</p>
                                      {r.empStatus === 'inactive' && (
                                        <span className="text-xs text-red-500 font-semibold">Inactive employee</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{r.designation || '—'}</td>
                                    <td className="px-4 py-3">
                                      <div className="flex flex-wrap gap-1.5">
                                        {Object.entries(r.byType).map(([type, count]) => (
                                          <span key={type} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                                            {type}: {count}
                                          </span>
                                        ))}
                                        {r.totalDevices === 0 && <span className="text-xs text-slate-400">No devices held</span>}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                        r.totalDevices > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                                      }`}>
                                        {r.totalDevices}
                                      </span>
                                    </td>
                                  </tr>
                                  {isExpanded && (
                                    <tr>
                                      <td colSpan={5} className="px-4 py-3 bg-slate-50">
                                        <table className="w-full">
                                          <thead>
                                            <tr className="text-xs text-slate-500">
                                              <th className="text-left font-semibold py-1.5 px-3">IMEI</th>
                                              <th className="text-left font-semibold py-1.5 px-3">Device Type</th>
                                              <th className="text-left font-semibold py-1.5 px-3">ICCID</th>
                                              <th className="text-left font-semibold py-1.5 px-3">SIM Number</th>
                                              <th className="text-left font-semibold py-1.5 px-3">Health</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-200">
                                            {r.items.map(item => (
                                              <tr key={item.id} className="text-sm">
                                                <td className="py-1.5 px-3 font-mono text-slate-700">{item.imei}</td>
                                                <td className="py-1.5 px-3 text-slate-600">{item.deviceType || 'Unspecified'}</td>
                                                <td className="py-1.5 px-3 text-slate-600">{item.iccid || '—'}</td>
                                                <td className="py-1.5 px-3 text-slate-600">{item.simNumber || '—'}</td>
                                                <td className="py-1.5 px-3">
                                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                    item.healthStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                                  }`}>
                                                    {item.healthStatus || 'active'}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>

                        {visibleRows.length === 0 && (
                          <div className="text-center py-12 text-slate-400">
                            <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No technicians currently holding stock</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {inventoryReportType === 'stock_issuance' && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-800">Stock Issuance Report</h3>
                        <p className="text-sm text-slate-500">Runs a single fast query against your database — set filters and click "Load Report".</p>
                      </div>

                      {/* Query filters */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Employee</label>
                            <EmployeeSearchSelect employees={employees}
                              value={reportIssuanceEmployeeFilter === 'all' ? '' : reportIssuanceEmployeeFilter}
                              onChange={(id) => setReportIssuanceEmployeeFilter(id || 'all')}
                              activeOnly placeholder="All employees — search to filter…" />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Issuance Date From</label>
                            <input
                              type="date"
                              value={reportIssuanceDateFrom}
                              onChange={(e) => setReportIssuanceDateFrom(e.target.value)}
                              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-200 focus:border-red-400 outline-none text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Issuance Date To</label>
                            <input
                              type="date"
                              value={reportIssuanceDateTo}
                              onChange={(e) => setReportIssuanceDateTo(e.target.value)}
                              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-200 focus:border-red-400 outline-none text-sm"
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              onClick={runStockIssuanceReport}
                              disabled={reportIssuanceLoading}
                              className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg hover:shadow-md transition-all flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50"
                            >
                              {reportIssuanceLoading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
                              {reportIssuanceLoading ? 'Loading...' : 'Load Report'}
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-5">
                          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={reportIssuanceIncludeAvailable}
                              onChange={(e) => setReportIssuanceIncludeAvailable(e.target.checked)}
                            />
                            Include Available (currently with technician)
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={reportIssuanceIncludeInstalled}
                              onChange={(e) => setReportIssuanceIncludeInstalled(e.target.checked)}
                            />
                            Include Installed (on a vehicle)
                          </label>
                        </div>
                        {reportIssuanceError && (
                          <p className="text-sm text-red-600 font-semibold flex items-center gap-1.5">
                            <AlertCircle size={14} /> {reportIssuanceError}
                          </p>
                        )}
                        {reportIssuanceLoading && (
                          <p className="text-xs text-slate-500 flex items-center gap-1.5">
                            <RefreshCw size={12} className="animate-spin" /> Loading report...
                          </p>
                        )}
                      </div>

                      {/* Results */}
                      {reportIssuanceResults === null ? (
                        <div className="text-center py-16 text-slate-400">
                          <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg">Set your filters above and click "Load Report"</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm text-slate-500">{reportIssuanceResults.length} record{reportIssuanceResults.length !== 1 ? 's' : ''} found</p>
                            <button
                              onClick={exportIssuanceReportToCSV}
                              disabled={reportIssuanceResults.length === 0}
                              className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                            >
                              <Download size={16} />
                              Export CSV
                            </button>
                          </div>
                          <div className="overflow-x-auto border border-slate-200 rounded-xl">
                            <table className="w-full">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Employee Code</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Name</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Designation</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Issued Device</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">IMEI</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Issuance Date</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Status</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Vehicle Number</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Installation Date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {reportIssuanceResults.map((r, i) => (
                                  <tr key={`${r.imei}-${i}`} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 text-sm">
                                      {r.employeeCode ? (
                                        <span className="inline-block px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 font-mono text-xs font-bold">{r.employeeCode}</span>
                                      ) : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-800">{r.name}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{r.designation || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{r.deviceType}</td>
                                    <td className="px-4 py-3 text-sm font-mono text-slate-700">{r.imei}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{r.issuanceDate || '—'}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        r.status === 'Installed' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                                      }`}>
                                        {r.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-700">{r.vehicleNo || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{r.installDate || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {reportIssuanceResults.length === 0 && (
                              <div className="text-center py-12 text-slate-400">
                                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">No records found matching your filters</p>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                </div>
              </div>
            )}

            {reportsHead === 'invoices' && (
              <div className="flex">
                {/* Reports sub-menu — moved to header chips, hidden */}
                <div className="hidden">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">Financial Reports</p>
                  {canReport('fin_imei_profit') && (
                  <button
                    onClick={() => setInvoiceReportType('imei_profit')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      invoiceReportType === 'imei_profit'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <DollarSign size={16} />
                    IMEI Profit Report
                  </button>
                  )}
                  {canReport('fin_vehicle_profit') && (
                  <button
                    onClick={() => setInvoiceReportType('vehicle_profit')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      invoiceReportType === 'vehicle_profit'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Car size={16} />
                    Vehicle Profit Report
                  </button>
                  )}
                  <p className="text-xs text-slate-400 px-3 pt-3">More reports coming soon</p>
                </div>

                {/* Report content */}
                <div className="flex-1 p-4">
                  {invoiceReportType === 'imei_profit' && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-800">IMEI Profit Report</h3>
                        <p className="text-sm text-slate-500">Enter an IMEI to see everything billed — and earned — against that specific device.</p>
                      </div>

                      {/* Search */}
                      <div className="flex gap-3 mb-5">
                        <div className="flex-1 relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <input
                            type="text"
                            placeholder="Search by IMEI..."
                            value={imeiProfitSearch}
                            onChange={(e) => { setImeiProfitSearch(e.target.value); setShowImeiSuggestions(true); }}
                            onFocus={() => setShowImeiSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowImeiSuggestions(false), 150)}
                            onKeyDown={(e) => e.key === 'Enter' && (setShowImeiSuggestions(false), runImeiProfitSearch())}
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-red-400 outline-none text-sm font-mono"
                          />
                          {showImeiSuggestions && imeiProfitSearch.trim().length >= 2 && (() => {
                            const q = imeiProfitSearch.trim().toLowerCase();
                            const matches = inventory.filter(item => item.imei && item.imei.toLowerCase().includes(q)).slice(0, 8);
                            if (matches.length === 0) return null;
                            return (
                              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-64 overflow-y-auto">
                                {matches.map(item => (
                                  <button
                                    key={item.id}
                                    onMouseDown={(e) => { e.preventDefault(); setImeiProfitSearch(item.imei); setShowImeiSuggestions(false); runImeiProfitSearch(item.imei); }}
                                    className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between border-b border-slate-100 last:border-0"
                                  >
                                    <span className="font-mono text-sm text-slate-800">{item.imei}</span>
                                    <span className="text-xs text-slate-400">{item.deviceType || 'Unspecified'}</span>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          onClick={() => runImeiProfitSearch()}
                          disabled={imeiProfitLoading}
                          className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-2.5 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                        >
                          {imeiProfitLoading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
                          {imeiProfitLoading ? 'Searching...' : 'Search'}
                        </button>
                      </div>

                      {imeiProfitError && (
                        <p className="text-sm text-red-600 font-semibold flex items-center gap-1.5 mb-4">
                          <AlertCircle size={14} /> {imeiProfitError}
                        </p>
                      )}

                      {!imeiProfitResult && !imeiProfitLoading && !imeiProfitError && (
                        <div className="text-center py-16 text-slate-400">
                          <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg">Search an IMEI to see its profit report</p>
                        </div>
                      )}

                      {imeiProfitResult && (
                        <div>
                          {/* Device context */}
                          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 flex items-center justify-between">
                            <div>
                              <p className="font-mono font-bold text-slate-800">{imeiProfitResult.imei}</p>
                              <p className="text-sm text-slate-500">
                                {imeiProfitResult.device?.device_type || 'Unspecified'}
                                {imeiProfitResult.device?.status ? ` — ${imeiProfitResult.device.status}` : ''}
                                {imeiProfitResult.device?.holder_name ? ` — Held by ${imeiProfitResult.device.holder_name}` : ''}
                              </p>
                            </div>
                            <button
                              onClick={exportImeiProfitToCSV}
                              disabled={imeiProfitResult.invoices.length === 0}
                              className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                            >
                              <Download size={16} />
                              Export CSV
                            </button>
                          </div>

                          {/* Summary cards */}
                          <div className="grid grid-cols-3 gap-4 mb-5">
                            <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                              <p className="text-xs text-blue-700 font-semibold">Total Invoiced</p>
                              <p className="text-xl font-bold text-blue-800">Rs. {imeiProfitResult.total_invoiced.toLocaleString()}</p>
                            </div>
                            <div className="rounded-xl p-3 border bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                              <p className="text-xs text-green-700 font-semibold">Earned (Paid)</p>
                              <p className="text-xl font-bold text-green-800">Rs. {imeiProfitResult.total_paid.toLocaleString()}</p>
                            </div>
                            <div className="rounded-xl p-3 border bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                              <p className="text-xs text-orange-700 font-semibold">Outstanding</p>
                              <p className="text-xl font-bold text-orange-800">Rs. {imeiProfitResult.total_balance.toLocaleString()}</p>
                            </div>
                          </div>

                          {/* Invoice breakdown */}
                          <div className="overflow-x-auto border border-slate-200 rounded-xl">
                            <table className="w-full">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Invoice #</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Type</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Customer</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Vehicle</th>
                                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Amount</th>
                                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Paid</th>
                                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Balance</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Status</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Issue Date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {imeiProfitResult.invoices.map(inv => (
                                  <tr key={inv.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-800">{inv.invoice_number}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600 capitalize">{inv.invoice_type}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{inv.customer_name || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{inv.vehicle_no || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">Rs. {Number(inv.amount - (inv.discount || 0)).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm text-right text-green-600">Rs. {Number(inv.paid_amount).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm text-right text-orange-600">Rs. {Number(inv.balance).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                                        inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                        inv.status === 'partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {inv.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{inv.issue_date}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {imeiProfitResult.invoices.length === 0 && (
                              <div className="text-center py-12 text-slate-400">
                                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">No invoices billed against this device yet</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {invoiceReportType === 'vehicle_profit' && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-800">Vehicle Profit Report</h3>
                        <p className="text-sm text-slate-500">Enter a vehicle number to see everything billed — and earned — against that vehicle.</p>
                      </div>

                      {/* Search */}
                      <div className="flex gap-3 mb-5">
                        <div className="flex-1 relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                          <input
                            type="text"
                            placeholder="Search by vehicle number..."
                            value={vehicleProfitSearch}
                            onChange={(e) => { setVehicleProfitSearch(e.target.value); setShowVehicleSuggestions(true); }}
                            onFocus={() => setShowVehicleSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowVehicleSuggestions(false), 150)}
                            onKeyDown={(e) => e.key === 'Enter' && (setShowVehicleSuggestions(false), runVehicleProfitSearch())}
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border-2 border-slate-200 focus:border-red-400 outline-none text-sm font-mono uppercase"
                          />
                          {showVehicleSuggestions && vehicleProfitSearch.trim().length >= 2 && (() => {
                            const q = vehicleProfitSearch.trim().toLowerCase();
                            const seen = new Set();
                            const matches = [];
                            for (const cust of customers) {
                              for (const v of (cust.vehicles || [])) {
                                if (v.vehicleNo && v.vehicleNo.toLowerCase().includes(q) && !seen.has(v.vehicleNo)) {
                                  seen.add(v.vehicleNo);
                                  matches.push({ ...v, customerName: cust.name });
                                  if (matches.length >= 8) break;
                                }
                              }
                              if (matches.length >= 8) break;
                            }
                            if (matches.length === 0) return null;
                            return (
                              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-64 overflow-y-auto">
                                {matches.map(v => (
                                  <button
                                    key={v.id}
                                    onMouseDown={(e) => { e.preventDefault(); setVehicleProfitSearch(v.vehicleNo); setShowVehicleSuggestions(false); runVehicleProfitSearch(v.vehicleNo); }}
                                    className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between border-b border-slate-100 last:border-0"
                                  >
                                    <span className="font-mono text-sm text-slate-800">{v.vehicleNo}</span>
                                    <span className="text-xs text-slate-400">{v.customerName}</span>
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <button
                          onClick={() => runVehicleProfitSearch()}
                          disabled={vehicleProfitLoading}
                          className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-2.5 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                        >
                          {vehicleProfitLoading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
                          {vehicleProfitLoading ? 'Searching...' : 'Search'}
                        </button>
                      </div>

                      {vehicleProfitError && (
                        <p className="text-sm text-red-600 font-semibold flex items-center gap-1.5 mb-4">
                          <AlertCircle size={14} /> {vehicleProfitError}
                        </p>
                      )}

                      {!vehicleProfitResult && !vehicleProfitLoading && !vehicleProfitError && (
                        <div className="text-center py-16 text-slate-400">
                          <Car className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg">Search a vehicle number to see its profit report</p>
                        </div>
                      )}

                      {vehicleProfitResult && (
                        <div>
                          {/* Vehicle context */}
                          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 flex items-center justify-between">
                            <div>
                              <p className="font-bold text-slate-800">{vehicleProfitResult.vehicle_no}</p>
                              <p className="text-sm text-slate-500">
                                {vehicleProfitResult.vehicle?.model || 'Unspecified model'}
                                {vehicleProfitResult.vehicle?.customer_name ? ` — ${vehicleProfitResult.vehicle.customer_name}` : ''}
                                {vehicleProfitResult.vehicle?.device_imei ? ` — IMEI ${vehicleProfitResult.vehicle.device_imei}` : ''}
                                {vehicleProfitResult.vehicle?.status ? ` — ${vehicleProfitResult.vehicle.status}` : ''}
                              </p>
                            </div>
                            <button
                              onClick={exportVehicleProfitToCSV}
                              disabled={vehicleProfitResult.invoices.length === 0}
                              className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold disabled:opacity-50"
                            >
                              <Download size={16} />
                              Export CSV
                            </button>
                          </div>

                          {/* Summary cards */}
                          <div className="grid grid-cols-3 gap-4 mb-5">
                            <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                              <p className="text-xs text-blue-700 font-semibold">Total Invoiced</p>
                              <p className="text-xl font-bold text-blue-800">Rs. {vehicleProfitResult.total_invoiced.toLocaleString()}</p>
                            </div>
                            <div className="rounded-xl p-3 border bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                              <p className="text-xs text-green-700 font-semibold">Earned (Paid)</p>
                              <p className="text-xl font-bold text-green-800">Rs. {vehicleProfitResult.total_paid.toLocaleString()}</p>
                            </div>
                            <div className="rounded-xl p-3 border bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                              <p className="text-xs text-orange-700 font-semibold">Outstanding</p>
                              <p className="text-xl font-bold text-orange-800">Rs. {vehicleProfitResult.total_balance.toLocaleString()}</p>
                            </div>
                          </div>

                          {/* Invoice breakdown */}
                          <div className="overflow-x-auto border border-slate-200 rounded-xl">
                            <table className="w-full">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Invoice #</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Type</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Customer</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">IMEI</th>
                                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Amount</th>
                                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Paid</th>
                                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Balance</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Status</th>
                                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Issue Date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {vehicleProfitResult.invoices.map(inv => (
                                  <tr key={inv.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 text-sm font-semibold text-slate-800">{inv.invoice_number}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600 capitalize">{inv.invoice_type}</td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{inv.customer_name || '—'}</td>
                                    <td className="px-4 py-3 text-sm font-mono text-slate-600">{inv.imei || '—'}</td>
                                    <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">Rs. {Number(inv.amount - (inv.discount || 0)).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm text-right text-green-600">Rs. {Number(inv.paid_amount).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm text-right text-orange-600">Rs. {Number(inv.balance).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                                        inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                        inv.status === 'partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {inv.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">{inv.issue_date}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {vehicleProfitResult.invoices.length === 0 && (
                              <div className="text-center py-12 text-slate-400">
                                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                <p className="text-lg">No invoices billed against this vehicle yet</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {reportsHead === 'sales' && (() => {
              const salesData = getSalesPersonPerformanceData();
              return (
              <div className="flex">
                {/* Reports sub-menu — moved to header chips, hidden */}
                <div className="hidden">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">Sales Reports</p>
                  {canReport('sales_performance') && (
                  <button
                    onClick={() => setSalesReportType('performance')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      salesReportType === 'performance'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Target size={16} />
                    Salesperson Wise Performance
                  </button>
                  )}
                  {canReport('sales_trend') && (
                  <button
                    onClick={() => setSalesReportType('trend')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      salesReportType === 'trend'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <TrendingUp size={16} />
                    Sales Trend Over Time
                  </button>
                  )}
                  {canReport('sales_renewal') && (
                  <button
                    onClick={() => setSalesReportType('renewal')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      salesReportType === 'renewal'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <RefreshCw size={16} />
                    Renewal Performance
                  </button>
                  )}
                  {canReport('sales_upcoming') && (
                  <button
                    onClick={() => setSalesReportType('upcoming_renewals')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      salesReportType === 'upcoming_renewals'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Bell size={16} />
                    Upcoming Renewals
                  </button>
                  )}
                  {canReport('sales_by_area') && (
                  <button
                    onClick={() => setSalesReportType('by_area')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                      salesReportType === 'by_area'
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <MapPin size={16} />
                    Sales by City / Area
                  </button>
                  )}
                  <p className="text-xs text-slate-400 px-3 pt-3">More reports coming soon</p>
                </div>

                {/* Report content */}
                <div className="flex-1 p-4">
                  {salesReportType === 'performance' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Salesperson Wise Performance</h3>
                          <p className="text-sm text-slate-500">Customers and vehicles with a Setup sale in the selected date range, per salesperson (renewal/AMC invoices excluded).</p>
                        </div>
                        <button
                          onClick={exportSalesPerformanceToCSV}
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold"
                        >
                          <Download size={16} />
                          Export CSV
                        </button>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap items-end gap-3 mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Invoice Date From</label>
                          <input
                            type="date"
                            value={salesReportFrom}
                            onChange={(e) => setSalesReportFrom(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Invoice Date To</label>
                          <input
                            type="date"
                            value={salesReportTo}
                            onChange={(e) => setSalesReportTo(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Sort By</label>
                          <select
                            value={salesReportSortBy}
                            onChange={(e) => setSalesReportSortBy(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          >
                            <option value="revenue">Revenue (Invoiced)</option>
                            <option value="customers">Customers</option>
                            <option value="vehicles">Vehicles</option>
                            <option value="outstanding">Outstanding</option>
                          </select>
                        </div>
                        {(salesReportFrom || salesReportTo) && (
                          <button
                            onClick={() => { setSalesReportFrom(''); setSalesReportTo(''); }}
                            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-200 transition-colors"
                          >
                            Clear dates
                          </button>
                        )}
                      </div>

                      {/* Summary cards */}
                      <div className="grid grid-cols-5 gap-4 mb-5">
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                          <p className="text-xs text-slate-500 font-semibold">Salespeople</p>
                          <p className="text-xl font-bold text-slate-800">{salesData.rows.length}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
                          <p className="text-xs text-violet-700 font-semibold">New Sales</p>
                          <p className="text-xl font-bold text-violet-800">{salesData.totals.invoiceCount}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                          <p className="text-xs text-blue-700 font-semibold">Total Invoiced</p>
                          <p className="text-xl font-bold text-blue-800">Rs. {salesData.totals.invoiced.toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                          <p className="text-xs text-green-700 font-semibold">Total Collected</p>
                          <p className="text-xl font-bold text-green-800">Rs. {salesData.totals.paid.toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                          <p className="text-xs text-orange-700 font-semibold">Outstanding</p>
                          <p className="text-xl font-bold text-orange-800">Rs. {salesData.totals.balance.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Salesperson</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Customers</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Vehicles</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">No of Sales</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Total Invoiced</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Collected</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Outstanding</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {salesData.rows.map(r => (
                              <tr key={r.name} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-semibold text-slate-800">{r.name}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.customerCount}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.vehicleCount}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.invoiceCount}</td>
                                <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">Rs. {r.invoiced.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-right text-green-600">Rs. {r.paid.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-right text-orange-600">Rs. {r.balance.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {salesData.rows.length === 0 && (
                          <div className="text-center py-12 text-slate-400">
                            <Target className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No sales data available for the selected filters</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {salesReportType === 'trend' && (() => {
                    const trendData = getSalesTrendData();
                    return (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Sales Trend Over Time</h3>
                          <p className="text-sm text-slate-500">Month-wise New Sales (Setup invoices) — count and revenue.</p>
                        </div>
                        <button
                          onClick={exportSalesTrendToCSV}
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold"
                        >
                          <Download size={16} />
                          Export CSV
                        </button>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap items-end gap-3 mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Invoice Date From</label>
                          <input
                            type="date"
                            value={salesReportFrom}
                            onChange={(e) => setSalesReportFrom(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Invoice Date To</label>
                          <input
                            type="date"
                            value={salesReportTo}
                            onChange={(e) => setSalesReportTo(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        {(salesReportFrom || salesReportTo) && (
                          <button
                            onClick={() => { setSalesReportFrom(''); setSalesReportTo(''); }}
                            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-200 transition-colors"
                          >
                            Clear dates
                          </button>
                        )}
                        {!salesReportFrom && !salesReportTo && (
                          <p className="text-xs text-slate-400 pb-2">Showing the last 12 months. Pick dates for a custom range.</p>
                        )}
                      </div>

                      {/* Summary cards */}
                      <div className="grid grid-cols-3 gap-4 mb-5">
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
                          <p className="text-xs text-violet-700 font-semibold">New Sales</p>
                          <p className="text-xl font-bold text-violet-800">{trendData.totals.count}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                          <p className="text-xs text-blue-700 font-semibold">Total Invoiced</p>
                          <p className="text-xl font-bold text-blue-800">Rs. {trendData.totals.invoiced.toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                          <p className="text-xs text-green-700 font-semibold">Total Collected</p>
                          <p className="text-xl font-bold text-green-800">Rs. {trendData.totals.paid.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Bar chart (revenue by month) */}
                      {trendData.months.length > 0 && (
                        <div className="border border-slate-200 rounded-xl p-4 mb-5">
                          <div className="flex items-end gap-2 h-48">
                            {trendData.months.map(m => (
                              <div key={m.key} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                                <div className="text-[10px] font-semibold text-slate-500 mb-1">
                                  {m.invoiced > 0 ? `${Math.round(m.invoiced / 1000)}k` : ''}
                                </div>
                                <div
                                  className="w-full bg-gradient-to-t from-red-500 to-red-300 rounded-t-md transition-all"
                                  style={{ height: `${Math.max(4, (m.invoiced / trendData.maxInvoiced) * 100)}%` }}
                                  title={`${m.label}: Rs. ${m.invoiced.toLocaleString()} (${m.count} sales)`}
                                ></div>
                                <div className="text-[10px] text-slate-500 mt-1 whitespace-nowrap">{m.label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Month</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">No of Sales</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Total Invoiced</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Collected</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {trendData.months.map(m => (
                              <tr key={m.key} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-semibold text-slate-800">{m.label}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{m.count}</td>
                                <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">Rs. {m.invoiced.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-right text-green-600">Rs. {m.paid.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {trendData.months.length === 0 && (
                          <div className="text-center py-12 text-slate-400">
                            <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No sales data available for the selected filters</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}

                  {salesReportType === 'renewal' && (() => {
                    const renewalData = getRenewalPerformanceData();
                    return (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Renewal Performance</h3>
                          <p className="text-sm text-slate-500">Renewals completed in the selected period vs vehicles currently overdue, per salesperson.</p>
                        </div>
                        <button
                          onClick={exportRenewalPerformanceToCSV}
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold"
                        >
                          <Download size={16} />
                          Export CSV
                        </button>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap items-end gap-3 mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Renewal Date From</label>
                          <input
                            type="date"
                            value={salesReportFrom}
                            onChange={(e) => setSalesReportFrom(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Renewal Date To</label>
                          <input
                            type="date"
                            value={salesReportTo}
                            onChange={(e) => setSalesReportTo(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        {(salesReportFrom || salesReportTo) && (
                          <button
                            onClick={() => { setSalesReportFrom(''); setSalesReportTo(''); }}
                            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-200 transition-colors"
                          >
                            Clear dates
                          </button>
                        )}
                        <p className="text-xs text-slate-400 pb-2">"Currently Overdue" is a live snapshot as of today, not affected by the dates above.</p>
                      </div>

                      {/* Summary cards */}
                      <div className="grid grid-cols-4 gap-4 mb-5">
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
                          <p className="text-xs text-violet-700 font-semibold">Renewals Completed</p>
                          <p className="text-xl font-bold text-violet-800">{renewalData.totals.completed}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                          <p className="text-xs text-blue-700 font-semibold">Renewal Revenue</p>
                          <p className="text-xl font-bold text-blue-800">Rs. {renewalData.totals.revenue.toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                          <p className="text-xs text-green-700 font-semibold">Collected</p>
                          <p className="text-xl font-bold text-green-800">Rs. {renewalData.totals.collected.toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                          <p className="text-xs text-red-700 font-semibold">Currently Overdue</p>
                          <p className="text-xl font-bold text-red-800">{renewalData.totals.overdue}</p>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Salesperson</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Renewals Completed</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Renewal Revenue</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Collected</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Currently Overdue</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Renewal Rate</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {renewalData.rows.map(r => (
                              <tr key={r.name} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-semibold text-slate-800">{r.name}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.completed}</td>
                                <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">Rs. {r.revenue.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-right text-green-600">Rs. {r.collected.toLocaleString()}</td>
                                <td className={`px-4 py-3 text-sm text-right font-semibold ${r.overdue > 0 ? 'text-red-600' : 'text-slate-600'}`}>{r.overdue}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.renewalRate.toFixed(0)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {renewalData.rows.length === 0 && (
                          <div className="text-center py-12 text-slate-400">
                            <RefreshCw className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No renewal data available for the selected filters</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}

                  {salesReportType === 'upcoming_renewals' && (() => {
                    const upcomingList = getUpcomingRenewalsData();
                    const salespersonNames = Array.from(new Set(
                      customers.flatMap(c => (c.vehicles || []).map(v => (v.salesperson || c.salesperson || '').trim()).filter(Boolean))
                    )).sort();
                    return (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Upcoming Renewals</h3>
                          <p className="text-sm text-slate-500">Vehicles due for renewal soon — follow up before they lapse.</p>
                        </div>
                        <button
                          onClick={exportUpcomingRenewalsToCSV}
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold"
                        >
                          <Download size={16} />
                          Export CSV
                        </button>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap items-end gap-3 mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Due Within</label>
                          <select
                            value={upcomingRenewalWindow}
                            onChange={(e) => setUpcomingRenewalWindow(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          >
                            <option value={30}>Next 30 days</option>
                            <option value={60}>Next 60 days</option>
                            <option value={90}>Next 90 days</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Salesperson</label>
                          <select
                            value={upcomingRenewalSalesperson}
                            onChange={(e) => setUpcomingRenewalSalesperson(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          >
                            <option value="all">All Salespeople</option>
                            {salespersonNames.map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Summary card */}
                      <div className="grid grid-cols-1 gap-4 mb-5">
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 w-fit">
                          <p className="text-xs text-amber-700 font-semibold">Renewals Due in Next {upcomingRenewalWindow} Days</p>
                          <p className="text-xl font-bold text-amber-800">{upcomingList.length}</p>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Customer</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Phone</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Vehicle No</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Salesperson</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">Renewal Due</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Days Left</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {upcomingList.map((r, idx) => (
                              <tr key={`${r.vehicleNo}-${idx}`} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-semibold text-slate-800">{r.customerName}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{r.phone}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{r.vehicleNo}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{r.salesperson}</td>
                                <td className="px-4 py-3 text-sm text-slate-600">{r.serviceUpto}</td>
                                <td className={`px-4 py-3 text-sm text-right font-semibold ${r.daysLeft <= 7 ? 'text-red-600' : r.daysLeft <= 15 ? 'text-orange-600' : 'text-slate-600'}`}>{r.daysLeft}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {upcomingList.length === 0 && (
                          <div className="text-center py-12 text-slate-400">
                            <Bell className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No renewals due in the selected window</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}

                  {salesReportType === 'by_area' && (() => {
                    const areaData = getSalesByAreaData();
                    return (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-800">Sales by City / Area</h3>
                          <p className="text-sm text-slate-500">New Sales (Setup invoices) grouped by customer's city/area.</p>
                        </div>
                        <button
                          onClick={exportSalesByAreaToCSV}
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-xl hover:shadow-md transition-all flex items-center gap-2 text-sm font-semibold"
                        >
                          <Download size={16} />
                          Export CSV
                        </button>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap items-end gap-3 mb-5 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Invoice Date From</label>
                          <input
                            type="date"
                            value={salesReportFrom}
                            onChange={(e) => setSalesReportFrom(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Invoice Date To</label>
                          <input
                            type="date"
                            value={salesReportTo}
                            onChange={(e) => setSalesReportTo(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-sm"
                          />
                        </div>
                        {(salesReportFrom || salesReportTo) && (
                          <button
                            onClick={() => { setSalesReportFrom(''); setSalesReportTo(''); }}
                            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-200 transition-colors"
                          >
                            Clear dates
                          </button>
                        )}
                      </div>

                      {/* Summary cards */}
                      <div className="grid grid-cols-4 gap-4 mb-5">
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                          <p className="text-xs text-slate-500 font-semibold">Cities / Areas</p>
                          <p className="text-xl font-bold text-slate-800">{areaData.rows.length}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
                          <p className="text-xs text-violet-700 font-semibold">New Sales</p>
                          <p className="text-xl font-bold text-violet-800">{areaData.totals.count}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                          <p className="text-xs text-blue-700 font-semibold">Total Invoiced</p>
                          <p className="text-xl font-bold text-blue-800">Rs. {areaData.totals.invoiced.toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl p-3 border bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                          <p className="text-xs text-green-700 font-semibold">Total Collected</p>
                          <p className="text-xl font-bold text-green-800">Rs. {areaData.totals.paid.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-slate-700">City / Area</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Customers</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Vehicles</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">No of Sales</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Total Invoiced</th>
                              <th className="px-4 py-3 text-right text-xs font-bold text-slate-700">Collected</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {areaData.rows.map(r => (
                              <tr key={r.name} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm font-semibold text-slate-800">{r.name}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.customerCount}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.vehicleCount}</td>
                                <td className="px-4 py-3 text-sm text-right text-slate-600">{r.count}</td>
                                <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">Rs. {r.invoiced.toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-right text-green-600">Rs. {r.paid.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {areaData.rows.length === 0 && (
                          <div className="text-center py-12 text-slate-400">
                            <MapPin className="w-16 h-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No sales data available for the selected filters</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                </div>
              </div>
              );
            })()}
          </div>
          );
        })()}

      </div>

      {/* Follow-Up Reschedule Modal */}
      {rescheduleTarget && (() => {
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 3);
        const defaultDateStr = defaultDate.toISOString().split('T')[0];
        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <RescheduleFollowupModal
              invoice={rescheduleTarget}
              defaultDate={defaultDateStr}
              todayStr={todayStr}
              onSave={handleSaveReschedule}
              onClose={() => setRescheduleTarget(null)}
            />
          </div>
        );
      })()}

      {/* Invoice Detail Popup */}
      {invoicePopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setInvoicePopup(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-violet-600 to-violet-700">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Receipt size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-base font-mono">{invoicePopup.invoiceNumber}</p>
                  {invoicePopup.createdDate && (
                    <p className="text-violet-200 text-xs">Created: {fmtDate(invoicePopup.createdDate)}</p>
                  )}
                </div>
              </div>
              <button onClick={() => setInvoicePopup(null)} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all">
                <X size={16} className="text-white" />
              </button>
            </div>

            {/* Summary totals */}
            {(() => {
              const totalAmt   = invoicePopup.vehicles.reduce((s, inv) => s + parseFloat(inv.amount || 0) - parseFloat(inv.discount || 0), 0);
              const totalPaid  = invoicePopup.vehicles.reduce((s, inv) => s + (inv.payments?.reduce((sp, p) => sp + parseFloat(p.amount || 0), 0) || 0), 0);
              const totalBal   = totalAmt - totalPaid;
              return (
                <div className="grid grid-cols-3 gap-0 border-b border-slate-200">
                  <div className="px-4 py-2 text-center border-r border-slate-200">
                    <p className="text-xs text-slate-500 mb-0.5">Invoice Amount</p>
                    <p className="font-bold text-slate-800 text-sm">{fmtAmt(totalAmt)}</p>
                  </div>
                  <div className="px-4 py-2 text-center border-r border-slate-200">
                    <p className="text-xs text-slate-500 mb-0.5">Received</p>
                    <p className="font-bold text-emerald-600 text-sm">{fmtAmt(totalPaid)}</p>
                  </div>
                  <div className="px-4 py-2 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Pending</p>
                    <p className="font-bold text-red-600 text-sm">{fmtAmt(totalBal)}</p>
                  </div>
                </div>
              );
            })()}

            {/* Table */}
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">#</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Vehicle No.</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Invoice Date</th>
                    <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Amount</th>
                    <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Received</th>
                    <th className="text-right px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoicePopup.vehicles.map((inv, idx) => {
                    const paid       = inv.payments?.reduce((s, p) => s + parseFloat(p.amount || 0), 0) || 0;
                    const discount   = parseFloat(inv.discount || 0);
                    const effAmount  = parseFloat(inv.amount || 0) - discount;
                    const pending    = effAmount - paid;
                    return (
                      <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3 text-xs text-slate-400">{idx + 1}</td>
                        <td className="px-5 py-3 font-semibold text-slate-800">{inv.vehicleNo || '—'}</td>
                        <td className="px-5 py-3 text-slate-500">{inv.createdDate ? fmtDate(inv.createdDate) : '—'}</td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-700">
                          {fmtAmt(effAmount)}
                          {discount > 0 && <span className="block text-xs text-slate-400 font-normal">(-{fmtAmt(discount)} disc.)</span>}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-emerald-600">{fmtAmt(paid)}</td>
                        <td className="px-5 py-3 text-right font-bold text-red-600">{fmtAmt(pending)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-right">
              <button onClick={() => setInvoicePopup(null)} className="px-5 py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Full Detail Modal */}
      {showCustomerDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-800">{selectedCustomer.name}</h3>
                <p className="text-sm text-slate-500">Customer Code: {formatCustomerCode(selectedCustomer.id, selectedCustomer.type)} · {selectedCustomer.type}</p>
              </div>
              <button onClick={() => setShowCustomerDetail(false)} className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-6">

              {/* Contact */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Contact Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Cell" value={selectedCustomer.cell} />
                  <InfoField label="Additional Contact" value={selectedCustomer.phone} />
                  <InfoField label="WhatsApp" value={selectedCustomer.whatsapp} />
                  <InfoField label="Email" value={selectedCustomer.email} />
                </div>
              </div>

              {/* Identity */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">
                  {selectedCustomer.type === 'Corporate' ? 'Corporate Identity' : 'Identity'}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField
                    label={selectedCustomer.type === 'Corporate' ? 'NTN / GST Number' : 'CNIC'}
                    value={selectedCustomer.cnic}
                  />
                  {selectedCustomer.type === 'Corporate' && (
                    <InfoField label="Contact Person" value={selectedCustomer.contactPerson} />
                  )}
                  {selectedCustomer.type === 'Corporate' && (
                    <InfoField label="POC Email" value={selectedCustomer.pocEmail} />
                  )}
                  {selectedCustomer.type !== 'Corporate' && (
                    <InfoField label="Gender" value={selectedCustomer.gender} />
                  )}
                </div>
              </div>

              {/* Address */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Address</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <InfoField label="Address" value={selectedCustomer.address} />
                  </div>
                  <InfoField label="City" value={selectedCustomer.city} />
                  <InfoField label="State" value={selectedCustomer.state} />
                  <InfoField label="Country" value={selectedCustomer.country} />
                </div>
              </div>

              {/* Account */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Account</p>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Follow Up Person" value={selectedCustomer.followUp} />
                  <InfoField label="Salesperson" value={selectedCustomer.salesperson} />
                  <InfoField label="Member Since" value={selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'} />
                  <InfoField label="Total Vehicles" value={selectedCustomer.vehicles?.length || 0} />
                </div>
              </div>

            </div>
            <div className="px-6 pb-6 flex gap-3">
              {canDo('customers', 'edit') && (
              <button
                onClick={() => { setShowCustomerDetail(false); handleEditCustomerClick(); }}
                className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all flex items-center justify-center gap-2"
              >
                <Edit2 size={16} /> Edit Customer
              </button>
              )}
              <button
                onClick={() => setShowCustomerDetail(false)}
                className="flex-1 border-2 border-slate-200 text-slate-600 py-3 rounded-xl font-semibold hover:bg-slate-50 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomer && (
        <Modal title="Add New Customer" onClose={() => setShowAddCustomer(false)}>
          <CustomerFormFields customer={newCustomer} setCustomer={setNewCustomer} employees={employees} cities={cities} />
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleAddCustomer}
              className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
            >
              <Check className="inline mr-2" size={20} />
              Save Customer
            </button>
            <button
              onClick={() => setShowAddCustomer(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Customer Modal */}
      {showEditCustomer && (
        <Modal title="Edit Customer" onClose={() => setShowEditCustomer(false)}>
          <CustomerFormFields customer={editCustomer} setCustomer={setEditCustomer} employees={employees} cities={cities} />
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSaveEditCustomer}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
            >
              <Save className="inline mr-2" size={20} />
              Update Customer
            </button>
            <button
              onClick={() => setShowEditCustomer(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
	  


      {/* Add Vehicle Modal */}
      {showAddVehicle && (
        <Modal title="Add New Vehicle" onClose={() => { setShowAddVehicle(false); setInstallTechId(''); setInstallTechAmount('0'); setShowVehicleCustomerPicker(false); }}>
          {/* Customer selector — shows the target customer, with a search to pick/change */}
          <div className="mb-4 p-3 rounded-xl border-2 border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5">Customer</p>
                {selectedCustomer ? (
                  <p className="font-semibold text-slate-800 truncate">
                    {selectedCustomer.name} <span className="text-xs text-slate-400 font-normal">({formatCustomerCode(selectedCustomer.id, selectedCustomer.type)})</span>
                  </p>
                ) : (
                  <p className="text-sm text-red-500 font-semibold">No customer selected</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowVehicleCustomerPicker(v => !v)}
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 shrink-0"
              >
                <Search size={14} /> {selectedCustomer ? 'Change' : 'Select Customer'}
              </button>
            </div>
            {showVehicleCustomerPicker && (
              <div className="mt-3">
                <input
                  autoFocus
                  value={vehicleCustomerSearch}
                  onChange={e => setVehicleCustomerSearch(e.target.value)}
                  placeholder="Search customer by name, ID, or phone…"
                  className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-red-400 outline-none text-sm"
                />
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-100 bg-white">
                  {customers.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-slate-400">Loading customers…</p>
                  ) : (
                    (() => {
                      const q = vehicleCustomerSearch.trim().toLowerCase();
                      const matches = customers
                        .filter(c =>
                          !q ||
                          c.name.toLowerCase().includes(q) ||
                          String(c.id).includes(q) ||
                          (c.cell || '').includes(q) ||
                          (c.phone || '').includes(q)
                        )
                        .slice(0, 40);
                      if (matches.length === 0) {
                        return <p className="px-3 py-3 text-sm text-slate-400">No customers found</p>;
                      }
                      return matches.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => { setSelectedCustomer(c); setShowVehicleCustomerPicker(false); setVehicleCustomerSearch(''); }}
                          className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center justify-between gap-2"
                        >
                          <span className="font-medium text-slate-700 truncate">{c.name}</span>
                          <span className="text-xs text-slate-400 shrink-0">{c.id} · {c.cell || c.phone || ''}</span>
                        </button>
                      ));
                    })()
                  )}
                </div>
              </div>
            )}
          </div>

          <VehicleFormFields
            vehicle={newVehicle}
            setVehicle={setNewVehicle}
            devices={devices}
            inventory={inventory}
            inventoryWithStatus={inventoryWithStatus}
            customerDivisions={customerDivisions}
            selectedCustomer={selectedCustomer}
            vehicleModels={vehicleModels}
          />

          {/* Technician Info */}
          <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Installation Technician</p>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Installed By</label>
              {(() => {
                // The device can only be installed by the technician it is issued to,
                // so lock the dropdown to that person (no other technician selectable).
                const issuedItem = inventory.find(i => i.imei === newVehicle.imei);
                const issuedTech = issuedItem?.issuedTo
                  ? employees.find(e => String(e.id) === String(issuedItem.issuedTo))
                  : null;
                if (issuedTech) {
                  return (
                    <>
                      <select
                        value={installTechId}
                        onChange={e => setInstallTechId(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all text-sm"
                      >
                        <option value={issuedTech.id}>{issuedTech.name}{issuedTech.designation ? ` — ${issuedTech.designation}` : ''}</option>
                      </select>
                      <p className="text-xs text-slate-400 mt-1">Device is issued to this technician.</p>
                    </>
                  );
                }
                return (
                  <EmployeeSearchSelect employees={employees} value={installTechId} onChange={setInstallTechId} activeOnly accent="green"
                    placeholder="Search technician by name or code…" />
                );
              })()}
            </div>
            {installTechId && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Technician Amount <span className="text-slate-400 font-normal text-xs">— to be paid later (0 if none)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                  <input
                    type="number"
                    min="0"
                    value={installTechAmount}
                    onChange={e => setInstallTechAmount(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all text-sm"
                  />
                </div>
                {parseFloat(installTechAmount) > 0 && (
                  <p className="text-xs text-green-600 mt-1">✓ Rs. {parseFloat(installTechAmount).toLocaleString()} will be recorded for this technician</p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleAddVehicle}
              disabled={!selectedCustomer}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="inline mr-2" size={20} />
              {selectedCustomer ? 'Save Vehicle' : 'Select a customer first'}
            </button>
            <button
              onClick={() => { setShowAddVehicle(false); setInstallTechId(''); setInstallTechAmount('0'); setShowVehicleCustomerPicker(false); }}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Vehicle Modal */}
      {showEditVehicle && (
        <Modal title="Edit Vehicle" onClose={() => setShowEditVehicle(false)}>
          <VehicleFormFields
            vehicle={editVehicle}
            setVehicle={setEditVehicle}
            devices={devices}
            inventory={inventory}
            inventoryWithStatus={inventoryWithStatus}
            customerDivisions={customerDivisions}
            selectedCustomer={selectedCustomer}
            vehicleModels={vehicleModels}
          />
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSaveEditVehicle}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
            >
              <Save className="inline mr-2" size={20} />
              Update Vehicle
            </button>
            <button
              onClick={() => setShowEditVehicle(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Manage Divisions Modal */}
      {showManageDivisions && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-700 px-8 py-6 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-bold text-white flex items-center gap-3">
                <Briefcase className="w-6 h-6" />
                Manage Divisions - {selectedCustomer.name}
              </h2>
              <button 
                onClick={() => setShowManageDivisions(false)} 
                className="text-white hover:bg-white/20 p-1.5 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5">
              {/* Add New Division */}
              <div className="mb-4 flex gap-3">
                <input
                  type="text"
                  value={newDivisionName}
                  onChange={(e) => setNewDivisionName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDivision()}
                  placeholder="Enter division name"
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                />
                <button
                  onClick={handleAddDivision}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:shadow-md transition-all flex items-center gap-2 font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Add Division
                </button>
              </div>

              {/* Divisions tree (nested up to 3 levels) */}
              <div className="space-y-1">
                {customerDivisions && customerDivisions.filter(d => String(d.customer_id) === String(selectedCustomer.id)).length > 0 ? (
                  <DivisionTree
                    nodes={buildDivTree(customerDivisions.filter(d => String(d.customer_id) === String(selectedCustomer.id)))}
                    level={1}
                    onAddSub={addSubDivision}
                    onRename={renameDivisionPrompt}
                    onDelete={handleDeleteDivision}
                    can={{ edit: true, delete: true }}
                  />
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No divisions added yet</p>
                    <p className="text-sm">Add a top-level division above, then use "+ sub-group" to nest up to 3 levels.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Inventory Modal */}
      {showAddInventory && (
        <Modal title="Add New Device" onClose={() => setShowAddInventory(false)}>
          <InventoryFormFields item={newInventoryItem} setItem={setNewInventoryItem} devices={devices} vendors={vendors} />
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Device status will be automatically calculated based on whether it's attached to an active vehicle or issued to someone.
            </p>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleAddInventory}
              className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
            >
              <Check className="inline mr-2" size={20} />
              Save Device
            </button>
            <button
              onClick={() => setShowAddInventory(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Inventory Modal */}
      {showEditInventory && (
        <Modal title="Edit Device" onClose={() => setShowEditInventory(false)}>
          <InventoryFormFields item={editInventoryItem} setItem={setEditInventoryItem} devices={devices} vendors={vendors} />
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Device status will be automatically calculated based on whether it's attached to an active vehicle or issued to someone.
            </p>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleSaveEditInventory}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
            >
              <Save className="inline mr-2" size={20} />
              Update Device
            </button>
            <button
              onClick={() => setShowEditInventory(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}


      {/* Add Vendor Modal */}
      {showAddVendor && (
        <Modal title="Add New Vendor" onClose={() => setShowAddVendor(false)}>
          <VendorFormFields vendor={newVendor} setVendor={setNewVendor} />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleAddVendor}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Add Vendor
            </button>
            <button
              onClick={() => setShowAddVendor(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Vendor Modal */}
      {showEditVendor && (
        <Modal title="Edit Vendor" onClose={() => setShowEditVendor(false)}>
          <VendorFormFields vendor={editVendorData} setVendor={setEditVendorData} />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleUpdateVendor}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Changes
            </button>
            <button
              onClick={() => setShowEditVendor(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Add Device Modal */}
      {showAddDevice && (
        <Modal title="Add New Device" onClose={() => setShowAddDevice(false)}>
          <DeviceFormFields device={newDevice} setDevice={setNewDevice} />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleAddDevice}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Add Device
            </button>
            <button
              onClick={() => setShowAddDevice(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Device Modal */}
      {showEditDevice && (
        <Modal title="Edit Device" onClose={() => setShowEditDevice(false)}>
          <DeviceFormFields device={editDeviceData} setDevice={setEditDeviceData} />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleUpdateDevice}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Changes
            </button>
            <button
              onClick={() => setShowEditDevice(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <Modal title="Add New Employee" onClose={() => setShowAddEmployee(false)}>
          <EmployeeFormFields employee={newEmployee} setEmployee={setNewEmployee} cities={cities} />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleAddEmployee}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Add Employee
            </button>
            <button
              onClick={() => setShowAddEmployee(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Employee Modal */}
      {showEditEmployee && (
        <Modal title="Edit Employee" onClose={() => setShowEditEmployee(false)}>
          <EmployeeFormFields employee={editEmployeeData} setEmployee={setEditEmployeeData} cities={cities} />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleUpdateEmployee}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Changes
            </button>
            <button
              onClick={() => setShowEditEmployee(false)}
              className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Record Payment Modal */}
      {showRecordPayment && selectedInvoice && (
        <Modal title="Record Payment" onClose={() => {
          setShowRecordPayment(false);
          setSelectedInvoice(null);
          setPaymentAmount('');
          setPaymentDate('');
          setPaymentMethod('Cash');
          setPaymentReference('');
        }}>
          <div className="space-y-4">
            {/* Bulk Invoice Banner */}
            {selectedInvoice.isBulkInvoice && (
              <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="text-purple-600" size={20} />
                  <span className="font-bold text-purple-900">Bulk Invoice</span>
                </div>
                <p className="text-sm text-purple-800">
                  This payment will be applied to <span className="font-bold">{selectedInvoice.totalVehicles} vehicles</span> under this invoice number.
                </p>
              </div>
            )}

            {(() => {
              const totalAmt   = selectedInvoice.totalAmount || parseFloat(selectedInvoice.amount);
              const discount   = parseFloat(selectedInvoice.discount || 0);
              const effectiveAmt = totalAmt - discount;
              const alreadyPaid = selectedInvoice.payments?.reduce((s, p) => s + parseFloat(p.amount), 0) || 0;
              const maxAllowed  = Math.max(0, effectiveAmt - alreadyPaid);
              const enteredAmt  = parseFloat(paymentAmount) || 0;
              const isOverMax   = enteredAmt > maxAllowed;

              return (
                <>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-sm text-slate-600 mb-2">
                      Invoice: <span className="font-mono font-semibold text-slate-800">{selectedInvoice.invoiceNumber || selectedInvoice.invoice_number}</span>
                    </p>
                    {selectedInvoice.isBulkInvoice && (
                      <p className="text-sm text-slate-600 mb-2">
                        Vehicles: <span className="font-semibold text-slate-800">{selectedInvoice.totalVehicles}</span>
                      </p>
                    )}
                    <p className="text-sm text-slate-600 mb-1">
                      Invoice Amount: <span className="font-semibold text-slate-800">Rs. {totalAmt.toLocaleString()}</span>
                    </p>
                    {discount > 0 && (
                      <p className="text-sm text-orange-600 mb-1">
                        Discount: <span className="font-semibold">- Rs. {discount.toLocaleString()}</span>
                      </p>
                    )}
                    {discount > 0 && (
                      <p className="text-sm text-slate-600 mb-1">
                        Net Payable: <span className="font-semibold text-slate-800">Rs. {effectiveAmt.toLocaleString()}</span>
                      </p>
                    )}
                    <p className="text-sm text-slate-600 mb-1">
                      Already Paid: <span className="font-semibold text-green-700">Rs. {alreadyPaid.toLocaleString()}</span>
                    </p>
                    <p className="text-sm font-semibold mt-2 text-orange-700">
                      Max Receivable: <span className="text-orange-800">Rs. {maxAllowed.toLocaleString()}</span>
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Amount *</label>
                      <input
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          if (val > maxAllowed) {
                            setPaymentAmount(maxAllowed.toString());
                          } else {
                            setPaymentAmount(e.target.value);
                          }
                        }}
                        placeholder="0.00"
                        min="0"
                        max={maxAllowed}
                        className={`w-full px-4 py-2.5 rounded-xl border-2 outline-none transition-all ${
                          isOverMax
                            ? 'border-red-500 focus:ring-2 focus:ring-red-200'
                            : 'border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                        }`}
                      />
                      {isOverMax && (
                        <p className="text-xs text-red-600 mt-1">Cannot exceed remaining balance of Rs. {maxAllowed.toLocaleString()}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Date *</label>
                      <input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                      />
                    </div>
                  </div>
                </>
              );
            })()}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Method *</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Online Payment">Online Payment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Reference #</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleRecordPayment}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
              >
                <Check className="inline mr-2" size={20} />
                Record Payment
              </button>
              <button
                onClick={() => {
                  setShowRecordPayment(false);
                  setSelectedInvoice(null);
                  setPaymentAmount('');
                  setPaymentDate('');
                  setPaymentMethod('Cash');
                  setPaymentReference('');
                }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Apply Discount Modal */}
      {showDiscountModal && discountInvoice && (
        <Modal title="Apply Discount" onClose={() => {
          setShowDiscountModal(false);
          setDiscountInvoice(null);
          setDiscountAmount('');
          setDiscountNote('');
        }}>
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
              <p className="text-sm text-slate-600 mb-1">Invoice: <span className="font-mono font-semibold">{discountInvoice.invoiceNumber}</span></p>
              <p className="text-sm text-slate-600 mb-1">Customer: <span className="font-semibold">{discountInvoice.customerName}</span></p>
              <p className="text-sm text-slate-600 mb-1">Vehicle: <span className="font-semibold">{discountInvoice.vehicleNo}</span></p>
              <p className="text-sm text-slate-600 mb-1">Original Amount: <span className="font-semibold">Rs. {parseFloat(discountInvoice.amount).toLocaleString()}</span></p>
              {parseFloat(discountInvoice.discount || 0) > 0 && (
                <p className="text-sm text-orange-700">Current Discount: <span className="font-semibold">Rs. {parseFloat(discountInvoice.discount).toLocaleString()}</span></p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Discount Amount (Rs.) *</label>
              <input
                type="number"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                placeholder="0"
                min="0"
                max={discountInvoice.amount}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
              />
            </div>

            {discountAmount && parseFloat(discountAmount) > 0 && (
              <div className="bg-slate-50 p-3 rounded-xl text-sm">
                <p className="text-slate-600">Original Amount: <span className="font-semibold">Rs. {parseFloat(discountInvoice.amount).toLocaleString()}</span></p>
                <p className="text-orange-600">Discount: <span className="font-semibold">- Rs. {parseFloat(discountAmount).toLocaleString()}</span></p>
                <hr className="my-1 border-slate-200" />
                <p className="text-slate-800 font-bold">Net Amount: Rs. {Math.max(0, parseFloat(discountInvoice.amount) - parseFloat(discountAmount)).toLocaleString()}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Note (Optional)</label>
              <input
                type="text"
                value={discountNote}
                onChange={(e) => setDiscountNote(e.target.value)}
                placeholder="Reason for discount..."
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
              />
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleApplyDiscount}
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
              >
                <Tag className="inline mr-2" size={20} />
                Apply Discount
              </button>
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountInvoice(null);
                  setDiscountAmount('');
                  setDiscountNote('');
                }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Invoice Details Modal */}
      {showInvoiceDetails && selectedInvoice && (() => {
        const inv       = selectedInvoice;
        const status    = getInvoiceStatus(inv);
        const theme     = invoiceStatusTheme(status);
        const totalPaid = inv.payments?.reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;
        const discount  = parseFloat(inv.discount || 0);
        const netAmount = parseFloat(inv.amount) - discount;
        const balance   = netAmount - totalPaid;
        const cust      = customers.find(c => String(c.id) === String(inv.customerId));
        const canEdit   = canDo('invoices', 'edit');
        const closeDetails = () => { setShowInvoiceDetails(false); setSelectedInvoice(null); };
        const dueText = (() => {
          if (status === 'PAID' || status === 'CANCELLED') return null;
          const d = new Date(inv.dueDate); d.setHours(0, 0, 0, 0);
          const t0 = new Date(); t0.setHours(0, 0, 0, 0);
          const days = Math.round((t0 - d) / 86400000);
          return days > 0 ? `Overdue ${days}d` : days === 0 ? 'Due today' : `Due in ${-days}d`;
        })();
        const Info = ({ l, children }) => (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-0.5">{l}</p>
            <p className="text-sm font-semibold text-slate-800">{children}</p>
          </div>
        );
        return (
          <Modal title="Invoice Details" onClose={closeDetails}>
            <div className="space-y-4">
              {/* Header — status color bar on the left */}
              <div className="flex items-start justify-between gap-3 pl-3 py-1" style={{ borderLeft: `4px solid ${theme.bar}` }}>
                <div className="min-w-0">
                  <p className="font-mono font-bold text-slate-800 text-lg">{inv.invoiceNumber}</p>
                  <p className="text-sm text-slate-500 truncate">
                    {inv.customerName}
                    {inv.customerId != null && <> · {formatCustomerCode(inv.customerId, cust?.type)}</>}
                    {inv.vehicleNo && <> · <span className="font-mono">{inv.vehicleNo}</span></>}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap" style={{ background: theme.bg, color: theme.fg }}>{theme.label}</span>
                  {dueText && <p className="text-xs font-semibold mt-1" style={{ color: dueText.startsWith('Overdue') ? '#d23b4e' : '#64748b' }}>{dueText}</p>}
                </div>
              </div>

              {/* Money */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ['Amount',   `Rs ${netAmount.toLocaleString()}`, '#1e293b', discount > 0 ? `-${discount.toLocaleString()} disc` : null],
                  ['Received', `Rs ${totalPaid.toLocaleString()}`, '#16a34a', null],
                  ['Balance',  `Rs ${balance.toLocaleString()}`,   balance > 0 ? '#e11d48' : '#16a34a', null],
                  ['Due date', inv.dueDate || '—',                  '#1e293b', null],
                ].map(([l, v, c, sub]) => (
                  <div key={l} className="rounded-xl bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{l}</p>
                    <p className="font-mono font-bold tabular-nums text-sm" style={{ color: c }}>{v}</p>
                    {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
                  </div>
                ))}
              </div>

              {/* GPS Service — expiry per vehicle + Extend / Block (unpaid / partial / overdue only) */}
              {status !== 'PAID' && status !== 'CANCELLED' && (() => {
                const fmtD = (s) => new Date(s.replace(' ', 'T')).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const label = (g) => {
                  if (!g.has_device) return 'No GPS device';
                  if (!g.expiry_pk) return 'No expiry set';
                  if (g.expired) {
                    const days = Math.max(0, Math.floor((Date.now() - new Date(g.expiry_pk.replace(' ', 'T') + '+05:00').getTime()) / 86400000));
                    return `Expired ${fmtD(g.expiry_pk)} · ${days === 0 ? 'today' : days + (days === 1 ? ' day' : ' days') + ' ago'}`;
                  }
                  return `Valid till ${fmtD(g.expiry_pk)}, ${new Date(g.expiry_pk.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
                };
                const withLabel = (g) => ({ ...g, expiry_label: label(g) });
                const canExtend = (g) => g.has_device && g.expired;
                const canBlock  = (g) => g.has_device && !g.expired;
                const row = (g, mine) => (
                  <div key={g.vehicle_id} className={`flex items-center justify-between px-3 py-2 bg-white ${mine ? 'rounded-lg border-2 border-indigo-300' : ''}`}>
                    <div>
                      <p className="font-mono font-semibold text-sm text-slate-800">{g.vehicle_no}{mine && others.length > 0 && <span className="text-[10px] font-sans font-bold text-indigo-600 ml-1">THIS ROW</span>}</p>
                      <p className={`text-xs font-semibold ${!g.has_device ? 'text-slate-400' : g.expired ? 'text-red-600' : 'text-green-600'}`}>● {label(g)}</p>
                    </div>
                    {canExtend(g) && (
                      <button onClick={() => setGpsDialog({ mode: 'extend', vehicles: [withLabel(g)] })}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg text-white" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}>Extend</button>
                    )}
                    {canBlock(g) && (
                      <button onClick={() => setGpsDialog({ mode: 'block', vehicles: [withLabel(g)] })}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50">Block</button>
                    )}
                  </div>
                );
                const all    = invGps || [];
                const mine   = all.find(g => String(g.vehicle_id) === String(inv.vehicleId));
                const others = all.filter(g => g !== mine);
                const expiredAll = all.filter(canExtend);
                const validAll   = all.filter(canBlock);
                return (
                  <div className="rounded-xl border-2 border-indigo-100 bg-indigo-50/40 p-3">
                    <p className="text-[11px] uppercase tracking-wide font-bold text-indigo-700 mb-2">📡 GPS Service</p>
                    {invGps === null ? (
                      <p className="text-xs text-slate-400">Checking GPS status…</p>
                    ) : all.length === 0 ? (
                      <p className="text-xs text-slate-400">No vehicle linked to this invoice.</p>
                    ) : (
                      <>
                        {mine && row(mine, true)}
                        {others.length > 0 && (
                          <>
                            <div className="flex items-center justify-between mt-3 mb-1.5 gap-2 flex-wrap">
                              <p className="text-[11px] font-bold text-slate-500">Same invoice — {others.length} more vehicle{others.length > 1 ? 's' : ''}</p>
                              <div className="flex gap-1.5">
                                {expiredAll.length > 0 && (
                                  <button onClick={() => setGpsDialog({ mode: 'extend', vehicles: expiredAll.map(withLabel) })}
                                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg text-white" style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}>Extend all ({expiredAll.length})</button>
                                )}
                                {validAll.length > 0 && (
                                  <button onClick={() => setGpsDialog({ mode: 'block', vehicles: validAll.map(withLabel) })}
                                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50">Block all ({validAll.length})</button>
                                )}
                              </div>
                            </div>
                            <div className="divide-y divide-indigo-100 rounded-lg border border-indigo-100 overflow-hidden">
                              {others.map(g => row(g, false))}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Details */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Info l="Type">{inv.type || '—'}</Info>
                <Info l="Created">{inv.createdDate || '—'}</Info>
                {inv.periodFrom && inv.periodTo && <Info l="Period">{inv.periodFrom} → {inv.periodTo}</Info>}
                {cust?.followUp && <Info l="Follow-up">{cust.followUp}</Info>}
                {cust?.salesperson && <Info l="Salesperson">{cust.salesperson}</Info>}
                {inv.description && <div className="col-span-2"><Info l="Description">{inv.description}</Info></div>}
              </div>

              {/* Payment history */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1.5">Payments</p>
                {inv.payments && inv.payments.length > 0 ? (
                  <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
                    {inv.payments.map(payment => (
                      <div key={payment.id} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-xs text-slate-500">{payment.date} · {payment.method}</p>
                          {payment.reference && <p className="text-xs text-slate-400">Ref: {payment.reference}</p>}
                        </div>
                        <p className="font-mono text-sm font-semibold text-green-700 tabular-nums">Rs {parseFloat(payment.amount).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No payments yet.</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap pt-1">
                {status !== 'PAID' && status !== 'CANCELLED' && canEdit && (
                  <button
                    onClick={() => { setShowInvoiceDetails(false); setShowRecordPayment(true); setPaymentDate(new Date().toISOString().split('T')[0]); }}
                    className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-semibold hover:shadow-md transition-all"
                    style={{ background: 'linear-gradient(135deg,#1f9d63,#3bbd7e)' }}
                  ><Wallet size={16} /> Record payment</button>
                )}
                {(status === 'PARTIALLY PAID' || status === 'OVERDUE (Partial)') && canEdit && (
                  <button
                    onClick={() => { setDiscountInvoice(inv); setDiscountAmount(discount > 0 ? inv.discount.toString() : ''); setDiscountNote(''); closeDetails(); setShowDiscountModal(true); }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:text-orange-600 hover:border-orange-200 transition-all"
                  ><Tag size={16} /> Discount</button>
                )}
                {status !== 'CANCELLED' && totalPaid <= 0 && canEdit && (
                  <button
                    onClick={() => { closeDetails(); handleCancelInvoice(inv); }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:text-red-600 hover:border-red-200 transition-all"
                  ><Ban size={16} /> Cancel invoice</button>
                )}
                <button
                  onClick={() => generatePDFInvoice(invoices.find(i => i.id === inv.id) || inv)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:text-purple-600 hover:border-purple-200 transition-all"
                ><Download size={16} /> Print</button>
                <button onClick={closeDetails} className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-600 font-semibold hover:bg-slate-200 transition-all">Close</button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {gpsDialog && (
        <GpsServiceDialog
          mode={gpsDialog.mode}
          vehicles={gpsDialog.vehicles}
          apiBaseUrl={API_BASE_URL}
          userId={authUser?.id}
          onClose={() => setGpsDialog(null)}
          onDone={() => {
            const ids = gpsDialog.vehicles.map(v => v.vehicle_id);
            setGpsDialog(null);
            if (selectedInvoice) loadInvGps(selectedInvoice);
            loadFollowupGps(ids);
          }}
        />
      )}

      {/* Bulk Invoice Creation Modal */}
      {showBulkInvoiceCreate && selectedCustomerForBulk && (
        <Modal 
          title={bulkInvoiceType === 'setup' ? `Create Setup Invoices - ${selectedCustomerForBulk.name}` : `Create Renewal Invoices - ${selectedCustomerForBulk.name}`} 
          onClose={() => {
            setShowBulkInvoiceCreate(false);
            setSelectedCustomerForBulk(null);
            setSelectedVehiclesForInvoice([]);
          }}
        >
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-xl">
              <p className="text-sm text-slate-600 mb-1">Customer: <span className="font-semibold text-slate-800">{selectedCustomerForBulk.name}</span></p>
              <p className="text-sm text-slate-600">Vehicles Due: <span className="font-semibold text-slate-800">{bulkInvoiceType === 'setup' ? selectedCustomerForBulk.setupDueVehicles?.length || 0 : selectedCustomerForBulk.dueVehicles?.length || 0}</span></p>
            </div>

            <div className="bg-white rounded-xl border-2 border-slate-200 max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedVehiclesForInvoice.length > 0 && selectedVehiclesForInvoice.length === ((bulkInvoiceType === 'setup' ? selectedCustomerForBulk.setupDueVehicles : selectedCustomerForBulk.dueVehicles) || []).filter(v => !isPricingPending(v.id)).length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const vehicles = bulkInvoiceType === 'setup' ? selectedCustomerForBulk.setupDueVehicles : selectedCustomerForBulk.dueVehicles;
                            // Vehicles awaiting a pricing approval can't be invoiced — leave them out.
                            setSelectedVehiclesForInvoice((vehicles || []).filter(v => !isPricingPending(v.id)).map(v => v.id));
                          } else {
                            setSelectedVehiclesForInvoice([]);
                          }
                        }}
                        className="w-4 h-4 text-red-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Vehicle No</th>
                    {bulkInvoiceType === 'renewal' && <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Period</th>}
                    {bulkInvoiceType === 'setup' && <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Active Date</th>}
                    <th className="px-4 py-3 text-right text-sm font-bold text-slate-700">Amount</th>
                    {bulkInvoiceType === 'renewal' && <th className="px-4 py-3 text-left text-sm font-bold text-slate-700">Next Due Date</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(bulkInvoiceType === 'setup' ? selectedCustomerForBulk.setupDueVehicles : selectedCustomerForBulk.dueVehicles)?.map(vehicle => {
                    let amount;
                    let nextDueDate = '';
                    
                    if (bulkInvoiceType === 'setup') {
                      // For setup invoices, calculate based on period (shared helper)
                      amount = calculateSetupInvoiceAmount(vehicle);
                    } else {
                      // For renewal invoices, calculate renewal amount
                      const check = canCreateRenewalInvoice(vehicle);
                      amount = calculateRenewalAmount(vehicle.amc, vehicle.invoicingPeriod);
                      nextDueDate = check.nextDueDate;
                    }
                    
                    return (
                      <tr key={vehicle.id} className={isPricingPending(vehicle.id) ? 'bg-amber-50/60' : 'hover:bg-slate-50'}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            disabled={isPricingPending(vehicle.id)}
                            title={isPricingPending(vehicle.id) ? PRICING_PENDING_MSG : undefined}
                            checked={selectedVehiclesForInvoice.includes(vehicle.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVehiclesForInvoice([...selectedVehiclesForInvoice, vehicle.id]);
                              } else {
                                setSelectedVehiclesForInvoice(selectedVehiclesForInvoice.filter(id => id !== vehicle.id));
                              }
                            }}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-700">
                          {vehicle.vehicleNo}
                          {isPricingPending(vehicle.id) && <span className="ml-2 align-middle text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title={PRICING_PENDING_MSG}>Approval pending</span>}
                        </td>
                        {bulkInvoiceType === 'renewal' && <td className="px-4 py-3 text-sm text-slate-600">{vehicle.invoicingPeriod}</td>}
                        {bulkInvoiceType === 'setup' && (
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {vehicle.activeDate ? vehicle.activeDate : <span className="text-orange-400 italic">Not Set</span>}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right font-semibold text-slate-700">Rs. {parseFloat(amount).toLocaleString()}</td>
                        {bulkInvoiceType === 'renewal' && <td className="px-4 py-3 text-sm text-slate-600">{nextDueDate}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
              <p className="text-sm font-semibold text-orange-800 mb-2">
                Selected: {selectedVehiclesForInvoice.length} vehicle(s)
              </p>
              <p className="text-sm text-orange-700">
                Total Amount: Rs. {selectedVehiclesForInvoice.reduce((sum, vehicleId) => {
                  const vehicles = bulkInvoiceType === 'setup' ? selectedCustomerForBulk.setupDueVehicles : selectedCustomerForBulk.dueVehicles;
                  const vehicle = vehicles?.find(v => v.id === vehicleId);
                  if (!vehicle) return sum;
                  
                  if (bulkInvoiceType === 'setup') {
                    // Calculate setup invoice amount based on period (shared helper)
                    return sum + calculateSetupInvoiceAmount(vehicle);
                  } else {
                    // Calculate renewal amount for renewal invoices
                    return sum + parseFloat(calculateRenewalAmount(vehicle.amc, vehicle.invoicingPeriod));
                  }
                }, 0).toLocaleString()}
              </p>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={bulkInvoiceType === 'setup' ? handleBulkCreateSetupInvoices : handleBulkCreateRenewalInvoices}
                disabled={selectedVehiclesForInvoice.length === 0}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="inline mr-2" size={20} />
                Create {selectedVehiclesForInvoice.length} {bulkInvoiceType === 'setup' ? 'Setup' : 'Renewal'} Invoice(s)
              </button>
              <button
                onClick={() => {
                  setShowBulkInvoiceCreate(false);
                  setSelectedCustomerForBulk(null);
                  setSelectedVehiclesForInvoice([]);
                }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}


      {/* Issuance Modal */}
      {showIssuance && (
        <Modal title="Device Issuance to Technician" onClose={() => {
          setShowIssuance(false);
          setSelectedDevicesForIssuance([]);
          setIssuanceTechnician('');
          setIssuanceNotes('');
          setIssuanceDeviceSearch('');
        }}>
          <div className="space-y-6">
            {/* Technician and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Technician *</label>
                <EmployeeSearchSelect
                  employees={employees}
                  value={issuanceTechnician}
                  onChange={setIssuanceTechnician}
                  activeOnly
                  accent="blue"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Issuance Date *</label>
                <input 
                  type="date" 
                  value={issuanceDate} 
                  onChange={(e) => setIssuanceDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                />
              </div>
            </div>

            {/* Available Store Devices with Search and Checkboxes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Select Devices to Issue *
                <span className="ml-2 text-xs font-normal text-slate-500">
                  ({inventoryWithStatus.filter(item => item.status === 'Available' && !item.isIssued && item.healthStatus === 'active').length} available in store)
                </span>
              </label>

              {/* Search bar — type IMEI + Enter to add the top match, then it clears for the next */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  autoFocus
                  placeholder="Type IMEI and press Enter to add…"
                  value={issuanceDeviceSearch}
                  onChange={(e) => setIssuanceDeviceSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const q = issuanceDeviceSearch.trim();
                    if (!q) return;
                    const ql = q.toLowerCase();
                    const matches = inventoryWithStatus.filter(item =>
                      item.status === 'Available' && !item.isIssued && item.healthStatus === 'active' &&
                      (item.imei.includes(q) || item.deviceType.toLowerCase().includes(ql) || item.vendor.toLowerCase().includes(ql))
                    );
                    // add the first match that isn't already selected, then HIGHLIGHT the text
                    // (don't clear) so the next keystroke replaces it automatically.
                    const next = matches.find(d => !selectedDevicesForIssuance.includes(d.imei));
                    if (next) {
                      setSelectedDevicesForIssuance(prev => [...prev, next.imei]);
                      e.target.select();
                    } else if (matches.length > 0) {
                      e.target.select();   // all matches already added — highlight for retype
                    }
                    // no match → keep the text so a typo stays visible
                  }}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-sm"
                />
              </div>

              {/* Select All / Clear All */}
              {(() => {
                const storeDevices = inventoryWithStatus.filter(item => 
                  item.status === 'Available' && !item.isIssued && item.healthStatus === 'active' &&
                  (issuanceDeviceSearch === '' ||
                    item.imei.includes(issuanceDeviceSearch) ||
                    item.deviceType.toLowerCase().includes(issuanceDeviceSearch.toLowerCase()) ||
                    item.vendor.toLowerCase().includes(issuanceDeviceSearch.toLowerCase()))
                );
                return (
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        const allImeis = storeDevices.map(d => d.imei);
                        setSelectedDevicesForIssuance(prev => {
                          const existing = prev.filter(imei => !storeDevices.find(d => d.imei === imei));
                          return [...existing, ...allImeis];
                        });
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Select All ({storeDevices.length})
                    </button>
                    {selectedDevicesForIssuance.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedDevicesForIssuance([])}
                        className="text-xs text-red-600 hover:text-red-700 font-semibold"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Device list */}
              <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {(() => {
                    const storeDevices = inventoryWithStatus.filter(item => 
                      item.status === 'Available' && !item.isIssued && item.healthStatus === 'active' &&
                      (issuanceDeviceSearch === '' ||
                        item.imei.includes(issuanceDeviceSearch) ||
                        item.deviceType.toLowerCase().includes(issuanceDeviceSearch.toLowerCase()) ||
                        item.vendor.toLowerCase().includes(issuanceDeviceSearch.toLowerCase()))
                    );
                    if (storeDevices.length === 0) {
                      return (
                        <div className="text-center py-8 text-slate-400">
                          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No available store devices found</p>
                        </div>
                      );
                    }
                    return storeDevices.map((device, idx) => {
                      const isSelected = selectedDevicesForIssuance.includes(device.imei);
                      return (
                        <div
                          key={device.id}
                          onClick={() => {
                            setSelectedDevicesForIssuance(prev =>
                              isSelected ? prev.filter(i => i !== device.imei) : [...prev, device.imei]
                            );
                          }}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-slate-100 last:border-b-0 ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4 text-blue-600 rounded cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-sm font-semibold text-slate-800">{device.imei}</p>
                            <p className="text-xs text-slate-500 truncate">{device.deviceType} • {device.vendor} {device.simNumber ? `• SIM: ${device.simNumber}` : ''}</p>
                          </div>
                          {isSelected && <Check size={16} className="text-blue-600 flex-shrink-0" />}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <p className="text-sm text-slate-500 mt-2">
                <span className="font-semibold text-blue-600">{selectedDevicesForIssuance.length}</span> device(s) selected
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Notes</label>
              <textarea 
                value={issuanceNotes}
                onChange={(e) => setIssuanceNotes(e.target.value)}
                rows="3"
                placeholder="Add any notes about this issuance..."
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleIssuance}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : `Issue ${selectedDevicesForIssuance.length > 0 ? `${selectedDevicesForIssuance.length} ` : ''}Device${selectedDevicesForIssuance.length !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => {
                  setShowIssuance(false);
                  setSelectedDevicesForIssuance([]);
                  setIssuanceTechnician('');
                  setIssuanceNotes('');
                  setIssuanceDeviceSearch('');
                }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Return Modal */}
      {showReturn && (
        <Modal title="Device Return to Store" onClose={() => {
          setShowReturn(false);
          setSelectedDevicesForReturn([]);
          setReturnTechnician('');
          setReturnNotes('');
          setReturnDeviceSearch('');
        }}>
          <div className="space-y-6">
            {/* Technician and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Technician *</label>
                <EmployeeSearchSelect
                  employees={employees}
                  value={returnTechnician}
                  onChange={(id) => { setReturnTechnician(id); setSelectedDevicesForReturn([]); }}
                  activeOnly={false}
                  accent="green"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Return Date *</label>
                <input 
                  type="date" 
                  value={returnDate} 
                  onChange={(e) => setReturnDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                />
              </div>
            </div>

            {/* Issued Devices with Search and Checkboxes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Select Devices to Return *
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {(() => {
                    const techName = employees.find(e => String(e.id) === String(returnTechnician))?.name || '';
                    return returnTechnician
                      ? `(${inventoryWithStatus.filter(item => item.isIssued && String(item.issuedTo) === String(returnTechnician)).length} devices with ${techName})`
                      : `(${inventoryWithStatus.filter(item => item.isIssued).length} total issued devices)`;
                  })()}
                </span>
              </label>

              {/* Search bar — type IMEI + Enter to add the top match, text stays highlighted for the next */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  autoFocus
                  placeholder="Type IMEI and press Enter to add…"
                  value={returnDeviceSearch}
                  onChange={(e) => setReturnDeviceSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const q = returnDeviceSearch.trim();
                    if (!q) return;
                    const ql = q.toLowerCase();
                    const matches = inventoryWithStatus.filter(item =>
                      item.isIssued &&
                      (returnTechnician === '' || String(item.issuedTo) === String(returnTechnician)) &&
                      (item.imei.includes(q) || item.deviceType.toLowerCase().includes(ql) ||
                       item.vendor.toLowerCase().includes(ql) || (item.issuedToName || '').toLowerCase().includes(ql))
                    );
                    const next = matches.find(d => !selectedDevicesForReturn.includes(d.imei));
                    if (next) {
                      setSelectedDevicesForReturn(prev => [...prev, next.imei]);
                      e.target.select();
                    } else if (matches.length > 0) {
                      e.target.select();
                    }
                  }}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all text-sm"
                />
              </div>

              {/* Select All / Clear All */}
              {(() => {
                const issuedDevices = inventoryWithStatus.filter(item =>
                  item.isIssued &&
                  (returnTechnician === '' || String(item.issuedTo) === String(returnTechnician)) &&
                  (returnDeviceSearch === '' ||
                    item.imei.includes(returnDeviceSearch) ||
                    item.deviceType.toLowerCase().includes(returnDeviceSearch.toLowerCase()) ||
                    item.vendor.toLowerCase().includes(returnDeviceSearch.toLowerCase()) ||
                    (item.issuedToName || '').toLowerCase().includes(returnDeviceSearch.toLowerCase()))
                );
                return (
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        const allImeis = issuedDevices.map(d => d.imei);
                        setSelectedDevicesForReturn(prev => {
                          const existing = prev.filter(imei => !issuedDevices.find(d => d.imei === imei));
                          return [...existing, ...allImeis];
                        });
                      }}
                      className="text-xs text-green-600 hover:text-green-800 font-semibold"
                    >
                      Select All ({issuedDevices.length})
                    </button>
                    {selectedDevicesForReturn.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedDevicesForReturn([])}
                        className="text-xs text-red-600 hover:text-red-700 font-semibold"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Device list */}
              <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {(() => {
                    const issuedDevices = inventoryWithStatus.filter(item =>
                      item.isIssued &&
                      (returnTechnician === '' || String(item.issuedTo) === String(returnTechnician)) &&
                      (returnDeviceSearch === '' ||
                        item.imei.includes(returnDeviceSearch) ||
                        item.deviceType.toLowerCase().includes(returnDeviceSearch.toLowerCase()) ||
                        item.vendor.toLowerCase().includes(returnDeviceSearch.toLowerCase()) ||
                        (item.issuedToName || '').toLowerCase().includes(returnDeviceSearch.toLowerCase()))
                    );
                    if (issuedDevices.length === 0) {
                      return (
                        <div className="text-center py-8 text-slate-400">
                          <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">
                            {returnTechnician ? `No issued devices found for ${returnTechnician}` : 'No issued devices found'}
                          </p>
                        </div>
                      );
                    }
                    return issuedDevices.map((device) => {
                      const isSelected = selectedDevicesForReturn.includes(device.imei);
                      return (
                        <div
                          key={device.id}
                          onClick={() => {
                            setSelectedDevicesForReturn(prev =>
                              isSelected ? prev.filter(i => i !== device.imei) : [...prev, device.imei]
                            );
                          }}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all border-b border-slate-100 last:border-b-0 ${isSelected ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4 text-green-600 rounded cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-mono text-sm font-semibold text-slate-800">{device.imei}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {device.deviceType} • {device.vendor}
                              {device.simNumber ? ` • SIM: ${device.simNumber}` : ''}
                              {!returnTechnician && <span className="ml-1 text-orange-600 font-semibold">• With: {device.location}</span>}
                            </p>
                          </div>
                          {isSelected && <Check size={16} className="text-green-600 flex-shrink-0" />}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <p className="text-sm text-slate-500 mt-2">
                <span className="font-semibold text-green-600">{selectedDevicesForReturn.length}</span> device(s) selected
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Notes</label>
              <textarea 
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                rows="3"
                placeholder="Add any notes about this return..."
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleReturn}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : `Return ${selectedDevicesForReturn.length > 0 ? `${selectedDevicesForReturn.length} ` : ''}Device${selectedDevicesForReturn.length !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => {
                  setShowReturn(false);
                  setSelectedDevicesForReturn([]);
                  setReturnTechnician('');
                  setReturnNotes('');
                  setReturnDeviceSearch('');
                }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}


      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <Modal title="Bulk Upload Devices" onClose={() => {
          setShowBulkUpload(false);
          setBulkUploadFile(null);
          setBulkUploadResults(null);
        }}>
          <div className="space-y-4">

            {/* ── Upload Results Panel ─────────────────────────────── */}
            {bulkUploadResults ? (
              <div className="space-y-4">
                {/* Summary bar */}
                <div className="flex gap-3">
                  <div className="flex-1 bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="text-xl font-bold text-green-600">{bulkUploadResults.success}</p>
                    <p className="text-sm font-semibold text-green-700 mt-0.5">Saved Successfully</p>
                  </div>
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <p className="text-xl font-bold text-red-600">{bulkUploadResults.failed.length}</p>
                    <p className="text-sm font-semibold text-red-700 mt-0.5">Failed</p>
                  </div>
                </div>

                {/* Failed rows detail table */}
                {bulkUploadResults.failed.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
                      <AlertCircle size={15} className="text-red-500" />
                      Failed Devices — fix these rows in your CSV and re-upload
                    </p>
                    <div className="border border-red-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-3 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 uppercase tracking-wide border-b border-red-200">
                        <span>Row #</span>
                        <span>IMEI</span>
                        <span>Reason</span>
                      </div>
                      <div className="max-h-52 overflow-y-auto divide-y divide-red-100">
                        {bulkUploadResults.failed.map((f, idx) => (
                          <div key={idx} className="grid grid-cols-3 px-3 py-2 text-xs hover:bg-red-50 transition-colors">
                            <span className="font-semibold text-slate-600">Row {f.row}</span>
                            <span className="font-mono text-slate-800 truncate">{f.imei}</span>
                            <span className="text-red-600">{f.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons after results */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setBulkUploadResults(null)}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all"
                  >
                    Upload Another File
                  </button>
                  <button
                    onClick={() => {
                      setShowBulkUpload(false);
                      setBulkUploadResults(null);
                    }}
                    className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              /* ── Upload Form ─────────────────────────────────────── */
              <>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-5 text-center hover:border-red-500 transition-all">
                  <Upload size={48} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600 font-semibold mb-2">
                    {bulkUploadFile ? `Selected: ${bulkUploadFile.name}` : 'Drop your Excel or CSV file here or click to browse'}
                  </p>
                  <p className="text-sm text-slate-400 mb-4">Supported formats: <span className="font-semibold text-slate-500">.xlsx, .xls, .csv</span> — columns: IMEI, Device Type, ICCID, SIM Number, Vendor, Amount</p>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="inline-block bg-slate-200 text-slate-700 px-6 py-2 rounded-xl font-semibold hover:bg-slate-300 transition-all cursor-pointer"
                  >
                    Choose File
                  </label>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleBulkUpload}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-50"
                  >
                    <Upload className="inline mr-2" size={20} />
                    {loading ? 'Uploading...' : 'Upload File'}
                  </button>
                  <button
                    onClick={() => {
                      setShowBulkUpload(false);
                      setBulkUploadFile(null);
                    }}
                    className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

          </div>
        </Modal>
      )}
      
      {/* Removal Employee Popup */}
      {showRemovalPopup && (
        <Modal title="Tracker Removal" onClose={() => {
          setShowRemovalPopup(false);
          setRemovalEmployee('');
          setRemovalTechAmount('0');
          setRemovalType('removed');
          setPendingInactiveVehicle(null);
        }}>
          <div className="space-y-5">
            {/* What happened to the tracker? */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRemovalType('removed')}
                className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${removalType === 'removed' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
              >
                🔧 Removed (recovered)
              </button>
              <button
                type="button"
                onClick={() => setRemovalType('lost')}
                className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${removalType === 'lost' ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
              >
                ⚠️ Lost / Not recovered
              </button>
            </div>

            <div className={`rounded-xl p-4 border ${removalType === 'lost' ? 'bg-rose-50 border-rose-200' : 'bg-orange-50 border-orange-200'}`}>
              <p className="text-slate-700 font-semibold mb-2">
                Vehicle <span className={removalType === 'lost' ? 'text-rose-600' : 'text-orange-600'}>{pendingInactiveVehicle?.vehicleNo}</span> is being marked as inactive.
              </p>
              <p className="text-slate-600 text-sm">
                {removalType === 'lost' ? (
                  <>The tracker (IMEI: <span className="font-mono font-semibold">{pendingInactiveVehicle?.imei}</span>) will be marked <span className="font-semibold text-rose-600">Lost</span> and cannot be re-installed until its health is reset.</>
                ) : (
                  <>The tracker (IMEI: <span className="font-mono font-semibold">{pendingInactiveVehicle?.imei}</span>) will be issued to the employee who removed it.</>
                )}
              </p>
            </div>

            {removalType === 'removed' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Who removed the tracker? *
                  </label>
                  <EmployeeSearchSelect
                    employees={employees}
                    value={(() => { try { return String(JSON.parse(removalEmployee || '{}').id || ''); } catch { return ''; } })()}
                    onChange={(id) => { const emp = employees.find(x => String(x.id) === String(id)); setRemovalEmployee(emp ? JSON.stringify({ id: emp.id, name: emp.name }) : ''); }}
                    activeOnly accent="blue" placeholder="Search employee by name or code…"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Technician Amount <span className="text-slate-400 font-normal text-xs">— to be paid later (0 if none)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                    <input
                      type="number"
                      min="0"
                      value={removalTechAmount}
                      onChange={e => setRemovalTechAmount(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    />
                  </div>
                  {parseFloat(removalTechAmount) > 0 && (
                    <p className="text-xs text-green-600 mt-1">✓ Rs. {parseFloat(removalTechAmount).toLocaleString()} will be recorded for this technician</p>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  if (removalType === 'removed' && !removalEmployee) {
                    alert('Please select who removed the tracker');
                    return;
                  }
                  setShowRemovalPopup(false);
                  await performVehicleSave(removalType === 'removed' ? removalEmployee : null, removalType === 'lost');
                  setRemovalEmployee('');
                  setRemovalTechAmount('0');
                  setRemovalType('removed');
                  setPendingInactiveVehicle(null);
                }}
                className={`flex-1 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all ${removalType === 'lost' ? 'bg-gradient-to-r from-rose-500 to-red-600' : 'bg-gradient-to-r from-orange-500 to-red-500'}`}
              >
                {removalType === 'lost' ? 'Confirm — Mark Lost' : 'Confirm & Issue Tracker'}
              </button>
              <button
                onClick={() => {
                  setShowRemovalPopup(false);
                  setRemovalEmployee('');
                  setRemovalTechAmount('0');
                  setRemovalType('removed');
                  setPendingInactiveVehicle(null);
                }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Helper Components
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-0.5 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 font-medium">{value || 'N/A'}</p>
    </div>
  );
}

// Customer Form Fields Component
function CustomerFormFields({ customer, setCustomer, employees, cities }) {
  const isCorporate = customer.type === 'Corporate';
  // Auto-format CNIC as xxxxx-xxxxxxx-x (5-7-1) while typing — same as the
  // employee form. Corporate customers use this field for NTN/GST (no format).
  const formatCNIC = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return digits.slice(0, 5) + '-' + digits.slice(5);
    return digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12, 13);
  };
  // City options from the master (+ current value as fallback for old records).
  const cityOptions = React.useMemo(() => {
    const set = [...new Set((cities || []).map(c => c.city).filter(Boolean))].sort();
    if (customer.city && !set.includes(customer.city)) set.push(customer.city);
    return set;
  }, [cities, customer.city]);
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Name *</label>
        <input type="text" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Type</label>
        <select value={customer.type} onChange={(e) => setCustomer({ ...customer, type: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option>Individual</option>
          <option>Corporate</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Gender</label>
        <select value={customer.gender} onChange={(e) => setCustomer({ ...customer, gender: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option>Male</option>
          <option>Female</option>
          <option>Other</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Cell *</label>
        <input type="text" value={customer.cell} onChange={(e) => setCustomer({ ...customer, cell: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Additional Contact Number</label>
        <input type="text" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">WhatsApp</label>
        <input type="text" value={customer.whatsapp} onChange={(e) => setCustomer({ ...customer, whatsapp: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
        <input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          {isCorporate ? 'NTN / GST Number' : 'CNIC'}
        </label>
        <input type="text" value={customer.cnic}
          onChange={(e) => setCustomer({ ...customer, cnic: isCorporate ? e.target.value : formatCNIC(e.target.value) })}
          placeholder={isCorporate ? 'Enter NTN or GST number' : 'xxxxx-xxxxxxx-x'}
          maxLength={isCorporate ? undefined : 15}
          className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-semibold text-slate-700 mb-2">Address</label>
        <input type="text" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">City</label>
        <SearchableSelect
          value={customer.city || ''}
          options={cityOptions}
          onChange={(val) => {
            const match = (cities || []).find(c => (c.city || '').toLowerCase() === (val || '').toLowerCase());
            if (match) {
              // Auto-populate province (state) + country from the master; both stay editable.
              setCustomer({ ...customer, city: val, state: match.province || '', country: match.country || '' });
            } else {
              setCustomer({ ...customer, city: val });
            }
          }}
          placeholder="Search city..."
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">State</label>
        <input type="text" value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Country</label>
        <input type="text" value={customer.country} onChange={(e) => setCustomer({ ...customer, country: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Contact Person</label>
        <input type="text" value={customer.contactPerson} onChange={(e) => setCustomer({ ...customer, contactPerson: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      {isCorporate && (
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">POC Email</label>
          <input type="email" value={customer.pocEmail || ''} onChange={(e) => setCustomer({ ...customer, pocEmail: e.target.value })}
            placeholder="Point of contact email"
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Follow Up Person</label>
        <EmployeeSearchSelect employees={employees}
          value={(employees.find(x => x.name === customer.followUp)?.id ?? '').toString()}
          onChange={(id) => { const emp = employees.find(x => String(x.id) === String(id)); setCustomer({ ...customer, followUp: emp ? emp.name : '' }); }}
          activeOnly placeholder="Search person by name or code…" />
        {customer.followUp && !employees.find(x => x.name === customer.followUp) &&
          <p className="text-[11px] text-slate-400 mt-1">Current: {customer.followUp}</p>}
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Salesperson</label>
        <EmployeeSearchSelect employees={employees}
          value={(employees.find(x => x.name === customer.salesperson)?.id ?? '').toString()}
          onChange={(id) => { const emp = employees.find(x => String(x.id) === String(id)); setCustomer({ ...customer, salesperson: emp ? emp.name : '' }); }}
          activeOnly placeholder="Search salesperson by name or code…" />
        {customer.salesperson && !employees.find(x => x.name === customer.salesperson) &&
          <p className="text-[11px] text-slate-400 mt-1">Current: {customer.salesperson}</p>}
      </div>
    </div>
  );
}

// Vehicle Form Fields Component
// Reusable searchable dropdown. Filters `options` as you type and ONLY commits a
// value that exactly matches an option (invalid text reverts on blur) — so the
// stored value is always one of the DB-provided options (or empty).
function SearchableSelect({ value, options, onChange, placeholder = 'Type to search...', disabled = false }) {
  const [query, setQuery] = React.useState(value || '');
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const wrapRef = React.useRef(null);

  React.useEffect(() => { setQuery(value || ''); }, [value]);

  React.useEffect(() => {
    const h = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = React.useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [query, options]);

  const commit = (val) => { onChange(val); setQuery(val); setOpen(false); };

  const validateAndClose = () => {
    const q = (query || '').trim();
    if (q === '') { if (value) onChange(''); setQuery(''); setOpen(false); return; }
    const exact = options.find(o => o.toLowerCase() === q.toLowerCase());
    if (exact) { if (exact !== value) onChange(exact); setQuery(exact); }
    else { setQuery(value || ''); } // not a DB value -> revert to last valid
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => { if (!disabled) setOpen(true); }}
        onBlur={validateAndClose}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { if (open && filtered[highlight]) { e.preventDefault(); commit(filtered[highlight]); } }
          else if (e.key === 'Escape') { setQuery(value || ''); setOpen(false); }
        }}
        className={`w-full px-4 py-2.5 rounded-xl border-2 outline-none transition-all ${disabled ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-white border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'}`}
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-white border-2 border-slate-200 rounded-xl shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-4 py-2.5 text-sm text-slate-400">No match found</div>
          ) : filtered.map((o, i) => (
            <button
              type="button"
              key={o}
              onMouseDown={(e) => { e.preventDefault(); commit(o); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-4 py-2 text-sm transition-all ${i === highlight ? 'bg-red-50 text-red-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Management module: master list of Cities (city / province / country).
// Feeds the city → province/country auto-populate in the customer form.
function CitiesModule({ cities, reload, can = { create: true, edit: true, delete: true } }) {
  const [city, setCity] = React.useState('');
  const [province, setProvince] = React.useState('');
  const [country, setCountry] = React.useState('Pakistan');
  const [search, setSearch] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [eCity, setECity] = React.useState('');
  const [eProvince, setEProvince] = React.useState('');
  const [eCountry, setECountry] = React.useState('');

  const startEdit = (c) => { setEditingId(c.id); setECity(c.city); setEProvince(c.province); setECountry(c.country); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id) => {
    if (!eCity.trim() || !eProvince.trim() || !eCountry.trim()) {
      alert('City, Province and Country are all required.');
      return;
    }
    try {
      await api.cities.update({ id, city: eCity.trim(), province: eProvince.trim(), country: eCountry.trim() });
      setEditingId(null);
      await reload();
    } catch (e) {
      alert(e.message || 'Failed to update city');
    }
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = cities || [];
    if (!q) return list;
    return list.filter(c => `${c.city} ${c.province} ${c.country}`.toLowerCase().includes(q));
  }, [cities, search]);

  const handleAdd = async () => {
    if (!city.trim() || !province.trim() || !country.trim()) {
      alert('City, Province and Country are all required.');
      return;
    }
    setSaving(true);
    try {
      await api.cities.create({ city: city.trim(), province: province.trim(), country: country.trim() });
      setCity(''); setProvince(''); setCountry('Pakistan');
      await reload();
    } catch (e) {
      alert(e.message || 'Failed to add city');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this city?')) return;
    try {
      await api.cities.delete(id);
      await reload();
    } catch (e) {
      alert(e.message || 'Failed to delete');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-slate-800 mb-3">Cities</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">City *</label>
            <input
              type="text"
              placeholder="e.g. Lahore"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Province *</label>
            <input
              type="text"
              placeholder="e.g. Punjab"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Country *</label>
            <input
              type="text"
              placeholder="e.g. Pakistan"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
          </div>
          {can.create && (
          <button
            onClick={handleAdd}
            disabled={saving}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-md transition-all flex items-center gap-2 disabled:opacity-60"
          >
            <Plus size={16} />
            {saving ? 'Saving...' : 'Add'}
          </button>
          )}
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search city/province/country..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">City</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Province</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Country</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                {editingId === c.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input value={eCity} onChange={(e) => setECity(e.target.value)} className="w-full px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input value={eProvince} onChange={(e) => setEProvince(e.target.value)} className="w-full px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input value={eCountry} onChange={(e) => setECountry(e.target.value)} className="w-full px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(c.id)} className="text-green-600 hover:text-green-700 transition-all"><Check className="w-4 h-4" /></button>
                        <button onClick={cancelEdit} className="text-slate-500 hover:text-slate-700 transition-all"><X className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-sm font-semibold text-slate-800">{c.city}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{c.province}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{c.country}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-2">
                        {can.edit && <button onClick={() => startEdit(c)} className="text-slate-600 hover:text-red-600 transition-all"><Edit2 className="w-4 h-4" /></button>}
                        {can.delete && <button onClick={() => handleDelete(c.id)} className="text-slate-600 hover:text-red-600 transition-all"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <MapPin className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No cities yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Management module: master list of vehicle Make / Model / Fuel Average.
// All three fields are required. Feeds the cascading dropdown in the vehicle form.
// ===============================================
// QUOTATIONS MODULE
// ===============================================
const SW_LOGO = "data:image/png;base64,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";
const SW_COMPANY = {
  name: 'Secure Wheels (Pvt.) Ltd.',
  address: '1st Floor, Arshad Plaza, Sargodha Road, Faisalabad.',
  phone: '03 111 250 251',
  email: 'info@SecureWheels.pk',
  web: 'www.SecureWheels.pk',
};
const QUOTE_PRODUCTS = [
  { name: 'Vehicle Tracker', recurring: false },
  { name: 'Fuel Monitoring System', recurring: false },
  { name: 'Setup Amount (Installation Charges)', recurring: false },
  { name: 'Platform Charges', recurring: true },
];
const QUOTE_DEFAULT_TERMS =
  '• Hardware and Installation charges will be 100% advance.\n' +
  '• Deployment within 45 calendar days from the date of Purchase Order, subject to site readiness and vehicle availability.\n' +
  '• Warranty of installed equipment is 12 months after installation.\n' +
  '• Platform charges are recurring as per the selected billing period.\n' +
  '• All prices are in PKR. Taxes are shown separately.';

const rsFmt = (n) => 'Rs. ' + Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 2 });

// ── Proposal content: single source of truth for the quotation builder + print ──
const PROP_SVGS = {
  tracking: `<svg viewBox="0 0 700 380" width="100%" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="GPS trip playback map">
    <clipPath id="tkmap"><rect x="10" y="66" width="680" height="258" rx="10"/></clipPath>
    <g clip-path="url(#tkmap)">
      <rect x="10" y="66" width="680" height="258" fill="#e7efe1"/>
      <g opacity="0.7"><ellipse cx="130" cy="120" rx="130" ry="70" fill="#d6e5cb"/><ellipse cx="560" cy="118" rx="150" ry="75" fill="#dbe8d0"/><ellipse cx="330" cy="300" rx="200" ry="70" fill="#eef4e9"/><ellipse cx="120" cy="292" rx="120" ry="60" fill="#d9e7cf"/></g>
      <g stroke="#cbd6c2" stroke-width="2" fill="none" opacity="0.85"><path d="M20 140 H 690"/><path d="M250 66 V 324"/><path d="M470 66 V 324"/><path d="M40 262 C 220 250, 440 240, 660 252"/></g>
      <g stroke="#eef4ea" stroke-width="10" fill="none" stroke-linecap="round" opacity="0.9"><path d="M100 250 C 210 210, 330 190, 405 182 S 560 156, 605 150"/></g>
      <g transform="translate(212,214)"><rect x="-17" y="-10" width="34" height="18" rx="4" fill="#0f9d58"/><text x="0" y="3" font-family="Arial" font-size="10" font-weight="700" fill="#fff" text-anchor="middle">M-15</text></g>
      <path d="M100 250 C 165 224, 205 212, 255 205 S 335 190, 355 186 S 485 162, 545 156 S 592 152, 605 150" fill="none" stroke="#e53935" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="118" cy="258" r="6" fill="#f59e0b" stroke="#fff" stroke-width="2"/>
      <circle cx="142" cy="250" r="6" fill="#f59e0b" stroke="#fff" stroke-width="2"/>
      <circle cx="100" cy="250" r="7.5" fill="#16a34a" stroke="#fff" stroke-width="2.5"/>
    </g>
    <g transform="translate(100,250)"><rect x="-27" y="-42" width="54" height="20" rx="10" fill="#0f172a"/><text x="0" y="-28" font-family="Arial" font-size="10.5" font-weight="800" fill="#fff" text-anchor="middle">START</text></g>
    <line x1="276" y1="134" x2="298" y2="197" stroke="#111827" stroke-width="1.4" stroke-dasharray="3 3"/>
    <g transform="translate(238,92)"><rect x="0" y="0" width="74" height="42" rx="10" fill="#111827"/><text x="37" y="20" font-family="Arial" font-size="16" font-weight="800" fill="#fff" text-anchor="middle">52</text><text x="37" y="33" font-family="Arial" font-size="8" fill="#cbd5e1" text-anchor="middle">KM/H · 14:22:43</text></g>
    <circle cx="355" cy="186" r="7.5" fill="#111827" stroke="#fff" stroke-width="2.5"/>
    <g transform="translate(355,186)"><rect x="-41" y="14" width="82" height="20" rx="10" fill="#0f172a"/><text x="0" y="28" font-family="Arial" font-size="9.5" font-weight="800" fill="#fff" text-anchor="middle">LONGEST STOP</text></g>
    <circle cx="605" cy="150" r="8" fill="#e53935" stroke="#fff" stroke-width="2.5"/>
    <g transform="translate(605,150)"><rect x="-20" y="-42" width="40" height="20" rx="10" fill="#0f172a"/><text x="0" y="-28" font-family="Arial" font-size="10.5" font-weight="800" fill="#fff" text-anchor="middle">END</text></g>
    <rect x="10" y="12" width="680" height="46" rx="10" fill="#111827"/>
    <g font-family="Arial" text-anchor="middle">
      <text x="150" y="33" font-size="14" font-weight="800" fill="#fff">654.7 km</text><text x="150" y="47" font-size="8.5" letter-spacing="1" fill="#94a3b8">DISTANCE</text>
      <text x="320" y="33" font-size="14" font-weight="800" fill="#fff">35h 27m</text><text x="320" y="47" font-size="8.5" letter-spacing="1" fill="#94a3b8">DURATION</text>
      <text x="470" y="33" font-size="14" font-weight="800" fill="#fff">9</text><text x="470" y="47" font-size="8.5" letter-spacing="1" fill="#94a3b8">STOPS</text>
      <text x="600" y="33" font-size="14" font-weight="800" fill="#fff">51.0 km/h</text><text x="600" y="47" font-size="8.5" letter-spacing="1" fill="#94a3b8">AVG SPEED</text>
    </g>
    <g stroke="#334155" stroke-width="1"><line x1="235" y1="22" x2="235" y2="48"/><line x1="400" y1="22" x2="400" y2="48"/><line x1="530" y1="22" x2="530" y2="48"/></g>
    <g transform="translate(160,336)">
      <rect x="0" y="0" width="380" height="34" rx="17" fill="#fff" stroke="#e6ebf1"/>
      <circle cx="24" cy="17" r="12" fill="#e53935"/><path d="M20 11 L20 23 L30 17 Z" fill="#fff"/>
      <text x="48" y="21" font-family="Arial" font-size="9" fill="#94a3b8">14:22:43</text>
      <line x1="95" y1="17" x2="250" y2="17" stroke="#e2e8f0" stroke-width="3" stroke-linecap="round"/>
      <line x1="95" y1="17" x2="150" y2="17" stroke="#e53935" stroke-width="3" stroke-linecap="round"/>
      <circle cx="150" cy="17" r="6" fill="#e53935" stroke="#fff" stroke-width="2"/>
      <text x="270" y="21" font-family="Arial" font-size="9" fill="#94a3b8">14:51:33</text>
      <g font-family="Arial" font-size="9.5" font-weight="700"><text x="312" y="21" fill="#64748b">1×</text><text x="332" y="21" fill="#64748b">2×</text><text x="352" y="21" fill="#64748b">5×</text><rect x="362" y="8" width="26" height="18" rx="9" fill="#111827"/><text x="375" y="21" fill="#fff" text-anchor="middle">10×</text></g>
    </g>
  </svg>`,
  fuel: `<svg viewBox="0 0 680 250" width="100%" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fuel level over time">
    <defs><linearGradient id="pfg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f87171" stop-opacity=".5"/><stop offset="1" stop-color="#fecaca" stop-opacity=".05"/></linearGradient></defs>
    <rect x="44" y="24" width="606" height="184" fill="#fbfcfe"/>
    <g stroke="#e6ebf1" stroke-width="1"><line x1="44" y1="62" x2="650" y2="62"/><line x1="44" y1="100" x2="650" y2="100"/><line x1="44" y1="138" x2="650" y2="138"/><line x1="44" y1="176" x2="650" y2="176"/></g>
    <line x1="44" y1="24" x2="44" y2="208" stroke="#94a3b8" stroke-width="1.5"/><line x1="44" y1="208" x2="650" y2="208" stroke="#94a3b8" stroke-width="1.5"/>
    <text x="18" y="120" transform="rotate(-90 18 120)" font-family="Arial" font-size="11" fill="#64748b" text-anchor="middle">Fuel Level</text>
    <text x="345" y="234" font-family="Arial" font-size="11" fill="#64748b" text-anchor="middle">Time</text>
    <path d="M60 56 L190 102 L190 172 L320 172 L320 66 L470 116 L636 142 L636 208 L60 208 Z" fill="url(#pfg)"/>
    <polyline points="60,56 190,102 190,172 320,172 320,66 470,116 636,142" fill="none" stroke="#dc2626" stroke-width="3" stroke-linejoin="round"/>
    <circle cx="125" cy="79" r="6" fill="#f59e0b" stroke="#fff" stroke-width="2"/><circle cx="190" cy="137" r="6.5" fill="#dc2626" stroke="#fff" stroke-width="2"/><circle cx="255" cy="172" r="6" fill="#64748b" stroke="#fff" stroke-width="2"/><circle cx="320" cy="119" r="6.5" fill="#16a34a" stroke="#fff" stroke-width="2"/>
    <text x="125" y="66" font-family="Arial" font-size="10.5" font-weight="700" fill="#b45309" text-anchor="middle">Normal</text><text x="200" y="134" font-family="Arial" font-size="10.5" font-weight="700" fill="#b91c1c">Theft</text><text x="255" y="192" font-family="Arial" font-size="10.5" font-weight="700" fill="#475569" text-anchor="middle">Idle</text><text x="330" y="106" font-family="Arial" font-size="10.5" font-weight="700" fill="#15803d">Refill</text>
  </svg>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;margin-top:8px">
    <div style="font-size:11.5px;color:#475569;display:flex;gap:8px"><span style="width:12px;height:12px;border-radius:3px;background:#f59e0b;flex-shrink:0;margin-top:2px"></span><span><b>Gradual decrease</b> — normal consumption</span></div>
    <div style="font-size:11.5px;color:#475569;display:flex;gap:8px"><span style="width:12px;height:12px;border-radius:3px;background:#dc2626;flex-shrink:0;margin-top:2px"></span><span><b>Sudden drop</b> — possible theft/leakage</span></div>
    <div style="font-size:11.5px;color:#475569;display:flex;gap:8px"><span style="width:12px;height:12px;border-radius:3px;background:#16a34a;flex-shrink:0;margin-top:2px"></span><span><b>Sudden increase</b> — a refilling event</span></div>
    <div style="font-size:11.5px;color:#475569;display:flex;gap:8px"><span style="width:12px;height:12px;border-radius:3px;background:#64748b;flex-shrink:0;margin-top:2px"></span><span><b>Stable level</b> — parked / idle</span></div>
  </div>`,
  dashcam: `<svg viewBox="0 0 680 210" width="100%" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Dashcam field of view">
    <rect x="0" y="0" width="680" height="210" rx="8" fill="#f6f9fc" stroke="#e6ebf1"/>
    <path d="M120 105 L300 45 L300 165 Z" fill="#ef4444" fill-opacity=".12" stroke="#ef4444" stroke-width="1.5"/>
    <rect x="86" y="91" width="42" height="28" rx="5" fill="#0f172a"/><circle cx="107" cy="105" r="8" fill="#334155" stroke="#94a3b8" stroke-width="2"/><circle cx="107" cy="105" r="3" fill="#ef4444"/>
    <text x="107" y="142" font-family="Arial" font-size="10.5" fill="#475569" text-anchor="middle" font-weight="700">Dashcam</text>
    <text x="250" y="58" font-family="Arial" font-size="10.5" fill="#b91c1c" font-weight="700">Road view</text>
    <g transform="translate(360,44)"><rect width="130" height="52" rx="8" fill="#fff" stroke="#e2e8f0"/><circle cx="22" cy="26" r="10" fill="#16a34a" fill-opacity=".15"/><path d="M17 26 l4 4 l8 -9" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><text x="42" y="24" font-family="Arial" font-size="10.5" fill="#0f172a" font-weight="700">Driver alert</text><text x="42" y="38" font-family="Arial" font-size="9.5" fill="#64748b">Drowsiness / phone</text></g>
    <g transform="translate(360,114)"><rect width="130" height="52" rx="8" fill="#fff" stroke="#e2e8f0"/><circle cx="22" cy="26" r="10" fill="#ef4444" fill-opacity=".15"/><path d="M22 20 v8 M22 32 v.5" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round"/><text x="42" y="24" font-family="Arial" font-size="10.5" fill="#0f172a" font-weight="700">Event clip</text><text x="42" y="38" font-family="Arial" font-size="9.5" fill="#64748b">Collision / harsh brake</text></g>
    <g transform="translate(534,78)"><path d="M26 8 a10 10 0 0 0 -19 2 a8 8 0 0 0 1 16 h20 a9 9 0 0 0 1 -18 a10 10 0 0 0 -4 0 Z" fill="#dbe2ff" stroke="#6366f1" stroke-width="1.5"/><path d="M18 24 v10 M18 34 l-4 -4 M18 34 l4 -4" stroke="#6366f1" stroke-width="2" stroke-linecap="round" fill="none"/><text x="19" y="52" font-family="Arial" font-size="10.5" fill="#4338ca" text-anchor="middle" font-weight="700">Cloud</text></g>
  </svg>`,
};

const PROPOSAL_ORDER = ['tracking', 'fuel', 'dashcam'];
const PROPOSAL_CATALOG = {
  tracking: {
    title: 'Vehicle Tracking Content', h: 'Vehicle Tracking System',
    intro: [
      'Our GPS-based vehicle tracking system gives you live visibility and control over every vehicle, from a single car to an entire fleet. Rugged GPS devices report exact location, speed, ignition and movement in real time — so you always know where your assets are and can act the moment something goes wrong.',
      'Track vehicles live on an interactive map, review complete route history, and receive instant alerts for theft, over-speeding, geofence breaches and unauthorized movement — accessible from web and mobile, anywhere, anytime.',
    ],
    features: [
      { k: 'gps', t: 'Real-time GPS location on live map' },
      { k: 'route', t: 'Route history &amp; trip playback' },
      { k: 'geo_in', t: 'Geofence entry alerts' },
      { k: 'geo_out', t: 'Geofence exit alerts' },
      { k: 'over', t: 'Over-speeding alerts' },
      { k: 'harsh', t: 'Harsh driving alerts' },
      { k: 'engine', t: 'Remote engine immobilization (engine cut)' },
      { k: 'ign', t: 'Ignition on/off alerts' },
      { k: 'tow', t: 'Tow-away alerts' },
      { k: 'mileage', t: 'Mileage, idling &amp; stoppage reports' },
      { k: 'sos', t: 'Panic / SOS button support' },
    ],
    advantages: [
      { k: 'security', t: '<b>Vehicle security</b> — stolen vehicle assistance, and remote engine cut to stop unauthorized use.' },
      { k: 'fleet', t: '<b>Fleet efficiency</b> — optimize routes, cut idle time &amp; fuel waste, improve on-time delivery.' },
      { k: 'account', t: '<b>Accountability</b> — verify trips, timings &amp; driver conduct with tamper-proof data.' },
    ],
  },
  fuel: {
    title: 'Fuel Sensor Content', h: 'Fuel Monitoring System',
    intro: [
      'Advanced digital fuel sensors monitor tanks remotely and report every fill, drain or sudden change with GPS coordinates and time — preventing theft and reducing losses, cost and delays.',
      'The graphical fuel view lets management see consumption — when, where and why — to detect violations, draining and over-consumption while evaluating driver behaviour.',
    ],
    features: [
      { k: 'theft', t: 'Prevents fuel theft &amp; unauthorized usage' },
      { k: 'accuracy', t: 'Sensor accuracy above 95% (up to 99.9%)' },
      { k: 'fillreport', t: 'Reports on every fill &amp; drainage event' },
      { k: 'history', t: 'Fuel-consumption info &amp; history' },
      { k: 'store', t: 'Stores fuel data for historical analysis' },
      { k: 'graphview', t: 'Graphical fuel-level view to catch theft &amp; drainage events' },
    ],
    advantages: [
      { k: 'fuelctrl', t: '<b>Fuel-tank fuelling control</b> — monitor where, when &amp; how much fuel was filled or drained, avoiding manipulation and cutting cost.' },
      { k: 'theftctrl', t: '<b>Fuel-theft control</b> — unauthorized draining is identified from the graphical fuel data &amp; history; theft events appear as sudden drops on the fuel-level graph.' },
      { k: 'consumption', t: '<b>Actual engine consumption</b> — reads consumption vs mileage and calculates average km/litre to detect &amp; avoid misuse.' },
    ],
  },
  dashcam: {
    title: 'DashCam Content', h: 'DashCam &amp; Video Telematics',
    intro: [
      'Dashcam records HD video with GPS-tagged clips on SD card. Fully configurable — single or dual camera, optional extra cameras, and optional ADAS, AI driver monitoring and driving-behaviour detection — so the quotation matches exactly what the customer buys.',
    ],
    cameras: [
      { k: 'single', t: 'Single camera — road-facing (1 side)' },
      { k: 'dual', t: 'Dual camera — road + cabin (2 sides)' },
      { k: 'extra', t: 'Additional external camera(s)' },
    ],
    features: [
      { k: 'hd', t: 'HD recording + live streaming' },
      { k: 'clips', t: 'Auto event clips — harsh brake, impact, collision' },
      { k: 'adas', t: 'ADAS — forward-collision &amp; lane-departure (optional)' },
      { k: 'ai', t: 'AI driving monitoring — drowsiness, distraction, phone (optional)' },
      { k: 'behaviour', t: 'Driving-behaviour — harsh braking, acceleration (optional)' },
      { k: 'cloud', t: 'Cloud storage &amp; backup (optional)' },
      { k: 'platform', t: 'Integrated with the tracking platform' },
    ],
    advantages: [
      { k: 'evidence', t: '<b>Evidence &amp; protection</b> — clear video proof against false claims, disputes &amp; fraudulent accident cases.' },
      { k: 'prevent', t: '<b>Accident prevention</b> — with AI/ADAS, real-time coaching reduces risky driving before it causes a crash.' },
      { k: 'oversight', t: '<b>Cabin &amp; cargo oversight</b> — verify driver conduct and monitor what happens inside the vehicle.' },
    ],
  },
};

function defaultProposalContent() {
  return {
    tracking: { on: true, feat: ['gps', 'route', 'geo_in', 'geo_out', 'over', 'harsh', 'engine', 'ign', 'tow', 'mileage'], adv: ['security', 'fleet', 'account'] },
    fuel: { on: true, feat: ['theft', 'accuracy', 'fillreport', 'history', 'store', 'graphview'], adv: ['fuelctrl', 'theftctrl', 'consumption'] },
    dashcam: { on: true, cam: ['single'], feat: ['hd', 'clips', 'platform'], adv: ['evidence'], sd: '64 GB' },
  };
}

function parseProposalContent(raw) {
  const d = defaultProposalContent();
  if (!raw) return d;
  try {
    const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!p || typeof p !== 'object') return d;
    return {
      tracking: { ...d.tracking, ...(p.tracking || {}) },
      fuel: { ...d.fuel, ...(p.fuel || {}) },
      dashcam: { ...d.dashcam, ...(p.dashcam || {}) },
    };
  } catch { return d; }
}

// Build the selected proposal sections (in order) as printable HTML.
function renderProposalContent(raw) {
  const sel = parseProposalContent(raw);
  let out = '';
  for (const key of PROPOSAL_ORDER) {
    const s = sel[key]; const cat = PROPOSAL_CATALOG[key];
    if (!s || !s.on) continue;
    const cams = (cat.cameras || []).filter(x => (s.cam || []).includes(x.k));
    const feats = (cat.features || []).filter(f => (s.feat || []).includes(f.k));
    const advs = (cat.advantages || []).filter(a => (s.adv || []).includes(a.k));
    if (!cams.length && !feats.length && !advs.length && !(key === 'dashcam' && s.sd)) continue;
    out += `<section class="prop"><h2>${cat.h}</h2>${cat.intro.map(p => `<p>${p}</p>`).join('')}`;
    out += `<div class="chart">${PROP_SVGS[key]}</div>`;
    if (cams.length) out += `<h3>Camera Configuration</h3><ul class="grid2">${cams.map(x => `<li>${x.t}</li>`).join('')}</ul>`;
    if (feats.length) out += `<h3>Features</h3><ul class="grid2">${feats.map(f => `<li>${f.t}</li>`).join('')}</ul>`;
    if (key === 'dashcam' && s.sd) out += `<h3>Storage</h3><ul><li>SD card storage: <b>${s.sd}</b></li></ul>`;
    if (advs.length) out += `<h3>Advantages</h3><ul>${advs.map(a => `<li>${a.t}</li>`).join('')}</ul>`;
    out += `</section>`;
  }
  return out;
}

// Builder UI: per-section feature/advantage checkboxes (shown when profile is attached).
function ProposalContentPicker({ content, onChange }) {
  const c = content && content.tracking ? content : defaultProposalContent();
  const upd = (key, patch) => onChange({ ...c, [key]: { ...c[key], ...patch } });
  const tog = (key, field, k) => {
    const arr = c[key][field] || [];
    upd(key, { [field]: arr.includes(k) ? arr.filter(x => x !== k) : [...arr, k] });
  };
  const ckLabel = 'flex items-start gap-2 cursor-pointer text-xs text-slate-600 leading-snug';
  const ckInput = 'w-4 h-4 mt-0.5 accent-red-500 flex-shrink-0';
  const grpTitle = 'text-[10.5px] font-bold uppercase tracking-wide text-slate-500 mb-1.5';
  return (
    <div className="space-y-3">
      {PROPOSAL_ORDER.map((key, i) => {
        const cat = PROPOSAL_CATALOG[key]; const s = c[key];
        return (
          <div key={key} className="border border-slate-200 rounded-xl overflow-hidden">
            <label className="flex items-center gap-2.5 px-4 py-2.5 bg-red-50 border-b border-red-100 cursor-pointer">
              <span className="w-5 h-5 rounded-md bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
              <span className="flex-1 font-bold text-red-700 text-sm uppercase tracking-wide" dangerouslySetInnerHTML={{ __html: cat.title }} />
              <input type="checkbox" checked={!!s.on} onChange={e => upd(key, { on: e.target.checked })} className="w-4 h-4 accent-red-500" />
            </label>
            {s.on && (
              <div className="p-4 space-y-3">
                {cat.cameras && (
                  <div>
                    <div className={grpTitle}>Camera configuration</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {cat.cameras.map(x => (
                        <label key={x.k} className={ckLabel}>
                          <input type="checkbox" checked={(s.cam || []).includes(x.k)} onChange={() => tog(key, 'cam', x.k)} className={ckInput} />
                          <span dangerouslySetInnerHTML={{ __html: x.t }} />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className={grpTitle}>Features</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {cat.features.map(f => (
                      <label key={f.k} className={ckLabel}>
                        <input type="checkbox" checked={(s.feat || []).includes(f.k)} onChange={() => tog(key, 'feat', f.k)} className={ckInput} />
                        <span dangerouslySetInnerHTML={{ __html: f.t }} />
                      </label>
                    ))}
                  </div>
                </div>
                {key === 'dashcam' && (
                  <div>
                    <div className={grpTitle}>SD card storage <span className="normal-case font-normal text-slate-400">(printed in proposal)</span></div>
                    <input type="text" value={s.sd || ''} onChange={e => upd('dashcam', { sd: e.target.value })} placeholder="e.g. 64 GB" className="px-3 py-2 rounded-lg border border-slate-300 text-sm w-40 outline-none focus:border-red-400" />
                  </div>
                )}
                <div>
                  <div className={grpTitle}>Advantages</div>
                  <div className="space-y-2">
                    {cat.advantages.map(a => (
                      <label key={a.k} className={ckLabel}>
                        <input type="checkbox" checked={(s.adv || []).includes(a.k)} onChange={() => tog(key, 'adv', a.k)} className={ckInput} />
                        <span dangerouslySetInnerHTML={{ __html: a.t }} />
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Build a polished, printable A4 quotation document (opens in a new window).
function printQuotation(q) {
  const items = Array.isArray(q.items) ? q.items : [];
  const rows = items.map((it, i) => {
    const qty = parseFloat(it.qty) || 0;
    const up = parseFloat(it.unitPrice) || 0;
    const amt = qty * up;
    const period = it.period ? `<span class="pill">${it.period}</span>` : '<span class="muted">One-time</span>';
    return `<tr>
      <td class="c">${i + 1}</td>
      <td><div class="iname">${it.name || ''}</div>${it.desc ? `<div class="idesc">${it.desc}</div>` : ''}</td>
      <td class="c">${period}</td>
      <td class="r">${qty.toLocaleString()}</td>
      <td class="r">${rsFmt(up)}</td>
      <td class="r b">${rsFmt(amt)}</td>
    </tr>`;
  }).join('');
  const subtotal = parseFloat(q.subtotal) || 0;
  const gstPct = parseFloat(q.gstPercent ?? q.gst_percent) || 0;
  const gstAmt = parseFloat(q.gstAmount ?? q.gst_amount) || 0;
  const total = parseFloat(q.total) || 0;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const terms = (q.notes || '').split('\n').filter(Boolean).map(t => `<div>${t}</div>`).join('');
  const profile = !!(q.includeProfile ?? q.include_profile);
  const showGst = (parseFloat(q.gstPercent ?? q.gst_percent) || 0) > 0 || gstAmt > 0;
  const fuelGraphSvg = `<svg viewBox="0 0 680 250" width="100%" xmlns="http://www.w3.org/2000/svg">
    <defs><linearGradient id="fgArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f87171" stop-opacity=".5"/><stop offset="1" stop-color="#fecaca" stop-opacity=".05"/></linearGradient></defs>
    <rect x="44" y="24" width="606" height="184" fill="#fbfcfe"/>
    <g stroke="#e6ebf1" stroke-width="1"><line x1="44" y1="62" x2="650" y2="62"/><line x1="44" y1="100" x2="650" y2="100"/><line x1="44" y1="138" x2="650" y2="138"/><line x1="44" y1="176" x2="650" y2="176"/></g>
    <line x1="44" y1="24" x2="44" y2="208" stroke="#94a3b8" stroke-width="1.5"/>
    <line x1="44" y1="208" x2="650" y2="208" stroke="#94a3b8" stroke-width="1.5"/>
    <text x="18" y="120" transform="rotate(-90 18 120)" font-family="Arial" font-size="11" fill="#64748b" text-anchor="middle">Fuel Level</text>
    <text x="345" y="234" font-family="Arial" font-size="11" fill="#64748b" text-anchor="middle">Time</text>
    <path d="M60 56 L190 102 L190 172 L320 172 L320 66 L470 116 L636 142 L636 208 L60 208 Z" fill="url(#fgArea)"/>
    <polyline points="60,56 190,102 190,172 320,172 320,66 470,116 636,142" fill="none" stroke="#dc2626" stroke-width="3" stroke-linejoin="round"/>
    <circle cx="125" cy="79" r="6" fill="#f59e0b" stroke="#fff" stroke-width="2"/>
    <circle cx="190" cy="137" r="6.5" fill="#dc2626" stroke="#fff" stroke-width="2"/>
    <circle cx="255" cy="172" r="6" fill="#64748b" stroke="#fff" stroke-width="2"/>
    <circle cx="320" cy="119" r="6.5" fill="#16a34a" stroke="#fff" stroke-width="2"/>
    <text x="125" y="66" font-family="Arial" font-size="10.5" font-weight="700" fill="#b45309" text-anchor="middle">Normal</text>
    <text x="200" y="134" font-family="Arial" font-size="10.5" font-weight="700" fill="#b91c1c">Theft</text>
    <text x="255" y="192" font-family="Arial" font-size="10.5" font-weight="700" fill="#475569" text-anchor="middle">Idle</text>
    <text x="330" y="106" font-family="Arial" font-size="10.5" font-weight="700" fill="#15803d">Refill</text>
  </svg>`;
  const proposalHtml = !profile ? '' : `
    <section class="prop">
      <h2>About Secure Wheels</h2>
      <p class="lead">Pakistan's Leading Vehicle Tracking Company</p>
      <p>Secure Wheels (Pvt.) Ltd provides Vehicle Tracking and Fuel Monitoring Solutions across Pakistan. Our mission is to deliver top-tier vehicle tracking solutions that ensure the safety of vehicles, optimize business operations, and provide peace of mind to our customers.</p>
      <p>With 24/7 monitoring, real-time tracking and advanced security features, Secure Wheels is your trusted partner in vehicle security and fleet management.</p>
    </section>
    <section class="prop">
      <h2>Fuel Monitoring System</h2>
      <p>Our GPS-based vehicle tracking systems with fuel-monitoring function are the most useful tool for remote fuel management. Specially designed GPS devices, configured with advanced digital fuel sensors, monitor fuel tanks remotely and report any variation with GPS coordinates and time — helping prevent theft and reduce losses, costs, delays and damage to reputation.</p>
      <p>The real-time mechanism lets fleet managers know exactly when and where fuel losses occur by sending a prompt notification of any sudden change in fuel level.</p>
      <h3>Features</h3>
      <ul class="grid2">
        <li>Prevents fuel theft &amp; unauthorized fuel usage</li>
        <li>Sensor accuracy above 95% (up to 99.9% after calibration)</li>
        <li>Detailed reports on every fuel filling &amp; drainage event</li>
        <li>Detailed fuel-consumption information &amp; history</li>
        <li>Collects &amp; stores fuel data for historical analysis</li>
        <li>Instant alerts on sudden fuel-level changes</li>
      </ul>
      <h3>Advantages</h3>
      <ul>
        <li><b>Fuel-tank fuelling control</b> — monitor where, when and how much fuel was filled into or drained from the tank, avoiding manipulation and reducing operational costs.</li>
        <li><b>Fuel-theft control</b> — using the sensor data and calibration table, the user is alerted about unauthorized draining almost immediately.</li>
        <li><b>Actual engine consumption</b> — reads consumption with mileage and calculates average mileage per litre to detect and avoid misuse.</li>
      </ul>
    </section>
    <section class="prop">
      <h2>Quality &amp; Theft Control</h2>
      <p>With the fuel-monitoring graph, management can easily see fuel consumption — when, where and why — to detect violations and prevent draining, fraud and over-consumption, while evaluating driver behaviour. Fuel levels are measured by Secure Wheels' advanced digital fuel sensor with up to 99.9% accuracy after proper installation and calibration.</p>
      <div class="chart">${fuelGraphSvg}</div>
      <div class="fglegend">
        <div><span class="d" style="background:#f59e0b"></span><span><b>Gradual decrease</b> — normal fuel consumption during operation</span></div>
        <div><span class="d" style="background:#dc2626"></span><span><b>Sudden drop</b> — possible fuel theft or leakage</span></div>
        <div><span class="d" style="background:#16a34a"></span><span><b>Sudden increase</b> — a fuel refilling event</span></div>
        <div><span class="d" style="background:#64748b"></span><span><b>Stable level</b> — vehicle parked / idle, no consumption</span></div>
      </div>
    </section>
    <section class="prop">
      <h2>Platform Benefits</h2>
      <ul class="grid2">
        <li><b>Dashboard</b> — location, fuel, speed &amp; trip history in one portal</li>
        <li><b>Real-time alerts</b> — theft, refilling, geofence, over-speeding</li>
        <li><b>Advanced reporting &amp; analytics</b> — fuel, mileage, driver behaviour, idling</li>
        <li><b>Enhanced security</b> — prevent fuel/cargo theft &amp; unauthorized use</li>
        <li><b>Operational efficiency</b> — optimize routes, cut idle time, lower cost</li>
        <li><b>Live fleet visibility</b> on an interactive map</li>
        <li><b>Geofencing</b> with automatic entry/exit alerts</li>
        <li><b>Driver-performance monitoring</b> — harsh braking, acceleration, idling</li>
        <li><b>Scalable</b> for fleets of any size — transport, logistics, warehousing</li>
        <li><b>24/7 technical support</b> &amp; system monitoring</li>
      </ul>
    </section>`;
  const whyHtml = !profile ? '' : `
    <section class="prop">
      <h2>Why Secure Wheels</h2>
      <p>We offer customized solutions tailored to your fleet security and business needs. With advanced technology, we provide innovative solutions efficiently and cost-effectively.</p>
      <p style="font-size:10.5px;color:#94a3b8;margin-top:10px">Confidential: This document contains proprietary information of Secure Wheels (Pvt.) Ltd and is provided solely for the recipient's evaluation.</p>
    </section>`;

  // Company profile intro + the selected product sections (Vehicle Tracking → Fuel → DashCam).
  const proposalDynamic = !profile ? '' : (
    `<section class="prop"><h2>About Secure Wheels</h2>`
    + `<p class="lead">Pakistan's Leading Vehicle Tracking Company</p>`
    + `<p>Secure Wheels (Pvt.) Ltd provides Vehicle Tracking, Fuel Monitoring and Video Telematics solutions across Pakistan. Our mission is to deliver top-tier fleet-management technology that ensures vehicle safety, optimizes business operations, and gives our customers complete peace of mind.</p>`
    + `<p>With 24/7 monitoring, real-time tracking and advanced security features, Secure Wheels is your trusted partner in vehicle security and fleet management.</p></section>`
    + renderProposalContent(q.proposalContent ?? q.proposal_content)
  );

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${q.quoteNumber || q.quote_number || 'Quotation'}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Segoe UI',Arial,sans-serif;color:#1e293b;background:#eef1f4;padding:24px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .page{max-width:820px;margin:0 auto;background:#fff;box-shadow:0 8px 30px rgba(0,0,0,.12);border-radius:10px;overflow:hidden}
    .top{background:#fff;color:#0f172a;padding:22px 34px;display:flex;justify-content:space-between;align-items:center}
    .logo{height:66px;width:auto}
    .accent{height:6px;background:linear-gradient(90deg,#b91c1c,#ef4444)}
    .qtitle{text-align:right}
    .qtitle h1{font-size:28px;font-weight:800;letter-spacing:3px;color:#b91c1c}
    .qtitle .qn{margin-top:6px;font-size:13px;font-weight:700;background:#f1f5f9;color:#334155;padding:5px 12px;border-radius:20px;display:inline-block}
    .cmeta2{font-size:10.5px;color:#64748b;margin-top:7px;line-height:1.5}
    .prop{padding:18px 34px;border-bottom:1px solid #f1f5f9}
    .prop h2{color:#b91c1c;font-size:15px;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px;border-left:4px solid #ef4444;padding-left:10px}
    .prop h3{font-size:12px;color:#0f172a;text-transform:uppercase;letter-spacing:.5px;margin:14px 0 6px}
    .prop p{font-size:12.5px;color:#475569;line-height:1.7;margin-bottom:8px}
    .prop .lead{color:#b91c1c;font-weight:700;font-size:13px}
    .prop ul{list-style:none}
    .prop ul li{font-size:12px;color:#475569;line-height:1.6;padding-left:18px;position:relative;margin-bottom:5px}
    .prop ul li:before{content:'\\2713';position:absolute;left:0;color:#ef4444;font-weight:800}
    .grid2{display:grid;grid-template-columns:1fr 1fr;gap:0 28px}
    .pgbreak{page-break-before:always}
    .chart{margin:12px 0;border:1px solid #eef2f6;border-radius:10px;padding:8px 10px;background:#fff;page-break-inside:avoid}
    .chart svg{display:block;width:auto;height:auto;max-width:100%;max-height:225px;margin:0 auto}
    .prop h2,.prop h3{page-break-after:avoid}
    .prop p,.prop ul li{page-break-inside:avoid}
    .fglegend{display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;margin-top:6px}
    .fglegend div{font-size:11.5px;color:#475569;display:flex;align-items:flex-start;gap:8px;line-height:1.5}
    .fglegend .d{width:12px;height:12px;border-radius:3px;flex-shrink:0;margin-top:2px}
    .pricehd{color:#b91c1c;font-size:15px;font-weight:800;text-transform:uppercase;letter-spacing:1px;margin-bottom:14px;border-left:4px solid #ef4444;padding-left:10px}
    .band{display:flex;justify-content:space-between;padding:18px 34px;background:#fff7f7;border-bottom:1px solid #fde2e2}
    .to .lbl,.meta .lbl{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#b91c1c;font-weight:700;margin-bottom:3px}
    .to .name{font-size:17px;font-weight:800;color:#0f172a}
    .to .city{font-size:13px;color:#475569;margin-top:2px}
    .meta{text-align:right;font-size:12px;color:#334155}
    .meta .row{margin-bottom:4px}
    .meta b{color:#0f172a}
    .body{padding:24px 34px}
    table{width:100%;border-collapse:collapse;font-size:12.5px}
    thead th{background:#0f172a;color:#fff;text-align:left;padding:11px 12px;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
    thead th.r{text-align:right}thead th.c{text-align:center}
    tbody td{padding:11px 12px;border-bottom:1px solid #eef2f6;vertical-align:top}
    tbody tr:nth-child(even){background:#fafbfc}
    td.r{text-align:right}td.c{text-align:center}td.b{font-weight:700;color:#0f172a}
    .iname{font-weight:700;color:#0f172a}.idesc{font-size:11px;color:#64748b;margin-top:2px}
    .pill{background:#eef2ff;color:#4338ca;font-size:10.5px;font-weight:700;padding:3px 9px;border-radius:12px}
    .muted{color:#94a3b8;font-size:11px}
    .totwrap{display:flex;justify-content:flex-end;margin-top:18px}
    .tot{width:300px}
    .tot .l{display:flex;justify-content:space-between;padding:8px 4px;font-size:13px;color:#475569}
    .tot .l b{color:#0f172a}
    .tot .grand{margin-top:6px;background:linear-gradient(135deg,#b91c1c,#ef4444);color:#fff;padding:13px 16px;border-radius:10px;display:flex;justify-content:space-between;font-size:16px;font-weight:800}
    .terms{margin-top:26px;background:#f8fafc;border:1px solid #eef2f6;border-radius:10px;padding:16px 18px}
    .terms h3{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#b91c1c;margin-bottom:8px}
    .terms div{font-size:12px;color:#475569;line-height:1.7}
    .foot{padding:16px 34px;background:#0f172a;color:#cbd5e1;font-size:11px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px}
    .foot b{color:#fff}
    .sign{display:flex;justify-content:space-between;margin-top:34px;padding:0 4px}
    .sign .box{width:220px;border-top:2px solid #cbd5e1;padding-top:6px;font-size:12px;color:#64748b;text-align:center}
    @media print{body{background:#fff;padding:0}.page{box-shadow:none;border-radius:0;max-width:100%}}
  </style></head><body>
  <div class="page">
    <div class="top">
      <img class="logo" src="${SW_LOGO}" alt="Secure Wheels"/>
      <div class="qtitle"><h1>QUOTATION</h1><div class="qn">${q.quoteNumber || q.quote_number || ''}</div>
        <div class="cmeta2">${SW_COMPANY.address}<br>${SW_COMPANY.phone} &nbsp;·&nbsp; ${SW_COMPANY.email} &nbsp;·&nbsp; ${SW_COMPANY.web}</div>
      </div>
    </div>
    <div class="accent"></div>
    <div class="band">
      <div class="to"><div class="lbl">Quotation For</div>
        <div class="name">${q.customerName || q.customer_name || ''}</div>
        <div class="city">${q.city || ''}</div>
      </div>
      <div class="meta">
        <div class="row"><span class="lbl" style="display:inline">Date</span> &nbsp; <b>${fmtDate(q.quoteDate || q.quote_date)}</b></div>
        <div class="row"><span class="lbl" style="display:inline">Valid Upto</span> &nbsp; <b>${fmtDate(q.validUpto || q.valid_upto)}</b></div>
      </div>
    </div>
    ${proposalDynamic}
    <div class="body ${profile ? 'pgbreak' : ''}">
      <div class="pricehd">Price Quotation</div>
      <table>
        <thead><tr><th class="c">#</th><th>Item</th><th class="c">Billing</th><th class="r">Qty</th><th class="r">Unit Price</th><th class="r">Amount</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="6" class="c muted" style="padding:24px">No items</td></tr>'}</tbody>
      </table>
      <div class="totwrap"><div class="tot">
        <div class="l"><span>Subtotal</span><b>${rsFmt(subtotal)}</b></div>
        ${showGst ? `<div class="l"><span>GST (${gstPct}%)</span><b>${rsFmt(gstAmt)}</b></div>` : ''}
        <div class="grand"><span>Total</span><span>${rsFmt(total)}</span></div>
      </div></div>
      <div class="terms"><h3>Terms &amp; Conditions</h3>${terms}</div>
      <div class="sign"><div class="box">Customer Acceptance</div><div class="box">For ${SW_COMPANY.name}</div></div>
    </div>
    ${whyHtml}
    <div class="foot"><div>Thank you for your business. <b>${SW_COMPANY.name}</b></div><div>This is a computer-generated quotation.</div></div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},350);}</script>
  </body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Popup blocked — please allow popups to print the quotation.'); return; }
  w.document.write(html);
  w.document.close();
}

function QuotationsModule({ quotations, customers, reload, can = { create: true, edit: true, delete: true } }) {
  const currentUser = React.useMemo(() => {
    try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.full_name || u.username || ''; } catch { return ''; }
  }, []);
  const today = new Date().toISOString().split('T')[0];
  const plus = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().split('T')[0]; };
  const emptyForm = () => ({
    customerId: null, customerName: '', city: '',
    quoteDate: today, validUpto: plus(14), gstPercent: 18,
    includeGst: true, includeProfile: false,
    content: defaultProposalContent(),
    notes: QUOTE_DEFAULT_TERMS, items: [],
  });

  const [view, setView] = React.useState('list'); // 'list' | 'new'
  const [form, setForm] = React.useState(emptyForm());
  const [saving, setSaving] = React.useState(false);
  const [custOpen, setCustOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const uid = () => Math.random().toString(36).slice(2, 9);

  const addItem = (preset) => setForm(f => ({
    ...f,
    items: [...f.items, {
      id: uid(),
      name: preset ? preset.name : '',
      desc: '',
      period: preset && preset.recurring ? 'Monthly' : '',
      qty: 1,
      unitPrice: '',
    }],
  }));
  const updItem = (id, k, v) => setForm(f => ({ ...f, items: f.items.map(it => it.id === id ? { ...it, [k]: v } : it) }));
  const delItem = (id) => setForm(f => ({ ...f, items: f.items.filter(it => it.id !== id) }));

  const subtotal = form.items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unitPrice) || 0), 0);
  const gstAmount = form.includeGst ? subtotal * (parseFloat(form.gstPercent) || 0) / 100 : 0;
  const total = subtotal + gstAmount;

  const pickCustomer = (c) => {
    setForm(f => ({ ...f, customerId: c.id, customerName: c.name, city: c.city || '' }));
    setCustOpen(false); setSearch('');
  };

  const custMatches = (() => {
    const q = search.trim().toLowerCase();
    return (customers || []).filter(c => !q || c.name.toLowerCase().includes(q) || String(c.id).includes(q)).slice(0, 30);
  })();

  const buildPayload = () => ({
    customerId: form.customerId,
    customerName: form.customerName.trim(),
    city: form.city.trim(),
    quoteDate: form.quoteDate,
    validUpto: form.validUpto,
    gstPercent: form.includeGst ? (parseFloat(form.gstPercent) || 0) : 0,
    gstAmount, subtotal, total,
    includeProfile: !!form.includeProfile,
    proposalContent: JSON.stringify(form.content || defaultProposalContent()),
    notes: form.notes,
    status: 'sent',
    createdBy: currentUser || '',
    items: form.items.map(it => ({
      name: it.name, desc: it.desc, period: it.period,
      qty: parseFloat(it.qty) || 0, unitPrice: parseFloat(it.unitPrice) || 0,
    })),
  });

  const validate = () => {
    if (!form.customerName.trim()) { alert('Customer name is required'); return false; }
    if (form.items.length === 0) { alert('Add at least one product/item'); return false; }
    return true;
  };

  const save = async (printAfter) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await api.quotations.create(payload);
      await reload();
      if (printAfter) printQuotation({ ...payload, quoteNumber: res?.data?.quoteNumber });
      setForm(emptyForm());
      setView('list');
    } catch (e) {
      alert('Failed to save quotation: ' + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const printExisting = async (id, forceProfile) => {
    try {
      const res = await api.quotations.getOne(id);
      const d = res.data;
      printQuotation({
        quoteNumber: d.quote_number, customerName: d.customer_name, city: d.city,
        quoteDate: d.quote_date, validUpto: d.valid_upto, subtotal: d.subtotal,
        gstPercent: d.gst_percent, gstAmount: d.gst_amount, total: d.total,
        includeProfile: forceProfile ? true : Number(d.include_profile) === 1,
        proposalContent: d.proposal_content,
        notes: d.notes, items: d.items || [],
      });
    } catch (e) { alert('Failed to open quotation: ' + (e.message || e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try { await api.quotations.delete(id); await reload(); } catch (e) { alert(e.message || 'Failed'); }
  };

  const inp = 'w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all text-sm';

  // ---------- LIST VIEW ----------
  if (view === 'list') {
    return (
      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FileText className="text-red-500" size={22} /> Quotations</h2>
            <p className="text-sm text-slate-500 mt-0.5">{quotations.length} quotation{quotations.length !== 1 ? 's' : ''} generated</p>
          </div>
          {can.create && (
          <button onClick={() => { setForm(emptyForm()); setView('new'); }}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2.5 rounded-xl font-semibold hover:shadow-md transition-all flex items-center gap-2">
            <Plus size={18} /> New Quotation
          </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-600 uppercase">Quote #</th>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-600 uppercase">Customer</th>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-600 uppercase">City</th>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-600 uppercase">Valid Upto</th>
                <th className="px-4 py-2.5 text-right text-xs font-bold text-slate-600 uppercase">Total</th>
                <th className="px-4 py-2.5 text-left text-xs font-bold text-slate-600 uppercase">By</th>
                <th className="px-4 py-2.5 text-center text-xs font-bold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {quotations.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-slate-400">
                  <FileText className="mx-auto mb-3 opacity-40" size={40} /><p>No quotations yet. Click "New Quotation" to create one.</p>
                </td></tr>
              ) : quotations.map(q => (
                <tr key={q.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono font-semibold text-slate-800">{q.quote_number}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-700">{q.customer_name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{q.city || '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{q.quote_date}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{q.valid_upto || '—'}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{rsFmt(q.total)}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{q.created_by || '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => printExisting(q.id)} title="Print quotation (as saved)" className="px-2 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1 text-xs font-semibold"><FileText size={15} /> Print</button>
                      <button onClick={() => printExisting(q.id, true)} title="Print WITH full proposal content" className="px-2 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center gap-1 text-xs font-semibold"><Briefcase size={15} /> + Proposal</button>
                      {can.delete && <button onClick={() => remove(q.id)} title="Delete" className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ---------- NEW / BUILDER VIEW ----------
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={() => setView('list')} className="flex items-center gap-2 text-slate-600 hover:text-red-600 font-semibold">
          <ArrowRight size={16} className="rotate-180" /> Back to Quotations
        </button>
        <h2 className="text-xl font-bold text-slate-800">New Quotation</h2>
      </div>

      {/* Customer + meta */}
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Customer <span className="text-slate-400 font-normal">(pick existing or type a new/prospect name)</span></label>
            <div className="flex gap-2">
              <input className={inp} placeholder="Customer name" value={form.customerName} onChange={e => set('customerName', e.target.value)} />
              <button type="button" onClick={() => setCustOpen(o => !o)} className="px-3 rounded-xl border-2 border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold whitespace-nowrap flex items-center gap-1">
                <Search size={14} /> Pick
              </button>
            </div>
            {custOpen && (
              <div className="absolute z-20 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg p-2">
                <input autoFocus className={inp + ' mb-2'} placeholder="Search customer…" value={search} onChange={e => setSearch(e.target.value)} />
                <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                  {custMatches.length === 0 ? <p className="px-3 py-3 text-sm text-slate-400">No customers</p> :
                    custMatches.map(c => (
                      <button key={c.id} type="button" onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 hover:bg-red-50 flex items-center justify-between gap-2">
                        <span className="font-medium text-slate-700 truncate">{c.name}</span>
                        <span className="text-xs text-slate-400 shrink-0">{c.id}{c.city ? ' · ' + c.city : ''}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">City</label>
            <input className={inp} placeholder="City" value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Quotation Date</label>
            <input type="date" className={inp} value={form.quoteDate} onChange={e => set('quoteDate', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Valid Upto</label>
            <input type="date" className={inp} value={form.validUpto} onChange={e => set('validUpto', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-bold text-slate-800">Products / Items</h3>
          <div className="flex gap-2 flex-wrap">
            {QUOTE_PRODUCTS.map(p => (
              <button key={p.name} type="button" onClick={() => addItem(p)}
                className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-600 text-xs font-semibold flex items-center gap-1">
                <Plus size={13} /> {p.name}
              </button>
            ))}
            <button type="button" onClick={() => addItem(null)} className="px-3 py-1.5 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-red-400 hover:text-red-500 text-xs font-semibold flex items-center gap-1">
              <Plus size={13} /> Custom
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase">
                <th className="text-left py-2 pr-2">Item</th>
                <th className="text-left py-2 px-2 w-32">Billing</th>
                <th className="text-right py-2 px-2 w-20">Qty</th>
                <th className="text-right py-2 px-2 w-32">Unit Price</th>
                <th className="text-right py-2 px-2 w-32">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400">Add products using the buttons above.</td></tr>
              ) : form.items.map(it => {
                const amt = (parseFloat(it.qty) || 0) * (parseFloat(it.unitPrice) || 0);
                const isPlatform = /platform/i.test(it.name) || it.period;
                return (
                  <tr key={it.id} className="border-t border-slate-100">
                    <td className="py-2 pr-2">
                      <input className="w-full px-2 py-1.5 rounded-lg border border-slate-200 focus:border-red-400 outline-none" value={it.name} placeholder="Item name" onChange={e => updItem(it.id, 'name', e.target.value)} />
                      <input className="w-full mt-1 px-2 py-1 rounded-lg border border-slate-100 focus:border-red-300 outline-none text-xs text-slate-500" value={it.desc} placeholder="Description (optional)" onChange={e => updItem(it.id, 'desc', e.target.value)} />
                    </td>
                    <td className="py-2 px-2">
                      {isPlatform ? (
                        <select className="w-full px-2 py-1.5 rounded-lg border border-slate-200 focus:border-red-400 outline-none" value={it.period || 'Monthly'} onChange={e => updItem(it.id, 'period', e.target.value)}>
                          <option>Monthly</option><option>Quarterly</option><option>Annual</option>
                        </select>
                      ) : <span className="text-xs text-slate-400">One-time</span>}
                    </td>
                    <td className="py-2 px-2"><input type="number" min="0" className="w-full px-2 py-1.5 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-right" value={it.qty} onChange={e => updItem(it.id, 'qty', e.target.value)} /></td>
                    <td className="py-2 px-2"><input type="number" min="0" className="w-full px-2 py-1.5 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-right" value={it.unitPrice} placeholder="0" onChange={e => updItem(it.id, 'unitPrice', e.target.value)} /></td>
                    <td className="py-2 px-2 text-right font-semibold text-slate-700">{rsFmt(amt)}</td>
                    <td className="py-2 text-center"><button type="button" onClick={() => delItem(it.id)} className="text-slate-400 hover:text-red-500"><X size={16} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex flex-col md:flex-row md:justify-end gap-4 mt-5">
          <div className="w-full md:w-80 space-y-2">
            <div className="flex justify-between text-sm text-slate-600"><span>Subtotal</span><span className="font-semibold text-slate-800">{rsFmt(subtotal)}</span></div>
            <div className="flex justify-between items-center text-sm text-slate-600">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.includeGst} onChange={e => set('includeGst', e.target.checked)} className="w-4 h-4 accent-red-500" />
                GST
                {form.includeGst && (<>
                  <input type="number" min="0" step="0.5" value={form.gstPercent} onChange={e => set('gstPercent', e.target.value)} className="w-16 px-2 py-1 rounded-lg border border-slate-200 focus:border-red-400 outline-none text-right" />%
                </>)}
              </label>
              <span className="font-semibold text-slate-800">{form.includeGst ? rsFmt(gstAmount) : '—'}</span>
            </div>
            <div className="flex justify-between bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-3 rounded-xl font-bold"><span>Total</span><span>{rsFmt(total)}</span></div>
          </div>
        </div>
      </div>

      {/* Terms + output option */}
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-slate-50 border border-slate-200">
          <input type="checkbox" checked={form.includeProfile} onChange={e => set('includeProfile', e.target.checked)} className="w-4 h-4 mt-0.5 accent-red-500" />
          <span>
            <span className="font-semibold text-slate-800">Attach company profile &amp; proposal content</span>
            <span className="block text-xs text-slate-500 mt-0.5">Adds the company profile and the product sections you select below (About Secure Wheels → Vehicle Tracking → Fuel Sensor → DashCam → Why Secure Wheels) before the price table — turns the quotation into a full business proposal.</span>
          </span>
        </label>
        {form.includeProfile && (
          <ProposalContentPicker content={form.content} onChange={c => set('content', c)} />
        )}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Terms &amp; Conditions</label>
          <textarea rows={5} className={inp} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button disabled={saving} onClick={() => save(true)} className="flex-1 min-w-[180px] bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 rounded-xl font-semibold hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          <FileText size={18} /> {saving ? 'Saving…' : 'Save & Print'}
        </button>
        <button disabled={saving} onClick={() => save(false)} className="flex-1 min-w-[160px] bg-slate-800 text-white py-3 rounded-xl font-semibold hover:bg-slate-900 transition-all disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Quotation'}
        </button>
        <button onClick={() => setView('list')} className="px-6 bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-300 transition-all">Cancel</button>
      </div>
    </div>
  );
}

function VehicleModelsModule({ vehicleModels, reload, can = { create: true, edit: true, delete: true } }) {
  const [make, setMake] = React.useState('');
  const [model, setModel] = React.useState('');
  const [avg, setAvg] = React.useState('');
  const [type, setType] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [eMake, setEMake] = React.useState('');
  const [eModel, setEModel] = React.useState('');
  const [eAvg, setEAvg] = React.useState('');
  const [eType, setEType] = React.useState('');

  const startEdit = (m) => { setEditingId(m.id); setEMake(m.make); setEModel(m.model); setEAvg(String(m.fuel_average)); setEType(m.type || ''); };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (id) => {
    if (!eMake.trim() || !eModel.trim() || eAvg === '' || isNaN(parseFloat(eAvg))) {
      alert('Make, Model and Fuel Average are all required.');
      return;
    }
    try {
      await api.vehicleModels.update({ id, make: eMake.trim(), model: eModel.trim(), fuel_average: parseFloat(eAvg), type: eType.trim() });
      setEditingId(null);
      await reload();
    } catch (e) {
      alert(e.message || 'Failed to update model');
    }
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = vehicleModels || [];
    if (!q) return list;
    return list.filter(m => `${m.make} ${m.model}`.toLowerCase().includes(q));
  }, [vehicleModels, search]);

  const handleAdd = async () => {
    if (!make.trim() || !model.trim() || avg === '' || isNaN(parseFloat(avg))) {
      alert('Make, Model and Fuel Average are all required.');
      return;
    }
    setSaving(true);
    try {
      await api.vehicleModels.create({ make: make.trim(), model: model.trim(), fuel_average: parseFloat(avg), type: type.trim() });
      setMake(''); setModel(''); setAvg(''); setType('');
      await reload();
    } catch (e) {
      alert(e.message || 'Failed to add model');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this model?')) return;
    try {
      await api.vehicleModels.delete(id);
      await reload();
    } catch (e) {
      alert(e.message || 'Failed to delete');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-slate-800 mb-3">Vehicle Models</h2>
        {/* Add form — all three required */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Make *</label>
            <input
              type="text"
              placeholder="e.g. Toyota"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Model *</label>
            <input
              type="text"
              placeholder="e.g. Corolla"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
          </div>
          <div className="w-32">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Fuel Avg (km/L) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g. 12"
              value={avg}
              onChange={(e) => setAvg(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
          </div>
          <div className="w-36">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Type</label>
            <input
              type="text"
              list="vehicleTypeOptions"
              placeholder="e.g. Car"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
            />
            <datalist id="vehicleTypeOptions">
              <option value="Car" />
              <option value="Bus" />
              <option value="Truck" />
              <option value="Van" />
              <option value="Jeep" />
              <option value="Pickup" />
              <option value="Rickshaw" />
              <option value="Bike" />
              <option value="Loader" />
              <option value="Tractor" />
            </datalist>
          </div>
          {can.create && (
          <button
            onClick={handleAdd}
            disabled={saving}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-md transition-all flex items-center gap-2 disabled:opacity-60"
          >
            <Plus size={16} />
            {saving ? 'Saving...' : 'Add'}
          </button>
          )}
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search make/model..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-white border-2 border-slate-300 focus:border-red-400 outline-none text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Make</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Model</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Fuel Avg (km/L)</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide">Type</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50">
                {editingId === m.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input value={eMake} onChange={(e) => setEMake(e.target.value)} className="w-full px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input value={eModel} onChange={(e) => setEModel(e.target.value)} className="w-full px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input type="number" step="0.01" min="0" value={eAvg} onChange={(e) => setEAvg(e.target.value)} className="w-24 px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input type="text" list="vehicleTypeOptions" value={eType} onChange={(e) => setEType(e.target.value)} className="w-28 px-2 py-1 rounded border-2 border-slate-300 focus:border-red-400 outline-none text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(m.id)} className="text-green-600 hover:text-green-700 transition-all"><Check className="w-4 h-4" /></button>
                        <button onClick={cancelEdit} className="text-slate-500 hover:text-slate-700 transition-all"><X className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-sm font-semibold text-slate-800">{m.make}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{m.model}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{m.fuel_average}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{m.type || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-2">
                        {can.edit && <button onClick={() => startEdit(m)} className="text-slate-600 hover:text-red-600 transition-all"><Edit2 className="w-4 h-4" /></button>}
                        {can.delete && <button onClick={() => handleDelete(m.id)} className="text-slate-600 hover:text-red-600 transition-all"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Car className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No vehicle models yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Nested customer divisions (up to 3 levels) ──────────────────────────────
function buildDivTree(list) {
  const byId = {}; (list || []).forEach(d => { byId[d.id] = { ...d, children: [] }; });
  const roots = [];
  (list || []).forEach(d => {
    const node = byId[d.id];
    const pid = d.parent_id;
    if (pid && byId[pid]) byId[pid].children.push(node); else roots.push(node);
  });
  const sortRec = (arr) => { arr.sort((a, b) => (a.division_name || '').localeCompare(b.division_name || '')); arr.forEach(n => sortRec(n.children)); };
  sortRec(roots);
  return roots;
}

const LVL_PILL = { 1: 'bg-indigo-100 text-indigo-700', 2: 'bg-emerald-100 text-emerald-700', 3: 'bg-orange-100 text-orange-700' };

function DivisionTree({ nodes, level = 1, onAddSub, onRename, onDelete, can = {} }) {
  return (
    <div className={level > 1 ? 'ml-4 pl-2 border-l-2 border-slate-100' : ''}>
      {nodes.map(n => (
        <div key={n.id}>
          <div className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${LVL_PILL[level]}`}>L{level}</span>
            <span className="flex-1 text-sm font-medium text-slate-700">{n.division_name}</span>
            {can.edit && level < 3 && <button type="button" onClick={() => onAddSub(n.id)} className="text-xs text-red-600 font-semibold hover:underline">+ sub-group</button>}
            {can.edit && <button type="button" onClick={() => onRename(n.id, n.division_name)} className="text-xs text-slate-400 hover:text-slate-600">edit</button>}
            {can.delete && <button type="button" onClick={() => onDelete(n.id)} className="text-xs text-slate-400 hover:text-red-600">del</button>}
          </div>
          {n.children && n.children.length > 0 && (
            <DivisionTree nodes={n.children} level={level + 1} onAddSub={onAddSub} onRename={onRename} onDelete={onDelete} can={can} />
          )}
        </div>
      ))}
    </div>
  );
}

// Cascading Level 1 → 2 → 3 picker; stores the chosen node id + its full path.
function DivisionCascade({ list, customerId, valueId, onPick }) {
  const all = (list || []).filter(d => String(d.customer_id) === String(customerId));
  const byId = (id) => all.find(d => String(d.id) === String(id));
  const kids = (pid) => all.filter(d => (pid == null ? !d.parent_id : String(d.parent_id) === String(pid)));
  const pathOf = (id) => { const p = []; let c = byId(id); while (c) { p.unshift(c.division_name); c = c.parent_id ? byId(c.parent_id) : null; } return p.join(' › '); };

  const chain = []; let cur = valueId ? byId(valueId) : null;
  while (cur) { chain.unshift(cur); cur = cur.parent_id ? byId(cur.parent_id) : null; }
  const l1 = chain[0] ? String(chain[0].id) : '';
  const l2 = chain[1] ? String(chain[1].id) : '';
  const l3 = chain[2] ? String(chain[2].id) : '';

  const pick = (id) => onPick(id ? Number(id) : null, id ? pathOf(id) : '');
  const sel = 'w-full px-3 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all text-sm';
  const l2opts = l1 ? kids(l1) : [];
  const l3opts = l2 ? kids(l2) : [];

  return (
    <div className="space-y-2">
      <select className={sel} value={l1} onChange={e => pick(e.target.value)}>
        <option value="">Select division (Level 1)</option>
        {kids(null).map(d => <option key={d.id} value={d.id}>{d.division_name}</option>)}
      </select>
      {l1 && l2opts.length > 0 && (
        <select className={sel} value={l2} onChange={e => pick(e.target.value || l1)}>
          <option value="">— Sub-group (Level 2, optional) —</option>
          {l2opts.map(d => <option key={d.id} value={d.id}>{d.division_name}</option>)}
        </select>
      )}
      {l2 && l3opts.length > 0 && (
        <select className={sel} value={l3} onChange={e => pick(e.target.value || l2)}>
          <option value="">— Sub-group (Level 3, optional) —</option>
          {l3opts.map(d => <option key={d.id} value={d.id}>{d.division_name}</option>)}
        </select>
      )}
      {valueId && <div className="text-xs text-slate-500 mt-1">Assigned to: <b className="text-red-600">{pathOf(valueId)}</b></div>}
    </div>
  );
}

function VehicleFormFields({ vehicle, setVehicle, devices, inventory, inventoryWithStatus, customerDivisions, selectedCustomer, vehicleModels }) {
  // Distinct makes from the master list; include the vehicle's current make even
  // if it's no longer in the master (so old records still display correctly).
  const makeOptions = React.useMemo(() => {
    const set = [...new Set((vehicleModels || []).map(m => m.make).filter(Boolean))].sort();
    if (vehicle.make && !set.includes(vehicle.make)) set.push(vehicle.make);
    return set;
  }, [vehicleModels, vehicle.make]);

  // Models belonging to the selected make (+ current model as a fallback).
  const modelOptions = React.useMemo(() => {
    const set = (vehicleModels || [])
      .filter(m => m.make === vehicle.make)
      .map(m => m.model)
      .filter(Boolean);
    if (vehicle.model && !set.includes(vehicle.model)) set.push(vehicle.model);
    return set;
  }, [vehicleModels, vehicle.make, vehicle.model]);
  const [imeiQuery, setImeiQuery] = React.useState(vehicle.imei || '');
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [dropdownFocused, setDropdownFocused] = React.useState(false);
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // Issued devices: Available but not in Store (handed to technicians)
  const issuedDevices = React.useMemo(() => {
    const src = inventoryWithStatus || inventory || [];
    return src.filter(item =>
      item.status === 'Available' && item.location !== 'Store'
    );
  }, [inventoryWithStatus, inventory]);

  // Filter by typed query
  const filteredSuggestions = React.useMemo(() => {
    if (!imeiQuery) return issuedDevices;
    const q = imeiQuery.toLowerCase();
    return issuedDevices.filter(item =>
      item.imei.toLowerCase().includes(q) ||
      item.deviceType.toLowerCase().includes(q) ||
      item.vendor.toLowerCase().includes(q)
    );
  }, [imeiQuery, issuedDevices]);

  const handleImeiInput = (val) => {
    setImeiQuery(val);
    setShowDropdown(true);
    // If the typed value exactly matches an inventory item, auto-populate device name
    const match = (inventoryWithStatus || inventory || []).find(item => item.imei === val);
    setVehicle({ ...vehicle, imei: val, deviceName: match ? match.deviceType : '' });
  };

  const handleSelect = (item) => {
    setImeiQuery(item.imei);
    setVehicle({ ...vehicle, imei: item.imei, deviceName: item.deviceType });
    setShowDropdown(false);
    inputRef.current?.blur();
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (!inputRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const matchedInventoryItem = (inventoryWithStatus || inventory || []).find(item => item.imei === vehicle.imei);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicle No *</label>
        <input 
          type="text" 
          value={vehicle.vehicleNo} 
          onChange={(e) => setVehicle({ ...vehicle, vehicleNo: e.target.value.toUpperCase() })} 
          className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all uppercase" 
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Make</label>
        <SearchableSelect
          value={vehicle.make || ''}
          options={makeOptions}
          onChange={(val) => setVehicle({ ...vehicle, make: val, model: '' })}
          placeholder="Search make..."
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Model</label>
        <SearchableSelect
          value={vehicle.model || ''}
          options={modelOptions}
          onChange={(val) => setVehicle({ ...vehicle, model: val })}
          placeholder={vehicle.make ? 'Search model...' : 'Select make first'}
          disabled={!vehicle.make}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Engine No</label>
        <input type="text" value={vehicle.engineNo} onChange={(e) => setVehicle({ ...vehicle, engineNo: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Chassis No</label>
        <input type="text" value={vehicle.chassisNo} onChange={(e) => setVehicle({ ...vehicle, chassisNo: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>

      {/* IMEI with live autocomplete — shows only issued (not-in-store) devices */}
      <div className="col-span-2">
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          IMEI *
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({issuedDevices.length} issued device{issuedDevices.length !== 1 ? 's' : ''} available)
          </span>
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={imeiQuery}
            onChange={(e) => handleImeiInput(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder="Type partial IMEI to search issued devices..."
            className={`w-full px-4 py-2.5 rounded-xl border-2 outline-none transition-all font-mono text-sm ${
              matchedInventoryItem
                ? 'border-green-500 focus:border-green-500 focus:ring-2 focus:ring-green-200 bg-green-50'
                : 'border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
            }`}
          />
          {matchedInventoryItem && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Check size={18} className="text-green-600" />
            </div>
          )}

          {/* Dropdown */}
          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-xl overflow-hidden"
            >
              {filteredSuggestions.length === 0 ? (
                <div className="px-4 py-6 text-center text-slate-400">
                  <Package size={28} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {imeiQuery ? `No issued devices match "${imeiQuery}"` : 'No issued devices available'}
                  </p>
                  <p className="text-xs mt-1 text-slate-400">Only devices issued to technicians appear here</p>
                </div>
              ) : (
                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                  {filteredSuggestions.map(item => {
                    const isSelected = item.imei === vehicle.imei;
                    return (
                      <div
                        key={item.id}
                        onMouseDown={() => handleSelect(item)}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all ${isSelected ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm font-bold text-slate-800">
                            {imeiQuery
                              ? item.imei.split(new RegExp(`(${imeiQuery})`, 'i')).map((part, i) =>
                                  part.toLowerCase() === imeiQuery.toLowerCase()
                                    ? <mark key={i} className="bg-yellow-200 text-slate-900 rounded px-0.5">{part}</mark>
                                    : part
                                )
                              : item.imei
                            }
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {item.deviceType}
                            {item.vendor ? ` • ${item.vendor}` : ''}
                            {item.simNumber ? ` • SIM: ${item.simNumber}` : ''}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                            With: {item.location}
                          </span>
                        </div>
                        {isSelected && <Check size={16} className="text-green-600 flex-shrink-0" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected device info */}
        {matchedInventoryItem && (
          <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
            <Check size={14} />
            <span>
              <strong>{matchedInventoryItem.deviceType}</strong>
              {matchedInventoryItem.vendor ? ` • ${matchedInventoryItem.vendor}` : ''}
              {matchedInventoryItem.simNumber ? ` • SIM: ${matchedInventoryItem.simNumber}` : ''}
              {matchedInventoryItem.location && matchedInventoryItem.location !== 'Store'
                ? ` • Currently with: ${matchedInventoryItem.location}` : ''}
            </span>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Device Type</label>
        <input 
          type="text" 
          value={vehicle.deviceName} 
          disabled 
          className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 bg-slate-100 text-slate-600 outline-none cursor-not-allowed" 
          placeholder="Auto-populated from inventory"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Package Price</label>
        <input type="text" value={vehicle.packagePrice ?? ''} placeholder="Blank = not set, 0 = free" onChange={(e) => setVehicle({ ...vehicle, packagePrice: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">AMC</label>
        <input type="text" value={vehicle.amc ?? ''} placeholder="Blank = not set, 0 = free" onChange={(e) => setVehicle({ ...vehicle, amc: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Commission</label>
        <input type="text" value={vehicle.commission ?? ''} placeholder="Rs (optional)" onChange={(e) => setVehicle({ ...vehicle, commission: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
        <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={!!vehicle.invoiceFullSetup} onChange={(e) => setVehicle({ ...vehicle, invoiceFullSetup: e.target.checked })} className="w-4 h-4 rounded" />
          Invoice full setup charges (ignore commission)
        </label>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Active Date</label>
        <input type="date" value={vehicle.activeDate} onChange={(e) => setVehicle({ ...vehicle, activeDate: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Invoicing Period</label>
        <select value={vehicle.invoicingPeriod} onChange={(e) => setVehicle({ ...vehicle, invoicingPeriod: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option>Yearly</option>
          <option>Quarterly</option>
          <option>Monthly</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Division</label>
        <DivisionCascade
          list={customerDivisions}
          customerId={selectedCustomer?.id}
          valueId={vehicle.division_id}
          onPick={(id, path) => setVehicle({ ...vehicle, division_id: id, division: path })}
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
        <select value={vehicle.status} onChange={(e) => setVehicle({ ...vehicle, status: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
    </div>
  );
}

// Inventory Form Fields Component
function InventoryFormFields({ item, setItem, devices, vendors }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">IMEI *</label>
        <input type="text" value={item.imei} onChange={(e) => setItem({ ...item, imei: e.target.value })} placeholder="15-digit IMEI number" className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all font-mono" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Device Type *</label>
        <select value={item.deviceType} onChange={(e) => setItem({ ...item, deviceType: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="">Select device type</option>
          {devices.map(device => (
            <option key={device.id} value={device.name}>{device.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">ICCID *</label>
        <input type="text" value={item.iccid} onChange={(e) => setItem({ ...item, iccid: e.target.value })} placeholder="19-20 digit ICCID" className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all font-mono" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">SIM Number *</label>
        <input type="text" value={item.simNumber} onChange={(e) => setItem({ ...item, simNumber: e.target.value })} placeholder="03001234567" className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Vendor *</label>
        <select value={item.vendor} onChange={(e) => setItem({ ...item, vendor: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="">Select vendor</option>
          {vendors.map(vendor => (
            <option key={vendor.id} value={vendor.name}>{vendor.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Amount (Rs.) *</label>
        <input type="number" value={item.amount} onChange={(e) => setItem({ ...item, amount: e.target.value })} placeholder="5000" className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Health Status *</label>
        <select value={item.healthStatus || 'active'} onChange={(e) => setItem({ ...item, healthStatus: e.target.value })}
          className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="active">✅ Active</option>
          <option value="damaged">⚠️ Damaged</option>
          <option value="under_repair">🔧 Under Repair</option>
          <option value="repaired">✔️ Repaired</option>
          <option value="lost">❌ Lost</option>
        </select>
      </div>
    </div>
  );
}

// Vendor Form Fields Component
function VendorFormFields({ vendor, setVendor }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Name *</label>
        <input type="text" value={vendor.name} onChange={(e) => setVendor({ ...vendor, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Contact *</label>
        <input type="text" value={vendor.contact} onChange={(e) => setVendor({ ...vendor, contact: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
        <input type="email" value={vendor.email} onChange={(e) => setVendor({ ...vendor, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">City</label>
        <input type="text" value={vendor.city} onChange={(e) => setVendor({ ...vendor, city: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-semibold text-slate-700 mb-2">Address</label>
        <input type="text" value={vendor.address} onChange={(e) => setVendor({ ...vendor, address: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
    </div>
  );
}

// Device Form Fields Component
function DeviceFormFields({ device, setDevice }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Device Name *</label>
        <input type="text" value={device.name} onChange={(e) => setDevice({ ...device, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Category *</label>
        <select value={device.category} onChange={(e) => setDevice({ ...device, category: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="">Select category</option>
          <option value="GPS Tracker">GPS Tracker</option>
          <option value="Sensor">Sensor</option>
          <option value="Camera">Camera</option>
          <option value="Other">Other</option>
        </select>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
        <textarea value={device.description} onChange={(e) => setDevice({ ...device, description: e.target.value })} rows="3" className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
    </div>
  );
}

// Employee Form Fields Component
function EmployeeFormFields({ employee, setEmployee, cities }) {
  const cityOptions = [...new Set((cities || []).map(c => c.city).filter(Boolean))].sort();
  const formatCNIC = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format as xxxxx-xxxxxxx-x (5-7-1)
    if (digits.length <= 5) {
      return digits;
    } else if (digits.length <= 12) {
      return digits.slice(0, 5) + '-' + digits.slice(5);
    } else {
      return digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12, 13);
    }
  };

  const validateCNIC = (cnic) => {
    // Remove dashes for validation
    const digits = cnic.replace(/\D/g, '');
    return digits.length === 13;
  };

  const handleCNICChange = (e) => {
    const formatted = formatCNIC(e.target.value);
    setEmployee({ ...employee, cnic: formatted });
  };

  const cnicValid = validateCNIC(employee.cnic);
  const cnicError = employee.cnic && !cnicValid ? 'CNIC must be 13 digits (xxxxx-xxxxxxx-x)' : '';

  // Pick a photo → downscale to 256px JPEG (data URI) on the client → store in the form.
  const onPhotoPick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 256;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        cv.getContext('2d').drawImage(img, 0, 0, w, h);
        setEmployee({ ...employee, photo: cv.toDataURL('image/jpeg', 0.8) });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };
  const currentPhoto = employee.photo || employee.dp || '';

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Profile photo — resized in the browser, stored in the DB */}
      <div className="col-span-2 flex items-center gap-4">
        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-slate-200 bg-slate-100 flex items-center justify-center shrink-0">
          {currentPhoto
            ? <img src={currentPhoto} alt="photo" className="w-full h-full object-cover" />
            : <User size={30} className="text-slate-400" />}
        </div>
        <div className="flex flex-col gap-2">
          <label className="px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold border border-blue-200 cursor-pointer inline-flex items-center gap-1.5 w-fit">
            <Camera size={14} /> {currentPhoto ? 'Change photo' : 'Add photo'}
            <input type="file" accept="image/*" onChange={onPhotoPick} className="hidden" />
          </label>
          {currentPhoto && (
            <button type="button" onClick={() => setEmployee({ ...employee, photo: '' })}
              className="text-xs text-red-600 hover:underline font-semibold w-fit">Remove photo</button>
          )}
        </div>
      </div>
      {employee.employee_code && (
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Employee Code</label>
          <div className="w-full px-4 py-2.5 rounded-xl border-2 border-blue-300 bg-blue-50 flex items-center">
            <span className="inline-block px-3 py-1 rounded-lg bg-blue-100 text-blue-700 font-mono text-sm font-bold">
              {employee.employee_code}
            </span>
            <span className="text-xs text-blue-600 ml-3">(Auto-generated, cannot be edited)</span>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Type *</label>
        <select value={employee.emp_type || 'permanent'} onChange={(e) => setEmployee({ ...employee, emp_type: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="permanent">Permanent (code S###)</option>
          <option value="freelancer">Freelancer (code F###)</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Name *</label>
        <input type="text" value={employee.name} onChange={(e) => setEmployee({ ...employee, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Designation *</label>
        <input type="text" value={employee.designation} onChange={(e) => setEmployee({ ...employee, designation: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Department</label>
        <input type="text" value={employee.department} onChange={(e) => setEmployee({ ...employee, department: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Phone</label>
        <input type="text" value={employee.phone} onChange={(e) => setEmployee({ ...employee, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
        <input type="email" value={employee.email} onChange={(e) => setEmployee({ ...employee, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Role *</label>
        <select value={employee.role} onChange={(e) => setEmployee({ ...employee, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all">
          <option value="Sales">Sales</option>
          <option value="Support">Support</option>
          <option value="Technical">Technical</option>
          <option value="Management">Management</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">City <span className="text-xs font-normal text-slate-400">(for job broadcast)</span></label>
        <SearchableSelect
          value={employee.city || ''}
          options={cityOptions}
          onChange={(val) => setEmployee({ ...employee, city: val })}
          placeholder="Search city…"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Joining Date *</label>
        <input type="date" value={employee.joiningDate} onChange={(e) => setEmployee({ ...employee, joiningDate: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          CNIC * 
          {cnicError && <span className="text-red-500 text-xs ml-2">({cnicError})</span>}
          {cnicValid && employee.cnic && <span className="text-green-500 text-xs ml-2">✓ Valid</span>}
        </label>
        <input 
          type="text" 
          value={employee.cnic} 
          onChange={handleCNICChange}
          placeholder="35202-1234567-1" 
          maxLength="15"
          className={`w-full px-4 py-2.5 rounded-xl border-2 outline-none transition-all font-mono ${
            cnicError ? 'border-red-400 bg-red-50' : 'border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
          }`} 
        />
        <p className="text-xs text-slate-500 mt-1">Format: xxxxx-xxxxxxx-x (13 digits with automatic dashes)</p>
      </div>
    </div>
  );
}

// ── Reschedule Follow-Up Modal ──────────────────────────────────────────────
function RescheduleFollowupModal({ invoice, defaultDate, todayStr, onSave, onClose }) {
  const fmtAmt = (n) => `Rs. ${parseFloat(n || 0).toLocaleString()}`;
  const [newDate, setNewDate] = React.useState(defaultDate);
  // Vehicle-wise follow-ups: which of the customer's vehicles this call was about.
  const choices = invoice.choices || null;
  const [picked, setPicked] = React.useState(() => new Set((invoice.preselected || []).map(String)));
  const togglePick = (id) => setPicked(prev => { const n = new Set(prev); n.has(String(id)) ? n.delete(String(id)) : n.add(String(id)); return n; });
  const [note, setNote] = React.useState('');
  const [outcome, setOutcome] = React.useState('no_answer');
  const [dateError, setDateError] = React.useState('');

  const outcomes = [
    { val: 'no_answer', label: 'No Answer', icon: PhoneOff },
    { val: 'promised', label: 'Promised', icon: Check },
    { val: 'dispute', label: 'Dispute', icon: AlertCircle },
    { val: 'paid', label: 'Paid', icon: DollarSign },
  ];

  const handleDateChange = (e) => {
    const val = e.target.value;
    if (val < todayStr) {
      setDateError('Cannot select a past date.');
    } else {
      setDateError('');
      setNewDate(val);
    }
  };

  const handleSave = () => {
    if (!newDate || newDate < todayStr) {
      setDateError('Please select today or a future date.');
      return;
    }
    if (choices && picked.size === 0) { setDateError('Tick at least one vehicle.'); return; }
    onSave({ followupDate: newDate, outcome, note, vehicleIds: choices ? [...picked].map(Number) : undefined });
  };

  // Outcome-based date suggestions
  const quickDates = [
    { label: 'Tomorrow', days: 1 },
    { label: '3 Days', days: 3 },
    { label: '1 Week', days: 7 },
    { label: '2 Weeks', days: 14 },
  ];
  const addDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const s = d.toISOString().split('T')[0];
    setNewDate(s);
    setDateError('');
  };

  return (
    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
      <div className="flex items-center justify-between p-5 border-b border-slate-100">
        <div>
          <h3 className="font-bold text-slate-800 text-lg">Log Call & Reschedule</h3>
          <p className="text-sm text-slate-500">{invoice.customerName} · {choices ? `${invoice.vehicleCount} vehicle${invoice.vehicleCount !== 1 ? 's' : ''}` : `${invoice.invoiceCount} unpaid invoice${invoice.invoiceCount !== 1 ? 's' : ''}`} · {fmtAmt(invoice.totalOutstanding)} outstanding</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
          <X size={18} className="text-slate-500" />
        </button>
      </div>

      {/* Past call history inside modal */}
      {invoice.logs && invoice.logs.length > 0 && (
        <div className="mx-5 mt-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Previous Calls ({invoice.logs.length})</p>
          <div className="space-y-1.5 max-h-28 overflow-y-auto">
            {[...invoice.logs].reverse().map((log, i) => {
              const colors = { no_answer: 'bg-slate-100 text-slate-600', promised: 'bg-green-100 text-green-700', dispute: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', note: 'bg-blue-50 text-blue-700' };
              const labels = { no_answer: 'No Answer', promised: 'Promised', dispute: 'Dispute', paid: 'Paid', note: 'Note' };
              return (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-400 w-20 shrink-0">{log.date}</span>
                  <span className={`px-2 py-0.5 rounded-full font-semibold shrink-0 ${colors[log.outcome] || 'bg-slate-100 text-slate-600'}`}>{labels[log.outcome] || log.outcome}</span>
                  {log.note && <span className="text-slate-600 truncate">{log.note}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="p-5 space-y-4">
        {/* Vehicles discussed (vehicle-wise follow-ups) */}
        {choices && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicles discussed <span className="text-red-500">*</span></label>
            <div className="rounded-xl border-2 border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto">
              {choices.map(c => (
                <label key={c.vehicleId} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={picked.has(String(c.vehicleId))} onChange={() => togglePick(c.vehicleId)} className="w-4 h-4 rounded" />
                  <span className="font-mono font-semibold text-sm text-slate-800">{c.vehicleNo}</span>
                  <span className="text-xs text-slate-400">{c.label}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">The outcome, note and next date are saved for every ticked vehicle.</p>
          </div>
        )}
        {/* Outcome */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Today's Call Outcome <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-4 gap-2">
            {outcomes.map(opt => (
              <button key={opt.val} onClick={() => setOutcome(opt.val)}
                className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl border-2 text-xs font-semibold transition-all ${
                  outcome === opt.val ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}>
                <opt.icon size={15} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Note */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Call Note</label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="What was discussed, any promise made, etc..." rows={2}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all resize-none text-slate-800 placeholder-slate-400 text-sm" />
        </div>

        {/* Next Follow-Up Date */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Next Follow-Up Date <span className="text-red-500">*</span></label>
          <div className="flex gap-2 mb-2">
            {quickDates.map(qd => (
              <button key={qd.days} onClick={() => addDays(qd.days)}
                className="flex-1 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-600 border border-slate-200 hover:border-red-300 transition-all">
                {qd.label}
              </button>
            ))}
          </div>
          <input type="date" value={newDate} min={todayStr}
            onChange={handleDateChange}
            className={`w-full px-4 py-2.5 rounded-xl border-2 outline-none transition-all text-slate-800 ${dateError ? 'border-red-500 bg-red-50' : 'border-slate-200 focus:border-red-500'}`} />
          {dateError && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle size={11} />{dateError}</p>}
        </div>
      </div>

      <div className="flex gap-3 px-5 pb-5">
        <button onClick={onClose}
          className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm">
          Cancel
        </button>
        <button onClick={handleSave}
          className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors flex items-center justify-center gap-2 text-sm">
          <PhoneCall size={15} /> Save Call Log
        </button>
      </div>
    </div>
  );
}

// ============================================================
// COMPLAINTS MODULE — full inline implementation
// ============================================================

const COMPLAINT_CATEGORIES = [
  { value: 'new_installation', label: 'New Installation',       color: 'bg-violet-100 text-violet-700' },
  { value: 'removal',          label: 'Removal',                color: 'bg-red-100 text-red-700'       },
  { value: 'redo',             label: 'Redo',                   color: 'bg-cyan-100 text-cyan-700'     },
  { value: 'transfer',         label: 'Transfer',               color: 'bg-indigo-100 text-indigo-700' },
  { value: 'gps_tracking',     label: 'GPS / Tracking Issue',   color: 'bg-blue-100 text-blue-700'    },
  { value: 'app_bug',          label: 'App / Software Bug',     color: 'bg-purple-100 text-purple-700' },
  { value: 'billing',          label: 'Billing / Subscription', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'hardware',         label: 'Hardware / Device',      color: 'bg-orange-100 text-orange-700' },
  { value: 'support',          label: 'Support Response',       color: 'bg-pink-100 text-pink-700'     },
  { value: 'other',            label: 'Other',                  color: 'bg-slate-100 text-slate-600'   },
];

// Categories shown in the general complaint form (excludes activity types with dedicated forms)
const COMPLAINT_FORM_CATEGORIES = COMPLAINT_CATEGORIES.filter(c =>
  !['new_installation','removal','redo','transfer'].includes(c.value)
);

const COMPLAINT_STATUSES = [
  { value: 'open',        label: 'Open',        color: 'bg-red-100 text-red-700',       dot: 'bg-red-500'    },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  { value: 'resolved',    label: 'Resolved',    color: 'bg-green-100 text-green-700',   dot: 'bg-green-500'  },
  { value: 'closed',      label: 'Closed',      color: 'bg-slate-100 text-slate-500',   dot: 'bg-slate-400'  },
];

// Unified task/complaint lifecycle — single source of truth for badge + filters + counts.
const UNIFIED_STATUSES = [
  { value: 'open',        label: 'Open',        color: 'bg-red-100 text-red-700',       dot: 'bg-red-500',    c: '#d23b4e' },
  { value: 'assigned',    label: 'Assigned',    color: 'bg-slate-100 text-slate-600',   dot: 'bg-slate-400',  c: '#64748b' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500', c: '#c9820f' },
  { value: 'submitted',   label: 'Submitted',   color: 'bg-cyan-100 text-cyan-700',     dot: 'bg-cyan-500',   c: '#0891b2' },
  { value: 'completed',   label: 'Completed',   color: 'bg-green-100 text-green-700',    dot: 'bg-green-500',  c: '#1f9d63' },
  { value: 'rejected',    label: 'Rejected',    color: 'bg-orange-100 text-orange-700',  dot: 'bg-orange-500', c: '#f5732b' },
];
// Derive the one unified status from the technician task_status (if any) + CRM status.
const deriveUStatus = (c) => {
  const ts = c && c.task_status;
  if (ts) {
    if (ts === 'accepted') return 'in_progress';
    if (['assigned', 'in_progress', 'submitted', 'completed', 'rejected'].includes(ts)) return ts;
  }
  switch (c && c.status) {
    case 'open':        return 'open';
    case 'in_progress': return 'in_progress';
    case 'resolved':
    case 'closed':      return 'completed';
    default:            return 'open';
  }
};
const uStatInfo = (v) => UNIFIED_STATUSES.find(s => s.value === v) || UNIFIED_STATUSES[0];

const COMPLAINT_PRIORITIES = [
  { value: 'high',   label: 'High',   color: 'bg-red-100 text-red-700',       icon: '🔴' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700', icon: '🟡' },
  { value: 'low',    label: 'Low',    color: 'bg-green-100 text-green-700',   icon: '🟢' },
];

const TEST_PARAMS = [
  { key: 'test_ign_on',      label: 'Ignition On'     },
  { key: 'test_ign_off',     label: 'Ignition Off'    },
  { key: 'test_batt_tamper', label: 'Battery Tamper'  },
  { key: 'test_batt_resume', label: 'Battery Resume'  },
  { key: 'test_eng_kill',    label: 'Engine Kill'     },
  { key: 'test_eng_release', label: 'Engine Release'  },
];

const cCatInfo  = (v) => COMPLAINT_CATEGORIES.find(c => c.value === v)  || COMPLAINT_CATEGORIES[COMPLAINT_CATEGORIES.length - 1];
const cStatInfo = (v) => COMPLAINT_STATUSES.find(s => s.value === v)    || COMPLAINT_STATUSES[0];
const cPriInfo  = (v) => COMPLAINT_PRIORITIES.find(p => p.value === v)  || COMPLAINT_PRIORITIES[1];

const fmtCDate = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
};
const fmtCDateTime = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt);
  return fmtCDate(dt) + ' ' + d.toTimeString().slice(0,5);
};

function CStatusBadge({ status, taskStatus }) {
  const s = uStatInfo(deriveUStatus({ status, task_status: taskStatus }));
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function CBadge({ color, children }) {
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>{children}</span>;
}

function CStatCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500 font-medium mb-0.5">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value ?? '—'}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-50">
            <Icon size={16} className={color} />
          </div>
        )}
      </div>
    </div>
  );
}

function ComplaintsModule({
  complaints, complaintStats: stats, complaintView: view, setComplaintView: setView,
  selectedComplaint: selected, loading,
  search, setSearch, filterStatus, setFilterStatus, filterPriority, setFilterPriority,
  filterAssigned, setFilterAssigned, dateFrom, setDateFrom, dateTo, setDateTo,
  replyText, setReplyText, sendingReply,
  customers, employees,
  onOpenDetail, onDelete, onBulkDelete, onStatusChange, onTaskStage, onConvert, onAssign, onSendReply, onSubmitNew, onRefresh,
  complaintPrefill,
  can = { create: true, edit: true, delete: true },
}) {
  const [newMenu, setNewMenu] = React.useState(false);
  const NEW_TYPES = [
    { v: 'new',              label: 'Complaint',    icon: '🎫', cls: 'bg-red-50 text-red-600' },
    { v: 'new_installation', label: 'Installation', icon: '🔧', cls: 'bg-violet-50 text-violet-600' },
    { v: 'new_removal',      label: 'Removal',      icon: '✂️', cls: 'bg-rose-50 text-rose-600' },
    { v: 'new_redo',         label: 'Redo',         icon: '↺',  cls: 'bg-blue-50 text-blue-600' },
    { v: 'new_transfer',     label: 'Transfer',     icon: '⇄',  cls: 'bg-teal-50 text-teal-600' },
  ];
  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare size={24} className="text-red-500" />
            Complaints
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Track, assign and resolve customer issues</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end items-center">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-[11px] text-slate-400">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            Auto-refresh 30s
          </div>
          <button onClick={onRefresh}
            className="p-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          {can.create && (
            <div className="relative">
              <button onClick={() => setNewMenu(o => !o)}
                className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2.5 rounded-lg font-semibold hover:shadow-md hover:shadow-red-200 transition-all flex items-center gap-2 text-sm">
                <Plus size={16} /> New Ticket <ChevronDown size={15} className={`transition-transform ${newMenu ? 'rotate-180' : ''}`} />
              </button>
              {newMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setNewMenu(false)} />
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 p-1.5 z-40">
                    {NEW_TYPES.map(t => (
                      <button key={t.v} onClick={() => { setNewMenu(false); setView(t.v); }}
                        className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-all flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                        <span className={`w-7 h-7 rounded-lg grid place-items-center text-sm ${t.cls}`}>{t.icon}</span>{t.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stat tiles (unified counts, client-side, clickable filters) */}
      {(() => {
        const uc = { total: complaints.length, open: 0, in_progress: 0, submitted: 0, completed: 0, high: 0 };
        complaints.forEach(c => { const u = deriveUStatus(c); if (uc[u] !== undefined) uc[u]++; if (c.priority === 'high') uc.high++; });
        const tiles = [
          { l: 'Total',         v: uc.total,       c: '#64748b',              active: !filterStatus && filterPriority !== 'high', on: () => { setFilterStatus(''); setFilterPriority(''); } },
          { l: 'Open',          v: uc.open,        c: '#d23b4e', dot: true,   active: filterStatus === 'open',        on: () => setFilterStatus(filterStatus === 'open' ? '' : 'open') },
          { l: 'In Progress',   v: uc.in_progress, c: '#c9820f', dot: true,   active: filterStatus === 'in_progress', on: () => setFilterStatus(filterStatus === 'in_progress' ? '' : 'in_progress') },
          { l: 'Submitted',     v: uc.submitted,   c: '#0891b2', dot: true,   active: filterStatus === 'submitted',   on: () => setFilterStatus(filterStatus === 'submitted' ? '' : 'submitted') },
          { l: 'Completed',     v: uc.completed,   c: '#1f9d63', dot: true,   active: filterStatus === 'completed',   on: () => setFilterStatus(filterStatus === 'completed' ? '' : 'completed') },
          { l: 'High priority', v: uc.high,        c: '#d23b4e', flag: true,  active: filterPriority === 'high',      on: () => setFilterPriority(filterPriority === 'high' ? '' : 'high') },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {tiles.map(t => (
              <div key={t.l} onClick={t.on} title={`Filter: ${t.l}`}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 cursor-pointer transition-all hover:shadow-md"
                style={{ borderLeft: `3px solid ${t.c}`, boxShadow: t.active ? `0 0 0 2px ${t.c}` : undefined }}>
                <div className="text-xl font-extrabold tabular-nums leading-none" style={{ color: t.l === 'Total' ? '#1e293b' : t.c }}>{t.v ?? 0}</div>
                <div className="text-[11px] text-slate-500 font-semibold mt-1">{t.dot && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: t.c }} />}{t.flag && '⚑ '}{t.l}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {view === 'list'             && <CListView complaints={complaints} loading={loading} search={search} setSearch={setSearch} filterStatus={filterStatus} setFilterStatus={setFilterStatus} filterPriority={filterPriority} setFilterPriority={setFilterPriority} filterAssigned={filterAssigned} setFilterAssigned={setFilterAssigned} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} employees={employees} onOpen={onOpenDetail} onDelete={onDelete} onBulkDelete={onBulkDelete} can={can} />}
      {view === 'detail' && selected && <CDetailView complaint={selected} customers={customers} employees={employees} replyText={replyText} setReplyText={setReplyText} sendingReply={sendingReply} onSendReply={onSendReply} onStatusChange={onStatusChange} onTaskStage={onTaskStage} onConvert={onConvert} onAssign={onAssign} onDelete={onDelete} onBack={() => setView('list')} onRefresh={() => onOpenDetail(selected.id)} can={can} />}
      {view === 'new'              && <CNewForm customers={customers} employees={employees} onSaved={onSubmitNew} onCancel={() => setView('list')} />}
      {view === 'new_installation' && <CNewInstallationForm customers={customers} employees={employees} onSaved={onSubmitNew} onCancel={() => setView('list')} />}
      {view === 'new_removal'      && <CRemovalForm      customers={customers} employees={employees} onSaved={onSubmitNew} onCancel={() => setView('list')} />}
      {view === 'new_redo'         && <CRedoForm         customers={customers} employees={employees} onSaved={onSubmitNew} onCancel={() => setView('list')} prefillVehicle={complaintPrefill} />}
      {view === 'new_transfer'     && <CTransferForm     customers={customers} employees={employees} onSaved={onSubmitNew} onCancel={() => setView('list')} />}
    </div>
  );
}

function CListView({ complaints, loading, search, setSearch, filterStatus, setFilterStatus, filterPriority, setFilterPriority, filterAssigned, setFilterAssigned, dateFrom, setDateFrom, dateTo, setDateTo, employees, onOpen, onDelete, onBulkDelete, can = { create: true, edit: true, delete: true } }) {
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [bulkSales, setBulkSales] = React.useState(false);
  const [bulkConfirm, setBulkConfirm] = React.useState('');
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const q = (search || '').toLowerCase();
  const shown = (complaints || []).filter(c => {
    if (filterStatus && deriveUStatus(c) !== filterStatus) return false;
    if (filterPriority && c.priority !== filterPriority) return false;
    if (filterAssigned && String(c.assigned_to || '') !== String(filterAssigned)) return false;
    if (q) {
      const hay = `${c.ticket_no || ''} ${c.customer_name || ''} ${c.subject || ''} ${c.vehicle_name || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    const day = (c.created_at || '').slice(0, 10);
    if (dateFrom && day && day < dateFrom) return false;
    if (dateTo && day && day > dateTo) return false;
    return true;
  });
  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">
      <div className="p-4 border-b border-slate-100 space-y-2.5">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ticket, customer, subject…"
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:border-red-400 outline-none transition-all text-sm bg-slate-50" />
          </div>
          {[['', 'All'], ...UNIFIED_STATUSES.map(s => [s.value, s.label])].map(([val, label]) => (
            <button key={val || 'all'} onClick={() => setFilterStatus(val)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filterStatus === val ? 'bg-red-50 text-red-600 border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{label}</button>
          ))}
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 outline-none text-sm text-slate-600 font-medium">
            <option value="">All priorities</option>
            {COMPLAINT_PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <div className="min-w-[200px]">
            <EmployeeSearchSelect employees={employees} value={filterAssigned} onChange={setFilterAssigned}
              activeOnly placeholder="All assignees — search…" />
          </div>
        </div>
        <div className="flex gap-2 items-center text-xs">
          <span className="text-slate-400 font-medium">Date</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 outline-none text-xs" />
          <span className="text-slate-300">→</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 outline-none text-xs" />
          {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-red-500 hover:underline font-semibold">Clear</button>}
          <span className="ml-auto text-slate-500 font-medium">{shown.length} result{shown.length !== 1 ? 's' : ''}</span>
          {can.delete && shown.length > 0 && (
            <button onClick={() => { setBulkConfirm(''); setBulkSales(false); setBulkOpen(true); }}
              className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 font-semibold inline-flex items-center gap-1.5">
              <Trash2 size={13} /> Delete {shown.length}
            </button>
          )}
        </div>
      </div>

      {bulkOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0"><Trash2 size={20} /></div>
              <div>
                <h3 className="font-bold text-slate-800 text-lg">Delete {shown.length} complaint{shown.length !== 1 ? 's' : ''}?</h3>
                <p className="text-xs text-slate-500">The complaints currently shown (with this filter) and their replies, status logs &amp; install rows are permanently removed.</p>
              </div>
            </div>
            <label className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer mb-3">
              <input type="checkbox" checked={bulkSales} onChange={e => setBulkSales(e.target.checked)} className="w-4 h-4 mt-0.5 accent-red-500" />
              <span className="text-sm text-slate-700"><b>Also delete the linked sale records</b>
                <span className="block text-xs text-slate-500 mt-0.5">Your sales are genuine — keep this <b>OFF</b>. Off = the complaint is removed and its sale is kept (only the stale link is cleared). Turn on only if a sale itself is also junk.</span>
              </span>
            </label>
            <p className="text-xs text-slate-500 mb-1.5">Type <b className="text-red-600">DELETE</b> to confirm:</p>
            <input value={bulkConfirm} onChange={e => setBulkConfirm(e.target.value)} placeholder="DELETE"
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-300 focus:border-red-500 outline-none text-sm mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setBulkOpen(false)} disabled={bulkBusy} className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold">Cancel</button>
              <button disabled={bulkConfirm !== 'DELETE' || bulkBusy}
                onClick={async () => { setBulkBusy(true); await onBulkDelete(shown.map(c => c.id), bulkSales); setBulkBusy(false); setBulkOpen(false); }}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-semibold disabled:opacity-50">
                {bulkBusy ? 'Deleting…' : `Delete ${shown.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-slate-400">
          <RefreshCw size={32} className="animate-spin mx-auto mb-3 text-red-400" />
          <p>Loading complaints...</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="p-12 text-center">
          <MessageSquare size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">No complaints found</p>
          <p className="text-slate-400 text-sm mt-1">Try adjusting your filters or create a new complaint</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {['Ticket', 'Category', 'Customer / Subject', 'Priority', 'Assigned', 'Age', 'Status', ''].map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map(c => (
                <tr key={c.id} onClick={() => onOpen(c.id)} className="hover:bg-slate-50 cursor-pointer transition-colors group">
                  <td className="px-4 py-3 font-mono font-bold text-red-600 text-xs whitespace-nowrap">{c.ticket_no}</td>
                  <td className="px-4 py-3"><CBadge color={cCatInfo(c.category).color}>{cCatInfo(c.category).label}</CBadge></td>
                  <td className="px-4 py-3 max-w-[300px]">
                    <div className="text-sm font-semibold text-slate-800 truncate">{c.subject}</div>
                    <div className="text-[11.5px] text-slate-400 truncate">{c.customer_name || '—'}{c.vehicle_name ? ' · ' + c.vehicle_name : ''}</div>
                  </td>
                  <td className="px-4 py-3"><CBadge color={cPriInfo(c.priority).color}>{cPriInfo(c.priority).icon} {cPriInfo(c.priority).label}</CBadge></td>
                  <td className="px-4 py-3 text-sm">
                    {c.assigned_name
                      ? <span className="inline-flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 grid place-items-center text-[10px] font-bold shrink-0">{(c.assigned_name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}</span><span className="text-slate-600">{c.assigned_name}</span></span>
                      : <span className="text-slate-300 italic text-xs">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtCDate(c.created_at)}</td>
                  <td className="px-4 py-3"><CStatusBadge status={c.status} taskStatus={c.task_status} /></td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end items-center">
                      {can.delete && <button onClick={() => onDelete(c.id)} className="p-1.5 rounded-lg text-slate-300 hover:bg-red-50 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={14} /></button>}
                      <ArrowRight size={15} className="text-slate-300 group-hover:text-red-500 transition-colors" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function CAppComplaintEditor({ complaint: c, onRefresh }) {
  const [open,   setOpen]   = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form,   setForm]   = React.useState({
    subject:  c.subject  || '',
    detail:   c.detail   || '',
    category: c.category || 'other',
    priority: c.priority || 'medium',
    contact:  c.contact  || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.complaints.update({
        id:       c.id,
        subject:  form.subject.trim(),
        detail:   form.detail.trim(),
        category: form.category,
        priority: form.priority,
        contact:  form.contact.trim(),
      });
      c.subject  = form.subject.trim();
      c.detail   = form.detail.trim();
      c.category = form.category;
      c.priority = form.priority;
      c.contact  = form.contact.trim();
      onRefresh();
      setOpen(false);
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors text-xs font-semibold"
      >
        <Edit2 size={12} /> Edit Complaint
      </button>
    );
  }

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-blue-800 flex items-center gap-2">
          <Edit2 size={14}/> Edit Complaint
        </p>
        <button
          onClick={() => { setOpen(false); setForm({ subject: c.subject||'', detail: c.detail||'', category: c.category||'other', priority: c.priority||'medium', contact: c.contact||'' }); }}
          className="p-1 rounded-lg hover:bg-blue-100 text-blue-400"
        >
          <X size={15}/>
        </button>
      </div>

      {/* Category — ALL categories */}
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Category</label>
        <div className="grid grid-cols-2 gap-2">
          {COMPLAINT_CATEGORIES.map(cat => (
            <button key={cat.value} type="button"
              onClick={() => set('category', cat.value)}
              className={`px-3 py-2 rounded-xl border-2 text-xs font-semibold transition-all text-left ${
                form.category === cat.value
                  ? cat.color + ' border-current'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
              }`}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Priority</label>
        <div className="flex gap-2">
          {COMPLAINT_PRIORITIES.map(p => (
            <button key={p.value} type="button"
              onClick={() => set('priority', p.value)}
              className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                form.priority === p.value
                  ? p.color + ' border-current'
                  : 'bg-white border-slate-200 text-slate-500'
              }`}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Subject</label>
        <input
          value={form.subject}
          onChange={e => set('subject', e.target.value)}
          placeholder="Complaint subject..."
          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-blue-400 outline-none text-sm text-slate-800"
        />
      </div>

      {/* Detail */}
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Detail / Description</label>
        <textarea
          value={form.detail}
          onChange={e => set('detail', e.target.value)}
          placeholder="Full complaint details..."
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-blue-400 outline-none text-sm text-slate-800 resize-none"
        />
      </div>

      {/* Contact */}
      <div>
        <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Contact Number</label>
        <input
          value={form.contact}
          onChange={e => set('contact', e.target.value)}
          placeholder="03XX-XXXXXXX"
          className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-blue-400 outline-none text-sm text-slate-800"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving || !form.subject.trim() || !form.category}
          className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-all disabled:opacity-40 flex items-center justify-center gap-2"
        >
          {saving ? <><RefreshCw size={14} className="animate-spin"/>Saving...</> : <><Check size={14}/>Save Changes</>}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Office "Close & Bill" for a task the technician has SUBMITTED via the app.
// Sets customer charge (editable, pre-filled from create) + technician payment,
// then calls task.php?action=verify → applies effects (invoice, technician_payment,
// inventory) + the GPSWOX IMEI-match gate. Shown only when task_status === 'submitted'.
function CCloseAndBill({ complaint: c, onDone }) {
  const isService = ['redo', 'transfer'].includes(c.category);
  const [charge, setCharge]   = React.useState(c.activity_cost != null && c.activity_cost !== '' ? String(c.activity_cost) : '');
  const [techPay, setTechPay] = React.useState(c.svc_tech_amount != null && c.svc_tech_amount !== '' ? String(c.svc_tech_amount) : '');
  const [busy, setBusy]       = React.useState(false);
  const [err, setErr]         = React.useState(null);

  const doClose = async () => {
    setBusy(true); setErr(null);
    try {
      const p = new URLSearchParams({ action: 'verify', task_id: String(c.id), verified_by: 'Office' });
      if (isService) {
        if (charge  !== '') p.set('customer_charge', charge);
        if (techPay !== '') p.set('technician_pay', techPay);
      }
      const res = await fetch(`${API_BASE_URL}/task.php?${p.toString()}`, { method: 'POST' });
      const j = await res.json();
      if (!j.success) throw new Error(j.message || 'Close failed');
      if (onDone) onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border-2 border-cyan-200 p-5">
      <p className="text-xs text-cyan-600 uppercase font-bold mb-1 flex items-center gap-2"><CheckCircle size={14}/> Close &amp; Bill</p>
      <p className="text-xs text-slate-400 mb-3">Technician submitted this activity. Review charges and close.</p>
      {isService && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Customer Charge (Rs.)</label>
            <input type="number" min="0" value={charge} onChange={e => setCharge(e.target.value)} placeholder="0"
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-cyan-500 outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Technician Payment (Rs.)</label>
            <input type="number" min="0" value={techPay} onChange={e => setTechPay(e.target.value)} placeholder="0"
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-cyan-500 outline-none text-sm" />
          </div>
        </div>
      )}
      {err && <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>}
      <button onClick={doClose} disabled={busy}
        className="mt-4 w-full py-2.5 rounded-xl bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-500 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
        <CheckCircle size={15}/> {busy ? 'Closing…' : 'Close & Complete'}
      </button>
    </div>
  );
}

// Installation vehicles — structured + editable. Office can fix VRN / Make / Model
// at any time (including after the technician submitted), saving per row.
function CInstallVehiclesEditor({ complaint: c, canEdit, onRefresh }) {
  const [rows, setRows] = React.useState(() => (c.installation_vehicles || []).map(v => ({ ...v })));
  const [savingId, setSavingId] = React.useState(null);
  const [scanId, setScanId] = React.useState(null);
  const [scanMsg, setScanMsg] = React.useState('');
  const [ocrRow, setOcrRow] = React.useState(null);
  const [cands, setCands] = React.useState([]);
  const [lightbox, setLightbox] = React.useState(null);   // proof image opened large for visual confirm
  React.useEffect(() => { setRows((c.installation_vehicles || []).map(v => ({ ...v }))); }, [c.id, c.installation_vehicles]);

  // Proof images for this activity (used by the scanner + the "view large" thumbnails).
  const proofImgs = React.useMemo(
    () => (c.proofs || []).map(p => p.file_path ? `${API_BASE_URL}/${p.file_path}` : p.data_uri).filter(Boolean),
    [c.proofs]
  );

  const upd = (id, field, val) => setRows(rs => rs.map(r => r.id === id ? { ...r, [field]: val } : r));
  const save = async (row) => {
    setSavingId(row.id);
    try {
      await api.complaints.saveInstVehicle({
        id: row.id, vehicle_no: row.vehicle_no || '', make: row.make || '', model: row.model || '',
        engine_no: row.engine_no || '', chassis_no: row.chassis_no || ''
      });
      await onRefresh();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setSavingId(null); }
  };

  // Load the browser OCR engine (Tesseract.js) once, on demand.
  const ensureTesseract = () => new Promise((resolve, reject) => {
    if (window.Tesseract) return resolve(window.Tesseract);
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js';
    s.onload = () => resolve(window.Tesseract);
    s.onerror = () => reject(new Error('Could not load OCR engine'));
    document.body.appendChild(s);
  });

  // Preprocess a card image for OCR. `mode`:
  //  'gray' → grayscale + strong contrast stretch, NO hard threshold (keeps faint/small
  //           text like the Engine Number, which a binary threshold wipes out).
  //  'bw'   → high threshold to strip the watermark pattern (best for the bold chassis line).
  // `crop` = {y0,y1} fractions → OCR only that horizontal band, upscaled hard. Cropping the
  // bottom band and blowing it up is what makes the small Engine Number line legible.
  const preprocess = (src, mode, crop) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const sy = crop ? Math.round(img.height * crop.y0) : 0;
        const sh = crop ? Math.round(img.height * (crop.y1 - crop.y0)) : img.height;
        const targetW = crop ? 2600 : 2400;                       // cropped band → even bigger
        const scale = Math.min(4.5, Math.max(1.6, targetW / img.width));
        const w = Math.round(img.width * scale), h = Math.round(sh * scale);
        const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
        const cx = cv.getContext('2d'); cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high';
        cx.drawImage(img, 0, sy, img.width, sh, 0, 0, w, h);
        const d = cx.getImageData(0, 0, w, h), a = d.data;
        for (let i = 0; i < a.length; i += 4) {
          const g = 0.3 * a[i] + 0.59 * a[i + 1] + 0.11 * a[i + 2];
          let v;
          if (mode === 'bw') {
            v = g > 150 ? 255 : (g < 100 ? 0 : g);
          } else {
            // contrast stretch around mid-grey: darken text, lighten background, keep faint strokes
            v = Math.max(0, Math.min(255, (g - 128) * 1.9 + 128));
          }
          a[i] = a[i + 1] = a[i + 2] = v;
        }
        cx.putImageData(d, 0, 0);
        resolve(cv);
      } catch (_) { resolve(src); }   // tainted (CORS) → OCR the raw image instead
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });

  // OCR the activity's proof images and list EVERY value read. The platform does not
  // guess which is engine / chassis / CNIC — the user picks from the dropdowns.
  const scan = async (row) => {
    const imgs = proofImgs;
    if (imgs.length === 0) { setOcrRow(row.id); setCands([]); setScanMsg('No proof images on this activity.'); return; }
    setScanId(row.id); setOcrRow(row.id); setCands([]); setScanMsg('Loading OCR engine…');
    let worker;
    try {
      const T = await ensureTesseract();
      worker = await T.createWorker('eng');
      const WL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:.-/ ';
      let all = '';
      // Several passes per image and merge everything. Full-card passes (sparse, whitelisted)
      // catch the big values; cropped-band passes blow up small lines AND drop the whitelist +
      // use single-block/line mode so the LSTM reads e.g. "Q379927" naturally instead of forcing it.
      const passes = [
        { mode: 'gray', crop: null,                  psm: '11', wl: WL },  // whole card, faint text
        { mode: 'bw',   crop: null,                  psm: '11', wl: WL },  // whole card, no watermark
        { mode: 'gray', crop: { y0: 0.45, y1: 1.0 }, psm: '6',  wl: '' },  // bottom band → Engine line
        { mode: 'bw',   crop: { y0: 0.45, y1: 1.0 }, psm: '6',  wl: '' },  // bottom band, thresholded
        { mode: 'gray', crop: { y0: 0.25, y1: 0.70 },psm: '6',  wl: '' },  // middle band → Chassis line
      ];
      for (let i = 0; i < imgs.length; i++) {
        for (let p = 0; p < passes.length; p++) {
          setScanMsg(`Reading image ${i + 1} of ${imgs.length}… (${p + 1}/${passes.length})`);
          try {
            await worker.setParameters({ tessedit_char_whitelist: passes[p].wl, preserve_interword_spaces: '1', tessedit_pageseg_mode: passes[p].psm });
            const prep = await preprocess(imgs[i], passes[p].mode, passes[p].crop);
            const { data } = await worker.recognize(prep);
            all += '\n' + (data.text || '');
          } catch (_) { /* skip an unreadable pass */ }
        }
      }
      const up = all.toUpperCase().replace(/\|/g, 'I');
      // Every alphanumeric token of length ≥6 that contains a digit — no guessing, show them all
      // (engine, chassis, CNIC, phone…), longest first, so the user selects the right one.
      const toks = Array.from(new Set((up.match(/[A-Z0-9]{6,}/g) || []).filter(t => /[0-9]/.test(t))))
        .sort((a, b) => b.length - a.length);
      setCands(toks.slice(0, 20));
      setScanMsg(toks.length
        ? 'Read the values below. Open the card image to confirm, then pick Engine and Chassis from the dropdowns and Save.'
        : 'Couldn’t read any values. Open the card image below and type the numbers manually.');
    } catch (e) { setScanMsg('OCR failed: ' + e.message); }
    finally { if (worker) { try { await worker.terminate(); } catch (_) {} } setScanId(null); }
  };

  const inCls = "w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs disabled:bg-slate-100 focus:border-green-500 outline-none";

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
      {lightbox && <CFloatingCardZoom src={lightbox} onClose={() => setLightbox(null)} />}
      <p className="text-xs text-slate-400 uppercase font-bold mb-3">Installation Vehicles ({rows.length})</p>
      <div className="space-y-3">
        {rows.map((v, i) => (
          <div key={v.id} className={`rounded-xl border p-3 ${v.completed_at ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-black text-slate-600">#{i + 1}</span>
              {v.completed_at
                ? <span className="text-xs font-bold text-green-600">✓ Done {fmtCDate(v.completed_at)}</span>
                : <span className="text-xs text-slate-400 italic">Pending</span>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><p className="text-[10px] text-slate-400 font-bold uppercase mb-1">VRN</p>
                <input value={v.vehicle_no || ''} disabled={!canEdit} onChange={e => upd(v.id, 'vehicle_no', e.target.value.toUpperCase())} placeholder="VRN"
                  className={`${inCls} font-semibold uppercase`} /></div>
              <div><p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Make</p>
                <input value={v.make || ''} disabled={!canEdit} onChange={e => upd(v.id, 'make', e.target.value)} placeholder="Make" className={inCls} /></div>
              <div><p className="text-[10px] text-slate-400 font-bold uppercase mb-1">Model</p>
                <input value={v.model || ''} disabled={!canEdit} onChange={e => upd(v.id, 'model', e.target.value)} placeholder="Model" className={inCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {[
                { key: 'engine_no', label: 'Engine No', ring: 'focus:border-indigo-500' },
                { key: 'chassis_no', label: 'Chassis No', ring: 'focus:border-green-500' },
              ].map(f => (
                <div key={f.key}>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mb-1">{f.label}</p>
                  <input value={v[f.key] || ''} disabled={!canEdit}
                    onChange={e => upd(v.id, f.key, e.target.value.toUpperCase())}
                    placeholder={f.label} className={`${inCls} font-mono uppercase ${f.ring}`} />
                  {canEdit && ocrRow === v.id && cands.length > 0 && (
                    <select value="" onChange={e => { if (e.target.value) upd(v.id, f.key, e.target.value); }}
                      className="mt-1 w-full px-2 py-1 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-[11px] font-mono outline-none cursor-pointer">
                      <option value="">▾ Pick scanned value…</option>
                      {cands.map(tok => <option key={tok} value={tok}>{tok}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button onClick={() => scan(v)} disabled={scanId === v.id}
                  className="px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[11px] font-bold border border-indigo-200 disabled:opacity-50 inline-flex items-center gap-1.5">
                  <Camera size={12}/> {scanId === v.id ? 'Scanning…' : 'Scan images → Engine/Chassis'}
                </button>
                {proofImgs.length > 0 && (
                  <button type="button" onClick={() => setLightbox(proofImgs[0])}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-bold border border-slate-200 inline-flex items-center gap-1.5">
                    <Search size={12}/> View card image
                  </button>
                )}
                {scanId === v.id && <span className="text-[11px] text-slate-400">{scanMsg}</span>}
              </div>
            )}
            {ocrRow === v.id && scanId !== v.id && scanMsg && (
              <div className="mt-2 text-[11px] text-slate-500">
                <p>{scanMsg}</p>
                {proofImgs.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {proofImgs.map((src, ix) => (
                      <button key={ix} type="button" onClick={() => setLightbox(src)} title="Open large to read & confirm"
                        className="rounded-lg overflow-hidden border border-slate-200 hover:border-indigo-400 transition-all">
                        <img src={src} alt="card" loading="lazy" className="w-16 h-16 object-cover"
                          onError={e => { e.currentTarget.style.opacity = 0.3; }} />
                      </button>
                    ))}
                  </div>
                )}
                {cands.length > 0 && (
                  <p className="mt-1 text-slate-400">Everything the scan read: <span className="font-mono text-slate-500">{cands.join(' · ')}</span></p>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mt-2 gap-2">
              <p className="font-mono text-xs text-slate-500">IMEI: {v.imei_to || <span className="italic text-amber-600">not filled</span>}</p>
              {canEdit && (
                <button onClick={() => save(v)} disabled={savingId === v.id}
                  className="px-3 py-1 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold disabled:opacity-50 shrink-0">
                  {savingId === v.id ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {TEST_PARAMS.map(t => {
                const val = v[t.key];
                if (val === null || val === undefined) return null;
                return <span key={t.key} className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${val === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{t.label} {val === 1 ? '✓' : '✗'}</span>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Full-screen proof viewer — opens in-place (no new tab), with zoom (buttons + wheel) and pan.
function CImageLightbox({ src, onClose }) {
  const [scale, setScale] = React.useState(1);
  const [tx, setTx] = React.useState(0);
  const [ty, setTy] = React.useState(0);
  const [err, setErr] = React.useState(false);
  const drag = React.useRef(null);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const clamp = (s) => Math.min(6, Math.max(1, +s.toFixed(2)));
  const zoom = (d) => setScale(s => { const n = clamp(s + d); if (n === 1) { setTx(0); setTy(0); } return n; });
  const reset = () => { setScale(1); setTx(0); setTy(0); };
  const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY < 0 ? 0.25 : -0.25); };
  const onDown = (e) => { if (scale > 1) drag.current = { x: e.clientX - tx, y: e.clientY - ty }; };
  const onMove = (e) => { if (drag.current) { setTx(e.clientX - drag.current.x); setTy(e.clientY - drag.current.y); } };
  const onUp = () => { drag.current = null; };

  const btn = "w-9 h-9 flex items-center justify-center rounded-lg bg-white/15 hover:bg-white/30 text-white text-lg font-bold backdrop-blur transition-colors";

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center"
      onClick={onClose} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10" onClick={e => e.stopPropagation()}>
        <button className={btn} title="Zoom out" onClick={() => zoom(-0.5)}>&minus;</button>
        <span className="text-white/80 text-xs w-10 text-center select-none">{Math.round(scale * 100)}%</span>
        <button className={btn} title="Zoom in" onClick={() => zoom(0.5)}>+</button>
        <button className={btn} title="Reset" onClick={reset}><RefreshCw size={16}/></button>
        <button className={btn} title="Close (Esc)" onClick={onClose}><X size={18}/></button>
      </div>
      {err ? (
        <div className="text-white/70 text-sm px-6 text-center" onClick={e => e.stopPropagation()}>
          Image could not be loaded.<br/>
          <span className="text-white/40 text-xs break-all">{src}</span>
        </div>
      ) : (
        <img src={src} alt="proof" draggable={false} onError={() => setErr(true)}
          onClick={e => e.stopPropagation()} onWheel={onWheel} onMouseDown={onDown}
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transition: drag.current ? 'none' : 'transform 0.12s ease-out',
            cursor: scale > 1 ? 'grab' : 'zoom-in', maxHeight: '90vh', maxWidth: '92vw',
          }}
          className="object-contain select-none rounded" />
      )}
    </div>
  );
}

// Floating, movable, zoomable card viewer — NO full-screen backdrop, so the form (dropdowns,
// inputs) stays fully clickable underneath. Read the card here and select values in parallel;
// no need to close it to type. Drag the header to move it aside; drag a corner to resize.
function CFloatingCardZoom({ src, onClose }) {
  const [scale, setScale]   = React.useState(1.8);
  const [tx, setTx]         = React.useState(0);
  const [ty, setTy]         = React.useState(0);
  const [pos, setPos]       = React.useState({ x: 32, y: 96 });
  const [size, setSize]     = React.useState({ w: 840, h: 660 });
  const [err, setErr]       = React.useState(false);
  const winDrag = React.useRef(null);   // dragging the whole window (by header)
  const imgDrag = React.useRef(null);   // panning the image inside
  const resz    = React.useRef(null);   // resizing from the corner

  const clamp = (s) => Math.min(8, Math.max(1, +s.toFixed(2)));
  const zoom  = (d) => setScale(s => clamp(s + d));
  const reset = () => { setScale(1.8); setTx(0); setTy(0); };
  const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY < 0 ? 0.3 : -0.3); };

  const onHeaderDown = (e) => { winDrag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; e.preventDefault(); };
  const onImgDown    = (e) => { imgDrag.current = { x: e.clientX - tx, y: e.clientY - ty }; e.preventDefault(); };
  const onReszDown   = (e) => { resz.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h }; e.preventDefault(); e.stopPropagation(); };

  React.useEffect(() => {
    const move = (e) => {
      if (winDrag.current) setPos({ x: e.clientX - winDrag.current.x, y: e.clientY - winDrag.current.y });
      else if (resz.current) setSize({
        w: Math.max(240, resz.current.w + (e.clientX - resz.current.x)),
        h: Math.max(200, resz.current.h + (e.clientY - resz.current.y)),
      });
      else if (imgDrag.current) { setTx(e.clientX - imgDrag.current.x); setTy(e.clientY - imgDrag.current.y); }
    };
    const up = () => { winDrag.current = null; imgDrag.current = null; resz.current = null; };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const btn = "w-7 h-7 flex items-center justify-center rounded-md bg-white/20 hover:bg-white/35 text-white text-base font-bold transition-colors";

  return (
    <div className="fixed z-[90] rounded-xl shadow-2xl border border-slate-700 overflow-hidden select-none"
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}>
      {/* Header — drag to move */}
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-800 cursor-move" onMouseDown={onHeaderDown}>
        <Search size={13} className="text-white/70"/>
        <span className="text-white/80 text-[11px] font-semibold mr-auto">Card — zoom &amp; read</span>
        <button className={btn} title="Zoom out" onClick={() => zoom(-0.4)}>&minus;</button>
        <span className="text-white/70 text-[10px] w-9 text-center">{Math.round(scale * 100)}%</span>
        <button className={btn} title="Zoom in" onClick={() => zoom(0.4)}>+</button>
        <button className={btn} title="Reset" onClick={reset}><RefreshCw size={13}/></button>
        <button className={btn} title="Close (Esc)" onClick={onClose}><X size={14}/></button>
      </div>
      {/* Image area — drag to pan, wheel to zoom */}
      <div className="relative bg-black/90 overflow-hidden" style={{ height: size.h - 34 }} onWheel={onWheel}>
        {err ? (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-xs px-4 text-center">Image could not be loaded.</div>
        ) : (
          <img src={src} alt="card" draggable={false} onError={() => setErr(true)} onMouseDown={onImgDown}
            style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transformOrigin: 'top left', cursor: 'grab' }}
            className="absolute top-0 left-0 max-w-none" />
        )}
        {/* Resize handle */}
        <div onMouseDown={onReszDown} title="Drag to resize"
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
          style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.5) 50%)' }} />
      </div>
    </div>
  );
}

function CDetailView({ complaint: c, customers, employees, replyText, setReplyText, sendingReply, onSendReply, onStatusChange, onTaskStage, onConvert, onAssign, onDelete, onBack, onRefresh, can = { create: true, edit: true, delete: true } }) {
  const [showAssignMenu, setShowAssignMenu]     = React.useState(false);
  const [assignRemarks, setAssignRemarks]       = React.useState('');
  const [showAssignPanel, setShowAssignPanel]   = React.useState(false);
  const [convertType, setConvertType]           = React.useState(null); // null | new_installation | removal | redo | transfer
  const [lightbox, setLightbox]                 = React.useState(null); // proof image src open in the zoom viewer
  const replies   = c.replies    || [];
  const statusLog = c.status_log || [];
  const isAppSource = c.source === 'app';
  const ACTIVITY_CATS = ['new_installation', 'removal', 'redo', 'transfer', 'payment_collection'];
  const isActivity = ACTIVITY_CATS.includes(c.category);

  // Vehicle search state for app-source complaints
  const [vehicleQuery, setVehicleQuery]         = React.useState('');
  const [showVehicleDrop, setShowVehicleDrop]   = React.useState(false);
  const [resolvedCustomer, setResolvedCustomer] = React.useState(null);
  const [savingCustomer, setSavingCustomer]     = React.useState(false);
  const [savedCustomer, setSavedCustomer]       = React.useState(
    c.customer_name ? { name: c.customer_name, contact: c.contact, id: c.customer_id } : null
  );

  // Build flat list of all active vehicles with their customer info
  const allActiveVehicles = React.useMemo(() => {
    const list = [];
    (customers || []).forEach(cust => {
      (cust.vehicles || []).forEach(v => {
        if (v.status === 'active') {
          list.push({
            vehicleNo: v.vehicleNo,
            vehicleId: v.id,
            customerId: cust.id,
            customerName: cust.name,
            contact: cust.cell || cust.phone || '',
          });
        }
      });
    });
    return list;
  }, [customers]);

  const filteredVehicles = vehicleQuery.trim()
    ? allActiveVehicles.filter(v =>
        v.vehicleNo.toLowerCase().includes(vehicleQuery.toLowerCase())
      ).slice(0, 10)
    : [];

  const handleVehicleSelect = (entry) => {
    setResolvedCustomer(entry);
    setVehicleQuery(entry.vehicleNo);
    setShowVehicleDrop(false);
  };

  const handleSaveCustomer = async () => {
    if (!resolvedCustomer) return;
    setSavingCustomer(true);
    try {
      await api.complaints.setCustomer({
        id: c.id,
        customer_id:   resolvedCustomer.customerId,
        customer_name: resolvedCustomer.customerName,
        contact:       resolvedCustomer.contact,
        vehicle_name:  resolvedCustomer.vehicleNo,
      });
      setSavedCustomer(resolvedCustomer);
      setResolvedCustomer(null);
      setVehicleQuery('');
      onRefresh();
    } catch (e) { alert('Failed to save: ' + e.message); }
    finally { setSavingCustomer(false); }
  };

  // Resolve the complaint's vehicle number to a full active-vehicle object so the
  // Convert-to-Activity forms can pre-fill it (same source the forms' search uses).
  const { search: searchActiveVehicles } = useActiveVehicleSearch(customers);
  const prefillVehicle = React.useMemo(() => {
    const vno = (c.vehicle_name || '').trim();
    if (!vno) return null;
    const results = searchActiveVehicles(vno) || [];
    return results.find(v => (v.vehicleNo || '').toLowerCase() === vno.toLowerCase()) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.vehicle_name, searchActiveVehicles]);

  // Auto-resolve customer from the vehicle number the customer picked in the app.
  // If the complaint carries a vehicle number that matches an active vehicle,
  // link its customer (name + contact) automatically. No match / no number →
  // the office resolves it manually via the vehicle search above.
  React.useEffect(() => {
    if (!isAppSource || c.customer_id) return;
    if (!prefillVehicle) return;   // resolved from the complaint's vehicle number
    (async () => {
      try {
        await api.complaints.setCustomer({
          id: c.id, customer_id: prefillVehicle.customerId, customer_name: prefillVehicle.customerName,
          contact: prefillVehicle.contact, vehicle_name: prefillVehicle.vehicleNo,
        });
        setSavedCustomer({ name: prefillVehicle.customerName, contact: prefillVehicle.contact, id: prefillVehicle.customerId });
        onRefresh();
      } catch (e) { /* silent — office can still resolve manually */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppSource, c.id, c.customer_id, prefillVehicle]);

  // Convert-to-activity: reuse the dedicated activity forms in "convert" mode —
  // their onSaved UPDATES this ticket (category + fields + task) instead of creating a new one.
  if (convertType) {
    const cvLabel = { new_installation: 'Installation', removal: 'Removal', redo: 'Redo', transfer: 'Transfer' }[convertType] || convertType;
    const convertProps = {
      customers, employees,
      onSaved: async (payload) => { await onConvert(c.id, payload); setConvertType(null); },
      onCancel: () => setConvertType(null),
    };
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5">
          <AlertCircle size={15} className="text-indigo-500"/>
          <p className="text-sm font-semibold text-indigo-700">
            Converting <span className="font-mono">{c.ticket_no}</span> &rarr; {cvLabel} activity
          </p>
          <button onClick={() => setConvertType(null)} className="ml-auto text-xs font-semibold text-slate-500 hover:text-slate-700">Cancel</button>
        </div>
        {convertType === 'new_installation' && <CNewInstallationForm {...convertProps} />}
        {convertType === 'removal'          && <CRemovalForm {...convertProps} prefillVehicle={prefillVehicle} />}
        {convertType === 'redo'             && <CRedoForm {...convertProps} prefillVehicle={prefillVehicle} />}
        {convertType === 'transfer'         && <CTransferForm {...convertProps} prefillVehicle={prefillVehicle} />}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {lightbox && <CImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
      {/* Left: thread */}
      <div className="col-span-8 space-y-4">
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={onBack} className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500"><X size={18}/></button>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg font-bold text-red-600">{c.ticket_no}</span>
                  <CStatusBadge status={c.status} taskStatus={c.task_status} />
                  <CBadge color={cPriInfo(c.priority).color}>{cPriInfo(c.priority).icon} {cPriInfo(c.priority).label}</CBadge>
                  <CBadge color={cCatInfo(c.category).color}>{cCatInfo(c.category).label}</CBadge>
                </div>
                <h2 className="text-xl font-bold text-slate-800">{c.subject}</h2>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={onRefresh} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500"><RefreshCw size={16}/></button>
              {can.delete && <button onClick={() => onDelete(c.id)} className="p-2 rounded-xl border border-red-200 hover:bg-red-50 text-red-500"><Trash2 size={16}/></button>}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 py-4 border-t border-b border-slate-100 mb-4">
            <div>
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Submitted by</p>
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><User size={13} className="text-slate-400"/>{c.submitted_by || 'App User'}</p>
            </div>

            {/* Customer — static if CRM, vehicle-lookup if App */}
            <div>
              <p className="text-xs text-slate-400 uppercase font-bold mb-1 flex items-center gap-1">
                Customer
                {isAppSource && <CBadge color="bg-blue-100 text-blue-600">📱 App</CBadge>}
              </p>
              {isAppSource ? (
                savedCustomer ? (
                  <div className="flex items-start justify-between gap-1">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{savedCustomer.name}</p>
                      <p className="text-xs text-slate-500 font-mono">ID: {savedCustomer.id || savedCustomer.customerId}</p>
                    </div>
                    <button onClick={() => { setSavedCustomer(null); setVehicleQuery(''); setResolvedCustomer(null); }}
                      className="text-xs text-red-400 hover:text-red-600 mt-0.5 flex-shrink-0">
                      <Edit2 size={12}/>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="relative">
                      <Car className="absolute left-2.5 top-2.5 text-slate-400" size={13}/>
                      <input
                        value={vehicleQuery}
                        onChange={e => { setVehicleQuery(e.target.value); setShowVehicleDrop(true); setResolvedCustomer(null); }}
                        onFocus={() => { if (vehicleQuery) setShowVehicleDrop(true); }}
                        placeholder="Search vehicle no..."
                        className="w-full pl-8 pr-3 py-2 rounded-lg border-2 border-amber-300 focus:border-amber-500 outline-none text-sm text-slate-800 bg-amber-50 placeholder-amber-400"
                      />
                      {showVehicleDrop && filteredVehicles.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-52 overflow-y-auto">
                          {filteredVehicles.map((v, i) => (
                            <button key={i} onClick={() => handleVehicleSelect(v)}
                              className="w-full text-left px-3 py-2.5 hover:bg-amber-50 transition-colors border-b border-slate-50 last:border-0">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                                  <Car size={12} className="text-amber-500"/>{v.vehicleNo}
                                </span>
                                <span className="text-xs text-slate-400 font-mono">ID:{v.customerId}</span>
                              </div>
                              <p className="text-xs text-slate-600 font-semibold mt-0.5">{v.customerName}</p>
                              <p className="text-xs text-slate-400">{v.contact || '—'}</p>
                            </button>
                          ))}
                        </div>
                      )}
                      {showVehicleDrop && vehicleQuery.trim() && filteredVehicles.length === 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 px-3 py-2.5">
                          <p className="text-xs text-slate-400 italic">No active vehicle found</p>
                        </div>
                      )}
                    </div>
                    {resolvedCustomer && (
                      <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 space-y-0.5">
                        <p className="text-xs font-bold text-green-700">{resolvedCustomer.customerName}</p>
                        <p className="text-xs text-green-600">ID: {resolvedCustomer.customerId} · {resolvedCustomer.contact || '—'}</p>
                        <button onClick={handleSaveCustomer} disabled={savingCustomer}
                          className="mt-1.5 w-full py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1">
                          <Check size={11}/> {savingCustomer ? 'Saving...' : 'Save to complaint'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <p className="text-sm font-semibold text-slate-700">{c.customer_name || '—'}</p>
              )}
            </div>

            {/* Vehicle Number — separate section */}
            <div>
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Vehicle Number</p>
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                <Car size={13} className="text-slate-400"/>
                {c.vehicle_name || '—'}
              </p>
            </div>

            {/* Contact */}
            <div>
              <p className="text-xs text-slate-400 uppercase font-bold mb-1">Contact</p>
              <p className="text-sm font-semibold text-slate-700">
                {(isAppSource && (resolvedCustomer?.contact || savedCustomer?.contact)) ||
                  c.contact || '—'}
              </p>
            </div>

            <div><p className="text-xs text-slate-400 uppercase font-bold mb-1">Created</p><p className="text-sm text-slate-700">{fmtCDateTime(c.created_at)}</p></div>
            <div><p className="text-xs text-slate-400 uppercase font-bold mb-1">Updated</p><p className="text-sm text-slate-700">{fmtCDateTime(c.updated_at)}</p></div>
            {c.resolved_at && <div><p className="text-xs text-slate-400 uppercase font-bold mb-1">Resolved</p><p className="text-sm text-slate-700">{fmtCDateTime(c.resolved_at)}</p></div>}
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase font-bold mb-2">Original Complaint</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.detail}</p>
          </div>
        </div>

        {/* Technician proof photos */}
        {Array.isArray(c.proofs) && c.proofs.length > 0 && (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-4">
            <p className="text-xs text-slate-400 uppercase font-bold mb-3 flex items-center gap-2">
              <Camera size={13} className="text-slate-400"/> Technician Proof ({c.proofs.length})
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {c.proofs.map(p => {
                const src = p.file_path ? `${API_BASE_URL}/${p.file_path}` : p.data_uri;
                return (
                  <button key={p.id} type="button" onClick={() => setLightbox(src)}
                    className="block rounded-lg overflow-hidden border border-slate-200 hover:border-red-400 transition-all">
                    <img src={src} alt={p.kind || 'proof'} loading="lazy" className="w-full h-24 object-cover"
                      onError={e => { e.currentTarget.style.opacity = 0.3; }} />
                  </button>
                );
              })}
            </div>
            {(() => {
              const g = c.proofs.find(p => p.lat && p.lng);
              if (!g) return null;
              return (
                <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                  <MapPin size={11}/>
                  <span>{Number(g.lat).toFixed(5)}, {Number(g.lng).toFixed(5)}</span>
                  {g.captured_at && <span>· {fmtCDateTime(g.captured_at)}</span>}
                  <a className="ml-auto text-indigo-600 font-semibold hover:underline" target="_blank" rel="noreferrer"
                     href={`https://www.google.com/maps?q=${g.lat},${g.lng}`}>Open map</a>
                </div>
              );
            })()}
          </div>
        )}

        {/* Thread */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200">
          <div className="px-4 py-2.5 border-b border-slate-100">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <MessageSquare size={16} className="text-red-500"/>
              Conversation Thread
              <span className="ml-auto text-xs text-slate-400 font-normal">{replies.length} message{replies.length !== 1 ? 's' : ''}</span>
            </h3>
          </div>
          <div className="divide-y divide-slate-50 max-h-96 overflow-y-auto">
            {replies.map((r, i) => (
              <div key={i} className={`p-5 ${r.author_role === 'system' ? 'bg-slate-50' : r.author_role === 'customer' ? 'bg-blue-50' : 'bg-white'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    r.author_role === 'system' ? 'bg-slate-200 text-slate-500' : r.author_role === 'customer' ? 'bg-blue-200 text-blue-700' : 'bg-red-100 text-red-600'
                  }`}>{r.author_role === 'system' ? '⚙' : r.author.charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-slate-800">{r.author}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        r.author_role === 'system' ? 'bg-slate-100 text-slate-500' : r.author_role === 'customer' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
                      }`}>{r.author_role}</span>
                      <span className="text-xs text-slate-400 ml-auto">{fmtCDateTime(r.created_at)}</span>
                    </div>
                    <p className={`text-sm leading-relaxed ${r.author_role === 'system' ? 'text-slate-500 italic' : 'text-slate-700'}`}>{r.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {c.status !== 'closed' && can.edit && (
            <div className="p-5 border-t border-slate-100">
              <p className="text-xs text-slate-400 font-bold uppercase mb-2">Add Staff Reply</p>
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                placeholder="Type your reply..." rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-sm resize-none text-slate-800 placeholder-slate-400" />
              <div className="flex justify-end mt-2 gap-2">
                {c.status !== 'resolved' && (
                  <button onClick={() => { onSendReply(); onStatusChange(c.id, 'resolved'); }}
                    disabled={!replyText.trim() || sendingReply}
                    className="px-4 py-2 rounded-xl border border-green-300 text-green-700 text-sm font-semibold hover:bg-green-50 disabled:opacity-40 transition-all flex items-center gap-2">
                    <CheckCircle size={14}/> Reply & Resolve
                  </button>
                )}
                <button onClick={onSendReply} disabled={!replyText.trim() || sendingReply}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-5 py-2 rounded-xl text-sm font-semibold disabled:opacity-40 hover:shadow-md transition-all flex items-center gap-2">
                  <Send size={14} className={sendingReply ? 'animate-pulse' : ''}/>
                  {sendingReply ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: actions */}
      <div className="col-span-4 space-y-4">
        {can.edit && (<>
        {/* Convert an app/generic complaint into a technician activity (then it shows in the app) */}
        {!isActivity && (
          <div className="bg-white rounded-xl shadow-md border border-indigo-200 p-5">
            <p className="text-xs text-indigo-400 uppercase font-bold mb-1">Convert to Activity</p>
            <p className="text-xs text-slate-500 mb-3">This is an app/generic complaint. Convert it to an activity and fill in the details to send it to the technician app.</p>
            <div className="grid grid-cols-2 gap-2">
              {[['redo', 'Redo'], ['removal', 'Removal'], ['transfer', 'Transfer'], ['new_installation', 'Installation']].map(([v, l]) => (
                <button key={v} onClick={() => setConvertType(v)}
                  className="px-3 py-2 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
        {c.task_status === 'submitted' && <CCloseAndBill complaint={c} onDone={onRefresh} />}
        {/* Status — task lifecycle (read-only; driven by the app + Close & Bill) */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
          <p className="text-xs text-slate-400 uppercase font-bold mb-3">Status</p>
          {(() => {
            const needsReview = isAppSource && (c.category === 'other' || c.subject === 'Mobile User Complaint');
            const u = deriveUStatus(c);
            const info = uStatInfo(u);
            // Activities whose completion must run through Close & Bill (invoice / tech payment / stock).
            const needsCloseBill = ['new_installation', 'removal', 'transfer', 'redo', 'payment_collection'].includes(c.category);
            const flow = ['assigned', 'in_progress', 'submitted', 'completed'];
            const curIdx = flow.indexOf(u);
            return (
              <>
                {needsReview && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                    <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0"/>
                    <p className="text-xs text-amber-700 font-semibold">Edit the complaint details before assigning a technician.</p>
                  </div>
                )}

                {/* Current status */}
                <div className={`px-4 py-2.5 rounded-xl flex items-center gap-2 ${info.color}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${info.dot}`}/>
                  <span className="text-sm font-bold">{info.label}</span>
                </div>

                {/* Lifecycle — normally driven by the app + Close & Bill; click a stage to override */}
                {u !== 'rejected' && (
                  <div className="space-y-1 mt-3">
                    {flow.map((st, i) => {
                      const si = uStatInfo(st);
                      const done = curIdx >= 0 && i < curIdx;
                      const current = st === u;
                      const billGate = st === 'completed' && needsCloseBill;
                      return (
                        <button key={st}
                          onClick={() => {
                            if (current) return;
                            if (billGate) { alert('Use “Close & Bill” to complete this activity — it creates the invoice, technician payment and stock/vehicle updates.'); return; }
                            onTaskStage(c.id, st);
                          }}
                          title={billGate ? 'Complete via Close & Bill' : current ? 'Current stage' : `Move to ${si.label}`}
                          className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-all ${current ? 'bg-slate-50' : 'hover:bg-red-50'}`}>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            current ? `${si.dot} text-white` : done ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
                          }`}>{done ? '✓' : i + 1}</span>
                          <span className={`text-sm font-medium ${current ? 'text-slate-800' : done ? 'text-slate-500' : 'text-slate-600'}`}>{si.label}</span>
                          {current && <Check size={14} className="ml-auto text-slate-400"/>}
                          {billGate && !current && <span className="ml-auto text-[10px] text-slate-400">Close &amp; Bill</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Contextual hint */}
                {u === 'submitted' && (
                  <p className="mt-3 text-xs text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 font-medium">
                    Technician submitted. Use <b>Close &amp; Bill</b> above to finalize.
                  </p>
                )}
                {u === 'rejected' && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 font-medium">
                      Sent back to the technician for rework.
                    </p>
                    <button onClick={() => onTaskStage(c.id, 'in_progress')}
                      className="w-full px-4 py-2 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:border-red-400 hover:bg-red-50 transition-all">
                      Reopen → In Progress
                    </button>
                  </div>
                )}
                {u === 'assigned' && (
                  <p className="mt-3 text-xs text-slate-500">Waiting for the technician to start in the app.</p>
                )}
                {u === 'completed' && (
                  <p className="mt-3 text-xs text-green-700 font-medium">Task closed.</p>
                )}
                {u === 'open' && (
                  <p className="mt-3 text-xs text-slate-500">Assign a technician to begin, or resolve it from the reply box below.</p>
                )}
              </>
            );
          })()}
        </div>

        {/* Assign */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
          <p className="text-xs text-slate-400 uppercase font-bold mb-3">Assigned To</p>
          <div className={`px-4 py-2.5 rounded-xl mb-3 ${c.assigned_name ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Users size={14} className="text-slate-400"/>
              {c.assigned_name || <span className="text-slate-400 italic">Unassigned</span>}
            </p>
          </div>

          {!showAssignPanel ? (
            <button
              onClick={() => {
                const needsReview = isAppSource && (c.category === 'other' || c.subject === 'Mobile User Complaint');
                if (needsReview) { alert('Please edit and save the complaint details before assigning a technician.'); return; }
                setShowAssignPanel(true);
              }}
              className={`w-full px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all flex items-center justify-between ${
                isAppSource && (c.category === 'other' || c.subject === 'Mobile User Complaint')
                  ? 'border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed'
                  : 'border-slate-200 text-slate-600 hover:border-red-400 hover:bg-red-50'
              }`}>
              <span>Assign to staff</span>
              <ChevronDown size={14}/>
            </button>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Remarks (optional)</label>
                <textarea
                  value={assignRemarks}
                  onChange={e => setAssignRemarks(e.target.value)}
                  placeholder="Add any notes for the assignee..."
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none text-sm text-slate-800 resize-none placeholder-slate-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Assign to</label>
                <EmployeeSearchSelect employees={employees} value="" activeOnly
                  placeholder="Search employee by name or code…"
                  onChange={(id) => { const emp = employees.find(x => String(x.id) === String(id)); if (emp) { onAssign(c.id, emp, assignRemarks); setShowAssignMenu(false); setShowAssignPanel(false); setAssignRemarks(''); } }} />
              </div>
              <button onClick={() => { setShowAssignPanel(false); setAssignRemarks(''); setShowAssignMenu(false); }}
                className="w-full py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors">Cancel</button>
            </div>
          )}
        </div>
        </>)}

        {/* Status history */}
        {statusLog.length > 0 && (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
            <p className="text-xs text-slate-400 uppercase font-bold mb-3">Status History</p>
            <div className="space-y-2">
              {[...statusLog].reverse().map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                  <span className="text-slate-400 w-20 shrink-0">{fmtCDate(l.changed_at)}</span>
                  <CStatusBadge status={l.from_status}/>
                  <ArrowRight size={10} className="text-slate-400"/>
                  <CStatusBadge status={l.to_status}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
          <p className="text-xs text-slate-400 uppercase font-bold mb-3">Info</p>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Source</span>
              <CBadge color={c.source === 'app' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                {c.source === 'app' ? '📱 GPS App' : '🖥 CRM'}
              </CBadge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Category</span>
              <CBadge color={cCatInfo(c.category).color}>{cCatInfo(c.category).label}</CBadge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Priority</span>
              <CBadge color={cPriInfo(c.priority).color}>{cPriInfo(c.priority).icon} {cPriInfo(c.priority).label}</CBadge>
            </div>
            {c.install_date && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Install Date</span>
                <span className="font-semibold text-slate-700">{fmtCDate(c.install_date)}</span>
              </div>
            )}
            {c.install_city && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">City / Area</span>
                <span className="font-semibold text-slate-700">{c.install_city}</span>
              </div>
            )}
            {c.removal_type && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Removal Type</span>
                <CBadge color={c.removal_type === 'temporary' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                  {c.removal_type === 'temporary' ? '↔ Temporary' : '✕ Permanent'}
                </CBadge>
              </div>
            )}
            {c.activity_cost != null && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Activity Cost</span>
                <span className="font-bold text-slate-800">Rs. {parseFloat(c.activity_cost).toLocaleString()}</span>
              </div>
            )}
            {c.transfer_pair_id && c.transfer_pair && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">
                  {c.category === 'removal' ? 'Linked Transfer' : 'From Removal'}
                </span>
                <span className="font-mono text-xs font-bold text-indigo-600">{c.transfer_pair.ticket_no}</span>
              </div>
            )}
          </div>
        </div>

        {/* App Complaint Edit Panel */}
        {isAppSource && (
          <CAppComplaintEditor complaint={c} onRefresh={onRefresh} />
        )}

        {/* IMEI & Testing */}
        {(c.imei_from || c.imei_to || TEST_PARAMS.some(t => c[t.key] !== null && c[t.key] !== undefined)) && (
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
            <p className="text-xs text-slate-400 uppercase font-bold mb-3">IMEI & Testing</p>
            <div className="space-y-2.5 text-sm">
              {c.imei_from && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">IMEI Removed</span>
                  <span className="font-mono text-xs font-bold text-slate-700">{c.imei_from}</span>
                </div>
              )}
              {c.imei_to && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">IMEI Installed</span>
                  <span className="font-mono text-xs font-bold text-green-700">{c.imei_to}</span>
                </div>
              )}
              {TEST_PARAMS.some(t => c[t.key] !== null && c[t.key] !== undefined) && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400 font-bold uppercase mb-2">Test Results</p>
                  <div className="space-y-1.5">
                    {TEST_PARAMS.map(t => {
                      const val = c[t.key];
                      if (val === null || val === undefined) return null;
                      return (
                        <div key={t.key} className="flex items-center justify-between">
                          <span className="text-xs text-slate-600">{t.label}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            val === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {val === 1 ? '✓ Pass' : '✗ Fail'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Installation Vehicles — editable (office can fix VRN / Make / Model even after submit) */}
        {c.category === 'new_installation' && Array.isArray(c.installation_vehicles) && c.installation_vehicles.length > 0 && (
          <CInstallVehiclesEditor complaint={c} canEdit={can.edit} onRefresh={onRefresh} />
        )}
      </div>
    </div>
  );
}

function CNewForm({ customers, employees, onSaved, onCancel }) {
  const [form, setForm] = React.useState({
    submitted_by: 'Staff', customer_id: '', customer_name: '', vehicle_id: '', vehicle_name: '',
    category: '', subject: '', detail: '', priority: 'medium', contact: '', source: 'crm',
  });
  const [saving, setSaving]         = React.useState(false);
  const [charCount, setCharCount]   = React.useState(0);
  const [selCust, setSelCust]       = React.useState(null);
  const [custQuery, setCustQuery]   = React.useState('');
  const [showCustDrop, setShowCustDrop] = React.useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Filter customers by ID, name, or cell number
  const filteredCusts = custQuery.trim()
    ? customers.filter(c =>
        String(c.id).toLowerCase().includes(custQuery.toLowerCase()) ||
        c.name.toLowerCase().includes(custQuery.toLowerCase()) ||
        (c.cell || '').includes(custQuery) ||
        (c.phone || '').includes(custQuery)
      ).slice(0, 10)
    : [];

  const handleCustSelect = (c) => {
    setSelCust(c);
    setCustQuery(c.name);
    setShowCustDrop(false);
    set('customer_id', c.id);
    set('customer_name', c.name);
    set('contact', c.cell || c.phone || '');
    set('vehicle_id', ''); set('vehicle_name', '');
  };

  const handleCustClear = () => {
    setSelCust(null); setCustQuery(''); setShowCustDrop(false);
    set('customer_id', ''); set('customer_name', ''); set('contact', '');
    set('vehicle_id', ''); set('vehicle_name', '');
  };

  const handleVehChange = (vid) => {
    const v = selCust?.vehicles?.find(v => String(v.id) === String(vid));
    set('vehicle_id', vid);
    set('vehicle_name', v?.vehicleNo || '');
  };

  const handleSubmit = async () => {
    if (!form.category) { alert('Please select a category.'); return; }
    if (!form.subject.trim()) { alert('Please enter a subject.'); return; }
    if (!form.detail.trim()) { alert('Please describe the issue.'); return; }
    setSaving(true);
    try { await onSaved(form); }
    catch (e) { alert('Failed: ' + e.message); setSaving(false); }
  };

  const activeVehicles = selCust?.vehicles?.filter(v => v.status === 'active') || [];

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div>
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Plus size={20} className="text-red-500"/> New Complaint</h3>
          <p className="text-sm text-slate-500 mt-0.5">Filed by staff via CRM</p>
        </div>
        <button onClick={onCancel} className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500"><X size={18}/></button>
      </div>

      <div className="p-4 space-y-5">
        <div className="grid grid-cols-2 gap-5">
          {/* Customer search by ID, name, or cell */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Customer</label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 text-slate-400" size={16}/>
              <input
                value={custQuery}
                onChange={e => { setCustQuery(e.target.value); setShowCustDrop(true); if (!e.target.value) handleCustClear(); }}
                onFocus={() => { if (custQuery) setShowCustDrop(true); }}
                placeholder="Search by ID, name or cell..."
                className="w-full pl-9 pr-9 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-slate-700 text-sm"
              />
              {selCust && (
                <button onClick={handleCustClear} className="absolute right-3 top-3.5 text-slate-400 hover:text-red-500"><X size={16}/></button>
              )}
              {showCustDrop && filteredCusts.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto">
                  {filteredCusts.map(c => (
                    <button key={c.id} onClick={() => handleCustSelect(c)}
                      className="w-full text-left px-4 py-2.5 hover:bg-red-50 transition-colors border-b border-slate-50 last:border-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                        <span className="text-xs text-slate-400 font-mono">#{c.id}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{c.cell || c.phone || '—'}</p>
                    </button>
                  ))}
                </div>
              )}
              {showCustDrop && custQuery.trim() && filteredCusts.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 px-4 py-3">
                  <p className="text-sm text-slate-400 italic">No customers found</p>
                </div>
              )}
            </div>
            {selCust && (
              <p className="text-xs text-green-600 font-medium mt-1.5 flex items-center gap-1">
                <Check size={12}/> Selected: {selCust.name} (ID: {selCust.id})
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicle</label>
            <select value={form.vehicle_id} onChange={e => handleVehChange(e.target.value)} disabled={!selCust}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-slate-700 disabled:bg-slate-50 disabled:text-slate-400">
              <option value="">— Select vehicle (optional) —</option>
              {activeVehicles.map(v => <option key={v.id} value={v.id}>{v.vehicleNo}{v.model ? ` (${v.model})` : ''}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Category <span className="text-red-500">*</span></label>
            <select value={form.category} onChange={e => set('category', e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-slate-700">
              <option value="">— Select category —</option>
              {COMPLAINT_FORM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Priority</label>
            <div className="grid grid-cols-3 gap-2">
              {COMPLAINT_PRIORITIES.map(p => (
                <button key={p.value} type="button" onClick={() => set('priority', p.value)}
                  className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${form.priority === p.value ? `${p.color} border-current` : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {p.icon} {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Subject <span className="text-red-500">*</span></label>
          <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Brief title of the issue" maxLength={200}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-slate-800" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Detail <span className="text-red-500">*</span></label>
          <textarea value={form.detail} onChange={e => { set('detail', e.target.value); setCharCount(e.target.value.length); }}
            placeholder="Describe the issue in detail..." rows={5} maxLength={2000}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all resize-none text-slate-800 placeholder-slate-400" />
          <p className="text-xs text-slate-400 text-right mt-1">{charCount} / 2000</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Contact
            {selCust && <span className="ml-2 text-xs text-green-600 font-medium">(auto-populated from customer)</span>}
          </label>
          <input value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="Phone or email for follow-up (optional)"
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-slate-800" />
        </div>
      </div>

      <div className="flex gap-3 px-6 pb-6">
        <button onClick={onCancel} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold hover:shadow-md hover:shadow-red-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          <Send size={16} className={saving ? 'animate-pulse' : ''}/>
          {saving ? 'Submitting...' : 'Submit Complaint'}
        </button>
      </div>
    </div>
  );
}

function CNewInstallationForm({ customers, employees, onSaved, onCancel }) {
  const TESTS = [
    { key: 'ign_on',       label: 'Ignition On'    },
    { key: 'ign_off',      label: 'Ignition Off'   },
    { key: 'batt_tamper',  label: 'Battery Tamper' },
    { key: 'batt_resume',  label: 'Battery Resume' },
    { key: 'eng_kill',     label: 'Engine Kill'    },
    { key: 'eng_release',  label: 'Engine Release' },
  ];

  // ── state ──────────────────────────────────────────────────────────────
  const [custQuery,     setCustQuery]     = React.useState('');
  const [showCustDrop,  setShowCustDrop]  = React.useState(false);
  const [selCust,       setSelCust]       = React.useState(null);

  const [custName,      setCustName]      = React.useState('');
  const [contact,       setContact]       = React.useState('');
  const [city,          setCity]          = React.useState('');
  const [installDate,   setInstallDate]   = React.useState('');
  const [technicianId,  setTechnicianId]  = React.useState('');
  const [priority,      setPriority]      = React.useState('medium');
  const [setupCharges,  setSetupCharges]  = React.useState('');
  const [amc,           setAmc]           = React.useState('');
  const [notes,         setNotes]         = React.useState('');
  const [numVehicles,   setNumVehicles]   = React.useState(1);

  // Per-vehicle rows: [{ vrn, imei, tests: {ign_on:null, ...} }]
  // null = pending, true = pass, false = fail
  const makeRow = () => ({
    vrn:   '',
    make:  '',
    model: '',
    imei:  '',
    technicianId: '',
    tests: Object.fromEntries(TESTS.map(t => [t.key, null])),
  });
  const [vehicles, setVehicles] = React.useState([makeRow()]);

  const [saving, setSaving] = React.useState(false);

  // ── customer search / autocomplete ────────────────────────────────────
  // Server-backed search — the `customers` prop is only the current page, so we
  // query the searchable API as the office types (that's why in-memory search missed most).
  const [filteredCusts, setFilteredCusts] = React.useState([]);
  React.useEffect(() => {
    const q = custQuery.trim();
    if (!q || (selCust && selCust.name === custQuery)) { setFilteredCusts([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.customers.list(1, 8, q);
        const rows = ((res && res.data) ? res.data : []).map(c => ({
          id: String(c.id), name: c.name, cell: c.cell || '', phone: c.phone || '', city: c.city || '',
        }));
        if (!cancelled) setFilteredCusts(rows);
      } catch (e) { if (!cancelled) setFilteredCusts([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [custQuery, selCust]);

  const selectCustomer = (c) => {
    setSelCust(c);
    setCustQuery(c.name);
    setCustName(c.name);
    setContact(c.cell || c.phone || '');
    setCity(c.city || '');
    setShowCustDrop(false);
  };

  const clearCustomer = () => {
    setSelCust(null); setCustQuery('');
    setCustName(''); setContact(''); setCity('');
  };

  // ── vehicle row helpers ───────────────────────────────────────────────
  const syncVehicleCount = (n) => {
    const count = Math.max(1, Math.min(50, Number(n) || 1));
    setNumVehicles(count);
    setVehicles(prev => {
      if (count > prev.length) return [...prev, ...Array(count - prev.length).fill(null).map(makeRow)];
      return prev.slice(0, count);
    });
  };

  const updateVehicle = (idx, field, value) =>
    setVehicles(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));

  // ── submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!custName.trim())    { alert('Customer name is required.');         return; }
    if (!contact.trim())     { alert('Contact number is required.');        return; }
    if (!city.trim())        { alert('Area / City is required.');           return; }
    if (!installDate)        { alert('Installation date is required.');     return; }

    // Build a structured detail block for a set of vehicles — human-readable AND
    // machine-parseable by the tech app.
    const mkDetail = (vlist) => {
      const vehicleLines = vlist.map((v, i) =>
        `Vehicle ${i + 1}: VRN=${v.vrn || 'TBD'} | IMEI=${v.imei || 'pending'} | Tests=${TESTS.map(t => `${t.label}=${v.tests[t.key] === true ? '✓' : v.tests[t.key] === false ? '✗' : '?'}`).join(', ')}`
      ).join('\n');
      return [
        `--- Customer ---`,
        `Name: ${custName}`,
        `Contact: ${contact}`,
        `City: ${city}`,
        ``,
        `--- Installation ---`,
        `Date: ${installDate}`,
        setupCharges ? `Setup Charges: Rs. ${Number(setupCharges).toLocaleString()} per vehicle` : '',
        amc          ? `AMC: Rs. ${Number(amc).toLocaleString()} per vehicle/year` : '',
        ``,
        `--- Vehicles (${vlist.length}) ---`,
        vehicleLines,
        notes.trim() ? `\n--- Notes ---\n${notes.trim()}` : '',
      ].filter(v => v !== '').join('\n');
    };

    // One payload for the given vehicle set; assigned tech = the row's own, else the default.
    const mkPayload = (vlist, techId) => {
      const single = vlist.length === 1 && vlist[0].vrn;
      const payload = {
        submitted_by:  'Staff',
        customer_id:   selCust ? selCust.id : '',
        customer_name: custName,
        vehicle_id:    '',
        vehicle_name:  '',
        category:      'new_installation',
        subject:       `New Installation – ${custName}${city ? ', ' + city : ''}` +
                       (single ? ` (${vlist[0].vrn})` : ` (${vlist.length} vehicle${vlist.length > 1 ? 's' : ''})`),
        detail:        mkDetail(vlist),
        priority,
        contact,
        source:        'crm',
        install_date:  installDate,
        install_city:  city,
        setup_charges: setupCharges || '',
        inst_amc:      amc || '',
        vehicles:      vlist.map(v => ({ vehicle_no: v.vrn || '', make: v.make || '', model: v.model || '' })),
      };
      const t = techId || technicianId;
      if (t) payload.assigned_to = parseInt(t);
      return payload;
    };

    setSaving(true);
    try {
      if (vehicles.length > 1) {
        // Each vehicle becomes its own ticket, independently assignable to a technician.
        await onSaved(vehicles.map(v => mkPayload([v], v.technicianId)));
      } else {
        await onSaved(mkPayload(vehicles, vehicles[0] && vehicles[0].technicianId));
      }
    }
    catch (e) { alert('Failed: ' + e.message); setSaving(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-800 text-sm";
  const labelCls = "block text-sm font-semibold text-slate-700 mb-2";
  const disabledCls = "w-full px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-slate-400 text-sm cursor-not-allowed select-none";

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div>
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <Zap size={18} className="text-green-600"/>
            </span>
            New Installation
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Schedule a tracker installation for an existing customer
          </p>
        </div>
        <button onClick={onCancel} className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500">
          <X size={18}/>
        </button>
      </div>

      <div className="p-4 space-y-7">

        {/* ── Section 1: Customer ── */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Customer Information</p>
          <div className="grid grid-cols-2 gap-5">

            {/* Customer search */}
            <div className="col-span-2">
              <label className={labelCls}>Search Customer <span className="text-slate-400 font-normal text-xs">(by name, ID or cell)</span></label>
              <div className="relative">
                <Search size={15} className="absolute left-3.5 top-3.5 text-slate-400"/>
                <input
                  value={custQuery}
                  onChange={e => { setCustQuery(e.target.value); setShowCustDrop(true); if (!e.target.value) clearCustomer(); }}
                  onFocus={() => { if (custQuery) setShowCustDrop(true); }}
                  placeholder="Type customer name, ID, or cell number..."
                  className="w-full pl-10 pr-9 py-3 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-700 text-sm"
                />
                {selCust && (
                  <button onClick={clearCustomer} className="absolute right-3 top-3.5 text-slate-400 hover:text-red-500 transition-colors">
                    <X size={16}/>
                  </button>
                )}
                {/* Dropdown */}
                {showCustDrop && filteredCusts.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                    {filteredCusts.map(c => (
                      <button key={c.id} onClick={() => selectCustomer(c)}
                        className="w-full text-left px-4 py-3 hover:bg-green-50 transition-colors border-b border-slate-50 last:border-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                          <span className="text-xs text-slate-400 font-mono">#{c.id}</span>
                        </div>
                        <div className="flex gap-3 mt-0.5 text-xs text-slate-500">
                          {(c.cell || c.phone) && <span>{c.cell || c.phone}</span>}
                          {c.city && <span>{c.city}</span>}
                          <span className="ml-auto">{c.vehicles?.length || 0} vehicle{c.vehicles?.length !== 1 ? 's' : ''}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {showCustDrop && custQuery.trim() && filteredCusts.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 px-4 py-3">
                    <p className="text-sm text-slate-400 italic">No customers found</p>
                  </div>
                )}
              </div>
              {selCust && (
                <p className="text-xs text-green-600 font-semibold mt-1.5 flex items-center gap-1">
                  <Check size={12}/> Selected: {selCust.name} · ID {selCust.id} · {selCust.vehicles?.length || 0} existing vehicle{selCust.vehicles?.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Customer Name */}
            <div>
              <label className={labelCls}>Customer Name <span className="text-red-500">*</span></label>
              <input value={custName} onChange={e => setCustName(e.target.value)}
                placeholder="Auto-filled from selection or type manually"
                className={inputCls} />
            </div>

            {/* Contact */}
            <div>
              <label className={labelCls}>Contact Number <span className="text-red-500">*</span></label>
              <input value={contact} onChange={e => setContact(e.target.value)}
                placeholder="Primary cell number"
                className={inputCls} />
            </div>

            {/* City */}
            <div>
              <label className={labelCls}>Area / City <span className="text-red-500">*</span></label>
              <input value={city} onChange={e => setCity(e.target.value)}
                placeholder="e.g. Lahore, DHA Phase 5"
                className={inputCls} />
            </div>

            {/* Installation Date */}
            <div>
              <label className={labelCls}>Date of Installation <span className="text-red-500">*</span></label>
              <input type="date" value={installDate} onChange={e => setInstallDate(e.target.value)}
                className={inputCls} />
            </div>
          </div>
        </div>

        {/* ── Section 2: Vehicles ── */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Vehicles</p>
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-slate-600">No. of Vehicles</label>
              <input
                type="number" min="1" max="50"
                value={numVehicles}
                onChange={e => syncVehicleCount(e.target.value)}
                className="w-16 px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-green-500 outline-none text-sm font-bold text-slate-800 text-center"
              />
            </div>
          </div>

          {numVehicles > 1 && (
            <div className="mb-4 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-xs text-blue-700">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>Each vehicle becomes its <strong>own separate ticket</strong> — assign a technician per vehicle below. Leave a vehicle blank to use the default technician.</span>
            </div>
          )}

          {/* Vehicle rows */}
          <div className="space-y-3">
            {vehicles.map((v, idx) => (
              <div key={idx} className="border-2 border-slate-100 rounded-xl overflow-hidden">

                {/* Row header */}
                <div className="bg-slate-50 px-4 py-2 flex items-center gap-2 border-b border-slate-100">
                  <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-black flex items-center justify-center shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-bold text-slate-700">Vehicle {idx + 1}</span>
                  {v.vrn && <span className="ml-auto text-xs font-mono font-semibold text-slate-500">{v.vrn}</span>}
                </div>

                <div className="p-4 space-y-3">
                  {/* VRN + IMEI */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                        Vehicle Reg No
                      </label>
                      <input
                        value={v.vrn}
                        onChange={e => updateVehicle(idx, 'vrn', e.target.value.toUpperCase())}
                        placeholder="e.g. LHR-1234"
                        className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-800 text-sm font-semibold uppercase"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
                        IMEI
                        <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs font-bold normal-case tracking-normal">Technician fills</span>
                      </label>
                      <div className={disabledCls}>
                        {v.imei || 'To be filled by technician'}
                      </div>
                    </div>
                  </div>

                  {/* Make + Model */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Make</label>
                      <input value={v.make} onChange={e => updateVehicle(idx, 'make', e.target.value)} placeholder="e.g. Toyota"
                        className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-800 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Model</label>
                      <input value={v.model} onChange={e => updateVehicle(idx, 'model', e.target.value)} placeholder="e.g. Corolla"
                        className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-800 text-sm" />
                    </div>
                  </div>

                  {/* Per-vehicle technician — each vehicle becomes its own ticket */}
                  {vehicles.length > 1 && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
                        Assign Technician
                        <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs font-bold normal-case tracking-normal">this vehicle</span>
                      </label>
                      <EmployeeSearchSelect employees={employees} value={v.technicianId}
                        onChange={(id) => updateVehicle(idx, 'technicianId', id)} activeOnly accent="green"
                        placeholder="Search technician by name or code…" />
                    </div>
                  )}

                  {/* Testing Parameters */}
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      Testing Parameters
                      <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs font-bold normal-case tracking-normal">Technician checks</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {TESTS.map(t => (
                        <div key={t.key}
                          className="flex items-center gap-2 bg-slate-50 border border-dashed border-slate-200 px-3 py-2 rounded-xl cursor-not-allowed">
                          {/* Disabled checkbox placeholder */}
                          <div className="w-4 h-4 rounded border-2 border-slate-300 bg-white flex items-center justify-center shrink-0">
                            {v.tests[t.key] === true  && <Check size={10} className="text-green-500"/>}
                            {v.tests[t.key] === false && <X    size={10} className="text-red-500"/>}
                          </div>
                          <span className="text-xs font-semibold text-slate-400">{t.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 3: Pricing ── */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Pricing</p>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Setup Charges <span className="text-slate-400 font-normal">(per vehicle)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                <input type="number" min="0" value={setupCharges} onChange={e => setSetupCharges(e.target.value)} placeholder="0"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-800 text-sm" />
              </div>
            </div>
            <div>
              <label className={labelCls}>AMC <span className="text-slate-400 font-normal">(per vehicle / year)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                <input type="number" min="0" value={amc} onChange={e => setAmc(e.target.value)} placeholder="0"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all text-slate-800 text-sm" />
              </div>
            </div>
          </div>
          {(setupCharges || amc) && numVehicles > 1 && (
            <div className="mt-3 flex gap-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
              {setupCharges && <span>Total Setup: <strong>Rs. {(Number(setupCharges) * numVehicles).toLocaleString()}</strong></span>}
              {amc          && <span>Total AMC: <strong>Rs. {(Number(amc) * numVehicles).toLocaleString()}</strong>/yr</span>}
            </div>
          )}
        </div>

        {/* ── Section 4: Assign + Priority + Notes ── */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>{numVehicles > 1 ? 'Default Technician' : 'Assign Technician'} {numVehicles > 1 && <span className="text-slate-400 font-normal text-xs">(for vehicles left unassigned)</span>}</label>
            <EmployeeSearchSelect employees={employees} value={technicianId} onChange={setTechnicianId} activeOnly accent="green"
              placeholder="Search technician by name or code…" />
          </div>
          <div>
            <label className={labelCls}>Priority</label>
            <div className="grid grid-cols-3 gap-2">
              {COMPLAINT_PRIORITIES.map(p => (
                <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                  className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${priority === p.value ? `${p.color} border-current` : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {p.icon} {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Additional Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any special instructions for the installation team..." rows={3} maxLength={2000}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none transition-all resize-none text-slate-800 placeholder-slate-400 text-sm" />
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="flex gap-3 px-6 pb-6">
        <button onClick={onCancel}
          className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold hover:shadow-md hover:shadow-green-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          <Zap size={16} className={saving ? 'animate-pulse' : ''}/>
          {saving ? 'Submitting...' : 'Submit Installation Request'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED HELPERS FOR ACTIVITY FORMS
// ─────────────────────────────────────────────────────────────────────────────

function useActiveVehicleSearch(customers) {
  // customers.php now returns a SLIM list (no nested vehicles) under smart-loading,
  // so we can't derive the picker list from `customers`. Fetch all active vehicles
  // from the dedicated lightweight endpoint instead.
  const [allVehicles, setAllVehicles] = React.useState([]);

  React.useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/active_vehicles.php`)
      .then(r => r.json())
      .then(j => {
        if (!alive || !j || !j.success) return;
        setAllVehicles((j.data || []).map(v => ({
          vehicleId:    v.id,
          vehicleNo:    v.registration || '',
          imei:         v.device_imei  || '',
          deviceName:   v.device_type  || '',
          serviceUpto:  v.service_upto || '',
          amc:          v.amc          || '',
          customerId:   v.customer_id,
          customerName: v.customer_name || '',
          contact:      (v.contact || '').trim(),
          city:         v.city || '',
        })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const search = (query) => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return allVehicles.filter(v =>
      (v.vehicleNo || '').toLowerCase().includes(q) ||
      (v.customerName || '').toLowerCase().includes(q) ||
      (v.imei || '').includes(q)
    ).slice(0, 10);
  };

  return { allVehicles, search };
}

const frmInput    = "w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all text-slate-800 text-sm";
const frmLabel    = "block text-sm font-semibold text-slate-700 mb-2";
const frmSection  = "text-xs font-bold text-slate-400 uppercase tracking-wider mb-4";
const frmDisabled = "w-full px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 text-slate-400 text-sm cursor-not-allowed";

function VehicleSearchField({ label, placeholder, onSelect, selectedVehicle, onClear, customers }) {
  const [query, setQuery]       = React.useState('');
  const [results, setResults]   = React.useState([]);
  const [showDrop, setShowDrop] = React.useState(false);
  const { search } = useActiveVehicleSearch(customers);

  React.useEffect(() => {
    if (selectedVehicle) setQuery(selectedVehicle.vehicleNo);
  }, [selectedVehicle]);

  const handleInput = (val) => {
    setQuery(val);
    setShowDrop(true);
    setResults(val.trim() ? search(val) : []);
    if (!val) { onClear(); }
  };

  return (
    <div>
      <label className={frmLabel}>{label}</label>
      <div className="relative">
        <Car className="absolute left-3 top-3.5 text-slate-400" size={15} />
        <input value={query} onChange={e => handleInput(e.target.value)}
          onFocus={() => { if (query) setShowDrop(true); }}
          placeholder={placeholder || 'Search vehicle no., customer, IMEI...'}
          className={`w-full pl-10 pr-9 py-3 rounded-xl border-2 ${selectedVehicle ? 'border-green-400 bg-green-50' : 'border-slate-200'} focus:border-red-500 outline-none transition-all text-slate-700 text-sm`} />
        {selectedVehicle && (
          <button onClick={() => { onClear(); setQuery(''); setShowDrop(false); }}
            className="absolute right-3 top-3.5 text-slate-400 hover:text-red-500">
            <X size={15}/>
          </button>
        )}
        {showDrop && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
            {results.map((v, i) => (
              <button key={i} onClick={() => { onSelect(v); setQuery(v.vehicleNo); setShowDrop(false); }}
                className="w-full text-left px-4 py-3 hover:bg-red-50 border-b border-slate-50 last:border-0 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-800 text-sm">{v.vehicleNo}</span>
                  <span className="text-xs font-mono text-slate-400">{v.imei || 'No IMEI'}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{v.customerName} · {v.contact || '—'}</p>
              </button>
            ))}
          </div>
        )}
        {showDrop && query.trim() && results.length === 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 px-4 py-3">
            <p className="text-sm text-slate-400 italic">No active vehicles found</p>
          </div>
        )}
      </div>
      {selectedVehicle && (
        <p className="text-xs text-green-600 font-semibold mt-1.5 flex items-center gap-1">
          <Check size={11}/> {selectedVehicle.customerName} · IMEI: {selectedVehicle.imei || 'None'} · Service upto: {selectedVehicle.serviceUpto || '—'}
        </p>
      )}
    </div>
  );
}

function PriorityPicker({ value, onChange }) {
  return (
    <div>
      <label className={frmLabel}>Priority</label>
      <div className="grid grid-cols-3 gap-2">
        {COMPLAINT_PRIORITIES.map(p => (
          <button key={p.value} type="button" onClick={() => onChange(p.value)}
            className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
              value === p.value ? `${p.color} border-current` : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}>
            {p.icon} {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CFormHeader({ title, subtitle, color, onCancel }) {
  return (
    <div className="flex items-center justify-between p-4 border-b border-slate-100">
      <div>
        <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
            <Wrench size={16} className="text-white" />
          </span>
          {title}
        </h3>
        <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
      </div>
      <button onClick={onCancel} className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500">
        <X size={18}/>
      </button>
    </div>
  );
}

function TechnicianPicker({ value, onChange, employees }) {
  return (
    <div>
      <label className={frmLabel}>Assign Technician</label>
      <EmployeeSearchSelect employees={employees} value={value} onChange={onChange} activeOnly placeholder="Search technician by name or code…" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVAL FORM
// ─────────────────────────────────────────────────────────────────────────────
function CRemovalForm({ customers, employees, onSaved, onCancel, prefillVehicle }) {
  const [selVehicle,   setSelVehicle]   = React.useState(prefillVehicle || null);
  const [removalType,  setRemovalType]  = React.useState('permanent');
  const [technicianId, setTechnicianId] = React.useState('');
  const [priority,     setPriority]     = React.useState('medium');
  const [installDate,  setInstallDate]  = React.useState('');
  const [notes,        setNotes]        = React.useState('');
  const [saving,       setSaving]       = React.useState(false);

  const handleSubmit = async () => {
    if (!selVehicle)  { alert('Please select the vehicle to remove.'); return; }
    if (!installDate) { alert('Please set the removal date.'); return; }

    const payload = {
      submitted_by:    'Staff',
      customer_id:     selVehicle.customerId,
      customer_name:   selVehicle.customerName,
      vehicle_id:      String(selVehicle.vehicleId),
      vehicle_name:    selVehicle.vehicleNo,
      vehicle_id_from: selVehicle.vehicleId,
      imei_from:       selVehicle.imei || '',
      category:        'removal',
      removal_type:    removalType,
      subject:         `Removal (${removalType}) \u2013 ${selVehicle.vehicleNo} \u00B7 ${selVehicle.customerName}`,
      detail:          notes.trim() || `Tracker removal for vehicle ${selVehicle.vehicleNo}.`,
      priority,
      contact:         selVehicle.contact,
      source:          'crm',
      install_date:    installDate,
      install_city:    selVehicle.city || '',
    };
    if (technicianId) payload.assigned_to = parseInt(technicianId);

    setSaving(true);
    try { await onSaved(payload); }
    catch (e) { alert('Failed: ' + e.message); setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">
      <CFormHeader title="New Removal" subtitle="Remove a tracker from a vehicle" color="bg-red-600" onCancel={onCancel} />
      <div className="p-4 space-y-6">
        <div>
          <p className={frmSection}>Vehicle to Remove From</p>
          <VehicleSearchField label="Select Vehicle *" customers={customers}
            selectedVehicle={selVehicle} onSelect={v => setSelVehicle(v)} onClear={() => setSelVehicle(null)} />
        </div>

        {selVehicle && (
          <div>
            <p className={frmSection}>Removal Details</p>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className={frmLabel}>Removal Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: 'permanent', label: '\u2715 Permanent', bg: 'bg-red-100 text-red-700'    },
                    { val: 'temporary', label: '\u2194 Temporary', bg: 'bg-amber-100 text-amber-700' },
                  ].map(opt => (
                    <button key={opt.val} type="button" onClick={() => setRemovalType(opt.val)}
                      className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        removalType === opt.val ? `${opt.bg} border-current` : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {removalType === 'temporary' && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-start gap-2">
                    <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0"/>
                    <p className="text-xs text-amber-700 font-medium">A linked <strong>Transfer</strong> ticket will be auto-created for vehicle B installation.</p>
                  </div>
                )}
              </div>
              <div>
                <label className={frmLabel}>Removal Date *</label>
                <input type="date" value={installDate} onChange={e => setInstallDate(e.target.value)} className={frmInput} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">IMEI Being Removed <span className="ml-1 bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded font-normal">auto-filled</span></label>
                <div className={frmDisabled}>{selVehicle.imei || 'No IMEI on vehicle'}</div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">Service Upto <span className="ml-1 bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded font-normal">auto-filled</span></label>
                <div className={frmDisabled}>{selVehicle.serviceUpto || '—'}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-5">
          <TechnicianPicker value={technicianId} onChange={setTechnicianId} employees={employees} />
          <PriorityPicker value={priority} onChange={setPriority} />
          <div className="col-span-2">
            <label className={frmLabel}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any special instructions..." rows={3} maxLength={2000}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none transition-all resize-none text-slate-800 placeholder-slate-400 text-sm" />
          </div>
        </div>
      </div>
      <div className="flex gap-3 px-6 pb-6">
        <button onClick={onCancel} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold hover:shadow-md hover:shadow-red-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          <Wrench size={16} className={saving ? 'animate-pulse' : ''}/>
          {saving ? 'Submitting...' : 'Submit Removal'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// REDO MODULE
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// GPS SERVICE dialog — Extend (expired → until a date, max 30 days, reason) or
// Block (valid → stop tracking now, reason). Works on one or many vehicles
// (bulk "Extend all" / "Block all"). Server: reconciliation.php extend_service /
// block_service — both logged with the user's name.
// ─────────────────────────────────────────────────────────────────────────────
function GpsServiceDialog({ mode, vehicles, apiBaseUrl, userId, onClose, onDone }) {
  const [until, setUntil]   = React.useState('');
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const isBlock  = mode === 'block';
  const pkToday  = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
  const plus     = (n) => { const d = new Date(pkToday + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };
  const maxDate  = plus(30);
  const fmt      = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  const save = async () => {
    if (!isBlock && !until) { alert('Choose how long to extend.'); return; }
    if (!reason.trim()) { alert('Please write a reason.'); return; }
    setSaving(true);
    const errors = [];
    for (const v of vehicles) {
      try {
        const res = await fetch(`${apiBaseUrl}/reconciliation.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(isBlock
            ? { action: 'block_service', vehicle_id: v.vehicle_id, reason: reason.trim(), user_id: userId }
            : { action: 'extend_service', vehicle_id: v.vehicle_id, until_date: until, reason: reason.trim(), user_id: userId }),
        });
        const j = await res.json();
        if (!j.success) errors.push(`${v.vehicle_no}: ${j.error || 'failed'}`);
      } catch (e) { errors.push(`${v.vehicle_no}: ${e.message}`); }
    }
    setSaving(false);
    if (errors.length) alert('Some vehicles could not be updated:\n' + errors.join('\n'));
    onDone();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className={`font-bold text-lg ${isBlock ? 'text-red-700' : 'text-slate-800'}`}>{isBlock ? 'Block Service' : 'Extend Service'}{vehicles.length > 1 ? ` (${vehicles.length} vehicles)` : ''}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className={`rounded-xl p-3 text-sm space-y-1 ${isBlock ? 'bg-red-50 border border-red-100' : 'bg-slate-50'}`}>
            {vehicles.map(v => (
              <p key={v.vehicle_id} className="text-slate-600">
                <span className="font-mono font-semibold text-slate-800">{v.vehicle_no}</span>
                {' · '}{v.expiry_label}
              </p>
            ))}
          </div>
          {isBlock ? (
            <p className="text-sm text-slate-600">Tracking will stop <b>immediately</b>. It resumes when payment is recorded or the service is extended again.</p>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Extend until *</label>
              <div className="flex gap-2 mb-2">
                {[3, 7, 15].map(n => (
                  <button key={n} type="button" onClick={() => setUntil(plus(n))}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition-colors ${until === plus(n) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    +{n} days
                  </button>
                ))}
              </div>
              <input type="date" value={until} min={pkToday} max={maxDate} onChange={e => setUntil(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all" />
              <p className="text-xs text-slate-400 mt-1">
                {until ? `Service will run until ${fmt(until)}, 11:59 PM.` : 'Pick +3 / +7 / +15 days or a date.'} Max {fmt(maxDate)}.
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Reason *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              placeholder={isBlock ? 'e.g. no response on 3 follow-up calls' : 'e.g. customer promised payment on 8 Nov'}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all resize-none text-sm" />
            <p className="text-xs text-slate-400 mt-1">Saved with your name in the IMEI Activity Log.</p>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={save} disabled={saving || (!isBlock && !until) || !reason.trim()}
              className={`flex-1 py-3 text-white rounded-xl font-semibold hover:shadow-md disabled:opacity-40 transition-all ${isBlock ? 'bg-gradient-to-r from-red-600 to-red-500' : 'bg-gradient-to-r from-emerald-500 to-green-500'}`}>
              {saving ? 'Saving...' : (isBlock ? 'Block Service' : 'Extend Service')}
            </button>
            <button onClick={onClose} className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-all">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SALESPERSON COMMISSIONS (Invoices → Commissions) — same flow as technician payments.
// A row appears when a vehicle flagged "invoice full setup charges" with a commission
// has its setup invoice fully paid (server side, payments.php). Paying here also logs a
// cashflow outflow under "Sales Commission".
// ─────────────────────────────────────────────────────────────────────────────
function CommissionsPanel({ apiBaseUrl, canPay }) {
  const [rows, setRows]         = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [search, setSearch]     = React.useState('');
  const [statusF, setStatusF]   = React.useState('all');
  const [payModal, setPayModal] = React.useState(null);
  const [payInput, setPayInput] = React.useState('');
  const [payDate, setPayDate]   = React.useState(new Date().toISOString().split('T')[0]);
  const [paying, setPaying]     = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/commission_payments.php`);
      const data = await res.json();
      if (data.success) setRows(data.data || []);
    } catch (e) { console.error('Failed to load commissions', e); }
    finally { setLoading(false); }
  }, [apiBaseUrl]);
  React.useEffect(() => { load(); }, [load]);

  const statusOf = (r) => {
    const total = parseFloat(r.amount || 0), paid = parseFloat(r.paid_amount || 0);
    if (paid <= 0) return 'pending';
    if (paid >= total) return 'paid';
    return 'partial';
  };
  const rs = (n) => `Rs. ${parseFloat(n || 0).toLocaleString()}`;

  const q = search.toLowerCase();
  const filtered = rows.filter(r => {
    const st = statusOf(r);
    const matchQ = !q || [r.salesperson_name, r.vehicle_no, r.customer_name, r.invoice_number].some(v => (v || '').toLowerCase().includes(q));
    const matchS = statusF === 'all' || (statusF === 'pending' ? st !== 'paid' : st === statusF);
    return matchQ && matchS;
  });
  const totalAmt  = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + parseFloat(r.paid_amount || 0), 0);

  const handlePay = async () => {
    const remaining = parseFloat(payModal.amount) - parseFloat(payModal.paid_amount || 0);
    const adding = parseFloat(payInput);
    if (!adding || adding <= 0) { alert('Please enter a valid amount'); return; }
    if (adding > remaining + 0.01) { alert(`Cannot exceed remaining Rs. ${remaining.toLocaleString()}`); return; }
    setPaying(true);
    try {
      const res = await fetch(`${apiBaseUrl}/commission_payments.php`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: payModal.id, add_amount: adding, paid_date: payDate }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed');
      await load();
      setPayModal(null); setPayInput('');
    } catch (e) { alert('Failed to record payment: ' + e.message); }
    finally { setPaying(false); }
  };

  return (
    <div>
      {/* KPI tiles */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { l: 'Commissions', v: rows.length, c: '#64748b', vc: '#1e293b' },
          { l: 'Pending',     v: rs(totalAmt - totalPaid), c: '#d23b4e', vc: '#e11d48', mono: true },
          { l: 'Paid Out',    v: rs(totalPaid), c: '#1f9d63', vc: '#16a34a', mono: true },
        ].map((t, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2" style={{ borderLeft: `3px solid ${t.c}` }}>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t.l}</div>
            <div className={`text-lg font-extrabold tabular-nums leading-tight ${t.mono ? 'font-mono' : ''}`} style={{ color: t.vc }}>{t.v}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search salesperson, vehicle, customer, invoice…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-red-400 outline-none bg-slate-50" />
        </div>
        <select value={statusF} onChange={e => setStatusF(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none text-slate-600 font-medium">
          <option value="all">All status</option>
          <option value="pending">Pending</option>
          <option value="partial">Partially Paid</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {['Date', 'Salesperson', 'Vehicle', 'Customer', 'Invoice', 'Commission', 'Status', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">No commissions found</td></tr>
            ) : filtered.map(r => {
              const st = statusOf(r);
              const total = parseFloat(r.amount || 0), paid = parseFloat(r.paid_amount || 0), remaining = total - paid;
              return (
                <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.earned_date}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{r.salesperson_name || '—'}</div>
                    {r.salesperson_id
                      ? <div className="text-[11px] text-slate-400 capitalize">{r.employee_code ? `${r.employee_code} · ` : ''}{r.emp_type || 'permanent'}</div>
                      : <div className="text-[11px] font-semibold text-red-600">Not in Employees</div>}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{r.vehicle_no || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{r.customer_name || '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{r.invoice_number}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-semibold text-slate-800">{rs(total)}</span>
                    {remaining > 0 && paid > 0 && <p className="text-xs text-red-600 font-medium">{rs(remaining)} due</p>}
                  </td>
                  <td className="px-4 py-3">
                    {st === 'paid'    && <div><span className="px-2 py-1 rounded-lg text-xs font-bold bg-emerald-100 text-emerald-700">✓ Paid</span>{r.paid_date && <p className="text-xs text-slate-400 mt-0.5">{r.paid_date}</p>}</div>}
                    {st === 'partial' && <span className="px-2 py-1 rounded-lg text-xs font-bold bg-yellow-100 text-yellow-700">Partial</span>}
                    {st === 'pending' && <span className="px-2 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-600">Pending</span>}
                  </td>
                  <td className="px-4 py-3">
                    {st !== 'paid' && canPay && (
                      <button
                        onClick={() => {
                          if (!r.salesperson_id) { alert(`"${r.salesperson_name}" is not in Employees. Add them (permanent or freelancer) first.`); return; }
                          setPayModal(r); setPayInput(String(remaining)); setPayDate(new Date().toISOString().split('T')[0]);
                        }}
                        className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all font-semibold whitespace-nowrap"
                      >+ Pay</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Payment modal */}
      {payModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="font-bold text-slate-800 text-lg">Pay Commission</h3>
              <button onClick={() => setPayModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-1.5">
                <p className="text-slate-600">Salesperson: <span className="font-semibold text-slate-800">{payModal.salesperson_name}</span></p>
                <p className="text-slate-600">Vehicle: <span className="font-semibold text-slate-800">{payModal.vehicle_no}</span> · <span className="font-mono text-xs">{payModal.invoice_number}</span></p>
                <hr className="border-slate-200 my-2" />
                <p className="text-slate-600">Commission: <span className="font-bold text-slate-800">{rs(payModal.amount)}</span></p>
                <p className="text-slate-600">Already Paid: <span className="font-bold text-emerald-700">{rs(payModal.paid_amount)}</span></p>
                <p className="text-slate-600">Remaining: <span className="font-bold text-red-600">{rs(parseFloat(payModal.amount) - parseFloat(payModal.paid_amount || 0))}</span></p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Amount *</label>
                <input type="number" min="0" value={payInput} onChange={e => setPayInput(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Date *</label>
                <input type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handlePay} disabled={paying || !payInput || parseFloat(payInput) <= 0}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-md disabled:opacity-40 transition-all">
                  {paying ? 'Saving...' : 'Confirm Payment'}
                </button>
                <button onClick={() => setPayModal(null)} className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-all">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesModule({ customers, employees, inventory, vehicleModels, apiBaseUrl, currentUser, onInvoiceCreated }) {
  const [view,          setView]          = React.useState('list');
  const [activities,    setActivities]    = React.useState([]);
  const [loading,       setLoading]       = React.useState(true);
  const [search,        setSearch]        = React.useState('');
  const [typeFilter,    setTypeFilter]    = React.useState('all');
  const [paidFilter,    setPaidFilter]    = React.useState('all');
  const [payModal,      setPayModal]      = React.useState(null);  // activity record
  const [payInput,      setPayInput]      = React.useState('');
  const [payDate,       setPayDate]       = React.useState(new Date().toISOString().split('T')[0]);
  const [paying,        setPaying]        = React.useState(false);
  const [detail,        setDetail]        = React.useState(null);  // activity shown in the detail modal

  const loadActivities = React.useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${apiBaseUrl}/technician_payments.php`);
      const data = await res.json();
      if (data.success) setActivities(data.data || []);
    } catch(e) { console.error('Failed to load activities', e); }
    finally { setLoading(false); }
  }, [apiBaseUrl]);

  React.useEffect(() => { loadActivities(); }, [loadActivities]);

  const handleRedoSaved = async () => {
    await loadActivities();
    if (onInvoiceCreated) await onInvoiceCreated();
    setView('list');
  };

  const handleRTSaved = async () => {
    await loadActivities();
    if (onInvoiceCreated) await onInvoiceCreated();
    setView('list');
  };

  const handleOTSaved = async () => {
    if (onInvoiceCreated) await onInvoiceCreated(); // refresh vehicles/invoices cache
    setView('list');
  };

  const getTechStatus = (a) => {
    const total = parseFloat(a.amount || 0);
    const paid  = parseFloat(a.paid_amount || 0);
    if (total === 0)       return 'free';
    if (paid <= 0)         return 'pending';
    if (paid >= total)     return 'paid';
    return 'partial';
  };

  const handleAddPayment = async () => {
    if (!payModal || !payInput || parseFloat(payInput) <= 0) {
      alert('Please enter a valid amount'); return;
    }
    const total    = parseFloat(payModal.amount || 0);
    const already  = parseFloat(payModal.paid_amount || 0);
    const adding   = parseFloat(payInput);
    const newPaid  = already + adding;
    if (newPaid > total) {
      alert(`Cannot exceed total amount of Rs. ${total.toLocaleString()}`); return;
    }
    setPaying(true);
    try {
      const res = await fetch(`${apiBaseUrl}/technician_payments.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id:          payModal.id,
          add_amount:  adding,
          paid_date:   payDate,
        })
      });
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON response */ }
      if (!res.ok || (data && data.success === false)) {
        const msg = (data && (data.error || data.message)) || `Server error (${res.status})`;
        alert(`Failed to record payment: ${msg}`);
        return;
      }
      await loadActivities();
      setPayModal(null);
      setPayInput('');
    } catch(e) { alert('Failed to record payment: ' + e.message); }
    finally { setPaying(false); }
  };

  if (view === 'new_redo') {
    return (
      <div className="p-4">
        <RedoForm
          customers={customers}
          employees={employees}
          inventory={inventory}
          apiBaseUrl={apiBaseUrl}
          onSaved={handleRedoSaved}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  if (view === 'new_rt') {
    return (
      <div className="p-4">
        <RTForm
          customers={customers}
          employees={employees}
          inventory={inventory}
          vehicleModels={vehicleModels}
          apiBaseUrl={apiBaseUrl}
          currentUser={currentUser}
          onSaved={handleRTSaved}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  if (view === 'new_ot') {
    return (
      <div className="p-4">
        <OTForm
          customers={customers}
          apiBaseUrl={apiBaseUrl}
          currentUser={currentUser}
          onSaved={handleOTSaved}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  const filtered = activities.filter(a => {
    const status = getTechStatus(a);
    const matchSearch =
      (a.vehicle_no     || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.customer_name  || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.technician_name|| '').toLowerCase().includes(search.toLowerCase()) ||
      [a.imei, a.redo_old_imei, a.redo_new_imei, a.rt_old_imei, a.rt_new_imei]
        .some(i => (i || '').toLowerCase().includes(search.toLowerCase()));
    const matchType = typeFilter === 'all' || a.activity_type === typeFilter;
    const matchPaid = paidFilter === 'all' ||
      (paidFilter === 'paid'    && status === 'paid') ||
      (paidFilter === 'partial' && status === 'partial') ||
      // "Pending" = anything not fully paid (both full-pending and partial)
      (paidFilter === 'pending' && (status === 'pending' || status === 'partial'));
    return matchSearch && matchType && matchPaid;
  });

  const totalAmount  = activities.reduce((s,a) => s + parseFloat(a.amount||0), 0);
  const totalPaidOut = activities.reduce((s,a) => s + parseFloat(a.paid_amount||0), 0);
  const totalPending = totalAmount - totalPaidOut;

  const typeColor = { installation: 'bg-green-100 text-green-700', removal: 'bg-orange-100 text-orange-700', redo: 'bg-blue-100 text-blue-700', transfer: 'bg-amber-100 text-amber-700' };
  const typeLabel = { installation: '🔧 Installation', removal: '📦 Removal', redo: '🔄 REDO', transfer: '➡️ R&T' };
  const activityLabel = (a) => a.activity_type === 'transfer'
    ? `➡️ R&T${a.rt_role === 'transfer' ? ' (Transfer)' : a.rt_role === 'removal' ? ' (Removal)' : ''}`
    : (typeLabel[a.activity_type] || a.activity_type);
  const rs = (n) => `Rs. ${parseFloat(n || 0).toLocaleString()}`;
  const openPay = (a) => { setPayModal(a); setPayInput(''); setPayDate(new Date().toISOString().split('T')[0]); };
  const statusBadge = (status) => (
    <>
      {status === 'free'    && <span className="px-2 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-500">Free</span>}
      {status === 'paid'    && <span className="px-2 py-1 rounded-lg text-xs font-bold bg-emerald-100 text-emerald-700">✓ Paid</span>}
      {status === 'partial' && <span className="px-2 py-1 rounded-lg text-xs font-bold bg-yellow-100 text-yellow-700">Partial</span>}
      {status === 'pending' && <span className="px-2 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-600">Pending</span>}
    </>
  );

  return (
    <div className="p-4">

      {/* Page Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3">
            <Briefcase className="text-red-500" size={28} />
            Services
          </h2>
          <p className="text-slate-500 text-sm mt-1">Manage field activities and technician payments</p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={() => setView('new_redo')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-semibold text-sm hover:shadow-md hover:shadow-red-200 transition-all"
          >
            <Plus size={16} />
            New REDO
          </button>
          <button
            onClick={() => setView('new_rt')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold text-sm hover:shadow-md hover:shadow-orange-200 transition-all"
          >
            <ArrowRight size={15} /> New R&T
          </button>
          <button
            onClick={() => setView('new_ot')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white rounded-lg font-semibold text-sm hover:shadow-md hover:shadow-indigo-200 transition-all"
          >
            <Users size={15} /> Ownership Transfer
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-4">
        {[
          { l: 'Total',         v: activities.length,                                                 c: '#64748b', vc: '#1e293b' },
          { l: 'Installations', v: activities.filter(a => a.activity_type === 'installation').length, c: '#1f9d63', vc: '#16a34a' },
          { l: 'Removals',      v: activities.filter(a => a.activity_type === 'removal').length,       c: '#f5732b', vc: '#c2410c' },
          { l: 'REDOs',         v: activities.filter(a => a.activity_type === 'redo').length,          c: '#3b6fd2', vc: '#3b6fd2' },
          { l: 'R&T',           v: activities.filter(a => a.activity_type === 'transfer').length,      c: '#d97706', vc: '#b45309' },
          { l: 'Pending',       v: `Rs ${totalPending.toLocaleString()}`,                              c: '#d23b4e', vc: '#e11d48', mono: true },
          { l: 'Paid Out',      v: `Rs ${totalPaidOut.toLocaleString()}`,                              c: '#1f9d63', vc: '#16a34a', mono: true },
        ].map((t, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2" style={{ borderLeft: `3px solid ${t.c}` }}>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{t.l}</div>
            <div className={`text-lg font-extrabold tabular-nums leading-tight ${t.mono ? 'font-mono' : ''}`} style={{ color: t.vc }}>{t.v}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vehicle, customer, technician…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-red-400 outline-none bg-slate-50" />
        </div>
        {[['all', 'All'], ['installation', 'Installation'], ['removal', 'Removal'], ['redo', 'REDO'], ['transfer', 'R&T']].map(([v, l]) => (
          <button key={v} onClick={() => setTypeFilter(v)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${typeFilter === v ? 'bg-red-50 text-red-600 border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>{l}</button>
        ))}
        <select value={paidFilter} onChange={e => setPaidFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none text-slate-600 font-medium">
          <option value="all">All status</option>
          <option value="pending">Pending</option>
          <option value="partial">Partially Paid</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {/* Activity Records Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {['Date','Vehicle','Customer','Activity','Technician','Tech Amount','Status',''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">
                <Briefcase className="mx-auto mb-2 opacity-30" size={40} />
                <p>No activity records found</p>
              </td></tr>
            ) : filtered.map(a => {
              const status    = getTechStatus(a);
              const total     = parseFloat(a.amount || 0);
              const paid      = parseFloat(a.paid_amount || 0);
              const remaining = total - paid;
              return (
                <tr key={a.id} onClick={() => setDetail(a)} className="hover:bg-slate-50 transition-colors cursor-pointer">
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{a.activity_date}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{a.vehicle_no || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{a.customer_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-lg text-xs font-bold whitespace-nowrap ${typeColor[a.activity_type] || 'bg-slate-100 text-slate-600'}`}>
                      {activityLabel(a)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{a.technician_name || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {total > 0 ? (
                      <div>
                        <span className="font-semibold text-slate-800">{rs(total)}</span>
                        {remaining > 0 && <p className="text-xs text-red-600 font-medium">{rs(remaining)} due</p>}
                      </div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">{statusBadge(status)}</td>
                  <td className="px-4 py-3">
                    {(status === 'pending' || status === 'partial') && total > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openPay(a); }}
                        className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all font-semibold whitespace-nowrap"
                      >
                        + Pay
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Activity Detail Modal (row click) */}
      {detail && (() => {
        const a = detail;
        const status    = getTechStatus(a);
        const total     = parseFloat(a.amount || 0);
        const paid      = parseFloat(a.paid_amount || 0);
        const remaining = total - paid;
        const isRT   = a.reference_type === 'rt_record';
        const isRedo = a.reference_type === 'redo_record';
        const actAmt = parseFloat(a.activity_amount ?? a.redo_amount ?? 0);
        const Row = ({ l, children }) => (
          <div className="flex items-start justify-between gap-4 py-1.5">
            <span className="text-slate-500 text-sm">{l}</span>
            <span className="text-slate-800 text-sm font-semibold text-right break-all">{children}</span>
          </div>
        );
        const Mono = ({ v }) => v ? <span className="font-mono tracking-wide">{v}</span> : <span className="text-slate-400 font-normal">—</span>;
        const Section = ({ t, children }) => (
          <div className="rounded-xl border border-slate-200 px-4 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold pt-1">{t}</div>
            {children}
          </div>
        );
        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between p-5 border-b border-slate-200">
                <div>
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold ${typeColor[a.activity_type] || 'bg-slate-100 text-slate-600'}`}>{activityLabel(a)}</span>
                  <h3 className="font-bold text-slate-800 text-lg mt-2">{a.vehicle_no || '—'}</h3>
                  <p className="text-slate-500 text-sm">{a.customer_name || '—'} · {a.activity_date}</p>
                </div>
                <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
              </div>

              <div className="p-5 space-y-3 overflow-y-auto">
                {/* Tracker */}
                <Section t="Tracker">
                  <Row l={isRT && a.rt_role === 'removal' ? 'IMEI (removed)' : 'IMEI'}><Mono v={a.imei} /></Row>
                  {a.vehicle_imei && a.vehicle_imei !== a.imei && <Row l="Vehicle's current IMEI"><Mono v={a.vehicle_imei} /></Row>}
                  {a.vehicle_status && <Row l="Vehicle status"><span className="capitalize">{a.vehicle_status}</span></Row>}
                </Section>

                {/* R&T link: both vehicles + both trackers */}
                {isRT && (
                  <Section t="Removal & Transfer">
                    <Row l="From (Vehicle A)">{a.rt_from_vehicle_no || '—'}</Row>
                    <Row l="To (Vehicle B)">{a.rt_to_vehicle_no || '—'}</Row>
                    <Row l="Old IMEI (A)"><Mono v={a.rt_old_imei} /></Row>
                    <Row l="New IMEI (B)"><Mono v={a.rt_new_imei} /></Row>
                    {a.rt_old_imei && a.rt_old_imei === a.rt_new_imei && <Row l="Mode">Same tracker moved</Row>}
                    {a.rt_old_health && <Row l="Old tracker health"><span className="capitalize">{a.rt_old_health}</span></Row>}
                    <Row l="Removal tech">{a.rt_removal_tech || '—'}</Row>
                    <Row l="Install tech">{a.rt_install_tech || '—'}</Row>
                    <Row l="Transfer charge">{parseFloat(a.rt_amount) > 0 ? rs(a.rt_amount) : '—'}</Row>
                    {a.rt_invoice_number && <Row l="Invoice">{a.rt_invoice_number}</Row>}
                    {a.rt_reason && <Row l="Reason">{a.rt_reason}</Row>}
                  </Section>
                )}

                {isRedo && (
                  <Section t="REDO">
                    {a.redo_type && <Row l="Type"><span className="capitalize">{a.redo_type}</span></Row>}
                    <Row l="Old IMEI"><Mono v={a.redo_old_imei} /></Row>
                    <Row l="New IMEI"><Mono v={a.redo_new_imei} /></Row>
                    <Row l="Customer charge">{actAmt > 0 ? rs(actAmt) : '—'}</Row>
                    {a.redo_invoice_number && <Row l="Invoice">{a.redo_invoice_number}</Row>}
                    {a.redo_reason && <Row l="Reason">{a.redo_reason}</Row>}
                  </Section>
                )}

                <Section t="Customer">
                  <Row l="Outstanding (this vehicle)">
                    {parseFloat(a.vehicle_outstanding) > 0
                      ? <span className="text-red-600">{rs(a.vehicle_outstanding)}</span>
                      : <span className="text-emerald-600">Clear</span>}
                  </Row>
                </Section>

                <Section t="Technician payment">
                  <Row l="Technician">{a.technician_name || '—'}</Row>
                  <Row l="Total">{total > 0 ? rs(total) : '—'}</Row>
                  <Row l="Paid"><span className="text-emerald-700">{paid > 0 ? rs(paid) : '—'}</span></Row>
                  <Row l="Remaining"><span className="text-red-600">{remaining > 0 ? rs(remaining) : '—'}</span></Row>
                  {a.paid_date && <Row l="Paid date">{a.paid_date}</Row>}
                  <Row l="Status">{statusBadge(status)}</Row>
                </Section>

                {a.notes && (
                  <Section t="Notes"><p className="text-sm text-slate-700 py-1.5">{a.notes}</p></Section>
                )}
              </div>

              <div className="flex gap-3 p-5 border-t border-slate-200">
                {(status === 'pending' || status === 'partial') && total > 0 && (
                  <button onClick={() => { setDetail(null); openPay(a); }}
                    className="flex-1 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-md transition-all">
                    + Pay Technician
                  </button>
                )}
                <button onClick={() => setDetail(null)}
                  className="flex-1 py-2.5 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-all">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment Modal */}
      {payModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="font-bold text-slate-800 text-lg">Record Payment</h3>
              <button onClick={() => setPayModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Summary */}
              <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-1.5">
                <p className="text-slate-600">Technician: <span className="font-semibold text-slate-800">{payModal.technician_name}</span></p>
                <p className="text-slate-600">Vehicle: <span className="font-semibold text-slate-800">{payModal.vehicle_no}</span></p>
                <p className="text-slate-600">Activity: <span className="font-semibold text-slate-800">{typeLabel[payModal.activity_type] || payModal.activity_type}</span></p>
                <hr className="border-slate-200 my-2" />
                <p className="text-slate-600">Total Amount: <span className="font-bold text-slate-800">Rs. {parseFloat(payModal.amount).toLocaleString()}</span></p>
                <p className="text-slate-600">Already Paid: <span className="font-bold text-emerald-700">Rs. {parseFloat(payModal.paid_amount||0).toLocaleString()}</span></p>
                <p className="text-slate-600">Remaining: <span className="font-bold text-red-600">Rs. {(parseFloat(payModal.amount) - parseFloat(payModal.paid_amount||0)).toLocaleString()}</span></p>
              </div>

              {/* Amount input */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                  <input
                    type="number" min="0"
                    max={parseFloat(payModal.amount) - parseFloat(payModal.paid_amount||0)}
                    value={payInput}
                    onChange={e => setPayInput(e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all"
                  />
                </div>
                {payInput && parseFloat(payInput) > 0 && (
                  <p className="text-xs mt-1">
                    {parseFloat(payInput) >= (parseFloat(payModal.amount) - parseFloat(payModal.paid_amount||0))
                      ? <span className="text-emerald-600">✓ Full payment — will be marked as Paid</span>
                      : <span className="text-yellow-600">Partial — Rs. {(parseFloat(payModal.amount) - parseFloat(payModal.paid_amount||0) - parseFloat(payInput)).toLocaleString()} will remain</span>
                    }
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Payment Date *</label>
                <input
                  type="date" value={payDate} onChange={e => setPayDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none transition-all"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleAddPayment}
                  disabled={paying || !payInput || parseFloat(payInput) <= 0}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  {paying ? 'Saving...' : 'Confirm Payment'}
                </button>
                <button onClick={() => setPayModal(null)}
                  className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-all">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// REDO FORM (standalone — inventory + invoice aware)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// OWNERSHIP TRANSFER (OT) FORM
//   Move a vehicle from its current customer to another EXISTING customer.
//   Billing continues unchanged; blocked if the vehicle has any pending dues.
// ─────────────────────────────────────────────────────────────────────────────
function OTForm({ customers, apiBaseUrl, currentUser, onSaved, onCancel }) {
  const [veh,        setVeh]        = React.useState(null);
  const [custQuery,  setCustQuery]  = React.useState('');
  const [showCust,   setShowCust]   = React.useState(false);
  const [toCust,     setToCust]     = React.useState(null);
  const [otDate,     setOtDate]     = React.useState(new Date().toISOString().split('T')[0]);
  const [reason,     setReason]     = React.useState('');
  const [saving,     setSaving]     = React.useState(false);
  // Dues on the selected vehicle + who will pay them (Master/Admin only when dues exist).
  const [dues,       setDues]       = React.useState(null);
  const [duesOption, setDuesOption] = React.useState('');
  React.useEffect(() => {
    setDues(null); setDuesOption('');
    if (!veh?.vehicleId) return;
    let cancelled = false;
    fetch(`${apiBaseUrl}/service.php?action=ot_dues&vehicle_id=${veh.vehicleId}`)
      .then(r => r.json()).then(j => { if (!cancelled && j.success) setDues(j.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [veh?.vehicleId, apiBaseUrl]);
  const role = String(currentUser?.role_name || currentUser?.role || '').toLowerCase();
  const canTransferWithDues = role === 'master' || role === 'admin';
  const hasDues = (dues?.due || 0) > 0.01;
  const latestOpen = (dues?.latest?.balance || 0) > 0.01;   // options 2/3 only act on the latest invoice

  const matches = React.useMemo(() => {
    const q = custQuery.trim().toLowerCase();
    if (!q) return [];
    return (customers || []).filter(c =>
      String(c.id).includes(q) ||
      (c.name || '').toLowerCase().includes(q) ||
      (c.cell || '').includes(q) ||
      (c.phone || '').includes(q)
    ).slice(0, 8);
  }, [custQuery, customers]);

  const sameOwner = veh && toCust && String(toCust.id) === String(veh.customerId);
  const canSubmit = veh && toCust && otDate && !sameOwner && dues !== null
    && (!hasDues || (canTransferWithDues && (!latestOpen || duesOption)));

  const handleSubmit = async () => {
    if (!veh)     { alert('Select a vehicle.'); return; }
    if (!toCust)  { alert('Select the new owner (customer).'); return; }
    if (sameOwner){ alert('The new owner must be different from the current owner.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${apiBaseUrl}/service.php?action=ot`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'ot',
          vehicle_id: veh.vehicleId,
          to_customer_id: toCust.id,
          ot_date: otDate,
          reason,
          created_by: currentUser?.id || null,
          dues_option: hasDues ? (latestOpen ? duesOption : 'old_pays') : '',
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed');
      alert(data.message || 'Ownership transferred.');
      await onSaved();
    } catch (e) { alert('Failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const fld = "w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-sm";

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-gradient-to-r from-indigo-500 to-violet-500 rounded-xl p-4 text-white mb-4 shadow-md shadow-indigo-200">
        <div className="flex items-center gap-3">
          <Users size={26} />
          <div>
            <h2 className="text-xl font-bold">Ownership Transfer</h2>
            <p className="text-indigo-100 text-sm">Move a vehicle to another customer — billing continues unchanged</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-6">
        {/* Vehicle */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Vehicle to transfer</p>
          <VehicleSearchField
            label="Select Vehicle *"
            customers={customers}
            selectedVehicle={veh}
            onSelect={v => setVeh(v)}
            onClear={() => setVeh(null)}
          />
          {veh && (
            <div className="mt-3 bg-slate-50 rounded-xl p-3 flex flex-wrap gap-4 text-sm">
              <span className="text-slate-500">Current owner: <strong className="text-slate-800">{veh.customerName}</strong></span>
              <span className="text-slate-500">IMEI: <strong className="font-mono text-slate-800">{veh.imei || 'None'}</strong></span>
            </div>
          )}
          {veh && dues === null && <p className="text-[11px] text-slate-400 mt-2">Checking dues…</p>}
          {veh && dues && !hasDues && <p className="text-[11px] text-emerald-600 mt-2">No dues on this vehicle. Billing (AMC/service date) stays the same.</p>}
          {veh && hasDues && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm">
              <p className="font-semibold text-amber-800">This vehicle has pending dues of Rs. {Number(dues.due).toLocaleString()}</p>
              <ul className="mt-1 text-xs text-amber-700 space-y-0.5">
                {dues.open.map(o => (
                  <li key={o.invoice_number}>
                    {o.invoice_number}: Rs. {Number(o.balance).toLocaleString()}
                    {dues.latest && o.invoice_number === dues.latest.invoice_number ? ' (latest invoice)' : ' (older — stays with the current owner)'}
                  </li>
                ))}
              </ul>
              {!canTransferWithDues ? (
                <p className="mt-2 text-xs font-semibold text-red-600">Only a Master or Admin user can transfer a vehicle that has dues.</p>
              ) : !latestOpen ? (
                <p className="mt-2 text-xs text-slate-600">The latest invoice is paid — the older dues stay with the current owner (a follow-up note will be added).</p>
              ) : (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Latest invoice {dues.latest.invoice_number} — Rs. {Number(dues.latest.balance).toLocaleString()} due. Who will pay?</p>
                  {[
                    ['old_pays',  'Old customer will pay',  'Dues stay with the current owner; a follow-up note is added. Service date stays the same.'],
                    ['new_pays',  'New customer will pay',  'A new invoice for this balance is made in the new owner\'s name. Service date stays the same.'],
                    ['write_off', 'Old customer will not pay', 'Balance is written off and the invoice period is closed the day before the transfer. New owner\'s service starts from the transfer date.'],
                  ].map(([val, title, desc]) => (
                    <label key={val} className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${duesOption === val ? 'border-indigo-400 bg-white' : 'border-amber-200 bg-amber-50/50'}`}>
                      <input type="radio" name="ot_dues_option" value={val} checked={duesOption === val} onChange={() => setDuesOption(val)} className="mt-1" />
                      <span><span className="font-semibold text-slate-800">{title}</span><span className="block text-xs text-slate-500">{desc}</span></span>
                    </label>
                  ))}
                  {!duesOption && <p className="text-xs font-semibold text-red-600">Select one option to continue.</p>}
                  <p className="text-[11px] text-slate-500">Older unpaid invoices always stay with the current owner — they used that service.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {veh && (
        <>
          <hr className="border-slate-100" />
          {/* New owner */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">New owner (existing customer)</p>
            {toCust ? (
              <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{toCust.name}</p>
                  <p className="text-xs text-slate-500">{formatCustomerCode(toCust.id, toCust.type)}{toCust.cell ? ` · ${toCust.cell}` : ''}</p>
                </div>
                <button onClick={() => { setToCust(null); setCustQuery(''); }} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-3 text-slate-400" size={16} />
                <input value={custQuery} onChange={e => { setCustQuery(e.target.value); setShowCust(true); }} onFocus={() => setShowCust(true)}
                  placeholder="Search customer by name, code, or phone…" className={fld + ' pl-9'} />
                {showCust && custQuery && (
                  <div className="absolute z-20 w-full mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-md max-h-56 overflow-y-auto">
                    {matches.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-400">No customer found</div>
                    ) : matches.map(c => (
                      <button key={c.id} onClick={() => { setToCust(c); setShowCust(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 border-b border-slate-50 last:border-0 flex items-center justify-between">
                        <span className="font-semibold text-slate-800">{c.name}</span>
                        <span className="text-xs text-slate-400">{formatCustomerCode(c.id, c.type)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {sameOwner && <p className="text-xs text-red-600 mt-1">This is already the current owner — choose a different customer.</p>}
          </div>

          <hr className="border-slate-100" />
          {/* Date + reason */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Transfer Date *</label>
              <input type="date" value={otDate} onChange={e => setOtDate(e.target.value)} className={fld} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Reason / Remarks</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Optional notes" className={fld + ' resize-none'} />
            </div>
          </div>
        </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={!canSubmit || saving}
            className={`px-5 py-2.5 rounded-xl text-white font-semibold text-sm flex items-center gap-2 transition-all ${canSubmit && !saving ? 'bg-gradient-to-r from-indigo-500 to-violet-500 hover:shadow-md hover:shadow-indigo-200' : 'bg-slate-300 cursor-not-allowed'}`}>
            {saving ? 'Processing…' : <><Check size={16} /> Transfer Ownership</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVAL & TRANSFER (R&T) FORM
//   Remove the tracker from Vehicle A and fit a tracker on a NEW Vehicle B of the
//   same customer. Two technicians (removal + install) — can be the same person.
// ─────────────────────────────────────────────────────────────────────────────
function RTForm({ customers, employees, inventory, vehicleModels, apiBaseUrl, currentUser, onSaved, onCancel }) {
  const [vA,            setVA]            = React.useState(null);
  const [mode,          setMode]          = React.useState(''); // 'same' | 'different'
  // Vehicle B (new)
  const [bReg,          setBReg]          = React.useState('');
  const [bMake,         setBMake]         = React.useState('');
  const [bModel,        setBModel]        = React.useState('');
  const [bEngine,       setBEngine]       = React.useState('');
  const [bChassis,      setBChassis]      = React.useState('');
  const [bDivision,     setBDivision]     = React.useState('');

  const bMakeOptions  = React.useMemo(() => [...new Set((vehicleModels || []).map(m => m.make).filter(Boolean))].sort(), [vehicleModels]);
  const bModelOptions = React.useMemo(() => (vehicleModels || []).filter(m => m.make === bMake).map(m => m.model).filter(Boolean), [vehicleModels, bMake]);
  const [bDivisions, setBDivisions] = React.useState([]);
  // Technicians + amounts
  const [removalTechId, setRemovalTechId] = React.useState('');
  const [removalAmount, setRemovalAmount] = React.useState('0');
  const [installTechId, setInstallTechId] = React.useState('');
  const [installAmount, setInstallAmount] = React.useState('0');
  // New tracker (from install tech stock) + removed tracker health
  const [newImei,       setNewImei]       = React.useState('');
  const [imeiSearch,    setImeiSearch]    = React.useState('');
  const [oldHealth,     setOldHealth]     = React.useState('active');
  // Customer charge + meta
  const [rtAmount,      setRtAmount]      = React.useState('0');
  const [reason,        setReason]        = React.useState('');
  const [rtDate,        setRtDate]        = React.useState(new Date().toISOString().split('T')[0]);
  const [saving,        setSaving]        = React.useState(false);
  const [syncing,       setSyncing]       = React.useState(false);
  const [syncedInfo,    setSyncedInfo]    = React.useState(null); // { imei, techName }

  const removalTech    = employees.find(e => String(e.id) === String(removalTechId));
  const installTech    = employees.find(e => String(e.id) === String(installTechId));
  // In "same" mode the tracker on B is A's own IMEI; in "different" mode it's the synced/entered one.
  const effNewImei     = mode === 'same' ? (vA?.imei || '') : newImei;
  const newTrackerItem = inventory.find(i => i.imei === effNewImei);

  // Sync Vehicle B from GPS. Engine + chassis fill in both modes. In "different"
  // mode we also pull the new IMEI and auto-select the install technician from
  // whoever holds that tracker in inventory (like Reconciliation Add).
  const handleSyncB = async () => {
    if (!bReg.trim()) { alert('Enter the Vehicle B number first.'); return; }
    setSyncing(true);
    try {
      const res  = await fetch(`${apiBaseUrl}/vehicles.php?action=gpswox_by_vehicle&vehicle_no=${encodeURIComponent(bReg.trim())}`);
      const data = await res.json();
      const imei = data?.data?.imei || '';
      const eng  = data?.data?.engineNo || '';
      const cha  = data?.data?.chassisNo || '';
      if (eng) setBEngine(eng);
      if (cha) setBChassis(cha);
      if (mode === 'different') {
        if (!imei) { alert('No GPS device found for this number. Check the number or enter the IMEI manually.'); return; }
        setNewImei(imei);
        const invItem = inventory.find(i => i.imei === imei);
        // The new tracker MUST be sitting in a technician's stock (issued), else R&T can't proceed.
        if (!invItem || !invItem.issuedTo) {
          setInstallTechId('');
          setSyncedInfo({ imei, techName: '', notIssued: true });
          alert(`IMEI ${imei} is not issued to any technician.\nIssue this IMEI to a technician first, then do the R&T.`);
          return;
        }
        setInstallTechId(String(invItem.issuedTo));
        const techName = employees.find(e => String(e.id) === String(invItem.issuedTo))?.name || '';
        setSyncedInfo({ imei, techName });
      } else {
        setSyncedInfo({ imei: vA?.imei || '', techName: '', engineChassisOnly: true });
      }
    } catch (e) {
      alert('Sync failed: ' + e.message);
    } finally { setSyncing(false); }
  };

  // Load the selected customer's divisions for the Division dropdown.
  React.useEffect(() => {
    if (!vA?.customerId) { setBDivisions([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res  = await fetch(`${apiBaseUrl}/divisions.php?customer_id=${vA.customerId}`);
        const data = await res.json();
        if (!cancelled && data.success) setBDivisions(data.data || []);
      } catch (e) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [vA?.customerId, apiBaseUrl]);

  const canSubmit = vA && vA.imei && mode && bReg.trim() && removalTechId && installTechId && effNewImei && rtDate;

  const handleSubmit = async () => {
    if (!vA)      { alert('Select the source vehicle (A).'); return; }
    if (!vA.imei) { alert('Source vehicle has no tracker installed — nothing to remove.'); return; }
    if (!mode)            { alert('Choose Same IMEI or Different IMEI.'); return; }
    if (!bReg.trim())     { alert('Enter the new vehicle (B) registration.'); return; }
    if (!removalTechId)   { alert('Select the removal technician.'); return; }
    if (!installTechId)   { alert('Select the install technician.'); return; }
    if (!effNewImei)      { alert(mode === 'different' ? 'Use Sync to fetch the new IMEI or enter it manually.' : 'The source vehicle has no IMEI.'); return; }
    setSaving(true);
    try {
      const payload = {
        action:                  'rt',
        mode,
        from_vehicle_id:         vA.vehicleId,
        from_vehicle_no:         vA.vehicleNo,
        customer_id:             vA.customerId,
        customer_name:           vA.customerName,
        old_imei:                vA.imei,
        new_imei:                effNewImei,
        old_tracker_health:      mode === 'different' ? oldHealth : 'active',
        removal_technician_id:   parseInt(removalTechId),
        removal_technician_name: removalTech?.name || '',
        removal_tech_amount:     parseFloat(removalAmount) || 0,
        install_technician_id:   parseInt(installTechId),
        install_technician_name: installTech?.name || '',
        install_tech_amount:     parseFloat(installAmount) || 0,
        rt_amount:               parseFloat(rtAmount) || 0,
        reason,
        rt_date:                 rtDate,
        b_registration:          bReg.trim(),
        b_make:                  bMake.trim(),
        b_model:                 bModel.trim(),
        b_engine_number:         bEngine.trim(),
        b_chassis_number:        bChassis.trim(),
        b_division:              bDivision.trim(),
        b_device_type:           newTrackerItem?.deviceType || '',
        b_iccid:                 newTrackerItem?.iccid || '',
        b_sim_number:            newTrackerItem?.simNumber || '',
        created_by:              currentUser?.id || null,
      };
      const res  = await fetch(`${apiBaseUrl}/service.php?action=rt`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed');
      alert(data.message || 'Removal & Transfer completed.');
      await onSaved();
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const fld = "w-full px-3 py-2.5 rounded-xl border-2 border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all text-sm";

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-4 text-white mb-4 shadow-md shadow-orange-200">
        <div className="flex items-center gap-3">
          <ArrowRight size={26} />
          <div>
            <h2 className="text-xl font-bold">Removal &amp; Transfer</h2>
            <p className="text-orange-100 text-sm">Move service from one vehicle to a new vehicle (same customer)</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-6">

        {/* Source Vehicle A */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Source Vehicle (A) — tracker to remove</p>
          <VehicleSearchField
            label="Select Source Vehicle *"
            customers={customers}
            selectedVehicle={vA}
            onSelect={v => { setVA(v); setMode(''); setNewImei(''); setSyncedInfo(null); }}
            onClear={() => { setVA(null); setMode(''); }}
          />
          {vA && (
            <div className="mt-3 bg-slate-50 rounded-xl p-3 flex flex-wrap gap-4 text-sm">
              <span className="text-slate-500">Customer: <strong className="text-slate-800">{vA.customerName}</strong></span>
              <span className="text-slate-500">Current IMEI: <strong className="font-mono text-slate-800">{vA.imei || 'None'}</strong></span>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-2">Note: Vehicle A must have its setup invoice created and all dues cleared, otherwise the server will block the transfer.</p>
        </div>

        {vA && (
        <>
          <hr className="border-slate-100" />

          {/* Tracker mode: same vs different IMEI */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Tracker — same ya different IMEI? *</p>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => { setMode('same'); setNewImei(vA.imei || ''); setInstallTechId(''); setSyncedInfo(null); }}
                className={`p-3 rounded-xl border-2 text-left transition-all ${mode === 'same' ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-slate-200 hover:bg-slate-50'}`}>
                <p className="font-bold text-slate-800 text-sm">🔁 Same IMEI</p>
                <p className="text-[11px] text-slate-500 mt-0.5">A's tracker is moved to B (directly A→B, it does not go to stock)</p>
              </button>
              <button type="button" onClick={() => { setMode('different'); setNewImei(''); setInstallTechId(''); setSyncedInfo(null); }}
                className={`p-3 rounded-xl border-2 text-left transition-all ${mode === 'different' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 hover:bg-slate-50'}`}>
                <p className="font-bold text-slate-800 text-sm">🔀 Different IMEI</p>
                <p className="text-[11px] text-slate-500 mt-0.5">New tracker on B (via Sync); A's tracker goes to the removal technician's stock (with health)</p>
              </button>
            </div>
          </div>

          {mode && (
          <>
          <hr className="border-slate-100" />

          {/* New Vehicle B */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">New Vehicle (B) — for {vA.customerName}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Registration (VRN) * <span className="text-slate-400 font-normal">— enter the number and press Sync</span></label>
                <div className="flex gap-2">
                  <input value={bReg} onChange={e => { setBReg(e.target.value); setSyncedInfo(null); setNewImei(''); }} placeholder="e.g. ABC-123" className={fld} />
                  <button type="button" onClick={handleSyncB} disabled={syncing || !bReg.trim()}
                    className={`shrink-0 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5 ${bReg.trim() && !syncing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-300 cursor-not-allowed'}`}>
                    <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync'}
                  </button>
                </div>
                {syncedInfo && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs text-blue-800 flex flex-wrap gap-x-4 gap-y-1">
                    {mode === 'different' ? (
                      <>
                        <span>IMEI: <b className="font-mono">{syncedInfo.imei}</b></span>
                        {syncedInfo.notIssued
                          ? <span className="text-red-700 font-semibold">⚠ This IMEI is not issued to any technician — issue it to a technician first.</span>
                          : syncedInfo.techName
                            ? <span>Install technician: <b>{syncedInfo.techName}</b></span>
                            : <span className="text-amber-700">No technician holds this IMEI in stock — select one manually below.</span>}
                      </>
                    ) : (
                      <span>Engine &amp; Chassis synced from GPS. Tracker = A's IMEI ({vA.imei || '—'}).</span>
                    )}
                  </div>
                )}
              </div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Make</label>
                <SearchableSelect value={bMake} options={bMakeOptions} onChange={val => { setBMake(val); setBModel(''); }} placeholder="Search make…" />
              </div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Model</label>
                <SearchableSelect value={bModel} options={bModelOptions} onChange={setBModel} placeholder={bMake ? 'Search model…' : 'Select make first'} disabled={!bMake} />
              </div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Engine No</label><input value={bEngine} onChange={e => setBEngine(e.target.value)} className={fld} /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Chassis No</label><input value={bChassis} onChange={e => setBChassis(e.target.value)} className={fld} /></div>
              <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1.5">Division</label>
                <select value={bDivision} onChange={e => setBDivision(e.target.value)} className={fld}>
                  <option value="">— Select division —</option>
                  {bDivisions.map(d => <option key={d.id} value={d.division_name}>{d.division_name}</option>)}
                </select>
              </div>
            </div>
            <p className="text-[11px] text-emerald-600 mt-2">B inherits AMC, setup charges and service-upto from A (no new setup invoice is created).</p>
          </div>

          <hr className="border-slate-100" />

          {/* Removal side */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Removal (Vehicle A)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Removal Technician *</label>
                <EmployeeSearchSelect employees={employees} value={removalTechId} onChange={setRemovalTechId} activeOnly
                  placeholder="Search technician by name or code…" />
                <p className="text-[11px] text-slate-400 mt-1">{mode === 'different' ? "The tracker removed from A goes to this technician's stock." : 'This technician removed the tracker from A and fitted it on B (pick a different install technician below if needed).'}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Removal Tech Amount</label>
                <div className="relative"><span className="absolute left-3 top-3 text-slate-400 text-sm font-semibold">Rs.</span>
                  <input type="number" min="0" value={removalAmount} onChange={e => setRemovalAmount(e.target.value)} className={fld + ' pl-10'} /></div>
              </div>
              {mode === 'different' && (
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Removed Tracker Health</label>
                <select value={oldHealth} onChange={e => setOldHealth(e.target.value)} className={fld + ' sm:w-64'}>
                  <option value="active">OK / Healthy (reusable)</option>
                  <option value="faulty">Faulty (not reusable)</option>
                  <option value="damaged">Damaged</option>
                  <option value="lost">Lost</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">If the health is not OK, the technician will not be able to install it again.</p>
              </div>
              )}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Install side */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Install (Vehicle B)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tracker IMEI on B *{mode === 'same' ? <span className="text-slate-400 font-normal"> — same IMEI as A (reused)</span> : <span className="text-slate-400 font-normal"> — filled by Sync</span>}</label>
                {mode === 'same'
                  ? <input value={vA.imei || ''} readOnly className={fld + ' font-mono bg-slate-50 text-slate-500 cursor-not-allowed'} />
                  : <input value={newImei} onChange={e => setNewImei(e.target.value)} placeholder="Press Sync, or enter the IMEI" className={fld + ' font-mono'} />}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Install Technician *</label>
                <EmployeeSearchSelect employees={employees} value={installTechId} onChange={setInstallTechId} activeOnly
                  placeholder="Search technician by name or code…" />
                <p className="text-[11px] text-slate-400 mt-1">{mode === 'different' ? 'Auto-selected from the IMEI on Sync.' : 'Technician who fits the tracker on B.'}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Install Tech Amount</label>
                <div className="relative"><span className="absolute left-3 top-3 text-slate-400 text-sm font-semibold">Rs.</span>
                  <input type="number" min="0" value={installAmount} onChange={e => setInstallAmount(e.target.value)} className={fld + ' pl-10'} /></div>
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Customer charge + date + reason */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Charge &amp; Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">R&amp;T Amount <span className="text-slate-400 font-normal">(customer) — 0 if free</span></label>
                <div className="relative"><span className="absolute left-3 top-3 text-slate-400 text-sm font-semibold">Rs.</span>
                  <input type="number" min="0" value={rtAmount} onChange={e => setRtAmount(e.target.value)} className={fld + ' pl-10'} /></div>
                {parseFloat(rtAmount) > 0
                  ? <p className="text-xs text-green-600 mt-1">✓ Transfer invoice Rs. {parseFloat(rtAmount).toLocaleString()} (Vehicle B)</p>
                  : <p className="text-xs text-slate-400 mt-1">No customer invoice</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">R&amp;T Date *</label>
                <input type="date" value={rtDate} onChange={e => setRtDate(e.target.value)} className={fld} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Reason / Remarks</label>
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Optional notes" className={fld + ' resize-none'} />
              </div>
            </div>
          </div>
          </>
          )}
        </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={handleSubmit} disabled={!canSubmit || saving}
            className={`px-5 py-2.5 rounded-xl text-white font-semibold text-sm flex items-center gap-2 transition-all ${canSubmit && !saving ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:shadow-md hover:shadow-orange-200' : 'bg-slate-300 cursor-not-allowed'}`}>
            {saving ? 'Processing…' : <><Check size={16} /> Complete Removal &amp; Transfer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function RedoForm({ customers, employees, inventory, apiBaseUrl, onSaved, onCancel }) {
  const [selVehicle,   setSelVehicle]   = React.useState(null);
  const [redoType,     setRedoType]     = React.useState('');
  const [technicianId, setTechnicianId] = React.useState('');
  const [techSearch,   setTechSearch]   = React.useState('');
  const [showTechList, setShowTechList] = React.useState(false);
  const [newImei,      setNewImei]      = React.useState('');
  const [imeiSearch,   setImeiSearch]   = React.useState('');
  const [reason,       setReason]       = React.useState('');
  const [redoAmount,   setRedoAmount]   = React.useState('0');
  const [techAmount,   setTechAmount]   = React.useState('0');
  const [redoDate,     setRedoDate]     = React.useState(new Date().toISOString().split('T')[0]);
  const [saving,       setSaving]       = React.useState(false);

  const selectedTech  = employees.find(e => String(e.id) === String(technicianId));
  const techStock     = inventory.filter(item =>
    item.isIssued && String(item.issuedTo) === String(technicianId) && item.status !== 'Installed'
  );
  const filteredStock = techStock.filter(item =>
    item.imei.toLowerCase().includes(imeiSearch.toLowerCase())
  );
  const filteredTechs = employees.filter(e =>
    e.name.toLowerCase().includes(techSearch.toLowerCase()) ||
    (e.designation || '').toLowerCase().includes(techSearch.toLowerCase())
  );

  const step1Done = selVehicle && redoType;
  const step2Done = step1Done && technicianId && reason &&
    (redoType === 'refitting' || (redoType === 'tracker_change' && newImei));

  const handleSubmit = async () => {
    if (!step2Done) { alert('Please fill all required fields.'); return; }
    setSaving(true);
    try {
      const payload = {
        vehicle_id:      selVehicle.vehicleId,
        vehicle_no:      selVehicle.vehicleNo,
        customer_id:     selVehicle.customerId,
        customer_name:   selVehicle.customerName,
        redo_type:       redoType,
        old_imei:        selVehicle.imei || '',
        new_imei:        redoType === 'tracker_change' ? newImei : (selVehicle.imei || ''),
        technician_id:   parseInt(technicianId),
        technician_name: selectedTech?.name || '',
        redo_amount:     parseFloat(redoAmount) || 0,
        tech_amount:     parseFloat(techAmount) || 0,
        reason,
        redo_date:       redoDate,
      };
      const res  = await fetch(`${apiBaseUrl}/redo.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      const msg = data.data?.invoice_number
        ? `REDO saved!\nInvoice created: ${data.data.invoice_number}`
        : 'REDO saved successfully! No invoice (amount = 0).';
      alert(msg);
      await onSaved();
    } catch(e) {
      alert('Failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const steps = ['Vehicle & Type', 'Details & Charges', 'Confirm'];
  const currentStep = step2Done ? 3 : (step1Done ? 2 : 1);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 text-white mb-4 shadow-md shadow-red-200">
        <div className="flex items-center gap-3 mb-4">
          <RefreshCw size={28} />
          <div>
            <h2 className="text-xl font-bold">New REDO</h2>
            <p className="text-red-100 text-sm">Tracker change or re-fitting</p>
          </div>
        </div>
        {/* Step indicators */}
        <div className="flex items-center gap-2">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                currentStep > i + 1 ? 'bg-white/30 text-white' :
                currentStep === i + 1 ? 'bg-white text-red-600' :
                'bg-white/10 text-red-200'
              }`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                  currentStep > i + 1 ? 'bg-white/40 text-white' :
                  currentStep === i + 1 ? 'bg-red-500 text-white' :
                  'bg-white/10 text-red-200'
                }`}>{currentStep > i + 1 ? '✓' : i + 1}</span>
                {s}
              </div>
              {i < steps.length - 1 && <div className="flex-1 h-0.5 bg-white/20 rounded" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-6">

        {/* Step 1 — Vehicle & Type */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Step 1 — Vehicle & REDO Type</p>
          <VehicleSearchField
            label="Select Vehicle *"
            customers={customers}
            selectedVehicle={selVehicle}
            onSelect={v => { setSelVehicle(v); setNewImei(''); setImeiSearch(''); }}
            onClear={() => { setSelVehicle(null); setRedoType(''); setNewImei(''); }}
          />
          {selVehicle && (
            <div className="mt-3 bg-slate-50 rounded-xl p-3 flex flex-wrap gap-4 text-sm">
              <span className="text-slate-500">Customer: <strong className="text-slate-800">{selVehicle.customerName}</strong></span>
              <span className="text-slate-500">Current IMEI: <strong className="font-mono text-slate-800">{selVehicle.imei || 'None'}</strong></span>
            </div>
          )}
        </div>

        {selVehicle && (
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">REDO Type *</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => { setRedoType('tracker_change'); setNewImei(''); }}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  redoType === 'tracker_change'
                    ? 'border-orange-500 bg-orange-50 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="text-xl mb-2">🔄</div>
                <p className="font-bold text-slate-800">Tracker Change</p>
                <p className="text-xs text-slate-500 mt-1">Old IMEI removed, new IMEI installed from technician stock</p>
              </button>
              <button
                onClick={() => { setRedoType('refitting'); setNewImei(''); }}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  redoType === 'refitting'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="text-xl mb-2">🔧</div>
                <p className="font-bold text-slate-800">Re-fitting</p>
                <p className="text-xs text-slate-500 mt-1">Same IMEI removed and re-installed on the same vehicle</p>
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Details */}
        {step1Done && (
          <>
            <hr className="border-slate-100" />
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Step 2 — Details & Charges</p>

              {/* Technician */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Technician *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 text-slate-400" size={16} />
                  <input
                    type="text"
                    value={technicianId && !showTechList ? (selectedTech?.name || '') : techSearch}
                    onChange={e => {
                      setTechSearch(e.target.value);
                      setShowTechList(true);
                      if (!e.target.value) { setTechnicianId(''); setNewImei(''); setImeiSearch(''); }
                    }}
                    onFocus={() => setShowTechList(true)}
                    placeholder="Search technician by name..."
                    className="w-full pl-9 pr-10 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all text-sm"
                  />
                  {technicianId && (
                    <button
                      onClick={() => { setTechnicianId(''); setTechSearch(''); setNewImei(''); setImeiSearch(''); setShowTechList(false); }}
                      className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                  {showTechList && techSearch && filteredTechs.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border-2 border-slate-200 rounded-xl shadow-md max-h-48 overflow-y-auto">
                      {filteredTechs.map(e => (
                        <button
                          key={e.id}
                          onClick={() => {
                            setTechnicianId(String(e.id));
                            setTechSearch('');
                            setShowTechList(false);
                            setNewImei('');
                            setImeiSearch('');
                          }}
                          className="w-full text-left px-4 py-3 text-sm hover:bg-red-50 transition-colors flex items-center justify-between border-b border-slate-50 last:border-0"
                        >
                          <span className="font-semibold text-slate-800">{e.name}</span>
                          {e.designation && <span className="text-xs text-slate-400">{e.designation}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {technicianId && selectedTech && (
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm text-red-700 flex items-center gap-2">
                    <Check size={16} />
                    <span className="font-semibold">{selectedTech.name}</span>
                    {selectedTech.designation && <span className="text-xs text-red-400">({selectedTech.designation})</span>}
                  </div>
                )}
              </div>

              {/* New IMEI — tracker_change only */}
              {redoType === 'tracker_change' && technicianId && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    New IMEI *
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      {selectedTech?.name}'s stock — {techStock.length} device{techStock.length !== 1 ? 's' : ''} available
                    </span>
                  </label>
                  {techStock.length === 0 ? (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                      ⚠️ No devices in {selectedTech?.name}'s stock. Issue a device to this technician first.
                    </div>
                  ) : (
                    <>
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-3.5 text-slate-400" size={16} />
                        <input
                          value={imeiSearch}
                          onChange={e => setImeiSearch(e.target.value)}
                          placeholder="Search IMEI..."
                          className="w-full pl-9 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all text-sm"
                        />
                      </div>
                      <div className="max-h-40 overflow-y-auto border-2 border-slate-200 rounded-xl divide-y divide-slate-100">
                        {filteredStock.map(item => (
                          <button
                            key={item.id}
                            onClick={() => setNewImei(item.imei)}
                            className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 transition-colors flex items-center justify-between ${
                              newImei === item.imei ? 'bg-red-50 border-l-4 border-red-500' : ''
                            }`}
                          >
                            <span className="font-mono font-semibold text-slate-800">{item.imei}</span>
                            <span className="text-xs text-slate-400">{item.device_type || 'Device'}</span>
                          </button>
                        ))}
                        {filteredStock.length === 0 && (
                          <div className="px-4 py-3 text-sm text-slate-400 text-center">No match found</div>
                        )}
                      </div>
                      {newImei && (
                        <div className="mt-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2 text-sm text-green-700 flex items-center gap-2">
                          <Check size={16} /> Selected: <span className="font-mono font-bold">{newImei}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Reason */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Reason / Remarks *</label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Why is this REDO being done?"
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all resize-none text-sm"
                />
              </div>

              {/* Charges */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    REDO Amount <span className="text-slate-400 font-normal text-xs">(customer) — 0 if free</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                    <input
                      type="number" min="0" value={redoAmount}
                      onChange={e => setRedoAmount(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all text-sm"
                    />
                  </div>
                  {parseFloat(redoAmount) > 0
                    ? <p className="text-xs text-green-600 mt-1">✓ Invoice will be created for Rs. {parseFloat(redoAmount).toLocaleString()}</p>
                    : <p className="text-xs text-slate-400 mt-1">No invoice will be created</p>
                  }
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Technician Amount <span className="text-slate-400 font-normal text-xs">— to be paid later</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                    <input
                      type="number" min="0" value={techAmount}
                      onChange={e => setTechAmount(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">REDO Date *</label>
                <input
                  type="date" value={redoDate}
                  onChange={e => setRedoDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none transition-all text-sm"
                />
              </div>
            </div>

            {/* Step 3 — Confirm summary */}
            {step2Done && (
              <>
                <hr className="border-slate-100" />
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Step 3 — Confirm</p>
                  <div className="bg-slate-50 rounded-xl p-5 space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div><p className="text-slate-400 text-xs mb-0.5">Vehicle</p><p className="font-semibold text-slate-800">{selVehicle.vehicleNo}</p></div>
                      <div><p className="text-slate-400 text-xs mb-0.5">Customer</p><p className="font-semibold text-slate-800">{selVehicle.customerName}</p></div>
                      <div><p className="text-slate-400 text-xs mb-0.5">Type</p>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${redoType === 'tracker_change' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {redoType === 'tracker_change' ? '🔄 Tracker Change' : '🔧 Re-fitting'}
                        </span>
                      </div>
                      <div><p className="text-slate-400 text-xs mb-0.5">Technician</p><p className="font-semibold text-slate-800">{selectedTech?.name}</p></div>
                      <div><p className="text-slate-400 text-xs mb-0.5">Old IMEI</p><p className="font-mono text-slate-700 text-xs">{selVehicle.imei || '—'}</p></div>
                      <div><p className="text-slate-400 text-xs mb-0.5">New IMEI</p>
                        <p className="font-mono text-xs">
                          {redoType === 'tracker_change'
                            ? <span className="text-slate-800 font-bold">{newImei}</span>
                            : <span className="text-blue-600">Same — {selVehicle.imei}</span>}
                        </p>
                      </div>
                      <div><p className="text-slate-400 text-xs mb-0.5">REDO Amount</p>
                        <p className={`font-bold ${parseFloat(redoAmount) > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                          {parseFloat(redoAmount) > 0 ? `Rs. ${parseFloat(redoAmount).toLocaleString()} — invoice will be created` : 'Free'}
                        </p>
                      </div>
                      <div><p className="text-slate-400 text-xs mb-0.5">Tech Amount</p>
                        <p className="font-semibold text-slate-800">
                          {parseFloat(techAmount) > 0 ? `Rs. ${parseFloat(techAmount).toLocaleString()}` : '—'}
                        </p>
                      </div>
                    </div>
                    <div><p className="text-slate-400 text-xs mb-0.5">Reason</p><p className="text-slate-700">{reason}</p></div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !step2Done}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold hover:shadow-md hover:shadow-red-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            <RefreshCw size={16} className={saving ? 'animate-spin' : ''} />
            {saving ? 'Saving...' : 'Save REDO'}
          </button>
        </div>
      </div>
    </div>
  );
}

// REDO FORM
// ─────────────────────────────────────────────────────────────────────────────
function CRedoForm({ customers, employees, onSaved, onCancel, prefillVehicle }) {
  const [selVehicle,   setSelVehicle]   = React.useState(prefillVehicle || null);
  const [technicianId, setTechnicianId] = React.useState('');
  const [priority,     setPriority]     = React.useState('medium');
  const [installDate,  setInstallDate]  = React.useState('');
  const [activityCost, setActivityCost] = React.useState('');
  const [notes,        setNotes]        = React.useState('');
  const [saving,       setSaving]       = React.useState(false);

  const handleSubmit = async () => {
    if (!selVehicle)  { alert('Please select the vehicle.'); return; }
    if (!installDate) { alert('Please set the redo date.'); return; }
    if (activityCost !== '' && parseFloat(activityCost) < 0) { alert('Activity cost cannot be negative.'); return; }

    const payload = {
      submitted_by:    'Staff',
      customer_id:     selVehicle.customerId,
      customer_name:   selVehicle.customerName,
      vehicle_id:      String(selVehicle.vehicleId),
      vehicle_name:    selVehicle.vehicleNo,
      vehicle_id_from: selVehicle.vehicleId,
      imei_from:       selVehicle.imei || '',
      category:        'redo',
      subject:         `Redo - ${selVehicle.vehicleNo} - ${selVehicle.customerName}`,
      detail:          notes.trim() || `Redo for vehicle ${selVehicle.vehicleNo}. Technician will determine if IMEI needs replacement on site.`,
      priority,
      contact:         selVehicle.contact,
      source:          'crm',
      install_date:    installDate,
      install_city:    selVehicle.city || '',
      activity_cost:   parseFloat(activityCost) || 0,
    };
    if (technicianId) payload.assigned_to = parseInt(technicianId);

    setSaving(true);
    try { await onSaved(payload); }
    catch (e) { alert('Failed: ' + e.message); setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">
      <CFormHeader title="New Redo" subtitle="Re-fit or replace tracker on same vehicle" color="bg-cyan-600" onCancel={onCancel} />
      <div className="p-4 space-y-6">
        <div>
          <p className={frmSection}>Vehicle</p>
          <VehicleSearchField label="Select Vehicle *" customers={customers}
            selectedVehicle={selVehicle} onSelect={v => setSelVehicle(v)} onClear={() => setSelVehicle(null)} />
        </div>

        {selVehicle && (
          <div>
            <p className={frmSection}>Redo Details</p>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className={frmLabel}>Redo Date *</label>
                <input type="date" value={installDate} onChange={e => setInstallDate(e.target.value)} className={frmInput} />
              </div>
              <div>
                <label className={frmLabel}>Activity Cost (Rs.) <span className="text-slate-400 font-normal">— 0 allowed</span></label>
                <div className="relative">
                  <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                  <input type="number" min="0" value={activityCost} onChange={e => setActivityCost(e.target.value)} placeholder="0"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-cyan-500 outline-none transition-all text-slate-800 text-sm" />
                </div>
                <p className="text-xs text-slate-400 mt-1">A Service invoice will be created on completion.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">
                  Current IMEI
                  <span className="ml-2 bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded font-normal">auto-filled</span>
                </label>
                <div className={frmDisabled}>{selVehicle.imei || 'No IMEI on record'}</div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">
                  IMEI After Redo
                  <span className="ml-2 bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded font-normal">Technician decides on site</span>
                </label>
                <div className={frmDisabled}>Same or replaced — technician fills in app</div>
              </div>
            </div>

            {/* Info banner */}
            <div className="mt-4 bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <AlertCircle size={14} className="text-cyan-500 mt-0.5 shrink-0"/>
              <p className="text-xs text-cyan-700">
                The technician will decide on site whether to re-fit the same tracker or replace it with a new one, and will record this in the technician app.
              </p>
            </div>

            {/* Testing parameters */}
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                Testing Parameters
                <span className="bg-amber-100 text-amber-600 px-2 py-0.5 rounded text-xs font-bold normal-case tracking-normal">Technician checks</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {TEST_PARAMS.map(t => (
                  <div key={t.key} className="flex items-center gap-2 bg-white border border-dashed border-slate-200 px-3 py-2 rounded-xl">
                    <div className="w-4 h-4 rounded border-2 border-slate-300 bg-white" />
                    <span className="text-xs font-semibold text-slate-400">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-5">
          <TechnicianPicker value={technicianId} onChange={setTechnicianId} employees={employees} />
          <PriorityPicker value={priority} onChange={setPriority} />
          <div className="col-span-2">
            <label className={frmLabel}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Describe the issue or reason for redo..." rows={3} maxLength={2000}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-cyan-500 outline-none transition-all resize-none text-slate-800 placeholder-slate-400 text-sm" />
          </div>
        </div>
      </div>
      <div className="flex gap-3 px-6 pb-6">
        <button onClick={onCancel} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-semibold hover:shadow-md hover:shadow-cyan-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          <Wrench size={16} className={saving ? 'animate-pulse' : ''}/>
          {saving ? 'Submitting...' : 'Submit Redo'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSFER FORM
// ─────────────────────────────────────────────────────────────────────────────
function CTransferForm({ customers, employees, onSaved, onCancel, prefillVehicle }) {
  const [selVehicleA,  setSelVehicleA]  = React.useState(prefillVehicle || null);
  const [vehicleBReg,  setVehicleBReg]  = React.useState('');
  const [imeiSame,     setImeiSame]     = React.useState(true);
  const [technicianId, setTechnicianId] = React.useState('');
  const [priority,     setPriority]     = React.useState('medium');
  const [installDate,  setInstallDate]  = React.useState('');
  const [activityCost, setActivityCost] = React.useState('');
  const [notes,        setNotes]        = React.useState('');
  const [saving,       setSaving]       = React.useState(false);

  const handleSubmit = async () => {
    if (!selVehicleA)        { alert('Please select Vehicle A.'); return; }
    if (!vehicleBReg.trim()) { alert('Please enter Vehicle B registration number.'); return; }
    if (!installDate)        { alert('Please set the transfer date.'); return; }
    if (activityCost !== '' && parseFloat(activityCost) < 0) { alert('Activity cost cannot be negative.'); return; }

    const vBReg = vehicleBReg.trim().toUpperCase();
    const detail = [
      `Transfer from Vehicle A: ${selVehicleA.vehicleNo} (IMEI: ${selVehicleA.imei || '—'})`,
      `Transfer to Vehicle B: ${vBReg}`,
      `IMEI: ${imeiSame ? 'Same IMEI transferred' : 'New IMEI to be assigned by technician'}`,
      `Service Upto (copied from A): ${selVehicleA.serviceUpto || '—'}`,
      notes.trim() ? `Notes: ${notes.trim()}` : '',
    ].filter(Boolean).join('\n');

    const payload = {
      submitted_by:    'Staff',
      customer_id:     selVehicleA.customerId,
      customer_name:   selVehicleA.customerName,
      vehicle_id:      String(selVehicleA.vehicleId),
      vehicle_name:    vBReg,
      vehicle_id_from: selVehicleA.vehicleId,
      imei_from:       selVehicleA.imei || '',
      imei_to:         imeiSame ? (selVehicleA.imei || '') : '',
      category:        'transfer',
      subject:         `Transfer \u2013 ${selVehicleA.vehicleNo} \u2192 ${vBReg} \u00B7 ${selVehicleA.customerName}`,
      detail,
      priority,
      contact:         selVehicleA.contact,
      source:          'crm',
      install_date:    installDate,
      install_city:    selVehicleA.city || '',
      activity_cost:   parseFloat(activityCost) || 0,
    };
    if (technicianId) payload.assigned_to = parseInt(technicianId);

    setSaving(true);
    try { await onSaved(payload); }
    catch (e) { alert('Failed: ' + e.message); setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200">
      <CFormHeader title="New Transfer" subtitle="Move tracker from Vehicle A to a new Vehicle B" color="bg-indigo-600" onCancel={onCancel} />
      <div className="p-4 space-y-6">
        <div>
          <p className={frmSection}>Vehicle A — Being Removed</p>
          <VehicleSearchField label="Select Vehicle A *" customers={customers}
            selectedVehicle={selVehicleA} onSelect={v => setSelVehicleA(v)} onClear={() => setSelVehicleA(null)} />
        </div>

        {selVehicleA && (
          <>
            <div className="grid grid-cols-3 gap-4 bg-slate-50 rounded-xl p-3 border border-slate-200">
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase mb-1">IMEI on Vehicle A</p>
                <p className="font-mono text-sm font-bold text-slate-700">{selVehicleA.imei || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase mb-1">Service Upto</p>
                <p className="text-sm font-bold text-slate-700">{selVehicleA.serviceUpto || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase mb-1">Customer</p>
                <p className="text-sm font-bold text-slate-700">{selVehicleA.customerName}</p>
              </div>
            </div>

            <div>
              <p className={frmSection}>Vehicle B — Receiving the Tracker</p>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className={frmLabel}>Vehicle B Registration No. *</label>
                  <input value={vehicleBReg} onChange={e => setVehicleBReg(e.target.value.toUpperCase())}
                    placeholder="e.g. LHR-1234"
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none transition-all text-slate-800 font-bold uppercase text-sm" />
                  <p className="text-xs text-slate-400 mt-1">New vehicle — created in CRM on completion.</p>
                </div>
                <div>
                  <label className={frmLabel}>IMEI for Vehicle B *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { val: true,  label: '\u2194 Same IMEI' },
                      { val: false, label: '\uD83D\uDD04 New IMEI'  },
                    ].map(opt => (
                      <button key={String(opt.val)} type="button" onClick={() => setImeiSame(opt.val)}
                        className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                          imeiSame === opt.val ? 'bg-indigo-100 text-indigo-700 border-indigo-400' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {!imeiSame && (
                    <p className="text-xs text-amber-600 font-semibold mt-1.5 flex items-center gap-1">
                      <AlertCircle size={11}/> Technician assigns new IMEI from inventory.
                    </p>
                  )}
                </div>
                <div>
                  <label className={frmLabel}>Transfer Date *</label>
                  <input type="date" value={installDate} onChange={e => setInstallDate(e.target.value)} className={frmInput} />
                </div>
                <div>
                  <label className={frmLabel}>Activity Cost (Rs.) <span className="text-slate-400 font-normal">— 0 allowed</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-3.5 text-slate-400 text-sm font-semibold">Rs.</span>
                    <input type="number" min="0" value={activityCost} onChange={e => setActivityCost(e.target.value)} placeholder="0"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none transition-all text-slate-800 text-sm" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">A Service invoice will be created on completion.</p>
                </div>
              </div>
              <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-start gap-2">
                <AlertCircle size={14} className="text-indigo-500 mt-0.5 shrink-0"/>
                <p className="text-xs text-indigo-700">
                  Vehicle B will inherit <strong>service_upto {selVehicleA.serviceUpto || '—'}</strong> from Vehicle A regardless of which IMEI is installed.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                Testing Parameters
                <span className="bg-amber-100 text-amber-600 px-2 py-0.5 rounded text-xs font-bold normal-case tracking-normal">Technician checks</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {TEST_PARAMS.map(t => (
                  <div key={t.key} className="flex items-center gap-2 bg-white border border-dashed border-slate-200 px-3 py-2 rounded-xl">
                    <div className="w-4 h-4 rounded border-2 border-slate-300 bg-white" />
                    <span className="text-xs font-semibold text-slate-400">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-5">
          <TechnicianPicker value={technicianId} onChange={setTechnicianId} employees={employees} />
          <PriorityPicker value={priority} onChange={setPriority} />
          <div className="col-span-2">
            <label className={frmLabel}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any special instructions for the technician..." rows={3} maxLength={2000}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-indigo-500 outline-none transition-all resize-none text-slate-800 placeholder-slate-400 text-sm" />
          </div>
        </div>
      </div>
      <div className="flex gap-3 px-6 pb-6">
        <button onClick={onCancel} className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-semibold hover:shadow-md hover:shadow-indigo-200 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          <Wrench size={16} className={saving ? 'animate-pulse' : ''}/>
          {saving ? 'Submitting...' : 'Submit Transfer'}
        </button>
      </div>
    </div>
  );
}

// ===============================================
// NR VEHICLES MODULE
// Gets GPS last_update from reconciliation.php
// (same endpoint Reconciliation tab uses, which
//  already fetches live data from Traccar/GPSWox)
// ===============================================

function NRVehiclesModule({ customers, apiBaseUrl, setActiveModule, setComplaintView, setComplaintPrefill }) {
  const [nrVehicles, setNrVehicles] = React.useState([]);
  const [filteredVehicles, setFilteredVehicles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [severityFilter, setSeverityFilter] = React.useState('all');
  const [expandedId, setExpandedId] = React.useState(null);
  const [followupForm, setFollowupForm] = React.useState({});
  const [submittingFollowup, setSubmittingFollowup] = React.useState({});
  const [loadingFollowups, setLoadingFollowups] = React.useState({});
  const [lastFetched, setLastFetched] = React.useState(null);
  const [categoryFilter, setCategoryFilter] = React.useState('all');

  const currentUser = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.full_name || u.username || 'Unknown';
    } catch { return 'Unknown'; }
  }, []);

  React.useEffect(() => { loadNrVehicles(); }, []);

  React.useEffect(() => {
    let filtered = nrVehicles;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        v.vehicle_number?.toLowerCase().includes(q) ||
        v.customer_name?.toLowerCase().includes(q) ||
        v.contact?.toLowerCase().includes(q) ||
        v.imei?.toLowerCase().includes(q)
      );
    }
    if (severityFilter !== 'all') {
      filtered = filtered.filter(v => {
        const h = v.hours_since_update;
        if (h === null) return false; // exclude null hours
        if (severityFilter === '24h') return h > 24 && h <= 48;
        if (severityFilter === '48h') return h > 48 && h <= 72;
        if (severityFilter === '72h') return h > 72;
        return true;
      });
    }
    setFilteredVehicles(filtered);
  }, [nrVehicles, searchTerm, severityFilter]);

  const getHours = (ts) => {
    if (!ts) return Infinity;
    return (Date.now() - new Date(ts).getTime()) / 3600000;
  };

  const fmtAgo = (ts) => {
    if (!ts) return 'Never';
    const h = getHours(ts);
    if (h < 1) return 'Just now';
    if (h < 24) return `${Math.floor(h)}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ${Math.floor(h % 24)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  const severity = (h) => {
    if (h <= 48) return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'bg-yellow-50 border-yellow-200', label: 'Warning' };
    if (h <= 72) return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'bg-orange-50 border-orange-200', label: 'High' };
    if (h <= 168) return { bg: 'bg-red-100', text: 'text-red-800', border: 'bg-red-50 border-red-200', label: 'Severe' };
    return { bg: 'bg-red-200', text: 'text-red-900', border: 'bg-red-50 border-red-300', label: 'Critical' };
  };

  const categorizeVehicles = (vehicles) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return {
      todaysFollowups: vehicles.filter(v => {
        // Try to get last_followup_date from field, or calculate from followups array
        let lastFollowupDate = v.last_followup_date;
        if (!lastFollowupDate && v.followups && v.followups.length > 0) {
          // Get the most recent scheduled followup date
          lastFollowupDate = v.followups[0]?.followup_date;
        }
        if (!lastFollowupDate) return false;
        
        const followupDate = new Date(lastFollowupDate);
        followupDate.setHours(0, 0, 0, 0);
        return followupDate.getTime() === today.getTime();
      }),
      missedFollowups: vehicles.filter(v => {
        let lastFollowupDate = v.last_followup_date;
        if (!lastFollowupDate && v.followups && v.followups.length > 0) {
          lastFollowupDate = v.followups[0]?.followup_date;
        }
        if (!lastFollowupDate) return false;
        
        const followupDate = new Date(lastFollowupDate);
        followupDate.setHours(0, 0, 0, 0);
        return followupDate.getTime() < today.getTime();
      }),
      upcomingFollowups: vehicles.filter(v => {
        let lastFollowupDate = v.last_followup_date;
        if (!lastFollowupDate && v.followups && v.followups.length > 0) {
          lastFollowupDate = v.followups[0]?.followup_date;
        }
        if (!lastFollowupDate) return false;
        
        const followupDate = new Date(lastFollowupDate);
        followupDate.setHours(0, 0, 0, 0);
        return followupDate.getTime() >= tomorrow.getTime();
      }),
      newFollowups: vehicles.filter(v => {
        // Check both last_followup_date field and followups array
        const hasFollowupDate = v.last_followup_date || (v.followups && v.followups.length > 0);
        return !hasFollowupDate;
      }),
    };
  };

  const loadNrVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/nr_vehicles.php`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'nr_vehicles.php returned failure');

      setLastFetched(json.generated_at || new Date().toLocaleString());

      const nrList = (json.vehicles || []).map(v => ({
        id: v.id,
        imei: v.imei,
        gps_imei: v.gps_imei || null,
        validation: v.validation || null, // imei_mismatch | name_not_found | null
        vehicle_number: v.vehicle_number || 'Unknown',
        customer_id: v.customer_id,
        customer_name: v.customer_name || 'Unknown',
        contact: v.contact || '—',
        city: v.city || '—',
        device_type: v.device_type || '—',
        nr_reason: v.nr_reason || 'unknown', // not_in_gps | never_reported | not_reporting
        last_update: v.last_update,
        hours_since_update: v.hours_since_update ?? null,
        followups: [],
      }));

      // Sort by hours offline (longest first, nulls last)
      nrList.sort((a, b) => {
        const ah = a.hours_since_update ?? Infinity;
        const bh = b.hours_since_update ?? Infinity;
        return bh - ah;
      });
      setNrVehicles(nrList);
      
      // Auto-load followups for all vehicles to enable categorization
      loadAllFollowups(nrList);
    } catch (err) {
      setError(err.message || 'Failed to load NR vehicles');
    } finally {
      setLoading(false);
    }
  };

  const loadAllFollowups = async (vehicles) => {
    try {
      const followupsMap = {};
      // Fetch followups for each vehicle
      await Promise.all(
        vehicles.map(async (vehicle) => {
          try {
            const res = await fetch(`${apiBaseUrl}/nr_followups.php?vehicle_id=${vehicle.id}`);
            const json = await res.json();
            if (json.success) {
              followupsMap[vehicle.id] = json.followups || [];
            }
          } catch (e) {
            followupsMap[vehicle.id] = [];
          }
        })
      );
      
      // Update all vehicles with their followups at once
      setNrVehicles(prev => prev.map(v => ({
        ...v, followups: followupsMap[v.id] || [],
      })));
    } catch (e) {
      // Silent error - categorization will just use "New Follow-Ups"
      console.error('Failed to load followups:', e);
    }
  };

  const loadFollowups = async (vehicleId) => {
    setLoadingFollowups(prev => ({ ...prev, [vehicleId]: true }));
    try {
      const res = await fetch(`${apiBaseUrl}/nr_followups.php?vehicle_id=${vehicleId}`);
      const json = await res.json();
      if (json.success) {
        setNrVehicles(prev => prev.map(v => v.id !== vehicleId ? v : {
          ...v, followups: json.followups,
        }));
      }
    } catch (e) { /* silent */ } finally {
      setLoadingFollowups(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  const handleExpandCard = (vehicleId) => {
    if (expandedId === vehicleId) {
      setExpandedId(null);
    } else {
      setExpandedId(vehicleId);
      loadFollowups(vehicleId);
    }
  };

  const handleAddFollowup = async (vehicleId) => {
    const form = followupForm[vehicleId] || {};
    const remarks = (form.remarks || '').trim();
    const followup_date = (form.date || '').trim();
    if (!remarks || !followup_date) return;
    setSubmittingFollowup(prev => ({ ...prev, [vehicleId]: true }));
    try {
      const res = await fetch(`${apiBaseUrl}/nr_followups.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vehicle_id: vehicleId, followup_date, remarks, created_by: currentUser }),
      });
      const json = await res.json();
      if (json.success) {
        setNrVehicles(prev => prev.map(v => v.id !== vehicleId ? v : {
          ...v, followups: [json.followup, ...(v.followups || [])],
        }));
        setFollowupForm(prev => ({ ...prev, [vehicleId]: { date: '', remarks: '' } }));
      }
    } finally {
      setSubmittingFollowup(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  const handleDeleteFollowup = async (vehicleId, followupId) => {
    try {
      await fetch(`${apiBaseUrl}/nr_followups.php?id=${followupId}`, { method: 'DELETE' });
      setNrVehicles(prev => prev.map(v => v.id !== vehicleId ? v : {
        ...v, followups: (v.followups || []).filter(f => f.id !== followupId),
      }));
    } catch (e) { /* silent */ }
  };

  const handleOpenComplaintForm = (vehicle) => {
    // Store the prefilled data for the redo form
    setComplaintPrefill({
      vehicleId: vehicle.id,
      vehicleNo: vehicle.vehicle_number,
      customerId: vehicle.customer_id,
      customerName: vehicle.customer_name,
      contact: vehicle.contact,
      city: vehicle.city,
      device_type: vehicle.device_type,
      imei: vehicle.imei,
      last_update: vehicle.last_update,
    });
    // Navigate to complaints module with 'new_redo' view
    setActiveModule('complaints');
    setComplaintView('new_redo');
  };

  const stats = {
    total: nrVehicles.length,
    w24: nrVehicles.filter(v => v.hours_since_update !== null && v.hours_since_update > 24 && v.hours_since_update <= 48).length,
    w48: nrVehicles.filter(v => v.hours_since_update !== null && v.hours_since_update > 48 && v.hours_since_update <= 72).length,
    w72: nrVehicles.filter(v => v.hours_since_update !== null && v.hours_since_update > 72).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <AlertCircle className="text-orange-500" size={24} />
          Not Responding Vehicles
        </h2>
        <button
          onClick={loadNrVehicles}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors font-semibold text-slate-600 shadow-sm text-sm"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-4">
        {/* Search Bar and Time Filters */}
        <div className="flex gap-3 items-center">
          <div className="w-1/3 relative">
            <Search size={16} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search vehicle, IMEI..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm"
            />
          </div>
          <div className="flex gap-3 flex-1">
            {[
              { key: 'all', label: 'All', count: nrVehicles.length },
              { 
                key: '24h', 
                label: '>24h', 
                count: nrVehicles.filter(v => v.hours_since_update !== null && v.hours_since_update > 24 && v.hours_since_update <= 48).length 
              },
              { 
                key: '48h', 
                label: '>48h', 
                count: nrVehicles.filter(v => v.hours_since_update !== null && v.hours_since_update > 48 && v.hours_since_update <= 72).length 
              },
              { 
                key: '72h', 
                label: '>72h', 
                count: nrVehicles.filter(v => v.hours_since_update !== null && v.hours_since_update > 72).length 
              },
            ].map(({ key, label, count }) => (
              <button key={key}
                onClick={() => setSeverityFilter(key === 'all' ? 'all' : key.slice(0, -1))}
                className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                  severityFilter === (key === 'all' ? 'all' : key.slice(0, -1))
                    ? 'bg-orange-100 text-orange-700 border-orange-300'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {label}
                <span className="text-xs bg-white/50 px-1.5 py-0.5 rounded font-mono">{count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Filter Buttons */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all', label: "All Follow-Ups", color: 'slate', count: nrVehicles.length },
            { key: 'todaysFollowups', label: "📅 Today's", color: 'blue', count: categorizeVehicles(nrVehicles).todaysFollowups.length },
            { key: 'missedFollowups', label: '⚠️ Missed', color: 'red', count: categorizeVehicles(nrVehicles).missedFollowups.length },
            { key: 'upcomingFollowups', label: '📈 Upcoming', color: 'green', count: categorizeVehicles(nrVehicles).upcomingFollowups.length },
            { key: 'newFollowups', label: '✨ New', color: 'purple', count: categorizeVehicles(nrVehicles).newFollowups.length },
          ].map(({ key, label, color, count }) => {
            const colorMap = {
              slate: { active: 'bg-slate-500 text-white border-slate-600', inactive: 'bg-slate-50 text-slate-700 border-slate-200' },
              blue: { active: 'bg-blue-500 text-white border-blue-600', inactive: 'bg-blue-50 text-blue-700 border-blue-200' },
              red: { active: 'bg-red-500 text-white border-red-600', inactive: 'bg-red-50 text-red-700 border-red-200' },
              green: { active: 'bg-green-500 text-white border-green-600', inactive: 'bg-green-50 text-green-700 border-green-200' },
              purple: { active: 'bg-purple-500 text-white border-purple-600', inactive: 'bg-purple-50 text-purple-700 border-purple-200' },
            };
            const isActive = categoryFilter === key;
            const colors = colorMap[color];
            
            return (
              <button
                key={key}
                onClick={() => setCategoryFilter(key)}
                className={`px-4 py-2 rounded-lg border font-semibold text-sm transition-all flex items-center gap-1.5 ${
                  isActive ? colors.active : colors.inactive
                }`}
              >
                {label}
                <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                  isActive ? 'bg-white/30 text-white' : 'bg-white/50'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3 items-start">
          <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">Failed to load GPS data</p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-16">
          <RefreshCw size={36} className="mx-auto text-orange-400 animate-spin mb-3" />
          <p className="text-slate-600 font-semibold">Fetching live GPS data from Traccar...</p>
          <p className="text-sm text-slate-400 mt-1">Using reconciliation.php to get last update times</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filteredVehicles.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Check size={48} className="mx-auto text-green-400 mb-3" />
          <p className="text-slate-700 font-bold text-lg">All Clear!</p>
          <p className="text-sm text-slate-400 mt-1">No active vehicles are NR right now</p>
        </div>
      )}

      {/* Categorized Vehicle Sections */}
      {!loading && filteredVehicles.length > 0 && (() => {
        const sections = categorizeVehicles(filteredVehicles);
        const sectionConfigs = [
          { key: 'todaysFollowups', label: "Today's Follow-ups", icon: '📅', color: 'border-blue-200 bg-blue-50', headerBg: 'bg-blue-100' },
          { key: 'missedFollowups', label: 'Missed Follow-ups', icon: '⚠️', color: 'border-red-200 bg-red-50', headerBg: 'bg-red-100' },
          { key: 'upcomingFollowups', label: 'Upcoming Follow-Ups', icon: '📈', color: 'border-green-200 bg-green-50', headerBg: 'bg-green-100' },
          { key: 'newFollowups', label: 'New Follow-Ups', icon: '✨', color: 'border-purple-200 bg-purple-50', headerBg: 'bg-purple-100' },
        ];

        return (
          <div className="space-y-6">
            {sectionConfigs.map(config => {
              const vehicles = sections[config.key];
              if (vehicles.length === 0) return null;
              if (categoryFilter !== 'all' && categoryFilter !== config.key) return null;

              return (
                <div key={config.key} className={`border-2 ${config.color} rounded-xl overflow-hidden`}>
                  {/* Section Header */}
                  <div className={`${config.headerBg} px-4 py-2 border-b-2 border-white/50 flex items-center gap-3`}>
                    <span className="text-xl">{config.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-slate-800">{config.label}</h3>
                    </div>
                    <span className="text-xs font-bold text-slate-600 bg-white px-2.5 py-1 rounded-full">
                      {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'}
                    </span>
                  </div>

                  {/* Section Vehicles */}
                  <div className="space-y-2 p-4">
                    {vehicles.map(vehicle => {
                      const h = vehicle.hours_since_update ?? Infinity;
                      const sev = severity(h);
                      const isExpanded = expandedId === vehicle.id;
                      const hoursDisplay = h === Infinity ? '—' : `${Math.round(h)}h`;

                      // Badge for nr_reason
                      const reasonBadges = {
                        not_in_gps: { bg: 'bg-red-100', text: 'text-red-800', label: 'Not in GPS' },
                        never_reported: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Never Reported' },
                        not_reporting: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Not Reporting' },
                      };
                      const reasonBadge = reasonBadges[vehicle.nr_reason] || { bg: 'bg-gray-100', text: 'text-gray-800', label: vehicle.nr_reason };

                      // Tile border/background should reflect the follow-up section
                      // (Today/Missed/Upcoming/New), not the offline-duration severity —
                      // otherwise almost every card ends up the same light-red tone.
                      const cardTint = {
                        todaysFollowups: 'bg-blue-50/50 border-blue-200',
                        missedFollowups: 'bg-red-50/50 border-red-200',
                        upcomingFollowups: 'bg-green-50/50 border-green-200',
                        newFollowups: 'bg-purple-50/50 border-purple-200',
                      }[config.key] || sev.border;

                      // Validation badge - IMEI mismatch has priority
                      let validationBadge = null;
                      if (vehicle.validation === 'imei_mismatch') {
                        validationBadge = { bg: 'bg-red-100', text: 'text-red-800', label: '⚠️ IMEI Mismatch' };
                      } else if (vehicle.validation === 'name_not_found') {
                        validationBadge = { bg: 'bg-red-100', text: 'text-red-800', label: '⚠️ Name Not Found' };
                      }

                      return (
                        <div key={vehicle.id} className={`border rounded-xl transition-all ${cardTint}`}>
                {/* Card Header (clean, clickable) — full details on expand */}
                <div
                  onClick={() => handleExpandCard(vehicle.id)}
                  className="p-4 cursor-pointer hover:bg-white/40 transition-colors"
                >
                  {/* Row 1: vehicle + badges  |  hours + actions */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h3 className="text-base font-bold text-slate-800">{vehicle.vehicle_number}</h3>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${sev.bg} ${sev.text}`}>{sev.label}</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${reasonBadge.bg} ${reasonBadge.text}`}>{reasonBadge.label}</span>
                      {validationBadge && (
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${validationBadge.bg} ${validationBadge.text}`}>{validationBadge.label}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {hoursDisplay !== '—' && (
                        <span className="text-xs font-semibold text-slate-500 bg-white px-2.5 py-1 rounded-full border border-slate-200 whitespace-nowrap">{hoursDisplay} offline</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOpenComplaintForm(vehicle); }}
                        className="p-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors flex items-center gap-1.5 text-xs font-semibold"
                        title="Add complaint for this vehicle"
                      >
                        <MessageCircle size={15} />
                        <span className="hidden sm:inline">Complaint</span>
                      </button>
                      <ChevronDown size={18} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Row 2: key info inline */}
                  <div className="flex items-center gap-x-5 gap-y-1 mt-2 text-sm text-slate-600 flex-wrap">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <User size={14} className="text-slate-400 shrink-0" />
                      <span className="font-semibold text-slate-700 truncate">{vehicle.customer_name}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Phone size={14} className="text-slate-400 shrink-0" />
                      <span className="font-semibold text-slate-700">{vehicle.contact}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={14} className="text-slate-400 shrink-0" />
                      <span className="text-slate-500">GPS {fmtAgo(vehicle.last_update)}</span>
                    </span>
                  </div>

                  {/* Row 3: last comment (rest on click) */}
                  {(() => {
                    const last = (vehicle.followups || [])[0];
                    if (loadingFollowups[vehicle.id] && !last) {
                      return <p className="mt-2.5 text-xs text-slate-400">Loading notes…</p>;
                    }
                    if (!last) {
                      return (
                        <div className="mt-2.5 text-xs text-slate-400 italic flex items-center gap-1.5">
                          <MessageSquare size={13} /> No follow-up yet — click to add
                        </div>
                      );
                    }
                    return (
                      <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-white/70 border border-slate-200 px-3 py-2">
                        <MessageSquare size={13} className="text-orange-500 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-slate-700 line-clamp-2">{last.remarks}</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {last.created_by || 'Unknown'}
                            {last.created_at ? ` · ${new Date(last.created_at).toLocaleDateString()}` : ''}
                            {(vehicle.followups || []).length > 1 ? ` · +${vehicle.followups.length - 1} more` : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Expanded Followups */}
                {isExpanded && (
                  <div className="border-t-2 p-5 space-y-4 bg-white bg-opacity-60">
                    {/* Vehicle details */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-slate-400 uppercase font-semibold mb-0.5">IMEI</p>
                        <p className="font-mono font-semibold text-slate-700 break-all">{vehicle.imei || '—'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 uppercase font-semibold mb-0.5">Device</p>
                        <p className="font-semibold text-slate-700">{vehicle.device_type}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 uppercase font-semibold mb-0.5">City</p>
                        <p className="font-semibold text-slate-700">{vehicle.city}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 uppercase font-semibold mb-0.5">Exact Last Update</p>
                        <p className="font-semibold text-slate-700">{vehicle.last_update ? new Date(vehicle.last_update).toLocaleString() : '—'}</p>
                      </div>
                    </div>

                    {/* IMEI Validation Alert */}
                    {vehicle.validation === 'imei_mismatch' && (
                      <div className="p-3 bg-red-50 border border-red-300 rounded-lg">
                        <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                          ⚠️ IMEI Validation Issue
                        </p>
                        <table className="text-xs w-full">
                          <tbody>
                            <tr className="border-b border-red-200">
                              <td className="text-red-700 font-semibold py-1 pr-3">CRM IMEI:</td>
                              <td className="font-mono text-red-900 py-1">{vehicle.imei}</td>
                            </tr>
                            <tr>
                              <td className="text-red-700 font-semibold py-1 pr-3">GPS IMEI:</td>
                              <td className="font-mono text-red-900 py-1">{vehicle.gps_imei}</td>
                            </tr>
                          </tbody>
                        </table>
                        <p className="text-red-700 text-xs mt-2 leading-relaxed">
                          The vehicle name matches GPS records, but the IMEI numbers don't match. This indicates the wrong device may be configured for this vehicle. Please verify and update the IMEI in CRM.
                        </p>
                      </div>
                    )}
                    {vehicle.validation === 'name_not_found' && (
                      <div className="p-3 bg-red-50 border border-red-300 rounded-lg">
                        <p className="text-xs font-bold text-red-700 mb-2 flex items-center gap-1.5">
                          ⚠️ Vehicle Not Found in GPS
                        </p>
                        <p className="text-red-700 text-xs leading-relaxed">
                          Vehicle registration "{vehicle.vehicle_number}" does not exist in GPS tracking system. Please verify the vehicle name or check if the GPS device is properly configured.
                        </p>
                      </div>
                    )}

                    {/* Add Followup Form */}
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                      <p className="text-xs font-bold text-slate-600 uppercase mb-3 tracking-wide flex items-center gap-1.5">
                        <MessageSquare size={12} /> Add Followup
                      </p>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Followup Date</label>
                          <input
                            type="date"
                            value={(followupForm[vehicle.id] || {}).date || ''}
                            onChange={e => setFollowupForm(prev => ({ ...prev, [vehicle.id]: { ...(prev[vehicle.id] || {}), date: e.target.value } }))}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Added By</label>
                          <div className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 font-semibold">
                            {currentUser}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <textarea
                          value={(followupForm[vehicle.id] || {}).remarks || ''}
                          onChange={e => setFollowupForm(prev => ({ ...prev, [vehicle.id]: { ...(prev[vehicle.id] || {}), remarks: e.target.value } }))}
                          placeholder="Enter followup notes, action taken, remarks..."
                          rows={2}
                          maxLength={500}
                          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-orange-400 outline-none text-sm resize-none"
                        />
                        <button
                          onClick={() => handleAddFollowup(vehicle.id)}
                          disabled={
                            submittingFollowup[vehicle.id] ||
                            !(followupForm[vehicle.id] || {}).remarks?.trim() ||
                            !(followupForm[vehicle.id] || {}).date
                          }
                          className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-40 transition-colors flex items-center gap-1.5 self-end"
                        >
                          <Plus size={15} /> {submittingFollowup[vehicle.id] ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>

                    {/* Followup History */}
                    {loadingFollowups[vehicle.id] ? (
                      <p className="text-xs text-slate-400 text-center py-2">Loading history...</p>
                    ) : (vehicle.followups || []).length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-slate-600 uppercase mb-2 tracking-wide">
                          History ({vehicle.followups.length})
                        </p>
                        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                          {vehicle.followups.map(f => (
                            <div key={f.id} className="bg-white rounded-lg p-3 border border-slate-200 flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1 flex-wrap">
                                  <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                                    📅 {f.followup_date}
                                  </span>
                                  <span className="text-xs font-semibold text-slate-500">
                                    👤 {f.created_by}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {new Date(f.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-700">{f.remarks}</p>
                              </div>
                              <button onClick={() => handleDeleteFollowup(vehicle.id, f.id)}
                                className="p-1 hover:bg-red-50 rounded transition-colors shrink-0">
                                <X size={13} className="text-red-400" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
    </div>
  );
}
